// @refresh reset
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link, useLocation, useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Search, Car, MapPin, Home, Route, Clock, Filter, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, CheckCircle, XCircle, AlertCircle, Calendar, MinusCircle, FileText, Download, Unlock, Lock, Compass, MoreVertical, MessageSquare, Pencil, Trash2, Send, Check, Phone, Truck, Scale, Ruler, X, Layers } from 'lucide-react';
import { useCity } from '../../contexts/CityContext';
import { useDateFilter } from '../../contexts/DateFilterContext';
import { getVehicleDetailsByName, getVehicleNamesByClass } from '../../utils/vehicleDataLoader';
import config from '../../config';
import {
  fetchPendingDriverRegistrationCount,
  updatePendingDriverRegistrationCount,
  isNewRegisteredRow,
} from '../../actions/pendingDriverRegistration';
import { useGoogleMaps } from '../../context/GoogleMapsProvider';
import { GoogleMap, Marker } from '@react-google-maps/api';
import CompleteRouteDetails from '../../components/VehicleDetails/CompleteRouteDetails';
import DriverPayTab from '../vehicles/list/DriverPayTab';
import DriverCallLogs from '../callLogs/DriverCallLogs';
import TransportersTab from './TransportersTab';
import TransporterDetailsModal from './TransporterDetailsModal';
import VoiceBotLiveLogs from './VoiceBotLiveLogs';
import PlacesAutocompleteInput from '../../components/PlacesAutocompleteInput';
import './VehicleDetails.css';

// A phone extension entry is either a legacy bare phone string, or a
// { phone, label } object (e.g. label "gpay no", "spouse no").
const extPhoneOf = (entry) => (typeof entry === 'string' ? entry : (entry && entry.phone) || '');
const extLabelOf = (entry) => (typeof entry === 'string' ? '' : (entry && entry.label) || '');

const normalizeModelQuery = (str) => {
  if (!str) return '';
  return String(str)
    .toLowerCase()
    .replace(/[-_./\\+]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

const matchesVehicleModel = (vehicle, query) => {
  if (!query) return true;
  const qNorm = normalizeModelQuery(query);
  const qTokens = qNorm.split(' ').filter(Boolean);
  if (qTokens.length === 0) return true;

  const vehicleModelText = [
    vehicle.model,
    vehicle.make,
    vehicle.vehicle_name,
    vehicle.vehicle_type,
    vehicle.body_type,
  ].filter(Boolean).map(normalizeModelQuery).join(' ');

  return qTokens.every(token => vehicleModelText.includes(token));
};

const nearTarget = (raw) => {
  const target = Number(raw);
  return raw !== '' && Number.isFinite(target) ? target : null;
};

const withinOne = (value, target) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) && Math.abs(numeric - target) <= 1;
};

const SEARCH_CATEGORIES = [
  {
    id: 'all',
    label: 'All Fields',
    shortLabel: 'All',
    placeholder: 'Search by vehicle ID, driver, phone, registration, model...',
    icon: Search,
    description: 'ID, driver name, phone, registration, model & location'
  },
  {
    id: 'model',
    label: 'Vehicle Model / Name',
    shortLabel: 'Model',
    placeholder: 'Search model (e.g. Tata Ace, Bolero Pickup, Dost)...',
    icon: Truck,
    description: 'Smart search with variations (tata ace, tata-ace, bolero...)'
  },
  {
    id: 'phone',
    label: 'Driver Phone',
    shortLabel: 'Phone',
    placeholder: 'Search driver phone number...',
    icon: Phone,
    description: 'Driver phone digits matching'
  },
  {
    id: 'tonnage',
    label: 'Tonnage (Weight)',
    shortLabel: 'Tonnage',
    placeholder: 'Filter by tonnage (e.g. 1.5, 2.5, 3.5 T)...',
    icon: Scale,
    description: 'Find vehicles with weight capacity within ±1T'
  },
  {
    id: 'length',
    label: 'Bed Length',
    shortLabel: 'Length',
    placeholder: 'Filter by length in ft (e.g. 8, 10, 14 ft)...',
    icon: Ruler,
    description: 'Find vehicles with bed length within ±1 ft'
  },
  {
    id: 'class',
    label: 'Vehicle Class',
    shortLabel: 'Class',
    placeholder: 'Filter by vehicle class (e.g. 3, 4, 5)...',
    icon: Layers,
    description: 'Filter vehicles by classification number or type'
  },
  {
    id: 'location',
    label: 'Base / Current Location',
    shortLabel: 'Location',
    placeholder: 'Filter by base or current location...',
    icon: MapPin,
    description: 'Base registered address or live city'
  },
  {
    id: 'route',
    label: 'Transporter Route',
    shortLabel: 'Route',
    placeholder: 'Search routes (e.g. Bangalore to Mangalore)...',
    icon: Route,
    description: 'Transporter routes by origin or destination'
  },
];

const EMPTY_REG_SPECS = {
  body_type: '',
  tonnage: '',
  bed_length: '',
  per_km_price: '',
  ready_for_long_trip: false,
  long_inter_district: false,
  long_inter_state: false,
  local: false,
  intra_state_permit: false,
  phone_extensions: [],
};

const compressRegistrationImage = (file, maxWidth = 1024, maxHeight = 1024, quality = 0.7) => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target.result;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;
        if (width > height) {
          if (width > maxWidth) {
            height = Math.round((height * maxWidth) / width);
            width = maxWidth;
          }
        } else if (height > maxHeight) {
          width = Math.round((width * maxHeight) / height);
          height = maxHeight;
        }
        canvas.width = width;
        canvas.height = height;
        canvas.getContext('2d').drawImage(img, 0, 0, width, height);
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              resolve(file);
              return;
            }
            resolve(new File([blob], file.name, { type: 'image/jpeg', lastModified: Date.now() }));
          },
          'image/jpeg',
          quality
        );
      };
      img.onerror = () => resolve(file);
    };
    reader.onerror = () => resolve(file);
  });
};

function specsPayloadFromForm(s) {
  return {
    body_type: (s.body_type || '').trim() || null,
    tonnage: s.tonnage === '' || s.tonnage == null ? null : Number(s.tonnage),
    bed_length: s.bed_length === '' || s.bed_length == null ? null : Number(s.bed_length),
    per_km_price: s.per_km_price === '' || s.per_km_price == null ? null : Number(s.per_km_price),
    ready_for_long_trip: Boolean(s.ready_for_long_trip),
    long_inter_district: Boolean(s.long_inter_district),
    long_inter_state: Boolean(s.long_inter_state),
    local: Boolean(s.local),
    intra_state_permit: Boolean(s.intra_state_permit),
    phone_extensions: Array.isArray(s.phone_extensions) ? s.phone_extensions : [],
  };
}

function specsFromRegistrationRow(row) {
  return {
    body_type: row.body_type || '',
    tonnage: row.tonnage != null && row.tonnage !== '' ? String(row.tonnage) : '',
    bed_length: row.bed_length != null && row.bed_length !== '' ? String(row.bed_length) : '',
    per_km_price: row.per_km_price != null && row.per_km_price !== '' ? String(row.per_km_price) : '',
    ready_for_long_trip: Boolean(row.ready_for_long_trip),
    long_inter_district: Boolean(row.long_inter_district),
    long_inter_state: Boolean(row.long_inter_state),
    local: Boolean(row.local),
    intra_state_permit: Boolean(row.intra_state_permit),
    phone_extensions: Array.isArray(row.phone_extensions) ? row.phone_extensions : [],
  };
}

function getRegTonAndLength(row) {
  if (!row) return { ton: null, len: null };
  let ton = row.tonnage != null && row.tonnage !== '' ? String(row.tonnage) : (row.registration_specs?.tonnage != null && row.registration_specs?.tonnage !== '' ? String(row.registration_specs.tonnage) : null);
  let len = row.bed_length != null && row.bed_length !== '' ? String(row.bed_length) : (row.registration_specs?.bed_length != null && row.registration_specs?.bed_length !== '' ? String(row.registration_specs.bed_length) : null);
  if (!ton && !len && row.comments) {
    const tonMatch = String(row.comments).match(/(\d+(?:\.\d+)?\s*(?:ton|tons|t|kg))/i);
    const lenMatch = String(row.comments).match(/(\d+(?:\.\d+)?\s*(?:ft|feet|m\b))/i);
    if (tonMatch) ton = tonMatch[1];
    if (lenMatch) len = lenMatch[1];
  }
  return { ton, len };
}

const BODY_TYPE_OPTIONS = ['Open', 'Closed', 'Container'];

function selectedBodyType(value) {
  const raw = String(value || '').trim();
  const match = BODY_TYPE_OPTIONS.find((option) => option.toLowerCase() === raw.toLowerCase());
  return match || raw;
}

function RegistrationSpecsFields({ specs, setSpecs, extValue, setExtValue, extLabelValue, setExtLabelValue }) {
  const inputStyle = {
    width: '100%',
    padding: '10px',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    fontSize: '14px',
    boxSizing: 'border-box',
  };
  const addExt = () => {
    const val = (extValue || '').trim();
    if (!val || !/^\+?\d{8,15}$/.test(val)) return;
    if ((specs.phone_extensions || []).some((e) => extPhoneOf(e) === val)) return;
    const label = (extLabelValue || '').trim();
    setSpecs({ ...specs, phone_extensions: [...(specs.phone_extensions || []), { phone: val, label: label || null }] });
    setExtValue('');
    if (setExtLabelValue) setExtLabelValue('');
  };

  return (
    <div style={{
      marginBottom: '16px',
      maxWidth: '720px',
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
      padding: 14,
      border: '1px solid #bfdbfe',
      borderRadius: 10,
      background: '#f8fbff',
    }}>
      <div style={{ fontWeight: 700, color: '#1e3a5f', fontSize: 14 }}>Vehicle specs (optional — copied on onboard)</div>
      <div className="registration-specs-grid" style={{ display: 'grid', gap: '10px' }}>
        <div>
          <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: 4, color: '#374151' }}>Body type</label>
          <select
            value={specs.body_type || ''}
            onChange={(e) => setSpecs({ ...specs, body_type: e.target.value })}
            style={{ ...inputStyle, background: '#fff' }}
          >
            <option value="">Select body type</option>
            {specs.body_type && !BODY_TYPE_OPTIONS.includes(specs.body_type) && (
              <option value={specs.body_type}>{specs.body_type}</option>
            )}
            {BODY_TYPE_OPTIONS.map((option) => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: 4, color: '#374151' }}>Tonnage (ton) <span style={{ fontWeight: 400, color: '#9ca3af' }}>(optional)</span></label>
          <input
            type="number"
            step="0.01"
            value={specs.tonnage}
            onChange={(e) => setSpecs({ ...specs, tonnage: e.target.value })}
            placeholder="e.g. 1.5"
            style={inputStyle}
          />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: 4, color: '#374151' }}>Bed length <span style={{ fontWeight: 400, color: '#9ca3af' }}>(optional)</span></label>
          <input
            type="number"
            step="0.01"
            value={specs.bed_length}
            onChange={(e) => setSpecs({ ...specs, bed_length: e.target.value })}
            placeholder="e.g. 7.5"
            style={inputStyle}
          />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: 4, color: '#374151' }}>Per KM price (₹) <span style={{ fontWeight: 400, color: '#9ca3af' }}>(optional)</span></label>
          <input
            type="number"
            step="0.01"
            value={specs.per_km_price}
            onChange={(e) => setSpecs({ ...specs, per_km_price: e.target.value })}
            placeholder="e.g. 15.50"
            style={inputStyle}
          />
        </div>
      </div>
      <div>
        <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: 4, color: '#374151' }}>Phone extensions</label>
        {(specs.phone_extensions || []).length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 6 }}>
            {(specs.phone_extensions || []).map((entry, idx) => {
              const phone = extPhoneOf(entry);
              const label = extLabelOf(entry);
              return (
                <span key={`${phone}-${idx}`} style={{ fontSize: 12, background: '#f3f4f6', borderRadius: 6, padding: '3px 8px' }}>
                  {phone}{label ? ` — ${label}` : ''}
                  <button
                    type="button"
                    onClick={() => setSpecs({
                      ...specs,
                      phone_extensions: specs.phone_extensions.filter((e) => extPhoneOf(e) !== phone),
                    })}
                    style={{ marginLeft: 6, border: 'none', background: 'none', color: '#dc2626', cursor: 'pointer' }}
                  >
                    ×
                  </button>
                </span>
              );
            })}
          </div>
        )}
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            value={extValue}
            onChange={(e) => setExtValue(e.target.value)}
            placeholder="e.g. 919900000000"
            style={{ ...inputStyle, flex: 1 }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addExt();
              }
            }}
          />
          <input
            value={extLabelValue || ''}
            onChange={(e) => setExtLabelValue && setExtLabelValue(e.target.value)}
            placeholder="Label (optional) e.g. gpay no"
            style={{ ...inputStyle, flex: 1 }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addExt();
              }
            }}
          />
          <button type="button" onClick={addExt} style={{ padding: '10px 14px', border: 'none', background: '#6366f1', color: '#fff', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}>
            Add
          </button>
        </div>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px 16px', fontSize: 13, color: '#374151' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={Boolean(specs.intra_state_permit)}
            onChange={(e) => {
              const checked = e.target.checked;
              setSpecs({
                ...specs,
                intra_state_permit: checked,
                ready_for_long_trip: checked ? true : specs.ready_for_long_trip,
                long_inter_state: checked ? true : specs.long_inter_state,
              });
            }}
          />
          All India permit
        </label>
        {[
          ['ready_for_long_trip', 'Ready for long trip'],
          ['long_inter_district', 'Long intra-district'],
          ['long_inter_state', 'Long intra-state'],
          ['local', 'Local'],
        ].map(([key, label]) => (
          <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={Boolean(specs[key])}
              onChange={(e) => setSpecs({ ...specs, [key]: e.target.checked })}
            />
            {label}
          </label>
        ))}
      </div>
    </div>
  );
}

// ─── Single document card used inside the Vehicle Docs modal ─────────────────
function DriverDocCard({ docType, doc, vehicleId, onRefresh }) {
  const [expanded, setExpanded] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [uploadErr, setUploadErr] = useState('');
  const fileRef = useRef(null);

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    setUploadErr('');
    try {
      const token = localStorage.getItem('token');
      const base = config.opsApiBase.replace('/ops', '');
      const form = new FormData();
      form.append('document_type', docType.key);
      form.append('file', file);
      const res = await fetch(
        `${base}/ops/onboarding/driver-documents/${vehicleId}/upload`,
        { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: form }
      );
      const data = await res.json();
      if (!data.success) {
        setUploadErr(data.message || 'Upload failed');
      } else {
        onRefresh();
      }
    } catch (err) {
      setUploadErr(err.message || 'Upload error');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleRemove = async (e) => {
    e.stopPropagation();
    if (!doc.present || removing) return;
    if (!window.confirm(`Remove ${docType.label}? This marks it as missing.`)) {
      return;
    }
    setRemoving(true);
    setUploadErr('');
    try {
      const token = localStorage.getItem('token');
      const base = config.opsApiBase.replace('/ops', '');
      const res = await fetch(
        `${base}/ops/onboarding/driver-documents/${vehicleId}?document_type=${encodeURIComponent(docType.key)}`,
        { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await res.json();
      if (!data.success) {
        setUploadErr(data.message || data.detail || 'Failed to remove document');
      } else {
        onRefresh();
      }
    } catch (err) {
      setUploadErr(err.message || 'Remove error');
    } finally {
      setRemoving(false);
    }
  };

  return (
    <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, marginBottom: 10, overflow: 'hidden' }}>
      {/* Card header */}
      <div
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '10px 14px',
          background: doc.present ? '#f0fdf4' : '#fff7f7',
          cursor: 'pointer',
        }}
        onClick={() => setExpanded(v => !v)}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 18 }}>{docType.icon}</span>
          <span style={{ fontWeight: 600, fontSize: 14 }}>{docType.label}</span>
          <span style={{
            background: doc.present ? '#d1fae5' : '#fee2e2',
            color: doc.present ? '#065f46' : '#b91c1c',
            borderRadius: 20, padding: '2px 10px', fontSize: 12, fontWeight: 600,
          }}>
            {doc.present ? 'Present' : 'Missing'}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleUpload} />
          <button
            onClick={(e) => { e.stopPropagation(); fileRef.current?.click(); }}
            disabled={uploading || removing}
            style={{
              background: doc.present ? '#6b7280' : '#f59e0b',
              color: '#fff', border: 'none', borderRadius: 6,
              padding: '5px 12px', fontSize: 12, cursor: (uploading || removing) ? 'not-allowed' : 'pointer',
            }}
          >
            {uploading ? '⏳ Uploading…' : doc.present ? '🔄 Update' : '⬆️ Upload'}
          </button>
          {doc.present && (
            <button
              onClick={handleRemove}
              disabled={removing || uploading}
              style={{
                background: '#dc2626',
                color: '#fff', border: 'none', borderRadius: 6,
                padding: '5px 12px', fontSize: 12, cursor: (removing || uploading) ? 'not-allowed' : 'pointer',
              }}
            >
              {removing ? 'Removing…' : 'Remove'}
            </button>
          )}
          <span style={{ color: '#9ca3af', fontSize: 14 }}>{expanded ? '▲' : '▼'}</span>
        </div>
      </div>

      {uploadErr && (
        <div style={{ background: '#fee2e2', padding: '6px 14px', fontSize: 12, color: '#b91c1c' }}>
          ⚠️ {uploadErr}
        </div>
      )}

      {expanded && (
        <div style={{ padding: '12px 14px', background: '#fff' }}>
          {doc.url && doc.url !== 'manual-entry' && (
            <div style={{ marginBottom: 12 }}>
              <a href={doc.url} target="_blank" rel="noopener noreferrer">
                <img
                  src={doc.url}
                  alt={docType.label}
                  style={{ maxWidth: '100%', maxHeight: 160, borderRadius: 6, border: '1px solid #e5e7eb', objectFit: 'contain' }}
                />
              </a>
              <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>
                <a href={doc.url} target="_blank" rel="noopener noreferrer">Open full image ↗</a>
              </div>
            </div>
          )}
          {doc.data && Object.keys(doc.data).length > 0 ? (
            <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
              <tbody>
                {Object.entries(doc.data).map(([k, v]) =>
                  v !== null && v !== undefined && typeof v !== 'object' ? (
                    <tr key={k} style={{ borderBottom: '1px solid #f3f4f6' }}>
                      <td style={{ padding: '4px 8px', fontWeight: 600, color: '#374151', width: '42%', textTransform: 'capitalize' }}>
                        {k.replace(/_/g, ' ')}
                      </td>
                      <td style={{ padding: '4px 8px', color: '#111827' }}>{String(v)}</td>
                    </tr>
                  ) : null
                )}
              </tbody>
            </table>
          ) : (
            <p style={{ color: '#9ca3af', fontSize: 13, margin: 0 }}>
              {doc.present ? 'No extracted data available.' : 'No document uploaded yet. Click Upload above.'}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

const DRIVER_DOC_TYPES = [
  { key: 'dl', label: 'Driving License', icon: '🪪' },
  { key: 'rc', label: 'Registration Certificate', icon: '📄' },
  { key: 'insurance', label: 'Insurance', icon: '🛡️' },
  { key: 'emission', label: 'Emission / PUC', icon: '🌿' },
  { key: 'vehicle', label: 'Vehicle Photo', icon: '🚛' },
];

// ─── Bank Details section inside the Vehicle Docs modal ──────────────────────
function BankDetailsSection({ vehicleId }) {
  const [form, setForm] = useState({ bank: '', ifsc_code: '', acc_no: '', branch_name: '', upi_id: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null); // { type: 'success'|'error', text }

  useEffect(() => {
    const fetchBank = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        const base = config.opsApiBase.replace('/ops', '');
        const res = await fetch(`${base}/ops/onboarding/driver-bank/${vehicleId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (data.success) {
          setForm({
            bank: data.bank || '',
            ifsc_code: data.ifsc_code || '',
            acc_no: data.acc_no || '',
            branch_name: data.branch_name || '',
            upi_id: data.upi_id || '',
          });
        }
      } catch (_) { }
      setLoading(false);
    };
    if (vehicleId) fetchBank();
  }, [vehicleId]);

  const handleSave = async () => {
    setSaving(true);
    setMsg(null);
    try {
      const token = localStorage.getItem('token');
      const base = config.opsApiBase.replace('/ops', '');
      const res = await fetch(`${base}/ops/onboarding/driver-bank/${vehicleId}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (data.success) {
        setMsg({ type: 'success', text: 'Bank details saved' });
      } else {
        setMsg({ type: 'error', text: data.message || 'Save failed' });
      }
    } catch (err) {
      setMsg({ type: 'error', text: err.message || 'Save error' });
    }
    setSaving(false);
  };

  if (loading) return <div style={{ padding: 20, textAlign: 'center', color: '#6b7280' }}>⏳ Loading bank details…</div>;

  const field = (key, label, placeholder) => (
    <div style={{ marginBottom: 12 }}>
      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>{label}</label>
      <input
        type="text"
        value={form[key]}
        placeholder={placeholder}
        onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
        style={{
          width: '100%', border: '1px solid #d1d5db', borderRadius: 6,
          padding: '7px 10px', fontSize: 13, outline: 'none', boxSizing: 'border-box',
        }}
      />
    </div>
  );

  return (
    <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden', marginTop: 16 }}>
      {/* Header */}
      <div style={{ background: '#f0f9ff', padding: '10px 14px', borderBottom: '1px solid #e5e7eb' }}>
        <span style={{ fontWeight: 700, fontSize: 14 }}>🏦 Bank Details</span>
      </div>
      <div style={{ padding: '14px 16px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
          <div>{field('bank', 'Bank Name', 'e.g. State Bank of India')}</div>
          <div>{field('ifsc_code', 'IFSC Code', 'e.g. SBIN0001234')}</div>
          <div>{field('acc_no', 'Account Number', 'e.g. 123456789012')}</div>
          <div>{field('branch_name', 'Branch Name', 'e.g. Manipal Main Branch')}</div>
        </div>
        {field('upi_id', 'UPI ID', 'e.g. driver@upi')}
        {msg && (
          <div style={{
            background: msg.type === 'success' ? '#d1fae5' : '#fee2e2',
            color: msg.type === 'success' ? '#065f46' : '#b91c1c',
            borderRadius: 6, padding: '7px 12px', fontSize: 13, marginBottom: 10,
          }}>
            {msg.type === 'success' ? '✅' : '⚠️'} {msg.text}
          </div>
        )}
        <button
          onClick={handleSave}
          disabled={saving}
          style={{
            background: '#2563eb', color: '#fff', border: 'none', borderRadius: 6,
            padding: '8px 20px', fontSize: 13, fontWeight: 600,
            cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1,
          }}
        >
          {saving ? '⏳ Saving…' : '💾 Save Bank Details'}
        </button>
      </div>
    </div>
  );
}

const VEHICLE_CALL_ENTITY_LABELS = {
  trip: 'Trip',
  booking: 'Booking',
  draft_booking: 'Draft Booking',
  lead: 'Lead',
  transporter: 'Transporter',
  general_enquiry: 'General Enquiry',
};

const getVehicleCallLinkStatusBadge = (status) => {
  switch (status) {
    case 'auto': return { bg: '#d1fae5', color: '#065f46', border: '1px solid #a7f3d0' };
    case 'confirmed': return { bg: '#dbeafe', color: '#1e40af', border: '1px solid #bfdbfe' };
    case 'needs_review': return { bg: '#fef3c7', color: '#92400e', border: '1px solid #fde68a' };
    case 'rejected': return { bg: '#fee2e2', color: '#991b1b', border: '1px solid #fecaca' };
    default: return { bg: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0' };
  }
};

const collectVehicleCallLinkedEntities = (log) => {
  const order = ['trip', 'booking', 'draft_booking', 'lead', 'transporter'];
  const seen = new Set();
  const items = [];
  const add = (type, id) => {
    if (!type || type === 'general_enquiry' || id == null || id === '') return;
    const key = `${type}:${id}`;
    if (seen.has(key)) return;
    seen.add(key);
    items.push({ type, id });
  };

  add(log.linked_entity_type, log.linked_entity_id);
  (Array.isArray(log.link_candidates) ? log.link_candidates : []).forEach((c) => {
    if (c && (c.role === 'also_linked' || c.type)) add(c.type, c.id);
    (c && c.rows ? c.rows : []).forEach((row) => {
      if (row && row.type === log.linked_entity_type && String(row.id) === String(log.linked_entity_id) && row.booking_id) {
        add('booking', row.booking_id);
      }
    });
  });

  return items.sort((a, b) => order.indexOf(a.type) - order.indexOf(b.type));
};

const renderVehicleCallMarkdownNotes = (text) => {
  if (!text) return null;
  const lines = text.split('\n');
  const elements = [];
  let bulletItems = [];
  let skipUntilNextHeading = false;

  const flushBullets = () => {
    if (bulletItems.length > 0) {
      elements.push(
        <ul key={`bullets-${elements.length}`} style={{ margin: '4px 0 8px 18px', padding: 0, listStyle: 'disc' }}>
          {bulletItems.map((b, i) => (
            <li key={i} style={{ marginBottom: 3, lineHeight: 1.4 }}>{b}</li>
          ))}
        </ul>
      );
      bulletItems = [];
    }
  };

  for (const line of lines) {
    const headingMatch = line.match(/^##\s+(.+)/);
    if (headingMatch) {
      const heading = headingMatch[1].trim().toLowerCase();
      if (heading === 'summary') {
        flushBullets();
        skipUntilNextHeading = true;
        continue;
      }
      skipUntilNextHeading = false;
      flushBullets();
      elements.push(
        <div
          key={`h-${elements.length}`}
          style={{
            fontWeight: 700,
            fontSize: '0.82rem',
            color: '#1e40af',
            marginTop: elements.length > 0 ? 10 : 0,
            marginBottom: 2,
            borderBottom: '1px solid #e2e8f0',
            paddingBottom: 2,
          }}
        >
          {headingMatch[1].trim()}
        </div>
      );
      continue;
    }

    if (skipUntilNextHeading) continue;

    const bulletMatch = line.match(/^\s*[-*]\s+(.+)/);
    if (bulletMatch) {
      bulletItems.push(bulletMatch[1].trim());
      continue;
    }

    const trimmed = line.trim();
    if (trimmed) {
      flushBullets();
      elements.push(
        <div key={`p-${elements.length}`} style={{ marginTop: 2, lineHeight: 1.45 }}>
          {trimmed}
        </div>
      );
    }
  }
  flushBullets();
  return elements;
};

const renderVehicleCallLinkDetails = (log) => {
  const hasLink = !!log.linked_entity_type;
  const linkedEntities = collectVehicleCallLinkedEntities(log);
  const aboutLines = (log.conversation_summary || '')
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean)
    .slice(0, 2)
    .join(' ');
  const aboutText = (log.call_subject || aboutLines || '').trim();

  return (
    <div style={{
      marginTop: 10,
      background: '#f8fafc',
      border: '1px solid #e2e8f0',
      borderRadius: 8,
      padding: '10px 14px',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', marginBottom: hasLink ? 8 : 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: 20, height: 20, borderRadius: '50%',
            background: '#e0e7ff', color: '#4338ca', fontSize: 10,
          }}>
            <i className="fa fa-link" />
          </span>
          <span style={{ fontSize: '0.82rem', fontWeight: 700, color: '#1e293b' }}>
            Call Link Details
          </span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          {hasLink ? (
            <>
              {linkedEntities.length > 0 ? (
                linkedEntities.map((item) => (
                  <span
                    key={`${item.type}-${item.id}`}
                    style={{
                      background: '#0f172a', color: '#f8fafc', padding: '2px 8px',
                      borderRadius: 12, fontSize: 10, fontWeight: 600,
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                    }}
                  >
                    <i className="fa fa-tag" style={{ fontSize: 9, opacity: 0.7 }} />
                    {VEHICLE_CALL_ENTITY_LABELS[item.type] || item.type} #{item.id}
                  </span>
                ))
              ) : (
                <span
                  style={{
                    background: '#0f172a', color: '#f8fafc', padding: '2px 8px',
                    borderRadius: 12, fontSize: 10, fontWeight: 600,
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                  }}
                >
                  <i className="fa fa-tag" style={{ fontSize: 9, opacity: 0.7 }} />
                  {VEHICLE_CALL_ENTITY_LABELS[log.linked_entity_type] || log.linked_entity_type} #{log.linked_entity_id}
                </span>
              )}
              {log.link_status && (
                <span style={{
                  ...getVehicleCallLinkStatusBadge(log.link_status), padding: '2px 8px', borderRadius: 12,
                  fontSize: 10, fontWeight: 600, textTransform: 'capitalize',
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                }}>
                  <span style={{ width: 5, height: 5, borderRadius: '50%', background: getVehicleCallLinkStatusBadge(log.link_status).color }} />
                  {String(log.link_status).replace('_', ' ')}
                </span>
              )}
              {log.link_confidence != null && (
                <span style={{
                  background: '#fff', color: '#475569', border: '1px solid #e2e8f0',
                  padding: '2px 8px', borderRadius: 12, fontSize: 10, fontWeight: 500,
                }}>
                  confidence {Number(log.link_confidence).toFixed(2)}
                </span>
              )}
            </>
          ) : (
            <span style={{
              background: '#fff', color: '#64748b', border: '1px solid #e2e8f0',
              padding: '2px 8px', borderRadius: 12, fontSize: 10, fontWeight: 500,
            }}>
              Not linked yet
            </span>
          )}
        </div>
      </div>

      {hasLink && aboutText && (
        <div style={{
          background: '#ffffff', border: '1px solid #e2e8f0', borderLeft: '3px solid #3b82f6',
          borderRadius: 6, padding: '6px 10px', margin: '6px 0',
          fontSize: '0.8rem', fontWeight: 600, color: '#1e293b',
          display: 'flex', alignItems: 'center', gap: 6,
        }}>
          <i className="fa fa-info-circle" style={{ color: '#3b82f6', fontSize: 12, flexShrink: 0 }} />
          <span>{aboutText}</span>
        </div>
      )}

      {hasLink && (
        <div style={{
          display: 'grid', gridTemplateColumns: log.linked_by ? 'minmax(140px, auto) 1fr' : '1fr',
          gap: 10, background: '#ffffff', border: '1px solid #e2e8f0',
          borderRadius: 6, padding: '8px 12px', fontSize: '0.8rem',
        }}>
          {log.linked_by && (
            <div style={{ borderRight: '1px solid #f1f5f9', paddingRight: 10 }}>
              <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 2 }}>
                Linked By
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  padding: '2px 6px', borderRadius: 4, background: '#f1f5f9',
                  fontWeight: 600, color: '#334155', textTransform: 'uppercase', fontSize: 10,
                }}>
                  <i className="fa fa-magic" style={{ fontSize: 9, color: '#6366f1' }} />
                  {log.linked_by}
                </span>
              </div>
            </div>
          )}
          <div>
            <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 2 }}>
              Analysis & Reasoning
            </div>
            <div style={{ lineHeight: 1.45, color: '#334155', fontSize: '0.8rem' }}>
              {log.link_reasoning || log.conversation_summary || 'No detailed reasoning recorded for this call.'}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const VehicleDetails = ({ popupVehicleId = null, onPopupClose = null }) => {
  const REGISTRATION_PAGE_SIZE = 10;
  const VEHICLES_PAGE_SIZE = 20;
  const location = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();
  const pendingDriverRegCount = useSelector(
    (state) => state.pendingDriverRegistration?.pendingCount || 0
  );
  const registeredDriverRegCount = useSelector(
    (state) => state.pendingDriverRegistration?.registeredCount || 0
  );
  const registerVehicleBadgeCount = pendingDriverRegCount + registeredDriverRegCount;
  const { isLoaded: mapsLoaded, loadError: mapsLoadError } = useGoogleMaps();
  const { selectedCities } = useCity();
  const { isDateInRange, dateRange } = useDateFilter();
  const [vehicles, setVehicles] = useState([]);
  const [filteredVehicles, setFilteredVehicles] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchMode, setSearchMode] = useState('all');
  const [searchMenuOpen, setSearchMenuOpen] = useState(false);
  const searchBoxRef = useRef(null);
  const searchInputRef = useRef(null);

  const handleSelectSearchCategory = (categoryId) => {
    setSearchMode(categoryId);
    setSearchMenuOpen(false);
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchBoxRef.current && !searchBoxRef.current.contains(event.target)) {
        setSearchMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const [filterType, setFilterType] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [tonnageFilter, setTonnageFilter] = useState('');
  const [lengthFilter, setLengthFilter] = useState('');
  const [quickFilter, setQuickFilter] = useState('all');
  const [missingDocTypeFilter, setMissingDocTypeFilter] = useState('all');
  const [longTripTypeFilter, setLongTripTypeFilter] = useState('inter_district');
  const [transporterOnly, setTransporterOnly] = useState(false);
  const [routeSearch, setRouteSearch] = useState('');
  const [expandedRouteVehicles, setExpandedRouteVehicles] = useState(() => new Set());
  const [showAddVehicleMenu, setShowAddVehicleMenu] = useState(false);
  const [loading, setLoading] = useState(!popupVehicleId);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [cityCache, setCityCache] = useState({}); // vehicle_id -> current location address (on-demand only)
  const [baseLocationCache, setBaseLocationCache] = useState({}); // vehicle_id -> base location address (localStorage-backed)
  const [selectedPincodes, setSelectedPincodes] = useState([]);
  const [locationLoadingVehicles, setLocationLoadingVehicles] = useState(new Set()); // vehicle_id set of loading current location
  const [templateSentVehicles, setTemplateSentVehicles] = useState(new Set()); // Track vehicles where template was sent
  const [sendingTemplate, setSendingTemplate] = useState(new Set()); // Track vehicles currently sending template
  const [onboardingRegistrationIds, setOnboardingRegistrationIds] = useState(new Set());
  const [detailBookingsTotalPay, setDetailBookingsTotalPay] = useState(null);
  const [onboardMenuRowId, setOnboardMenuRowId] = useState(null);
  const [actionMenuRowId, setActionMenuRowId] = useState(null);
  const [expandedRegRowId, setExpandedRegRowId] = useState(null);
  const [showRegTransporterModal, setShowRegTransporterModal] = useState(false);
  const [regTransporterRow, setRegTransporterRow] = useState(null);
  const [regTransporterPhone, setRegTransporterPhone] = useState('');
  const [regTransporterProfile, setRegTransporterProfile] = useState(null);
  const [regTransporterLookupStatus, setRegTransporterLookupStatus] = useState(null);
  const [regTransporterLookupError, setRegTransporterLookupError] = useState('');
  const [regTransporterList, setRegTransporterList] = useState([]);
  const [regTransporterSuggestOpen, setRegTransporterSuggestOpen] = useState(false);
  const [bonusSentVehicles, setBonusSentVehicles] = useState(new Set()); // Track vehicles where bonus was sent
  const [sendingBonus, setSendingBonus] = useState(new Set()); // Track vehicles currently sending bonus

  // Tab state
  const [activeTab, setActiveTab] = useState('vehicles'); // 'vehicles' | 'driver-pay' | 'register-vehicle' | 'call-logs' | 'transporters' | 'voice-bot-logs'
  const [openTransporterOnboard, setOpenTransporterOnboard] = useState(false);
  const [registrationRecords, setRegistrationRecords] = useState([]);
  const [registrationLoading, setRegistrationLoading] = useState(false);
  const [registrationError, setRegistrationError] = useState('');
  const [registrationSuccess, setRegistrationSuccess] = useState('');
  const [registerName, setRegisterName] = useState('');
  const [registerPhone, setRegisterPhone] = useState('');
  const [registerVehicle, setRegisterVehicle] = useState('');
  const [registerRegistrationNo, setRegisterRegistrationNo] = useState('');
  const [registerComments, setRegisterComments] = useState('');
  const [registerSpecs, setRegisterSpecs] = useState(EMPTY_REG_SPECS);
  const [registerExtPhone, setRegisterExtPhone] = useState('');
  const [registerExtLabel, setRegisterExtLabel] = useState('');
  const [registerPlaceAddress, setRegisterPlaceAddress] = useState('');
  const [registerBaseLat, setRegisterBaseLat] = useState('');
  const [registerBaseLng, setRegisterBaseLng] = useState('');
  const [registerResolvedAddress, setRegisterResolvedAddress] = useState('');
  const [isRegisterGeocoding, setIsRegisterGeocoding] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const [registerVehiclePhoto, setRegisterVehiclePhoto] = useState(null);
  const [registerVehiclePhotoPreview, setRegisterVehiclePhotoPreview] = useState(null);
  const [registerPhotoPickerOpen, setRegisterPhotoPickerOpen] = useState(false);
  const [registerPhotoUploading, setRegisterPhotoUploading] = useState(false);
  const registerCameraInputRef = useRef(null);
  const registerGalleryInputRef = useRef(null);
  const registerPhotoPickerRef = useRef(null);
  const [registrationSearchTerm, setRegistrationSearchTerm] = useState('');
  const [registerPage, setRegisterPage] = useState('main'); // 'main' | 'pending' | 'registered'
  const [vehiclesCurrentPage, setVehiclesCurrentPage] = useState(1);
  const [vehiclesPageInput, setVehiclesPageInput] = useState(1);
  const [pendingCurrentPage, setPendingCurrentPage] = useState(1);
  const [registeredCurrentPage, setRegisteredCurrentPage] = useState(1);

  // Transporter Modal State
  const [transporterModalOpen, setTransporterModalOpen] = useState(false);
  const [selectedTransporterId, setSelectedTransporterId] = useState(null);
  const [selectedTransporterPhone, setSelectedTransporterPhone] = useState(null);

  const openTransporterModal = (transporterId, transporterPhone = null) => {
    if (!transporterId && !transporterPhone) return;
    setSelectedTransporterId(transporterId || null);
    setSelectedTransporterPhone(transporterPhone || null);
    setTransporterModalOpen(true);
  };

  // Specs and Pricing Modal State
  const [showSpecsModal, setShowSpecsModal] = useState(false);
  const [specsVehicle, setSpecsVehicle] = useState(null);
  const [specsFormData, setSpecsFormData] = useState({
    per_km_price: '',
    ready_for_long_trip: false,
    long_inter_district: false,
    long_inter_state: false,
    local: false,
    body_type: '',
    tonnage: '',
    phone_extensions: [],
  });
  const [newExtPhone, setNewExtPhone] = useState('');
  const [newExtLabel, setNewExtLabel] = useState('');
  const [isSavingSpecs, setIsSavingSpecs] = useState(false);
  const [specsError, setSpecsError] = useState('');

  // Edit Registration Modal State
  const [showEditRegModal, setShowEditRegModal] = useState(false);
  const [editRegRow, setEditRegRow] = useState(null);
  const [editRegName, setEditRegName] = useState('');
  const [editRegPhone, setEditRegPhone] = useState('');
  const [editRegVehicle, setEditRegVehicle] = useState('');
  const [editRegMake, setEditRegMake] = useState('');
  const [editRegModel, setEditRegModel] = useState('');
  const [editRegRegistrationNo, setEditRegRegistrationNo] = useState('');
  const [editRegClass, setEditRegClass] = useState('');
  const [editRegAddress, setEditRegAddress] = useState('');
  const [editRegBaseLat, setEditRegBaseLat] = useState('');
  const [editRegBaseLng, setEditRegBaseLng] = useState('');
  const [editRegComments, setEditRegComments] = useState('');
  const [editRegSpecs, setEditRegSpecs] = useState(EMPTY_REG_SPECS);
  const [editRegExtPhone, setEditRegExtPhone] = useState('');
  const [editRegExtLabel, setEditRegExtLabel] = useState('');
  const [editRegSaving, setEditRegSaving] = useState(false);
  const [editRegError, setEditRegError] = useState('');
  const [isEditRegGeocoding, setIsEditRegGeocoding] = useState(false);
  const [callingDrivers, setCallingDrivers] = useState({});
  const [vehicleDriverCallLogs, setVehicleDriverCallLogs] = useState([]);
  const [vehicleDriverCallLogsLoading, setVehicleDriverCallLogsLoading] = useState(false);
  const [vehicleDriverCallLogsTotal, setVehicleDriverCallLogsTotal] = useState(0);
  const [vehicleDriverCallLogsPage, setVehicleDriverCallLogsPage] = useState(1);
  const [vehicleDriverCallLogsRef, setVehicleDriverCallLogsRef] = useState({ driverId: null, driverPhone: null });
  // Unknown call logs toggle
  const [showUnknownCallLogs, setShowUnknownCallLogs] = useState(false);
  const [vehicleUnknownCallLogs, setVehicleUnknownCallLogs] = useState([]);
  const [vehicleUnknownCallLogsLoading, setVehicleUnknownCallLogsLoading] = useState(false);
  const [vehicleUnknownCallLogsTotal, setVehicleUnknownCallLogsTotal] = useState(0);
  const [vehicleUnknownCallLogsPage, setVehicleUnknownCallLogsPage] = useState(1);
  const [expandedVehicleUnknownCallLogId, setExpandedVehicleUnknownCallLogId] = useState(null);
  const [expandedVehicleCallLogId, setExpandedVehicleCallLogId] = useState(null);
  const [playingVehicleRecordingId, setPlayingVehicleRecordingId] = useState(null);
  const [summarizingCallLogId, setSummarizingCallLogId] = useState(null);
  const VEHICLE_CALL_LOGS_PAGE_SIZE = 10;

  const handleSummarizeCallLog = async (log, force = false) => {
    if (!log?.id) return;
    if (!log.recording_url) {
      alert('No recording available for this call');
      return;
    }
    setSummarizingCallLogId(log.id);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.opsApiBase}/call-logs/${log.id}/summarize`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ force }),
      });
      if (response.ok) {
        const data = await response.json();
        const summary = data?.summary || '';
        const transcript = data?.transcript || '';
        const updater = (prev) =>
          prev.map((l) =>
            l.id === log.id
              ? {
                  ...l,
                  conversation_summary: summary,
                  conversation_transcript: transcript,
                  has_conversation_summary: true,
                }
              : l
          );
        setVehicleDriverCallLogs(updater);
        setVehicleUnknownCallLogs(updater);
      } else {
        const errData = await response.json().catch(() => ({}));
        alert('Failed to summarize: ' + (errData.detail || response.statusText));
      }
    } catch (err) {
      console.error('Failed to summarize call:', err);
      alert('Failed to summarize: ' + err.message);
    } finally {
      setSummarizingCallLogId(null);
    }
  };

  // Reset vehicle audio player back to "Play" button when clicking anywhere outside
  useEffect(() => {
    if (!playingVehicleRecordingId) return;

    const handleClickOutside = (event) => {
      const activeContainer = document.getElementById(`vehicle-audio-container-${playingVehicleRecordingId}`);
      if (activeContainer && activeContainer.contains(event.target)) {
        return;
      }
      setPlayingVehicleRecordingId(null);
    };

    const timer = setTimeout(() => {
      document.addEventListener('click', handleClickOutside);
    }, 0);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('click', handleClickOutside);
    };
  }, [playingVehicleRecordingId]);

  useEffect(() => {
    if (onboardMenuRowId == null && actionMenuRowId == null) return;
    const handleClickOutside = (e) => {
      if (
        e.target &&
        e.target.closest &&
        (e.target.closest('.reg-action-dropdown') ||
          e.target.closest('.reg-action-btn-more') ||
          e.target.closest('.reg-onboard-menu') ||
          e.target.closest('.reg-onboard-btn'))
      ) {
        return;
      }
      setOnboardMenuRowId(null);
      setActionMenuRowId(null);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onboardMenuRowId, actionMenuRowId]);

  useEffect(() => {
    const handle = (e) => {
      if (registerPhotoPickerRef.current && !registerPhotoPickerRef.current.contains(e.target)) {
        setRegisterPhotoPickerOpen(false);
      }
    };
    if (registerPhotoPickerOpen) document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [registerPhotoPickerOpen]);

  // Registration Messages Modal State
  const [showMessagesModal, setShowMessagesModal] = useState(false);
  const [selectedDriverForMessages, setSelectedDriverForMessages] = useState(null);
  const [driverMessages, setDriverMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [messagesError, setMessagesError] = useState('');
  const [isRegistrationBotPaused, setIsRegistrationBotPaused] = useState(false);
  const [isRegistrationBotStatusLoading, setIsRegistrationBotStatusLoading] = useState(false);
  const [isRegistrationBotToggling, setIsRegistrationBotToggling] = useState(false);
  const [registrationManualMessage, setRegistrationManualMessage] = useState('');
  const [isSendingRegistrationManualMessage, setIsSendingRegistrationManualMessage] = useState(false);
  const REGISTRATION_MESSAGES_PAGE_SIZE = 30;
  const REGISTRATION_MESSAGES_MAX_WINDOW = 200;
  const [loadedMessageOffset, setLoadedMessageOffset] = useState(0);
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [loadingOlderMessages, setLoadingOlderMessages] = useState(false);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const loadedMessageOffsetRef = useRef(0);
  const isPrependingMessagesRef = useRef(false);
  const previousScrollMetricsRef = useRef({ scrollTop: 0, scrollHeight: 0 });
  const shouldAutoScrollRef = useRef(true);

  const setRegisterView = (page) => {
    setActiveTab('register-vehicle');
    setRegisterPage(page);
    const params = new URLSearchParams(location.search || '');
    params.set('tab', 'register-vehicle');
    if (page === 'pending' || page === 'registered') {
      params.set('registerPage', page);
    } else {
      params.delete('registerPage');
    }
    const nextSearch = params.toString();
    if (location.search.replace(/^\?/, '') === nextSearch) return;
    const nextLoc = { pathname: location.pathname, search: nextSearch };
    // Push list views so browser Back returns to Register Vehicle, not Bookings.
    if (page === 'pending' || page === 'registered') {
      history.push(nextLoc);
    } else {
      history.replace(nextLoc);
    }
  };


  useEffect(() => {
    const tabFromState = location?.state?.activeTab;
    const query = new URLSearchParams(location?.search || '');
    const tabFromQuery = query.get('tab');
    const incomingTab = tabFromState || tabFromQuery;

    if (!incomingTab) return;

    const normalized = String(incomingTab).toLowerCase();
    if (normalized === 'register' || normalized === 'register-vehicle') {
      setActiveTab('register-vehicle');
      const registerView = query.get('registerPage');
      setRegisterPage(registerView === 'pending' || registerView === 'registered' ? registerView : 'main');
      return;
    }
    if (normalized === 'driver-pay') {
      setActiveTab('driver-pay');
      return;
    }
    if (normalized === 'vehicles') {
      setActiveTab('vehicles');
      return;
    }
    if (normalized === 'call-logs' || normalized === 'call_logs') {
      setActiveTab('call-logs');
      return;
    }
    if (normalized === 'transporters' || normalized === 'transporter') {
      setActiveTab('transporters');
      const openOnboard =
        !!location?.state?.openOnboard ||
        query.get('openOnboard') === '1' ||
        query.get('openOnboard') === 'true';
      setOpenTransporterOnboard(openOnboard);
      return;
    }
    if (
      normalized === 'voice-bot-logs' ||
      normalized === 'voice_bot_logs' ||
      normalized === 'live-logs-bot-call'
    ) {
      setActiveTab('voice-bot-logs');
    }
  }, [location]);

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'auto' });
    }
  };

  const isMessagesNearBottom = () => {
    const el = messagesContainerRef.current;
    if (!el) return true;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    return distanceFromBottom < 120;
  };

  const fetchRegistrationMessagesPage = async (phone, offset = 0, limit = REGISTRATION_MESSAGES_PAGE_SIZE) => {
    const response = await fetch(
      `${config.opsApiBase}/driver-registration/${phone}/messages?offset=${offset}&limit=${limit}`
    );
    const data = await response.json();
    if (!response.ok || !data.success) {
      throw new Error(data.detail || data.message || 'Failed to load messages');
    }
    return {
      messages: data.messages || [],
      hasMore: Boolean(data.has_more),
    };
  };

  const refreshMessagesWindow = async (phone, requiredWindowSize = REGISTRATION_MESSAGES_PAGE_SIZE) => {
    const safeWindowSize = Math.min(
      Math.max(requiredWindowSize, REGISTRATION_MESSAGES_PAGE_SIZE),
      REGISTRATION_MESSAGES_MAX_WINDOW
    );
    shouldAutoScrollRef.current = isMessagesNearBottom();
    const page = await fetchRegistrationMessagesPage(phone, 0, safeWindowSize);
    setDriverMessages(page.messages);
    setLoadedMessageOffset(page.messages.length);
    setHasMoreMessages(page.hasMore);
  };

  const loadOlderMessages = async () => {
    if (!selectedDriverForMessages || loadingOlderMessages || !hasMoreMessages) return;

    const container = messagesContainerRef.current;
    if (container) {
      previousScrollMetricsRef.current = {
        scrollTop: container.scrollTop,
        scrollHeight: container.scrollHeight,
      };
    }

    setLoadingOlderMessages(true);
    isPrependingMessagesRef.current = true;

    try {
      const page = await fetchRegistrationMessagesPage(
        selectedDriverForMessages,
        loadedMessageOffsetRef.current,
        REGISTRATION_MESSAGES_PAGE_SIZE
      );
      if (page.messages.length > 0) {
        setDriverMessages((prev) => [...page.messages, ...prev]);
        setLoadedMessageOffset((prev) => prev + page.messages.length);
      }
      setHasMoreMessages(page.hasMore);
    } catch (error) {
      setMessagesError(error.message || 'Failed to load older messages');
      isPrependingMessagesRef.current = false;
    } finally {
      setLoadingOlderMessages(false);
    }
  };

  useEffect(() => {
    loadedMessageOffsetRef.current = loadedMessageOffset;
  }, [loadedMessageOffset]);

  useEffect(() => {
    if (isPrependingMessagesRef.current) {
      requestAnimationFrame(() => {
        const container = messagesContainerRef.current;
        if (!container) return;
        const { scrollTop, scrollHeight } = previousScrollMetricsRef.current;
        const delta = container.scrollHeight - scrollHeight;
        container.scrollTop = scrollTop + delta;
        isPrependingMessagesRef.current = false;
      });
      return;
    }

    if (shouldAutoScrollRef.current) {
      scrollToBottom();
      setTimeout(scrollToBottom, 100);
    }
    shouldAutoScrollRef.current = true;
  }, [driverMessages.length]);

  const handleScrollMessages = () => {
    const container = messagesContainerRef.current;
    if (!container) return;
    if (container.scrollTop <= 80 && hasMoreMessages && !loadingOlderMessages) {
      loadOlderMessages();
    }
  };

  // Vehicle chat state
  const [selectedVehicleForChat, setSelectedVehicleForChat] = useState(null);
  const [chatHistory, setChatHistory] = useState([]);
  const [chatMessage, setChatMessage] = useState('');
  const [isChatBotPaused, setIsChatBotPaused] = useState(false);
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [driverHasApp, setDriverHasApp] = useState(false); // true if driver uses the app
  const [isChatRefreshing, setIsChatRefreshing] = useState(false);
  const chatContainerRef = useRef(null);
  const shouldAutoScrollChatRef = useRef(true);

  const getFlatChatHistory = (chatHistory) => {
    if (!chatHistory) return [];
    const flat = [];

    const processItem = (item) => {
      if (!item) return;

      // If it's a string, try to parse it
      if (typeof item === 'string') {
        try {
          const parsed = JSON.parse(item);
          processItem(parsed);
          return;
        } catch (e) {
          // Not valid JSON, add as plain text
          flat.push({ role: 'unknown', message: item });
          return;
        }
      }

      // If it's an array, recursively process each item
      if (Array.isArray(item)) {
        item.forEach(subItem => processItem(subItem));
        return;
      }

      // If it's an object
      if (typeof item === 'object') {
        // If it's a message object
        if (item.role || item.sender) {
          flat.push(item);
        } else if (item.messages && Array.isArray(item.messages)) {
          // Handle format: { messages: [...] }
          item.messages.forEach(subItem => processItem(subItem));
        } else {
          // Unknown object format, convert to string
          flat.push({ role: 'unknown', message: JSON.stringify(item) });
        }
      }
    };

    chatHistory.forEach(item => processItem(item));
    return flat;
  };

  const formatNonJsonChatMessage = (rawText) => {
    const raw = String(rawText || '').trim();
    if (!raw) return { role: 'system', message: '' };

    // Driver app sometimes sends technical event payloads like:
    // BUTTON_CLICK:BUTTON_CLICK:unloading_complete|trip_id:442|title:...
    // Render this as a clean driver message.
    if (raw.startsWith('BUTTON_CLICK:')) {
      const firstPart = raw.split('|')[0] || raw;
      const actionKey = (firstPart.split(':').pop() || '').trim();

      const labelMap = {
        unloading_complete: 'Unloading complete',
        loading_complete: 'Loading complete',
      };

      const fallbackLabel = actionKey
        ? actionKey
          .replace(/_/g, ' ')
          .replace(/\s+/g, ' ')
          .trim()
          .replace(/\b\w/g, (c) => c.toUpperCase())
        : 'Action';

      return {
        role: 'driver',
        sender: 'driver',
        message: labelMap[actionKey] || fallbackLabel,
        raw,
        timestamp: new Date().toISOString(),
      };
    }

    return { role: 'system', message: raw };
  };

  const formatDriverEventMessage = (messageText) => {
    const raw = String(messageText || '').trim();
    if (!raw.startsWith('BUTTON_CLICK:')) return null;

    const parts = raw.split('|').map(p => p.trim()).filter(Boolean);
    const actionPart = parts[0] || raw;
    const actionKey = (actionPart.split(':').pop() || '').trim();

    // Parse optional key:value fields after first '|'
    const meta = {};
    for (const p of parts.slice(1)) {
      const idx = p.indexOf(':');
      if (idx > 0) {
        const k = p.slice(0, idx).trim();
        const v = p.slice(idx + 1).trim();
        if (k) meta[k] = v;
      }
    }

    const labelMap = {
      unloading_complete: 'Unloading complete',
      loading_complete: 'Loading complete',
      accept_trip: 'Trip accepted',
      got_it: 'Got it',
    };

    const fallbackLabel = actionKey
      ? actionKey
        .replace(/_/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .replace(/\b\w/g, (c) => c.toUpperCase())
      : 'Action';

    // Keep it simple: show clean label only (optionally include trip id later if desired)
    const label = labelMap[actionKey] || fallbackLabel;
    return { label, raw, meta, actionKey };
  };

  // WebSocket state
  const [wsConnected, setWsConnected] = useState(false);
  const wsRef = useRef(null);
  const chatMessagesEndRef = useRef(null);


  // Unassigned bookings state
  const [unassignedBookings, setUnassignedBookings] = useState([]);
  const [bookingSearchTerm, setBookingSearchTerm] = useState('');
  const [selectedVehicleForAssignment, setSelectedVehicleForAssignment] = useState(null);

  // NEW: Validation state for two-step manual assignment flow
  const [selectedBookingForValidation, setSelectedBookingForValidation] = useState(null);
  const [validationStep, setValidationStep] = useState('select'); // 'select' | 'warnings' | 'confirmed'
  const [validationResult, setValidationResult] = useState(null);
  const [isValidating, setIsValidating] = useState(false);
  const [isAssigning, setIsAssigning] = useState(false);
  const [warningsAcknowledged, setWarningsAcknowledged] = useState(false);

  // Bonus Payment state
  const [showBonusPayModal, setShowBonusPayModal] = useState(false);
  const [showBonusHistoryModal, setShowBonusHistoryModal] = useState(false);
  const [selectedVehicleForBonus, setSelectedVehicleForBonus] = useState(null);
  const [bonusDate, setBonusDate] = useState(new Date().toISOString().split('T')[0]);
  const [bonusTrips, setBonusTrips] = useState([]);
  const [selectedTripId, setSelectedTripId] = useState('');
  const [bonusReason, setBonusReason] = useState('');
  const [bonusAmount, setBonusAmount] = useState('');
  const [showBonusConfirmation, setShowBonusConfirmation] = useState(false);
  const [isLoadingTrips, setIsLoadingTrips] = useState(false);
  const [isSubmittingBonus, setIsSubmittingBonus] = useState(false);
  const [bonusHistory, setBonusHistory] = useState([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [bonusHistoryDateFilter, setBonusHistoryDateFilter] = useState({ startDate: '', endDate: '' });
  const [bonusHistoryCurrentPage, setBonusHistoryCurrentPage] = useState(1);
  const BONUS_HISTORY_PAGE_SIZE = 5;
  const [editingBonusId, setEditingBonusId] = useState(null);
  const [editBonusData, setEditBonusData] = useState({ date: '', trip_id: '', amount: '', reason: '' });
  const [isUpdatingBonus, setIsUpdatingBonus] = useState(false);

  // Vehicle edit state
  const [isEditing, setIsEditing] = useState(false);
  const [isGeocodingBase, setIsGeocodingBase] = useState(false);
  const [detailExtPhone, setDetailExtPhone] = useState('');
  const [detailExtLabel, setDetailExtLabel] = useState('');

  // Driver Call Modal State
  const [driverCallModal, setDriverCallModal] = useState({
    open: false,
    driverPhone: '',
    driverName: '',
    onlineSims: [],
    selectedSim: '',
    isLoading: false,
    status: 'idle', // idle | calling | success | error
    errorMsg: '',
  });

  // Check Map modal
  const [showMapModal, setShowMapModal] = useState(false);
  const [mapModalCoords, setMapModalCoords] = useState({ lat: null, lng: null, label: '' });
  const [mapCenter, setMapCenter] = useState(null);

  const mainModalMouseDownTarget = useRef(null);
  const mapModalMouseDownTarget = useRef(null);
  const docsModalMouseDownTarget = useRef(null);

  useEffect(() => {
    if (!showMapModal) {
      setMapCenter(null);
      return;
    }
    if (mapModalCoords.lat && mapModalCoords.lng) {
      setMapCenter({ lat: Number(mapModalCoords.lat), lng: Number(mapModalCoords.lng) });
    } else if (mapModalCoords.label && window.google && window.google.maps) {
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ address: mapModalCoords.label }, (results, status) => {
        if (status === 'OK' && results[0]) {
          const loc = results[0].geometry.location;
          setMapCenter({ lat: loc.lat(), lng: loc.lng() });
        }
      });
    }
  }, [showMapModal, mapModalCoords]);

  // Vehicle Docs modal
  const [showDocsModal, setShowDocsModal] = useState(false);
  const [docsLoading, setDocsLoading] = useState(false);
  const [docsError, setDocsError] = useState('');
  const [driverDocs, setDriverDocs] = useState(null);
  const [availableVehicleNames, setAvailableVehicleNames] = useState([]);
  const [isLoadingVehicleNames, setIsLoadingVehicleNames] = useState(false);
  /** From GET /vehicles/:id when owner user differs from driver */
  const [registeredOwner, setRegisteredOwner] = useState(null);
  const [ownerForm, setOwnerForm] = useState({ name: '', phone: '' });
  const [ownerFormOpen, setOwnerFormOpen] = useState(false);
  const [ownerSaving, setOwnerSaving] = useState(false);
  const [showLightbox, setShowLightbox] = useState(false);
  const [lightboxImage, setLightboxImage] = useState('');

  const [editFormData, setEditFormData] = useState({
    // Vehicle fields
    registration_no: '',
    vehicle_name: '',
    make: '',
    model: '',
    class: '',
    status: '',
    is_active: true,
    is_blocked: false,
    capacity: '',
    capacity_uom: 'ton',
    body_type: '',
    specialization: '',
    tonnage: '',
    length_ft: '',
    breadth_ft: '',
    height_ft: '',
    insurance_number: '',
    insurance_expiry: '',
    base_lat: '',
    base_lng: '',
    // Driver fields (editable)
    driver_name: '',
    driver_language_preference: '',
    driver_active_km_rate: '',
    driver_dead_km_rate: '',
    driver_verification_status: '',
    driver_remarks: '',
    // Driver fields (read-only)
    driver_phone: '',
    driver_license_number: '',
    driver_license_expiry: '',
    driver_rating: '',
    driver_total_trips: '',
    joining_bonus: false,
    is_transporter: false,
    transport_routes: [],
    phone_extensions: [],
  });
  const [isMarkingJoiningBonus, setIsMarkingJoiningBonus] = useState(false);
  const [detailRoutesExpanded, setDetailRoutesExpanded] = useState(false);

  const emptyRouteRow = () => ({
    from: null,
    to: null,
    fromInput: '',
    toInput: '',
    priceMin: '',
    priceMax: '',
  });

  const routesFromApi = (routes) => {
    if (!Array.isArray(routes) || routes.length === 0) return [emptyRouteRow()];
    return routes.map((r) => ({
      from: r?.from
        ? {
          address: r.from.address || '',
          latitude: r.from.lat ?? r.from.latitude,
          longitude: r.from.lng ?? r.from.longitude,
          place_id: r.from.place_id || null,
        }
        : null,
      to: r?.to
        ? {
          address: r.to.address || '',
          latitude: r.to.lat ?? r.to.latitude,
          longitude: r.to.lng ?? r.to.longitude,
          place_id: r.to.place_id || null,
        }
        : null,
      fromInput: r?.from?.address || '',
      toInput: r?.to?.address || '',
      priceMin: r?.price_min != null ? String(r.price_min) : '',
      priceMax: r?.price_max != null && Number(r.price_max) !== Number(r.price_min) ? String(r.price_max) : '',
    }));
  };

  const routesToApiPayload = (rows) => {
    return (rows || [])
      .map((row) => {
        if (!row?.from || row.from.latitude == null || row.from.longitude == null) return null;
        if (!row?.to || row.to.latitude == null || row.to.longitude == null) return null;
        const priceMin = Number(row.priceMin);
        if (Number.isNaN(priceMin) || priceMin < 0) return null;
        let priceMax = row.priceMax !== '' && row.priceMax != null ? Number(row.priceMax) : priceMin;
        if (Number.isNaN(priceMax) || priceMax < priceMin) priceMax = priceMin;
        return {
          from: {
            address: row.from.address,
            lat: row.from.latitude,
            lng: row.from.longitude,
            place_id: row.from.place_id || null,
          },
          to: {
            address: row.to.address,
            lat: row.to.latitude,
            lng: row.to.longitude,
            place_id: row.to.place_id || null,
          },
          price_min: priceMin,
          price_max: priceMax,
        };
      })
      .filter(Boolean);
  };

  const openVehicleModal = async (vehicle) => {
    setSelectedVehicle(vehicle);
    setIsEditing(false); // Start in view mode
    document.body.style.overflow = 'hidden'; // Prevent background scrolling

    try {
      // Fetch complete vehicle details from the API with date filter
      const token = localStorage.getItem('token') || 'mock-jwt-token-for-testing';

      // Build URL with date filter if available
      let apiUrl = `${config.opsApiBase}/vehicles/${vehicle.vehicle_id}`;
      if (dateRange.startDate && dateRange.endDate) {
        apiUrl += `?from_date=${dateRange.startDate}&to_date=${dateRange.endDate}`;
      }

      const response = await fetch(apiUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const completeVehicleData = await response.json();
        const driver = completeVehicleData.driver || {};
        const ownerData = completeVehicleData.owner && completeVehicleData.owner.user_id
          ? completeVehicleData.owner
          : null;
        setRegisteredOwner(ownerData);
        setOwnerForm({ name: ownerData?.name || '', phone: ownerData?.phone || '' });
        setOwnerFormOpen(false);
        fetchDriverDocs(vehicle.vehicle_id);

        // Fetch driver call logs
        if (driver.driver_id || driver.phone) {
          fetchVehicleDriverCallLogs(driver.driver_id, driver.phone);
        } else {
          setVehicleDriverCallLogs([]);
        }

        // Update selectedVehicle with km stats (filtered by date if date filter applied)
        setSelectedVehicle({
          ...vehicle,
          is_part: completeVehicleData.is_part !== undefined ? completeVehicleData.is_part : vehicle.is_part,
          is_blocked: completeVehicleData.is_blocked !== undefined ? completeVehicleData.is_blocked : !!vehicle.is_blocked,
          total_active_km: completeVehicleData.total_active_km || 0,
          total_dead_km: completeVehicleData.total_dead_km || 0,
          total_km: completeVehicleData.total_km || 0,
          total_pay: completeVehicleData.total_pay || 0,
          linked_driver_id: driver.driver_id || null,
          linked_driver_name: driver.name || '',
          linked_driver_phone: driver.phone || '',
          is_transporter: completeVehicleData.is_transporter !== undefined
            ? !!completeVehicleData.is_transporter
            : !!vehicle.is_transporter,
          transporter_id: completeVehicleData.transporter_id != null
            ? Number(completeVehicleData.transporter_id)
            : (vehicle.transporter_id != null ? Number(vehicle.transporter_id) : null),
          transporter_name: completeVehicleData.transporter_name !== undefined
            ? completeVehicleData.transporter_name
            : (vehicle.transporter_name || null),
          transport_routes: Array.isArray(completeVehicleData.transport_routes)
            ? completeVehicleData.transport_routes
            : (vehicle.transport_routes || []),
          phone_extensions: Array.isArray(completeVehicleData.phone_extensions)
            ? completeVehicleData.phone_extensions
            : (vehicle.phone_extensions || []),
        });
        setDetailRoutesExpanded(false);

        // Load vehicle names for the current class
        const vehicleClass = completeVehicleData.class_ || 1;
        await loadVehicleNamesForClass(vehicleClass);

        // Create display name from make and model
        const currentVehicleName = completeVehicleData.make && completeVehicleData.model
          ? `${completeVehicleData.make} ${completeVehicleData.model}`
          : '';

        // Initialize edit form with complete vehicle data from API
        setEditFormData({
          // Vehicle fields - using complete API data
          registration_no: completeVehicleData.registration_no || `REG-${vehicle.vehicle_id}`,
          vehicle_name: currentVehicleName, // Show current vehicle name for display
          make: completeVehicleData.make || '',
          model: completeVehicleData.model || '',
          class: completeVehicleData.class_ ? String(completeVehicleData.class_) : '1',
          status: completeVehicleData.status || 'idle',
          is_active: completeVehicleData.is_active !== undefined ? completeVehicleData.is_active : true,
          is_blocked: completeVehicleData.is_blocked !== undefined ? completeVehicleData.is_blocked : false,
          capacity: completeVehicleData.capacity ? String(completeVehicleData.capacity) : '',
          capacity_uom: completeVehicleData.capacity_uom || 'ton',
          body_type: completeVehicleData.body_type || '',
          specialization: completeVehicleData.specialization || '',
          tonnage: completeVehicleData.tonnage != null ? String(completeVehicleData.tonnage) : '',
          length_ft: completeVehicleData.length_ft != null ? String(completeVehicleData.length_ft) : (completeVehicleData.length != null ? String(completeVehicleData.length) : ''),
          breadth_ft: completeVehicleData.breadth_ft != null ? String(completeVehicleData.breadth_ft) : (completeVehicleData.breadth != null ? String(completeVehicleData.breadth) : ''),
          height_ft: completeVehicleData.height_ft != null ? String(completeVehicleData.height_ft) : (completeVehicleData.height != null ? String(completeVehicleData.height) : ''),
          insurance_number: completeVehicleData.insurance_number || '',
          insurance_expiry: completeVehicleData.insurance_expiry || '',
          base_lat: completeVehicleData.base_lat ? String(completeVehicleData.base_lat) : '',
          base_lng: completeVehicleData.base_lng ? String(completeVehicleData.base_lng) : '',
          base_location: completeVehicleData.base_address || completeVehicleData.base_location || '',

          // Driver fields (editable) - using complete API data
          driver_name: driver.name || '',
          driver_phone: driver.phone || '',
          driver_language_preference: driver.language_preference || '',
          driver_active_km_rate: driver.active_km_rate ? String(driver.active_km_rate) : '',
          driver_dead_km_rate: driver.dead_km_rate ? String(driver.dead_km_rate) : '',
          driver_verification_status: driver.verification_status || 'pending',
          driver_remarks: completeVehicleData.remarks || vehicle.remarks || '',
          phone_extensions: Array.isArray(completeVehicleData.phone_extensions)
            ? [...completeVehicleData.phone_extensions]
            : (Array.isArray(vehicle.phone_extensions) ? [...vehicle.phone_extensions] : []),

          // Driver fields (read-only) - using complete API data
          driver_license_number: driver.license_number || '',
          driver_license_expiry: driver.license_expiry ? new Date(driver.license_expiry).toISOString().split('T')[0] : '',
          driver_rating: driver.rating ? String(driver.rating) : '',
          driver_total_trips: driver.total_trips_completed ? String(driver.total_trips_completed) : '0',
          joining_bonus: !!completeVehicleData.joining_bonus,
          is_transporter: completeVehicleData.is_transporter !== undefined
            ? !!completeVehicleData.is_transporter
            : !!vehicle.is_transporter,
          transporter_id: completeVehicleData.transporter_id != null
            ? Number(completeVehicleData.transporter_id)
            : (vehicle.transporter_id != null ? Number(vehicle.transporter_id) : null),
          transporter_name: completeVehicleData.transporter_name !== undefined
            ? completeVehicleData.transporter_name
            : (vehicle.transporter_name || null),
          transport_routes: routesFromApi(
            Array.isArray(completeVehicleData.transport_routes)
              ? completeVehicleData.transport_routes
              : (vehicle.transport_routes || [])
          ),
        });
      } else {
        setRegisteredOwner(null);
        setOwnerForm({ name: '', phone: '' });
        setOwnerFormOpen(false);
        // Fallback to original data if API call fails
        const originalData = vehicle.originalData || {};
        const driver = originalData.driver || {};

        setEditFormData({
          // Vehicle fields - using fallback data
          registration_no: originalData.registration_no || vehicle.registration_no || `REG-${vehicle.vehicle_id}`,
          vehicle_name: '',
          make: originalData.make || '',
          model: originalData.model || '',
          class: originalData.class_ ? String(originalData.class_) : (originalData.class ? String(originalData.class) : '1'),
          status: originalData.status || vehicle.current_status || 'idle',
          is_active: originalData.is_active !== undefined ? originalData.is_active : (vehicle.is_active !== undefined ? vehicle.is_active : true),
          is_blocked: originalData.is_blocked !== undefined ? originalData.is_blocked : !!vehicle.is_blocked,
          capacity: originalData.capacity ? String(originalData.capacity) : '',
          capacity_uom: originalData.capacity_uom || 'ton',
          body_type: originalData.body_type || '',
          specialization: originalData.specialization || '',
          tonnage: originalData.tonnage != null ? String(originalData.tonnage) : (vehicle.tonnage != null ? String(vehicle.tonnage) : ''),
          length_ft: originalData.length_ft != null ? String(originalData.length_ft) : (originalData.length != null ? String(originalData.length) : ''),
          breadth_ft: originalData.breadth_ft != null ? String(originalData.breadth_ft) : (originalData.breadth != null ? String(originalData.breadth) : ''),
          height_ft: originalData.height_ft != null ? String(originalData.height_ft) : (originalData.height != null ? String(originalData.height) : ''),
          insurance_number: originalData.insurance_number || '',
          insurance_expiry: originalData.insurance_expiry || '',
          base_lat: originalData.base_lat ? String(originalData.base_lat) : '',
          base_lng: originalData.base_lng ? String(originalData.base_lng) : '',
          base_location: originalData.base_location || vehicle.base_location || '',

          // Driver fields (editable) - using fallback data
          driver_name: driver.name || '',
          driver_phone: driver.phone || '',
          driver_language_preference: driver.language_preference || '',
          driver_active_km_rate: driver.active_km_rate ? String(driver.active_km_rate) : '',
          driver_dead_km_rate: driver.dead_km_rate ? String(driver.dead_km_rate) : '',
          driver_verification_status: driver.verification_status || 'pending',
          driver_remarks: vehicle.remarks || driver.remarks || '',

          // Driver fields (read-only) - using fallback data
          driver_license_number: driver.license_number || '',
          driver_license_expiry: driver.license_expiry ? new Date(driver.license_expiry).toISOString().split('T')[0] : '',
          driver_rating: driver.rating ? String(driver.rating) : '',
          driver_total_trips: driver.total_trips_completed ? String(driver.total_trips_completed) : '0',
          joining_bonus: !!originalData.joining_bonus,
          is_transporter: !!vehicle.is_transporter,
          transporter_id: vehicle.transporter_id != null ? Number(vehicle.transporter_id) : null,
          transport_routes: routesFromApi(vehicle.transport_routes || []),
        });
      }
    } catch (error) {
      console.error('Error fetching complete vehicle details:', error);
      setRegisteredOwner(null);
      setOwnerForm({ name: '', phone: '' });
      setOwnerFormOpen(false);
      // Fallback to original implementation on error
      const originalData = vehicle.originalData || {};
      const driver = originalData.driver || {};

      setEditFormData({
        // Use original fallback logic
        registration_no: originalData.registration_no || vehicle.registration_no || `REG-${vehicle.vehicle_id}`,
        vehicle_name: '',
        make: originalData.make || '',
        model: originalData.model || '',
        class: originalData.class_ ? String(originalData.class_) : (originalData.class ? String(originalData.class) : '1'),
        is_active: originalData.is_active !== undefined ? originalData.is_active : (vehicle.is_active !== undefined ? vehicle.is_active : true),
        is_blocked: originalData.is_blocked !== undefined ? originalData.is_blocked : !!vehicle.is_blocked,
        status: originalData.status || vehicle.current_status || 'idle',
        capacity: originalData.capacity ? String(originalData.capacity) : '',
        capacity_uom: originalData.capacity_uom || 'ton',
        body_type: originalData.body_type || '',
        specialization: originalData.specialization || '',
        length_ft: originalData.length_ft != null ? String(originalData.length_ft) : (originalData.length != null ? String(originalData.length) : ''),
        breadth_ft: originalData.breadth_ft != null ? String(originalData.breadth_ft) : (originalData.breadth != null ? String(originalData.breadth) : ''),
        height_ft: originalData.height_ft != null ? String(originalData.height_ft) : (originalData.height != null ? String(originalData.height) : ''),
        insurance_number: originalData.insurance_number || '',
        insurance_expiry: originalData.insurance_expiry || '',
        base_lat: originalData.base_lat ? String(originalData.base_lat) : '',
        base_lng: originalData.base_lng ? String(originalData.base_lng) : '',

        driver_name: driver.name || '',
        driver_phone: driver.phone || '',
        driver_language_preference: driver.language_preference || '',
        driver_active_km_rate: driver.active_km_rate ? String(driver.active_km_rate) : '',
        driver_dead_km_rate: driver.dead_km_rate ? String(driver.dead_km_rate) : '',
        driver_verification_status: driver.verification_status || 'pending',
        driver_remarks: vehicle.remarks || driver.remarks || '',

        driver_license_number: driver.license_number || '',
        driver_license_expiry: driver.license_expiry ? new Date(driver.license_expiry).toISOString().split('T')[0] : '',
        driver_rating: driver.rating ? String(driver.rating) : '',
        driver_total_trips: driver.total_trips_completed ? String(driver.total_trips_completed) : '0',
        joining_bonus: !!originalData.joining_bonus,
      });
    }
  };

  useEffect(() => {
    if (!popupVehicleId) return;
    openVehicleModal({ vehicle_id: Number(popupVehicleId) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [popupVehicleId]);

  useEffect(() => {
    const query = new URLSearchParams(location?.search || '');
    const tab = String(query.get('tab') || '').toLowerCase();
    if (tab === 'transporters' || tab === 'transporter') return;
    const listSearch = query.get('search') || query.get('openVehicle');
    if (!listSearch) return;
    setActiveTab('vehicles');
    setSelectedVehicle(null);
    setSearchTerm(String(listSearch));
    setVehiclesCurrentPage(1);
    document.body.style.overflow = 'unset';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const closeVehicleModal = () => {
    setSelectedVehicle(null);
    setIsEditing(false);
    setRegisteredOwner(null);
    setOwnerForm({ name: '', phone: '' });
    setOwnerFormOpen(false);
    setShowLightbox(false);
    setLightboxImage('');
    setVehicleDriverCallLogs([]);
    setVehicleDriverCallLogsTotal(0);
    setVehicleDriverCallLogsPage(1);
    setVehicleDriverCallLogsRef({ driverId: null, driverPhone: null });
    setExpandedVehicleCallLogId(null);
    setPlayingVehicleRecordingId(null);
    setShowUnknownCallLogs(false);
    setVehicleUnknownCallLogs([]);
    setVehicleUnknownCallLogsTotal(0);
    setVehicleUnknownCallLogsPage(1);
    setExpandedVehicleUnknownCallLogId(null);
    document.body.style.overflow = 'unset'; // Restore scrolling
    if (onPopupClose) onPopupClose();
  };

  const fetchVehicleDriverCallLogs = async (driverId, driverPhone, page = 1) => {
    if (!driverId && !driverPhone) {
      setVehicleDriverCallLogs([]);
      setVehicleDriverCallLogsTotal(0);
      return;
    }
    setVehicleDriverCallLogsRef({ driverId, driverPhone });
    setVehicleDriverCallLogsPage(page);
    setVehicleDriverCallLogsLoading(true);
    try {
      const token = localStorage.getItem('token') || 'mock-jwt-token-for-testing';
      const offset = (page - 1) * VEHICLE_CALL_LOGS_PAGE_SIZE;
      let url = `${config.opsApiBase}/call-logs?limit=${VEHICLE_CALL_LOGS_PAGE_SIZE}&offset=${offset}&caller_type=driver`;
      if (driverId) {
        url += `&driver_id=${driverId}`;
      } else if (driverPhone) {
        url += `&driver_phone=${encodeURIComponent(driverPhone)}`;
      }

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (response.ok) {
        const data = await response.json();
        setVehicleDriverCallLogs(data.data || []);
        setVehicleDriverCallLogsTotal(data.total || 0);
      } else {
        setVehicleDriverCallLogs([]);
        setVehicleDriverCallLogsTotal(0);
      }
    } catch (err) {
      console.error('Failed to fetch driver call logs:', err);
      setVehicleDriverCallLogs([]);
      setVehicleDriverCallLogsTotal(0);
    } finally {
      setVehicleDriverCallLogsLoading(false);
    }
  };

  const fetchVehicleUnknownCallLogs = async (driverId, driverPhone, page = 1) => {
    if (!driverId && !driverPhone) return;
    setVehicleUnknownCallLogsPage(page);
    setVehicleUnknownCallLogsLoading(true);
    try {
      const token = localStorage.getItem('token') || 'mock-jwt-token-for-testing';
      const offset = (page - 1) * VEHICLE_CALL_LOGS_PAGE_SIZE;
      let url = `${config.opsApiBase}/call-logs?limit=${VEHICLE_CALL_LOGS_PAGE_SIZE}&offset=${offset}&caller_type=unknown`;
      if (driverId) {
        url += `&driver_id=${driverId}`;
      } else if (driverPhone) {
        url += `&driver_phone=${encodeURIComponent(driverPhone)}`;
      }
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (response.ok) {
        const data = await response.json();
        setVehicleUnknownCallLogs(data.data || []);
        setVehicleUnknownCallLogsTotal(data.total || 0);
      } else {
        setVehicleUnknownCallLogs([]);
        setVehicleUnknownCallLogsTotal(0);
      }
    } catch (err) {
      console.error('Failed to fetch unknown call logs:', err);
      setVehicleUnknownCallLogs([]);
      setVehicleUnknownCallLogsTotal(0);
    } finally {
      setVehicleUnknownCallLogsLoading(false);
    }
  };

  const handleEditToggle = () => {
    setIsEditing(!isEditing);
  };

  const handleToggleActiveState = async () => {
    if (!selectedVehicle) return;

    const newActiveState = !editFormData.is_active;
    const confirmMessage = newActiveState
      ? 'Are you sure you want to ACTIVATE this vehicle?'
      : 'Are you sure you want to DEACTIVATE this vehicle?';

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.opsApiBase}/vehicles/${selectedVehicle.vehicle_id}/active?active=${newActiveState}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        setEditFormData(prev => ({ ...prev, is_active: newActiveState }));
        setSelectedVehicle(prev => ({ ...prev, is_active: newActiveState }));
        alert(`Vehicle ${newActiveState ? 'activated' : 'deactivated'} successfully!`);
      } else {
        const data = await response.json();
        alert(`Failed to update state: ${data.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error toggling vehicle state:', error);
      alert('Error updating vehicle state');
    }
  };

  const handleToggleBlockedState = async () => {
    if (!selectedVehicle) return;

    const newBlockedState = !editFormData.is_blocked;
    const confirmMessage = newBlockedState
      ? 'Block this vehicle? Assignment stops until ops unblocks; drivers cannot activate via the app while blocked.'
      : 'Unblock this vehicle?';

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.opsApiBase}/vehicles/${selectedVehicle.vehicle_id}/blocked?blocked=${newBlockedState}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        setEditFormData(prev => ({ ...prev, is_blocked: newBlockedState }));
        setSelectedVehicle(prev => ({ ...prev, is_blocked: newBlockedState }));
        alert(`Vehicle ${newBlockedState ? 'blocked' : 'unblocked'} successfully!`);
      } else {
        const data = await response.json();
        alert(`Failed to update blocked state: ${data.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error toggling vehicle blocked state:', error);
      alert('Error updating blocked state');
    }
  };

  // Keep handleGeocodeBasePlace as a fallback (used if Google Maps not available)
  const handleGeocodeBasePlace = async () => {
    const placeValue = document.getElementById('base-location-search')?.value?.trim();

    if (!placeValue) {
      alert('Please enter a place or address');
      return;
    }

    setIsGeocodingBase(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.opsApiBase}/onboarding/resolve-location`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ address_text: placeValue }),
      });
      const data = await response.json();
      if (response.ok && data.latitude && data.longitude) {
        setEditFormData(prev => ({
          ...prev,
          base_lat: data.latitude.toString(),
          base_lng: data.longitude.toString(),
          resolved_address: data.resolved_address || data.formatted_address || placeValue
        }));
      } else {
        alert(data.message || `Could not find coordinates for "${placeValue}". Please try a different search term.`);
      }
    } catch (error) {
      console.error('Geocoding error:', error);
      alert('Error getting coordinates. Please try again.');
    } finally {
      setIsGeocodingBase(false);
    }
  };

  const loadVehicleNamesForClass = async (classNumber) => {
    setIsLoadingVehicleNames(true);
    try {
      const vehicles = await getVehicleNamesByClass(classNumber);
      setAvailableVehicleNames(vehicles);
    } catch (error) {
      console.error('Error loading vehicle names:', error);
      setAvailableVehicleNames([]);
    } finally {
      setIsLoadingVehicleNames(false);
    }
  };

  const handleVehicleNameChange = async (e) => {
    const selectedName = e.target.value;

    // If using dropdown (file 2 approach)
    const vehicle = availableVehicleNames.find(v => v.vehicle_name === selectedName);

    if (vehicle) {
      setEditFormData(prev => ({
        ...prev,
        vehicle_name: vehicle.vehicle_name,
        make: vehicle.make,
        model: vehicle.model
      }));
    } else {
      // If using text input with autocomplete (file 1 approach)
      setEditFormData(prev => ({
        ...prev,
        vehicle_name: selectedName
      }));

      // Only search if user has typed something
      if (selectedName && selectedName.trim()) {
        const vehicleDetails = await getVehicleDetailsByName(selectedName);
        if (vehicleDetails) {
          setEditFormData(prev => ({
            ...prev,
            vehicle_name: selectedName,
            make: vehicleDetails.make,
            model: vehicleDetails.model,
            tonnage: vehicleDetails.tonnage ? String(vehicleDetails.tonnage) : prev.tonnage,
            class: prev.class && String(prev.class).trim() !== ''
              ? prev.class
              : vehicleDetails.class.toString()
          }));
        }
      }
    }
  };

  const handleEditFormChange = async (e) => {
    const { name, value } = e.target;

    // If vehicle_name changes (file 1 approach - text input)
    if (name === 'vehicle_name') {
      setEditFormData(prev => ({
        ...prev,
        vehicle_name: value
      }));

      // Only search if user has typed something
      if (value && value.trim()) {
        const vehicleDetails = await getVehicleDetailsByName(value);
        if (vehicleDetails) {
          setEditFormData(prev => ({
            ...prev,
            vehicle_name: value,
            make: vehicleDetails.make,
            model: vehicleDetails.model,
            tonnage: vehicleDetails.tonnage ? String(vehicleDetails.tonnage) : prev.tonnage,
            class: prev.class && String(prev.class).trim() !== ''
              ? prev.class
              : vehicleDetails.class.toString()
          }));
        }
      }
      return;
    }

    // If class changes (file 2 approach - reload dropdown)
    if (name === 'class') {
      const classNum = parseInt(value);
      if (classNum >= 1 && classNum <= 15) {
        loadVehicleNamesForClass(classNum);
        // Reset vehicle name, make, and model when class changes
        setEditFormData(prev => ({
          ...prev,
          class: value,
          vehicle_name: '',
          make: '',
          model: ''
        }));
        return;
      }
    }

    setEditFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const fetchDriverDocs = async (vehicleId) => {
    setDocsLoading(true);
    setDocsError('');
    setDriverDocs(null);
    try {
      const token = localStorage.getItem('token');
      const base = config.opsApiBase.replace('/ops', '');
      const res = await fetch(`${base}/ops/onboarding/driver-documents/${vehicleId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        setDriverDocs(data.documents);
      } else {
        setDocsError(data.message || 'Failed to load documents');
      }
    } catch (err) {
      setDocsError(err.message || 'Error loading documents');
    } finally {
      setDocsLoading(false);
    }
  };

  const openDocsModal = () => {
    setShowDocsModal(true);
    if (selectedVehicle?.vehicle_id) fetchDriverDocs(selectedVehicle.vehicle_id);
  };

  const handleDeleteDriver = async () => {
    if (!selectedVehicle?.vehicle_id) {
      alert('No vehicle selected.');
      return;
    }
    const vehicleLabel = `V-${selectedVehicle.vehicle_id}`;
    const driverLabel = selectedVehicle.linked_driver_name
      ? `"${selectedVehicle.linked_driver_name}" (${selectedVehicle.linked_driver_phone})`
      : selectedVehicle.linked_driver_id
        ? `driver ID ${selectedVehicle.linked_driver_id}`
        : 'linked driver';
    const confirmed = window.confirm(
      `Delete ${vehicleLabel}?\n\nThis permanently removes this vehicle from the system. The driver profile and all past trips will be preserved.\n\nThis cannot be undone.\n\nClick OK to confirm.`
    );
    if (!confirmed) return;

    try {
      const token = localStorage.getItem('token') || 'mock-jwt-token-for-testing';
      const response = await fetch(`${config.opsApiBase}/vehicles/${selectedVehicle.vehicle_id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json().catch(() => ({}));
        alert(data.message || '✅ Vehicle deleted successfully.');
        closeVehicleModal();
        if (fetchVehiclesRef.current) fetchVehiclesRef.current();
      } else {
        const data = await response.json().catch(() => ({}));
        alert(`Failed to delete vehicle: ${data.detail || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Error deleting vehicle:', err);
      alert('Error deleting vehicle. Please try again.');
    }
  };

  const addDetailPhoneExtension = () => {
    const inputVal = detailExtPhone.trim();
    const labelVal = detailExtLabel.trim();
    if (!inputVal) return;
    if (!/^\+?\d{8,15}$/.test(inputVal)) {
      alert('Please enter a valid phone number (8 to 15 digits)');
      return;
    }
    if (editFormData.phone_extensions?.some(ent => extPhoneOf(ent) === inputVal)) {
      alert('This phone number is already added');
      return;
    }
    setEditFormData(prev => ({
      ...prev,
      phone_extensions: [...(prev.phone_extensions || []), { phone: inputVal, label: labelVal || null }]
    }));
    setDetailExtPhone('');
    setDetailExtLabel('');
  };

  const handleSaveVehicle = async () => {
    try {
      // Get token for authentication
      const token = localStorage.getItem('token') || 'mock-jwt-token-for-testing';

      // Use your FastAPI backend endpoint
      const response = await fetch(`${config.opsApiBase}/vehicles/${selectedVehicle.vehicle_id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          // Vehicle fields (make and model can be updated via vehicle name dropdown)
          registration_no: editFormData.registration_no,
          make: editFormData.make || null,
          model: editFormData.model || null,
          class_: parseInt(editFormData.class) || 1,
          status: editFormData.status,
          capacity: parseFloat(editFormData.capacity) || null,
          capacity_uom: editFormData.capacity_uom,
          body_type: editFormData.body_type,
          specialization: editFormData.specialization,
          tonnage: parseFloat(editFormData.tonnage) || null,
          length: parseFloat(editFormData.length_ft) || null,
          breadth: parseFloat(editFormData.breadth_ft) || null,
          height: parseFloat(editFormData.height_ft) || null,
          insurance_number: editFormData.insurance_number || null,
          insurance_expiry: editFormData.insurance_expiry || null,
          base_lat: parseFloat(editFormData.base_lat) || null,
          base_lng: parseFloat(editFormData.base_lng) || null,
          base_location: editFormData.base_location,
          base_address: editFormData.base_location,
          phone_extensions: editFormData.phone_extensions || [],
          remarks: editFormData.driver_remarks?.trim() || null,
          ...(editFormData.is_transporter
            ? { transport_routes: routesToApiPayload(editFormData.transport_routes) }
            : {}),
          // Driver fields (editable, excluding read-only: license_number)
          driver: {
            name: editFormData.driver_name,
            phone: editFormData.driver_phone,
            language_preference: editFormData.driver_language_preference,
            active_km_rate: parseFloat(editFormData.driver_active_km_rate) || null,
            dead_km_rate: parseFloat(editFormData.driver_dead_km_rate) || null,
            verification_status: editFormData.driver_verification_status,
            remarks: editFormData.driver_remarks?.trim() || null,
          }
        }),
      });

      if (response.ok) {
        const savedLocation = String(editFormData.base_location ?? '').trim();
        const savedLat = parseFloat(editFormData.base_lat);
        const savedLng = parseFloat(editFormData.base_lng);
        const vehicleId = selectedVehicle.vehicle_id;

        if (savedLocation) {
          setBaseLocationCache((prev) => ({ ...prev, [vehicleId]: savedLocation }));
          if (savedLat && savedLng) {
            localStorage.setItem(`base_loc_${vehicleId}_${savedLat}_${savedLng}`, savedLocation);
          }
        }

        setSelectedVehicle((prev) => prev ? {
          ...prev,
          base_location: savedLocation,
          base_lat: savedLat || prev.base_lat,
          base_lng: savedLng || prev.base_lng,
          transport_routes: editFormData.is_transporter
            ? routesToApiPayload(editFormData.transport_routes)
            : prev.transport_routes,
          phone_extensions: editFormData.phone_extensions || [],
          make: editFormData.make || '',
          model: editFormData.model || '',
          body_type: editFormData.body_type || '',
          tonnage: editFormData.tonnage ? parseFloat(editFormData.tonnage) : null,
          length: editFormData.length_ft ? parseFloat(editFormData.length_ft) : null,
          remarks: editFormData.driver_remarks?.trim() || '',
        } : prev);

        const updatedSpecs = {
          make: editFormData.make || '',
          model: editFormData.model || '',
          body_type: editFormData.body_type || '',
          tonnage: editFormData.tonnage ? parseFloat(editFormData.tonnage) : null,
          length: editFormData.length_ft ? parseFloat(editFormData.length_ft) : null,
          remarks: editFormData.driver_remarks?.trim() || '',
        };
        setVehicles((prev) => prev.map(v => v.vehicle_id === vehicleId ? { ...v, ...updatedSpecs } : v));
        setFilteredVehicles((prev) => prev.map(v => v.vehicle_id === vehicleId ? { ...v, ...updatedSpecs } : v));

        setIsEditing(false);
        // Soft-refresh the vehicles list — no hard reload, pagination stays intact
        if (fetchVehiclesRef.current) fetchVehiclesRef.current();
      } else {
        const data = await response.json();
        alert(`Failed to update vehicle: ${data.detail || data.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error updating vehicle:', error);
      alert('Error updating vehicle. Please try again.');
    }
  };

  const openSpecsModal = (vehicle) => {
    setSpecsVehicle(vehicle);
    setSpecsFormData({
      per_km_price: vehicle.per_km_price != null ? String(vehicle.per_km_price) : '',
      ready_for_long_trip: !!vehicle.ready_for_long_trip,
      long_inter_district: !!vehicle.long_inter_district,
      long_inter_state: !!vehicle.long_inter_state,
      local: !!vehicle.local,
      intra_state_permit: !!vehicle.intra_state_permit,
      body_type: vehicle.body_type || '',
      tonnage: vehicle.tonnage != null ? String(vehicle.tonnage) : '',
      phone_extensions: Array.isArray(vehicle.phone_extensions) ? [...vehicle.phone_extensions] : [],
    });
    setNewExtPhone('');
    setSpecsError('');
    setShowSpecsModal(true);
    document.body.style.overflow = 'hidden';
  };

  const closeSpecsModal = () => {
    setShowSpecsModal(false);
    setSpecsVehicle(null);
    document.body.style.overflow = '';
  };

  const handleSpecsFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSpecsFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleAddPhoneExtension = () => {
    const val = newExtPhone.trim();
    if (!val) return;
    if (!/^\+?\d{8,15}$/.test(val)) {
      setSpecsError('Please enter a valid phone number (8 to 15 digits)');
      return;
    }
    if (specsFormData.phone_extensions.some(e => extPhoneOf(e) === val)) {
      setSpecsError('This phone number is already added');
      return;
    }
    const label = newExtLabel.trim();
    setSpecsFormData(prev => ({
      ...prev,
      phone_extensions: [...prev.phone_extensions, { phone: val, label: label || null }]
    }));
    setNewExtPhone('');
    setNewExtLabel('');
    setSpecsError('');
  };

  const handleRemovePhoneExtension = (phoneToRemove) => {
    setSpecsFormData(prev => ({
      ...prev,
      phone_extensions: prev.phone_extensions.filter(e => extPhoneOf(e) !== phoneToRemove)
    }));
  };

  const handleSaveSpecs = async () => {
    if (!specsVehicle) return;
    setIsSavingSpecs(true);
    setSpecsError('');

    try {
      const token = localStorage.getItem('token') || 'mock-jwt-token-for-testing';
      const response = await fetch(`${config.opsApiBase}/vehicles/${specsVehicle.vehicle_id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          per_km_price: specsFormData.per_km_price ? parseFloat(specsFormData.per_km_price) : null,
          ready_for_long_trip: specsFormData.ready_for_long_trip,
          long_inter_district: specsFormData.long_inter_district,
          long_inter_state: specsFormData.long_inter_state,
          local: specsFormData.local,
          intra_state_permit: specsFormData.intra_state_permit,
          body_type: specsFormData.body_type || null,
          tonnage: specsFormData.tonnage ? parseFloat(specsFormData.tonnage) : null,
          phone_extensions: specsFormData.phone_extensions,
        })
      });

      if (response.ok) {
        // Update both main state lists
        setVehicles(prev => prev.map(v => v.vehicle_id === specsVehicle.vehicle_id ? {
          ...v,
          per_km_price: specsFormData.per_km_price ? parseFloat(specsFormData.per_km_price) : null,
          ready_for_long_trip: specsFormData.ready_for_long_trip,
          long_inter_district: specsFormData.long_inter_district,
          long_inter_state: specsFormData.long_inter_state,
          local: specsFormData.local,
          intra_state_permit: specsFormData.intra_state_permit,
          body_type: specsFormData.body_type || null,
          tonnage: specsFormData.tonnage ? parseFloat(specsFormData.tonnage) : null,
          phone_extensions: specsFormData.phone_extensions,
        } : v));

        setFilteredVehicles(prev => prev.map(v => v.vehicle_id === specsVehicle.vehicle_id ? {
          ...v,
          per_km_price: specsFormData.per_km_price ? parseFloat(specsFormData.per_km_price) : null,
          ready_for_long_trip: specsFormData.ready_for_long_trip,
          long_inter_district: specsFormData.long_inter_district,
          long_inter_state: specsFormData.long_inter_state,
          local: specsFormData.local,
          intra_state_permit: specsFormData.intra_state_permit,
          body_type: specsFormData.body_type || null,
          tonnage: specsFormData.tonnage ? parseFloat(specsFormData.tonnage) : null,
          phone_extensions: specsFormData.phone_extensions,
        } : v));

        closeSpecsModal();
        if (fetchVehiclesRef.current) fetchVehiclesRef.current();
      } else {
        const data = await response.json();
        setSpecsError(data.detail || data.error || 'Failed to update vehicle specifications');
      }
    } catch (err) {
      console.error('Error saving vehicle specs:', err);
      setSpecsError('Failed to save changes. Please try again.');
    } finally {
      setIsSavingSpecs(false);
    }
  };

  const handleMarkJoiningBonusPaid = async () => {
    if (!selectedVehicle?.vehicle_id) return;
    if (editFormData.joining_bonus) return;

    const confirmed = window.confirm('Mark joining bonus as paid for this vehicle/driver?');
    if (!confirmed) return;

    try {
      setIsMarkingJoiningBonus(true);
      const token = localStorage.getItem('token') || 'mock-jwt-token-for-testing';

      const response = await fetch(`${config.opsApiBase}/vehicles/${selectedVehicle.vehicle_id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ joining_bonus: true }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || data.error || 'Failed to update joining bonus');
      }

      setEditFormData((prev) => ({ ...prev, joining_bonus: true }));
      setSelectedVehicle((prev) => ({
        ...prev,
        originalData: {
          ...(prev?.originalData || {}),
          joining_bonus: true,
        },
      }));

      alert('Joining bonus marked as paid successfully.');
    } catch (error) {
      console.error('Error marking joining bonus as paid:', error);
      alert(error.message || 'Failed to mark joining bonus as paid');
    } finally {
      setIsMarkingJoiningBonus(false);
    }
  };

  const handleSaveOwner = async () => {
    if (!selectedVehicle?.vehicle_id) {
      alert('No vehicle selected');
      return;
    }
    if (!ownerForm.name.trim() || !ownerForm.phone.trim()) {
      alert('Please enter both name and phone number');
      return;
    }

    const cleanPhone = ownerForm.phone.replace(/\D/g, '').replace(/^0+/, '');
    const isValidPhone = cleanPhone.length === 10 || (cleanPhone.length === 12 && cleanPhone.startsWith('91'));
    if (!isValidPhone) {
      alert('Please enter a valid 10-digit Indian phone number.');
      return;
    }

    setOwnerSaving(true);
    try {
      const token = localStorage.getItem('token') || 'mock-jwt-token-for-testing';
      const response = await fetch(`${config.opsApiBase}/vehicles/${selectedVehicle.vehicle_id}/set-owner`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          owner_name: ownerForm.name.trim(),
          owner_phone: ownerForm.phone.trim()
        })
      });
      const data = await response.json();
      if (response.ok) {
        alert('Owner updated successfully');
        setRegisteredOwner(data.owner);
        setOwnerFormOpen(false);
      } else {
        alert(data.detail || 'Failed to update owner');
      }
    } catch (err) {
      console.error('Error saving owner:', err);
      alert('Error: ' + err.message);
    } finally {
      setOwnerSaving(false);
    }
  };

  const openAssignBookingModal = (vehicle) => {
    setSelectedVehicleForAssignment(vehicle);
    setBookingSearchTerm(''); // Reset search
    // Reset validation state
    setSelectedBookingForValidation(null);
    setValidationStep('select');
    setValidationResult(null);
    setWarningsAcknowledged(false);
    document.body.style.overflow = 'hidden';
  };

  const closeAssignBookingModal = () => {
    setSelectedVehicleForAssignment(null);
    setBookingSearchTerm('');
    // Reset validation state
    setSelectedBookingForValidation(null);
    setValidationStep('select');
    setValidationResult(null);
    setWarningsAcknowledged(false);
    document.body.style.overflow = 'unset';
  };

  // ==================== BONUS PAYMENT FUNCTIONS ====================

  const openBonusPayModal = (vehicle) => {
    // Check if driver is assigned
    if (!vehicle.driver_id) {
      alert('❌ No driver assigned to this vehicle. Please assign a driver first.');
      return;
    }

    setSelectedVehicleForBonus(vehicle);
    setShowBonusPayModal(true);
    setBonusDate(new Date().toISOString().split('T')[0]); // Default to today
    setSelectedTripId('');
    setBonusReason('');
    setBonusAmount('');
    setShowBonusConfirmation(false);
    document.body.style.overflow = 'hidden';
    // Fetch trips for today
    fetchTripsForDate(vehicle.vehicle_id, new Date().toISOString().split('T')[0]);
  };

  const closeBonusPayModal = () => {
    setShowBonusPayModal(false);
    setSelectedVehicleForBonus(null);
    setBonusTrips([]);
    setShowBonusConfirmation(false);
    document.body.style.overflow = 'unset';
  };

  const openBonusHistoryModal = async (vehicle) => {
    setSelectedVehicleForBonus(vehicle);
    setShowBonusHistoryModal(true);
    setBonusHistoryDateFilter({ startDate: '', endDate: '' });
    setBonusHistoryCurrentPage(1);
    document.body.style.overflow = 'hidden';
    await fetchBonusHistory(vehicle.vehicle_id);
  };

  const closeBonusHistoryModal = () => {
    setShowBonusHistoryModal(false);
    setSelectedVehicleForBonus(null);
    setBonusHistory([]);
    setBonusHistoryDateFilter({ startDate: '', endDate: '' });
    setBonusHistoryCurrentPage(1);
    document.body.style.overflow = 'unset';
  };

  const fetchTripsForDate = async (vehicleId, date) => {
    setIsLoadingTrips(true);
    try {
      const response = await fetch(
        `${config.dispatchApiBase}/vehicle/${vehicleId}/trips-by-date?date=${date}`
      );

      if (response.ok) {
        const data = await response.json();
        setBonusTrips(data.trips || []);
      } else {
        console.error('Failed to fetch trips');
        setBonusTrips([]);
      }
    } catch (error) {
      console.error('Error fetching trips:', error);
      setBonusTrips([]);
    } finally {
      setIsLoadingTrips(false);
    }
  };

  const fetchBonusHistory = async (vehicleId) => {
    setIsLoadingHistory(true);
    try {
      const response = await fetch(
        `${config.dispatchApiBase}/vehicle/${vehicleId}/bonus-payments?limit=100`
      );

      if (response.ok) {
        const data = await response.json();
        setBonusHistory(data.payments || []);
      } else {
        console.error('Failed to fetch bonus history');
        setBonusHistory([]);
      }
    } catch (error) {
      console.error('Error fetching bonus history:', error);
      setBonusHistory([]);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  const handleEditBonus = (bonus) => {
    setEditingBonusId(bonus.id);
    setEditBonusData({
      date: bonus.date,
      trip_id: bonus.trip_id || '',
      amount: bonus.amount.toString(),
      reason: bonus.reason
    });
    // Fetch trips for the selected date
    fetchTripsForDate(selectedVehicleForBonus.vehicle_id, bonus.date);
  };

  const handleCancelEditBonus = () => {
    setEditingBonusId(null);
    setEditBonusData({ date: '', trip_id: '', amount: '', reason: '' });
  };

  const handleSaveEditBonus = async () => {
    // Validation
    if (!editBonusData.date) {
      alert('Date is required');
      return;
    }
    if (!editBonusData.amount || parseFloat(editBonusData.amount) <= 0) {
      alert('Valid amount is required');
      return;
    }
    if (!editBonusData.reason || editBonusData.reason.trim().length < 10) {
      alert('Reason must be at least 10 characters');
      return;
    }

    setIsUpdatingBonus(true);
    try {
      const payload = {
        date: editBonusData.date,
        trip_id: editBonusData.trip_id ? parseInt(editBonusData.trip_id) : 0,
        amount: parseFloat(editBonusData.amount),
        reason: editBonusData.reason.trim()
      };

      const response = await fetch(`${config.dispatchApiBase}/bonus-payment/${editingBonusId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        alert('✅ Bonus payment updated successfully!');
        setEditingBonusId(null);
        setEditBonusData({ date: '', trip_id: '', amount: '', reason: '' });
        // Refresh history
        await fetchBonusHistory(selectedVehicleForBonus.vehicle_id);
      } else {
        const errorData = await response.json();
        alert(`Failed to update bonus payment: ${errorData.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error updating bonus payment:', error);
      alert('Error updating bonus payment. Please try again.');
    } finally {
      setIsUpdatingBonus(false);
    }
  };

  const handleBonusDateChange = (newDate) => {
    setBonusDate(newDate);
    setSelectedTripId(''); // Reset trip selection
    if (selectedVehicleForBonus) {
      fetchTripsForDate(selectedVehicleForBonus.vehicle_id, newDate);
    }
  };

  const handleSetToday = () => {
    const today = new Date().toISOString().split('T')[0];
    handleBonusDateChange(today);
  };

  const validateBonusForm = () => {
    if (!bonusDate) {
      alert('Please select a date');
      return false;
    }

    // Check if date is in the future
    if (new Date(bonusDate) > new Date()) {
      alert('Date cannot be in the future');
      return false;
    }

    if (!bonusReason.trim() || bonusReason.trim().length < 10) {
      alert('Please provide a reason (minimum 10 characters)');
      return false;
    }

    if (!bonusAmount || parseFloat(bonusAmount) <= 0) {
      alert('Please enter a valid amount greater than 0');
      return false;
    }

    return true;
  };

  const handleBonusSubmit = () => {
    if (!validateBonusForm()) {
      return;
    }
    setShowBonusConfirmation(true);
  };

  const handleBonusConfirm = async () => {
    setIsSubmittingBonus(true);

    try {
      const operatorName = localStorage.getItem('user_name') ||
        localStorage.getItem('username') ||
        'Unknown Operator';

      // Ensure driver_id exists
      if (!selectedVehicleForBonus.driver_id) {
        alert('❌ No driver assigned to this vehicle. Cannot create bonus payment.');
        setIsSubmittingBonus(false);
        setShowBonusConfirmation(false);
        return;
      }

      const payload = {
        vehicle_id: selectedVehicleForBonus.vehicle_id,
        driver_id: selectedVehicleForBonus.driver_id,
        date: bonusDate,
        trip_id: selectedTripId ? parseInt(selectedTripId) : null,
        amount: parseFloat(bonusAmount),
        reason: bonusReason.trim(),
        created_by: operatorName
      };

      const response = await fetch(`${config.dispatchApiBase}/bonus-payment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const data = await response.json();
        alert(`✅ Bonus payment of ₹${bonusAmount} added successfully!\nIt will be marked as paid when added to driver's daily payment.`);
        closeBonusPayModal();
      } else {
        const errorData = await response.json();
        alert(`Failed to create bonus payment: ${errorData.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error creating bonus payment:', error);
      alert('Error creating bonus payment. Please try again.');
    } finally {
      setIsSubmittingBonus(false);
      setShowBonusConfirmation(false);
    }
  };

  // ==================== END BONUS PAYMENT FUNCTIONS ====================
  // Vehicle Chat Modal Functions
  const openVehicleChatModal = async (vehicle) => {
    setSelectedVehicleForChat(vehicle);
    document.body.style.overflow = 'hidden';
    shouldAutoScrollChatRef.current = true;

    // Fetch chat history
    try {
      const token = localStorage.getItem('token') || 'mock-jwt-token-for-testing';
      const response = await fetch(`${config.opsApiBase}/vehicles/${vehicle.vehicle_id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });


      if (response.ok) {
        const data = await response.json();
        setChatHistory(Array.isArray(data.chat_history) ? data.chat_history : []);
        setIsChatBotPaused(data.bot_paused || false);
        // Read has_app from driver info
        setDriverHasApp(data.driver?.has_app || false);
      } else {
        setChatHistory([]);
        setDriverHasApp(false);
      }
    } catch (error) {
      console.error('Error fetching chat history:', error);
      setChatHistory([]);
    }
  };

  const closeVehicleChatModal = () => {
    setSelectedVehicleForChat(null);
    setChatHistory([]);
    setChatMessage('');
    setDriverHasApp(false);
    document.body.style.overflow = 'unset';
  };

  const handleSendChatMessage = async () => {
    if (!chatMessage.trim() || isSendingMessage) return;

    setIsSendingMessage(true);
    try {
      const token = localStorage.getItem('token') || 'mock-jwt-token-for-testing';
      const operatorName = localStorage.getItem('user_name') || localStorage.getItem('username') || 'Operator';

      // Optimistically add message to UI
      const optimisticMessage = {
        sender: operatorName,
        message: chatMessage,
        timestamp: new Date().toISOString(),
        role: 'operator',
        status: 'pending'
      };

      setChatHistory(prev => [...prev, optimisticMessage]);
      const payloadToSend = chatMessage;
      setChatMessage(''); // Clear input immediately

      // Send via WebSocket-enabled endpoint
      const response = await fetch(
        `${config.opsApiBase}/vehicles/${selectedVehicleForChat.vehicle_id}/chat`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            message: payloadToSend,
            sender: operatorName
          })
        }
      );

      if (!response.ok) {
        // If failed, mark message as failed
        console.error('[Vehicle Chat] Failed to send message');
        setChatHistory(prev => prev.map(msg =>
          msg === optimisticMessage ? { ...msg, status: 'failed' } : msg
        ));
      }
      // WebSocket will handle updating the message with server confirmation

    } catch (error) {
      console.error('[Vehicle Chat] Error sending message:', error);
      alert('Error sending message');
    } finally {
      setIsSendingMessage(false);
    }
  };

  const handleToggleChatBot = async () => {
    try {
      const token = localStorage.getItem('token') || 'mock-jwt-token-for-testing';
      const operatorId = localStorage.getItem('user_id') || 1;

      const response = await fetch(`${config.opsApiBase}/vehicles/${selectedVehicleForChat.vehicle_id}/toggle-bot`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          paused: !isChatBotPaused,
          operator_id: operatorId
        })
      });

      if (response.ok) {
        setIsChatBotPaused(!isChatBotPaused);
      } else {
        const data = await response.json().catch(() => ({}));
        if (data.detail && data.detail.includes('No active trip')) {
          alert('⚠️ No active trip found for this driver. The driver needs an active trip to toggle bot mode.');
        } else {
          alert('Failed to toggle bot: ' + (data.detail || 'Unknown error'));
        }
      }
    } catch (error) {
      console.error('Error toggling bot:', error);
      alert('Error toggling bot');
    }
  };

  const isChatNearBottom = () => {
    const el = chatContainerRef.current;
    if (!el) return true;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    return distanceFromBottom < 120;
  };

  const handleChatScroll = () => {
    shouldAutoScrollChatRef.current = isChatNearBottom();
  };

  // Auto-scroll only if user is near bottom
  useEffect(() => {
    if (!shouldAutoScrollChatRef.current) return;
    if (chatMessagesEndRef.current) {
      chatMessagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatHistory]);

  // WebSocket connection management for vehicle chat
  useEffect(() => {
    if (!selectedVehicleForChat) {
      // Close WebSocket when modal closes
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
        setWsConnected(false);
      }
      return;
    }

    // Establish WebSocket connection
    const wsProtocol = config.opsApiBase.startsWith('https') ? 'wss' : 'ws';
    const wsBaseUrl = config.opsApiBase.replace(/^https?:\/\//, '');
    // Note: opsApiBase includes /api/v1/ops, so WebSocket URL will be ws://host/api/v1/ops/ws/vehicles/{id}
    const wsUrl = `${wsProtocol}://${wsBaseUrl}/ws/vehicles/${selectedVehicleForChat.vehicle_id}`;

    console.log('[Vehicle Chat] Connecting to WebSocket:', wsUrl);
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      setWsConnected(true);
      console.log('[Vehicle Chat] WebSocket connected');
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);

        if (data.type === 'new_message') {
          console.log('[Vehicle Chat] Received new message:', data.message);

          setChatHistory(prev => {
            // Check if we have a local pending message (no ID) that matches content
            const pendingIndex = prev.findIndex(msg =>
              !msg.id &&
              msg.role === data.message.role &&
              msg.message === data.message.message
            );

            if (pendingIndex !== -1) {
              // Replace pending message with confirmed server message
              const newMessages = [...prev];
              newMessages[pendingIndex] = data.message;
              return newMessages;
            }

            // Check if message already exists (by ID or exact content match)
            const messageExists = prev.some(
              msg => (data.message.id && msg.id === data.message.id) ||
                (msg.message === data.message.message && msg.timestamp === data.message.timestamp)
            );

            if (messageExists) return prev;

            return [...prev, data.message];
          });
        }
        else if (data.type === 'message_status') {
          console.log('[Vehicle Chat] Received status update:', data);

          setChatHistory(prev => {
            return prev.map(msg => {
              if (msg.id === data.message_id) {
                return { ...msg, status: data.status, note: data.note };
              }
              return msg;
            });
          });
        }
        else if (data.type === 'bot_status_changed') {
          console.log('[Vehicle Chat] Bot status changed:', data.bot_paused);
          setIsChatBotPaused(data.bot_paused);
        }
      } catch (err) {
        console.error('[Vehicle Chat] Error parsing WebSocket message:', err);
      }
    };

    ws.onerror = (error) => {
      console.error('[Vehicle Chat] WebSocket error:', error);
      setWsConnected(false);
    };

    ws.onclose = () => {
      setWsConnected(false);
      console.log('[Vehicle Chat] WebSocket closed');
    };

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [selectedVehicleForChat]);

  const refreshChatHistory = async () => {
    if (!selectedVehicleForChat?.vehicle_id || isChatRefreshing) return;
    const vehicleId = selectedVehicleForChat.vehicle_id;
    const token = localStorage.getItem('token') || 'mock-jwt-token-for-testing';

    const mergeServerAndPending = (serverList, prevList) => {
      const server = Array.isArray(serverList) ? serverList : [];
      const pending = (prevList || []).filter(
        (m) => m && m.role === 'operator' && !m.id && m.status === 'pending'
      );
      const pendingToKeep = pending.filter(
        (p) => !server.some((s) => s.role === 'operator' && s.message === p.message && s.id)
      );
      return [...server, ...pendingToKeep];
    };

    shouldAutoScrollChatRef.current = isChatNearBottom();
    setIsChatRefreshing(true);
    try {
      const response = await fetch(`${config.opsApiBase}/vehicles/${vehicleId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) return;
      const data = await response.json();
      const next = Array.isArray(data.chat_history) ? data.chat_history : [];
      setChatHistory((prev) => mergeServerAndPending(next, prev));
      setIsChatBotPaused(Boolean(data.bot_paused));
      setDriverHasApp(Boolean(data.driver?.has_app));
    } catch (e) {
      console.error('[Vehicle Chat] Refresh chat history failed:', e);
    } finally {
      setIsChatRefreshing(false);
    }
  };

  // Close modal on Escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        closeVehicleModal();
        closeAssignBookingModal();
        closeVehicleChatModal();
      }
    };

    if (selectedVehicle || selectedVehicleForAssignment || selectedVehicleForChat) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [selectedVehicle, selectedVehicleForAssignment, selectedVehicleForChat]);

  const [attentionNow, setAttentionNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setAttentionNow(Date.now()), 20000);
    return () => clearInterval(timer);
  }, []);

  const isRecentInbound = (lastMessageAt) => {
    const at = Date.parse(lastMessageAt || '');
    if (!Number.isFinite(at)) return false;
    const age = attentionNow - at;
    return age >= 0 && age < 3 * 60 * 60 * 1000;
  };

  // Check if driver is within 24-hour messaging window
  const isDriverInActiveWindow = (lastMessageAt) => {
    if (!lastMessageAt) return false; // Never messaged = OUT

    const lastMessage = new Date(lastMessageAt);
    const now = new Date();
    const hoursSinceLastMessage = (now - lastMessage) / (1000 * 60 * 60);

    return hoursSinceLastMessage < 24; // IN if within 24 hours
  };

  // Stable ref so the save handler can call fetchVehicles without a reload
  const fetchVehiclesRef = useRef(null);

  // Fetch vehicles data from API
  useEffect(() => {
    if (popupVehicleId) return undefined;
    const fetchVehicles = async (isInitialLoad = false) => {
      try {
        if (isInitialLoad) setLoading(true);

        // Fetch all vehicles — city filtering is done client-side using the
        // resolved base-location addresses stored in baseLocationCache.
        const vehiclesResponse = await fetch(`${config.dispatchApiBase}/vehicles`);
        if (!vehiclesResponse.ok) {
          throw new Error(`Dispatch vehicles failed: ${vehiclesResponse.status}`);
        }

        let driversData = { success: false, data: [] };
        try {
          const driversResponse = await fetch(`${config.opsApiUrl}/vehicle-drivers`);
          const contentType = driversResponse.headers.get('content-type') || '';
          if (driversResponse.ok && contentType.includes('application/json')) {
            driversData = await driversResponse.json();
          } else {
            console.warn(`Drivers endpoint failed: ${driversResponse.status}`);
          }
        } catch (error) {
          console.warn('Primary drivers endpoint failed:', error);
        }

        const vehiclesData = await vehiclesResponse.json();

        if (vehiclesData.success) {
          // Create a map of vehicle_id to driver info for quick lookup
          const driverMap = {};
          if (driversData.success && driversData.data) {
            driversData.data.forEach(item => {
              driverMap[item.vehicle_id] = {
                registration_no: item.registration_no,
                driver_name: item.driver_name,
                driver_phone: item.driver_phone,
                current_location_lat: item.current_location_lat || 0,
                current_location_long: item.current_location_long || 0,
                bot_paused: item.bot_paused || false,
                is_active: item.is_active !== false, // false only if explicitly false
                last_driver_message_at: item.last_driver_message_at || null,
                trip_count: item.trip_count || 0,
                recent_trips_count: item.recent_trips_count || 0,
                completed_trip_count: item.completed_trip_count || 0,
                has_dl: !!item.has_dl,
                has_rc: !!item.has_rc,
                has_insurance: !!item.has_insurance,
                has_emission: !!item.has_emission,
                has_vehicle_photo: !!item.has_vehicle_photo,
                has_upi: !!item.has_upi,
                total_pay: item.total_pay || 0,
                has_app: item.has_app || false,
                is_new: item.is_new || false,
                vehicle_photo_url: item.vehicle_photo_url || null,
                is_transporter: !!item.is_transporter,
                transporter_id: item.transporter_id != null ? Number(item.transporter_id) : null,
                transporter_name: item.transporter_name || null,
                transport_routes: Array.isArray(item.transport_routes)
                  ? item.transport_routes
                  : [],
                make: item.make || '',
                model: item.model || '',
                body_type: item.body_type || '',
                tonnage: item.tonnage != null ? item.tonnage : null,
                length: item.length != null ? item.length : null,
                remarks: item.remarks || '',
                per_km_price: item.per_km_price != null ? item.per_km_price : null,
                ready_for_long_trip: !!item.ready_for_long_trip,
                long_inter_district: !!item.long_inter_district,
                long_inter_state: !!item.long_inter_state,
                local: !!item.local,
                intra_state_permit: !!item.intra_state_permit,
                phone_extensions: Array.isArray(item.phone_extensions) ? item.phone_extensions : [],
              };
            });
          }

          // Transform the data to match our component structure
          const transformedVehicles = vehiclesData.data.map(vehicle => {
            // Transform assignments into route format for UI display
            let route = Array.isArray(vehicle.route) && vehicle.route.length > 0 ? vehicle.route : [];
            if (route.length === 0 && vehicle.assignments && vehicle.assignments.length > 0) {
              vehicle.assignments.forEach(assignment => {
                // Add pickup point
                route.push({
                  booking_id: assignment.booking_id,
                  type: 'pickup',
                  location: assignment.pickup_location,
                  lat: assignment.pickup_lat,
                  lng: assignment.pickup_lng,
                  time: assignment.pickup_time ? new Date(assignment.pickup_time).toLocaleTimeString() : '',
                  completed: assignment.state === 'passenger_picked_up' || assignment.state === 'en_route_to_drop' || assignment.state === 'completed'
                });
                // Add drop point
                route.push({
                  booking_id: assignment.booking_id,
                  type: 'drop',
                  location: assignment.drop_location,
                  lat: assignment.drop_lat,
                  lng: assignment.drop_lng,
                  time: '', // We don't have drop time in the data
                  completed: assignment.state === 'completed'
                });
              });
            }

            // Get driver info from port 8000 data
            const driverInfo = driverMap[vehicle.vehicle_id] || {};

            return {
              vehicle_id: vehicle.vehicle_id,
              driver_id: vehicle.driver_id,
              vehicle_type: vehicle.vehicle_type || (vehicle.vehicle_class ? `class${vehicle.vehicle_class}` : 'unknown'),
              // Keep empty string when API has no city — do NOT use 'Unknown Base' here or city filter hides the row
              base_location:
                (vehicle.base_address || vehicle.base_location) != null &&
                  String(vehicle.base_address || vehicle.base_location).trim() !== ''
                  ? (vehicle.base_address || vehicle.base_location)
                  : '',
              base_lat: vehicle.base_lat || 0,
              base_lng: vehicle.base_lng || 0,
              current_location_lat: driverInfo.current_location_lat || 0,
              current_location_long: driverInfo.current_location_long || 0,
              current_status: vehicle.current_status || 'idle',
              route: route,
              trip_count: driverInfo.trip_count || 0,
              recent_trips_count: driverInfo.recent_trips_count || 0,
              completed_trip_count: driverInfo.completed_trip_count || 0,
              has_dl: !!driverInfo.has_dl,
              has_rc: !!driverInfo.has_rc,
              has_insurance: !!driverInfo.has_insurance,
              has_emission: !!driverInfo.has_emission,
              has_vehicle_photo: !!driverInfo.has_vehicle_photo,
              has_upi: !!driverInfo.has_upi,
              total_pay: driverInfo.total_pay || 0,
              total_distance: vehicle.total_distance || 0,
              estimated_completion: vehicle.estimated_completion || '',
              registration_no: driverInfo.registration_no || `REG-${vehicle.vehicle_id}`,
              driver_name: driverInfo.driver_name || 'No Driver Assigned',
              driver_phone: driverInfo.driver_phone || null,
              bot_paused: driverInfo.bot_paused || false,
              last_driver_message_at: driverInfo.last_driver_message_at || null,
              is_active: driverInfo.is_active !== false, // false only if explicitly false
              has_app: driverInfo.has_app || false,
              is_new: driverInfo.is_new || false,
              vehicle_photo_url: driverInfo.vehicle_photo_url || null,
              is_transporter: !!driverInfo.is_transporter,
              transporter_id: driverInfo.transporter_id != null ? Number(driverInfo.transporter_id) : null,
              transporter_name: driverInfo.transporter_name || null,
              transport_routes: Array.isArray(driverInfo.transport_routes)
                ? driverInfo.transport_routes
                : [],
              make: driverInfo.make || vehicle.make || '',
              model: driverInfo.model || vehicle.model || '',
              body_type: driverInfo.body_type || vehicle.body_type || '',
              tonnage: driverInfo.tonnage != null ? driverInfo.tonnage : (vehicle.tonnage != null ? vehicle.tonnage : null),
              length: driverInfo.length != null ? driverInfo.length : (vehicle.length != null ? vehicle.length : null),
              remarks: driverInfo.remarks || vehicle.remarks || '',
              per_km_price: driverInfo.per_km_price != null ? driverInfo.per_km_price : null,
              ready_for_long_trip: !!driverInfo.ready_for_long_trip,
              long_inter_district: !!driverInfo.long_inter_district,
              long_inter_state: !!driverInfo.long_inter_state,
              local: !!driverInfo.local,
              intra_state_permit: !!driverInfo.intra_state_permit,
              phone_extensions: Array.isArray(driverInfo.phone_extensions) ? driverInfo.phone_extensions : [],
            };
          });

          // Sort: Strictly by vehicle_id in descending order (newest first)
          const sortedVehicles = transformedVehicles.sort((a, b) => {
            return b.vehicle_id - a.vehicle_id;
          });

          setVehicles(sortedVehicles);
        } else {
          console.error('Failed to fetch vehicles:', vehiclesData.error);
          setVehicles([]);
        }
      } catch (error) {
        console.error('Error fetching vehicles:', error);
        setVehicles([]);
      } finally {
        if (isInitialLoad) setLoading(false);
      }
    };

    // Expose to ref so the save handler can call it without window.location.reload()
    fetchVehiclesRef.current = fetchVehicles;

    fetchVehicles(true); // Initial load

    // Poll every 30 s — fast enough for live ops, slow enough not to block
    // the server with geocoding calls on every cycle.
    const interval = setInterval(() => fetchVehicles(false), 30000);

    return () => clearInterval(interval);
  }, []);


  // On-demand current location resolver — called only when user clicks "Get Location"
  const handleGetCurrentLocation = async (vehicle) => {
    if (!vehicle.current_location_lat || !vehicle.current_location_long) return;
    setLocationLoadingVehicles(prev => new Set([...prev, vehicle.vehicle_id]));
    try {
      const resp = await fetch(`${config.opsApiBase}/resolve-city?lat=${vehicle.current_location_lat}&lng=${vehicle.current_location_long}`);
      const data = await resp.json();
      if (data && data.success !== false) {
        setCityCache(prev => ({ ...prev, [vehicle.vehicle_id]: data.address || 'Unknown' }));
      }
    } catch (e) {
      // ignore error, button will just re-appear
    } finally {
      setLocationLoadingVehicles(prev => { const s = new Set(prev); s.delete(vehicle.vehicle_id); return s; });
    }
  };

  const fetchRegistrationRecords = async () => {
    try {
      setRegistrationLoading(true);
      setRegistrationError('');
      const response = await fetch(`${config.opsApiBase}/driver-registration/list?status=all`);
      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.detail || data.message || 'Failed to load registrations');
      }

      setRegistrationRecords(data.records || []);
      const records = data.records || [];
      dispatch(updatePendingDriverRegistrationCount({
        pendingCount: records.filter((r) => !r.is_registered && r.has_texted).length,
        registeredCount: records.filter((r) => isNewRegisteredRow(r, vehicles)).length,
      }));
    } catch (error) {
      console.error('Error fetching registration records:', error);
      setRegistrationError(error.message || 'Failed to load registration records');
    } finally {
      setRegistrationLoading(false);
    }
  };

  const handleRegisterGeocodePlace = async () => {
    const placeValue = (registerPlaceAddress || '').trim();
    if (!placeValue) {
      setRegistrationError('Enter a place or address, then tap Get Coordinates');
      return;
    }
    setIsRegisterGeocoding(true);
    setRegistrationError('');
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.opsApiBase}/onboarding/resolve-location`, {
        method: 'POST',
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ address_text: placeValue }),
      });
      const data = await response.json();
      if (response.ok && data.latitude != null && data.longitude != null) {
        setRegisterBaseLat(String(data.latitude));
        setRegisterBaseLng(String(data.longitude));
        setRegisterResolvedAddress(data.resolved_address || data.formatted_address || placeValue);
      } else {
        setRegistrationError(data.message || `Could not find coordinates for "${placeValue}".`);
      }
    } catch (err) {
      console.error('Register geocode error:', err);
      setRegistrationError('Error getting coordinates. Please try again.');
    } finally {
      setIsRegisterGeocoding(false);
    }
  };

  const handleRegisterPhotoFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setRegisterVehiclePhoto(file);
    const reader = new FileReader();
    reader.onload = (ev) => setRegisterVehiclePhotoPreview(ev.target.result);
    reader.readAsDataURL(file);
    setRegisterPhotoPickerOpen(false);
    e.target.value = '';
  };

  const clearRegisterVehiclePhoto = () => {
    setRegisterVehiclePhoto(null);
    setRegisterVehiclePhotoPreview(null);
  };

  const uploadRegisterVehiclePhoto = async (registrationId) => {
    if (!registerVehiclePhoto) return true;
    setRegisterPhotoUploading(true);
    try {
      const compressed = await compressRegistrationImage(registerVehiclePhoto);
      const formData = new FormData();
      formData.append('file', compressed);
      const res = await fetch(
        `${config.opsApiBase}/driver-registration/${registrationId}/vehicle-photo`,
        { method: 'POST', body: formData }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        throw new Error(data.detail || data.message || 'Photo upload failed');
      }
      return true;
    } catch (err) {
      console.warn('Photo upload error:', err);
      return false;
    } finally {
      setRegisterPhotoUploading(false);
    }
  };

  const handleManualRegister = async () => {
    try {
      setRegistrationError('');
      setRegistrationSuccess('');

      if (!registerName.trim()) {
        setRegistrationError('Name is required');
        return;
      }

      if (!registerPhone.trim()) {
        setRegistrationError('Phone number is required');
        return;
      }

      if (registerRegistrationNo.replace(/[^A-Za-z0-9]/g, '').length < 6) {
        setRegistrationError('Enter the full vehicle registration number');
        return;
      }

      if (!registerBaseLat || !registerBaseLng || !registerResolvedAddress.trim()) {
        setRegistrationError('Choose a stand location from the address search (or use Look up coordinates if maps are unavailable).');
        return;
      }

      setIsRegistering(true);
      const response = await fetch(`${config.opsApiBase}/driver-registration/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: registerName.trim(),
          phone_number: registerPhone.trim(),
          vehicle_name: registerVehicle.trim() || null,
          registration_no: registerRegistrationNo.trim() || null,
          is_registered: true,
          base_lat: Number(registerBaseLat),
          base_lng: Number(registerBaseLng),
          address: registerResolvedAddress.trim(),
          comments: registerComments.trim() || null,
          ...specsPayloadFromForm(registerSpecs),
        }),
      });

      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.detail || data.message || 'Failed to register driver');
      }

      const registrationId = data.record?.id;
      if (registerVehiclePhoto && registrationId) {
        const photoOk = await uploadRegisterVehiclePhoto(registrationId);
        if (!photoOk) {
          setRegistrationError('Driver registered, but vehicle photo upload failed. Edit the row to add the photo.');
          setRegisterName('');
          setRegisterPhone('');
          setRegisterVehicle('');
          setRegisterRegistrationNo('');
          setRegisterComments('');
          setRegisterSpecs(EMPTY_REG_SPECS);
          setRegisterExtPhone('');
          setRegisterPlaceAddress('');
          setRegisterBaseLat('');
          setRegisterBaseLng('');
          setRegisterResolvedAddress('');
          clearRegisterVehiclePhoto();
          await fetchRegistrationRecords();
          return;
        }
      }

      setRegistrationSuccess(registerVehiclePhoto ? 'Driver registered successfully with photo' : 'Driver registered successfully');
      setRegisterName('');
      setRegisterPhone('');
      setRegisterVehicle('');
      setRegisterRegistrationNo('');
      setRegisterComments('');
      setRegisterSpecs(EMPTY_REG_SPECS);
      setRegisterExtPhone('');
      setRegisterPlaceAddress('');
      setRegisterBaseLat('');
      setRegisterBaseLng('');
      setRegisterResolvedAddress('');
      clearRegisterVehiclePhoto();
      await fetchRegistrationRecords();
    } catch (error) {
      console.error('Error registering driver:', error);
      setRegistrationError(error.message || 'Failed to register driver');
    } finally {
      setIsRegistering(false);
    }
  };

  const handleApproveRegistration = async (registrationId) => {
    try {
      setRegistrationError('');
      setRegistrationSuccess('');

      const response = await fetch(`${config.opsApiBase}/driver-registration/${registrationId}/register`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.detail || data.message || 'Failed to approve registration');
      }

      setRegistrationSuccess('Pending number approved successfully');
      await fetchRegistrationRecords();
    } catch (error) {
      console.error('Error approving registration:', error);
      setRegistrationError(error.message || 'Failed to approve registration');
    }
  };

  const handleRemovePendingRegistration = async (registrationId) => {
    try {
      if (!window.confirm('Are you sure you want to remove this pending registration row?')) {
        return;
      }
      setRegistrationError('');
      setRegistrationSuccess('');

      const response = await fetch(`${config.opsApiBase}/driver-registration/${registrationId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.detail || data.message || 'Failed to remove pending registration');
      }

      setRegistrationSuccess('Pending row removed successfully');
      await fetchRegistrationRecords();
    } catch (error) {
      console.error('Error removing pending registration:', error);
      setRegistrationError(error.message || 'Failed to remove pending registration');
    }
  };

  const handleSendTemplate = async (registrationId) => {
    try {
      setRegistrationError('');
      setRegistrationSuccess('');
      setSendingTemplate(prev => new Set(prev).add(registrationId));

      const response = await fetch(`${config.opsApiBase}/driver-registration/${registrationId}/send-template`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });

      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.detail || data.message || 'Failed to send template');
      }

      setRegistrationSuccess('Onboarding template sent successfully');
      setTemplateSentVehicles(prev => new Set(prev).add(registrationId));
    } catch (error) {
      console.error('Error sending template:', error);
      setRegistrationError(error.message || 'Failed to send template');
    } finally {
      setSendingTemplate(prev => {
        const next = new Set(prev);
        next.delete(registrationId);
        return next;
      });
    }
  };

  const getOnboardMissingFields = (row) => {
    if (!row) return ['Registration row'];
    const missing = [];
    if (!(row.name || '').trim()) missing.push('Name');
    if (!(row.phone_number || '').trim()) missing.push('Phone');
    const vehicleClass = row.vehicle_class;
    const hasClass = vehicleClass != null && vehicleClass !== '' && !Number.isNaN(Number(vehicleClass));
    if (!hasClass) {
      missing.push('Vehicle class');
    }
    const lat = row.base_lat;
    const lng = row.base_lng;
    const hasCoords = lat !== null && lat !== undefined && lat !== ''
      && lng !== null && lng !== undefined && lng !== '';
    if (!hasCoords || !(row.address || '').trim()) {
      missing.push('Stand location (pick an address so coordinates are saved)');
    }
    return missing;
  };

  const getOnboardBlockMessage = (row) => {
    const missing = getOnboardMissingFields(row);
    if (!missing.length) return '';
    if (missing.includes('Vehicle class')) {
      return 'Vehicle class is required. Edit the row and set a class, or enter a known vehicle name.';
    }
    return `Cannot onboard yet. Add these fields first: ${missing.join(', ')}. Then click Onboard.`;
  };

  const blockOnboardIfIncomplete = (row) => {
    const msg = getOnboardBlockMessage(row);
    if (!msg) return false;
    setOnboardMenuRowId(null);
    closeRegTransporterModal();
    setRegistrationError(msg);
    handleEditRegistration(row, msg);
    return true;
  };

  const normalizeRegistrationPhone = (phone) => {
    let digits = String(phone || '').replace(/\D/g, '');
    if (digits.startsWith('0') && digits.length === 11) digits = digits.slice(1);
    if (digits.startsWith('91') && digits.length === 12) return digits;
    if (digits.length === 10) return `91${digits}`;
    return null;
  };

  const closeRegTransporterModal = () => {
    setShowRegTransporterModal(false);
    setRegTransporterRow(null);
    setRegTransporterPhone('');
    setRegTransporterProfile(null);
    setRegTransporterLookupStatus(null);
    setRegTransporterLookupError('');
    setRegTransporterSuggestOpen(false);
  };

  const handleMarkRegistrationRead = async (row) => {
    if (!row?.id) return;
    try {
      setRegistrationError('');
      const response = await fetch(
        `${config.opsApiBase}/driver-registration/${row.id}/mark-read`,
        { method: 'PATCH' }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.success) {
        throw new Error(data.detail || data.message || 'Failed to mark as read');
      }
      await fetchRegistrationRecords();
    } catch (error) {
      setRegistrationError(error.message || 'Failed to mark as read');
    }
  };

  const handleOnboardFromRegistration = async (row, options = {}) => {
    const isTransporter = Boolean(options.isTransporter);
    const transporterPhone = options.transporterPhone || '';

    const rowReg = String(row?.registration_no || '').replace(/[^A-Za-z0-9]/g, '').toUpperCase();
    const matchingVehicle = rowReg
      ? vehicles.find((v) => String(v.registration_no || '').replace(/[^A-Za-z0-9]/g, '').toUpperCase() === rowReg)
      : null;
    const onboardedVehicleId = row?.onboarded_vehicle_id || (matchingVehicle ? matchingVehicle.vehicle_id : null);

    if (onboardedVehicleId) {
      setRegistrationError(`Already onboarded as V-${onboardedVehicleId}`);
      return;
    }

    if (blockOnboardIfIncomplete(row)) return;

    if (isTransporter && !transporterPhone) {
      setRegistrationError('Enter a transporter number first');
      return;
    }

    if (!isTransporter) {
      const confirmed = window.confirm(
        `Onboard ${row.name} (${row.phone_number}) now?\n\n` +
        `This creates the driver and vehicle using name, phone, class ${row.vehicle_class ?? '(from vehicle name)'}, and stand location. RC/DL can be added later.`
      );
      if (!confirmed) return;
    }

    try {
      setRegistrationError('');
      setRegistrationSuccess('');
      setOnboardingRegistrationIds((prev) => new Set(prev).add(row.id));

      const response = await fetch(
        `${config.opsApiBase}/driver-registration/${row.id}/onboard`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            is_transporter: isTransporter,
            transporter_phone: isTransporter ? transporterPhone : null,
          }),
        }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.success) {
        const detail = data.detail || data.message || 'Failed to onboard vehicle';
        throw new Error(typeof detail === 'string' ? detail : JSON.stringify(detail));
      }

      const vehicleLabel = data.vehicle_id ? ` Vehicle ID ${data.vehicle_id}.` : '';
      setRegistrationSuccess(
        isTransporter
          ? (data.message || `Vehicle onboarded for ${row.name} under the transporter.${vehicleLabel}`)
          : `Vehicle onboarded for ${row.name}.${vehicleLabel}`
      );
      closeRegTransporterModal();
      await fetchRegistrationRecords();
      if (fetchVehiclesRef.current) {
        fetchVehiclesRef.current(false);
      }
    } catch (error) {
      console.error('Error onboarding from registration:', error);
      setRegistrationError(error.message || 'Failed to onboard vehicle');
      if (isTransporter) {
        setRegTransporterLookupError(error.message || 'Failed to onboard vehicle');
      }
    } finally {
      setOnboardingRegistrationIds((prev) => {
        const next = new Set(prev);
        next.delete(row.id);
        return next;
      });
    }
  };

  const handleOpenTransporterOnboard = (row) => {
    setOnboardMenuRowId(null);
    const rowReg = String(row?.registration_no || '').replace(/[^A-Za-z0-9]/g, '').toUpperCase();
    const matchingVehicle = rowReg
      ? vehicles.find((v) => String(v.registration_no || '').replace(/[^A-Za-z0-9]/g, '').toUpperCase() === rowReg)
      : null;
    const onboardedVehicleId = row?.onboarded_vehicle_id || (matchingVehicle ? matchingVehicle.vehicle_id : null);

    if (onboardedVehicleId) {
      setRegistrationError(`Already onboarded as V-${onboardedVehicleId}`);
      return;
    }
    if (blockOnboardIfIncomplete(row)) return;
    setRegTransporterRow(row);
    setRegTransporterPhone('');
    setRegTransporterProfile(null);
    setRegTransporterLookupStatus(null);
    setRegTransporterLookupError('');
    setRegTransporterSuggestOpen(false);
    setShowRegTransporterModal(true);
    fetch(`${config.opsApiBase}/transporters`)
      .then((res) => res.json())
      .then((data) => setRegTransporterList(Array.isArray(data.transporters) ? data.transporters : []))
      .catch(() => setRegTransporterList([]));
  };

  const matchOnboardedTransporters = (query) => {
    const q = String(query || '').trim().toLowerCase();
    if (!q) return [];
    const digits = q.replace(/\D/g, '');
    return (regTransporterList || []).filter((t) => {
      const name = `${t.agency_name || ''} ${t.contact_name || ''} ${t.user_name || ''}`.toLowerCase();
      if (name.includes(q)) return true;
      if (digits.length >= 3) {
        const phone = String(t.phone_number || '').replace(/\D/g, '');
        return phone.includes(digits) || phone.slice(-10).includes(digits);
      }
      return false;
    }).slice(0, 8);
  };

  const selectTransporterSuggestion = (t) => {
    if (!t) return;
    const phone = String(t.phone_number || '');
    setRegTransporterPhone(phone);
    setRegTransporterProfile(t);
    setRegTransporterLookupStatus('found');
    setRegTransporterLookupError('');
    setRegTransporterSuggestOpen(false);
  };

  const handleLookupTransporterForOnboard = async () => {
    const query = (regTransporterPhone || '').trim();
    if (!query) {
      setRegTransporterLookupStatus('not_found');
      setRegTransporterProfile(null);
      setRegTransporterLookupError('Enter a transporter name or number');
      return;
    }

    const nameMatches = matchOnboardedTransporters(query);
    const normalized = normalizeRegistrationPhone(query);

    if (!normalized && nameMatches.length === 1) {
      selectTransporterSuggestion(nameMatches[0]);
      return;
    }
    if (!normalized && nameMatches.length > 1) {
      setRegTransporterLookupStatus(null);
      setRegTransporterProfile(null);
      setRegTransporterLookupError('Multiple transporters match. Pick one from the suggestions.');
      setRegTransporterSuggestOpen(true);
      return;
    }
    if (!normalized) {
      setRegTransporterLookupStatus('not_found');
      setRegTransporterProfile(null);
      setRegTransporterLookupError('No transporter found for that name. Onboard the transporter first from the Transporters tab.');
      return;
    }

    setRegTransporterPhone(normalized);
    setRegTransporterLookupStatus('loading');
    setRegTransporterLookupError('');
    setRegTransporterProfile(null);
    setRegTransporterSuggestOpen(false);
    try {
      const res = await fetch(
        `${config.opsApiBase}/transporters/by-phone/${encodeURIComponent(normalized)}`
      );
      const data = await res.json().catch(() => ({}));
      if (data.found && data.transporter) {
        setRegTransporterLookupStatus('found');
        setRegTransporterProfile(data.transporter);
        setRegTransporterLookupError('');
      } else if (nameMatches.length === 1) {
        selectTransporterSuggestion(nameMatches[0]);
      } else {
        setRegTransporterLookupStatus('not_found');
        setRegTransporterProfile(null);
        setRegTransporterLookupError(
          data.message || 'No transporter found. Onboard the transporter first from the Transporters tab.'
        );
      }
    } catch (error) {
      setRegTransporterLookupStatus('not_found');
      setRegTransporterProfile(null);
      setRegTransporterLookupError(error.message || 'Network error looking up transporter');
    }
  };

  useEffect(() => {
    let intervalId;
    if (showMessagesModal && selectedDriverForMessages) {
      intervalId = setInterval(async () => {
        try {
          await refreshMessagesWindow(
            selectedDriverForMessages,
            loadedMessageOffsetRef.current || REGISTRATION_MESSAGES_PAGE_SIZE
          );

          const botStatusResponse = await fetch(`${config.opsApiBase}/driver-registration/${selectedDriverForMessages}/bot-status`);
          const botStatusData = await botStatusResponse.json();
          if (botStatusResponse.ok && botStatusData.success) {
            setIsRegistrationBotPaused(Boolean(botStatusData.bot_paused));
          }
        } catch (err) {
          console.error('Error polling messages:', err);
        }
      }, 2000);
    }
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [showMessagesModal, selectedDriverForMessages]);

  const fetchRegistrationBotStatus = async (phone) => {
    if (!phone) return;
    setIsRegistrationBotStatusLoading(true);
    try {
      const response = await fetch(`${config.opsApiBase}/driver-registration/${phone}/bot-status`);
      const data = await response.json();
      if (response.ok && data.success) {
        setIsRegistrationBotPaused(Boolean(data.bot_paused));
      }
    } catch (error) {
      console.error('Error loading registration bot status:', error);
    } finally {
      setIsRegistrationBotStatusLoading(false);
    }
  };

  const handleToggleRegistrationBot = async () => {
    if (!selectedDriverForMessages || isRegistrationBotToggling) return;

    setIsRegistrationBotToggling(true);
    try {
      const action = isRegistrationBotPaused ? 'resume' : 'pause';
      const response = await fetch(`${config.opsApiBase}/driver-registration/${selectedDriverForMessages}/bot-control`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action }),
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.detail || data.message || 'Failed to toggle bot mode');
      }
      setIsRegistrationBotPaused(Boolean(data.bot_paused));
    } catch (error) {
      setMessagesError(error.message || 'Failed to toggle bot mode');
    } finally {
      setIsRegistrationBotToggling(false);
    }
  };

  const handleSendRegistrationManualMessage = async () => {
    if (!selectedDriverForMessages || !registrationManualMessage.trim() || isSendingRegistrationManualMessage) {
      return;
    }

    setIsSendingRegistrationManualMessage(true);
    setMessagesError('');
    try {
      const response = await fetch(`${config.opsApiBase}/driver-registration/${selectedDriverForMessages}/manual-message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: registrationManualMessage.trim(), sender: 'ops' }),
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.detail || data.message || 'Failed to send message');
      }

      setRegistrationManualMessage('');
      shouldAutoScrollRef.current = true;
      await refreshMessagesWindow(
        selectedDriverForMessages,
        Math.max(loadedMessageOffsetRef.current, REGISTRATION_MESSAGES_PAGE_SIZE)
      );
    } catch (error) {
      setMessagesError(error.message || 'Failed to send message');
    } finally {
      setIsSendingRegistrationManualMessage(false);
    }
  };

  const handleViewMessages = async (phone) => {
    setSelectedDriverForMessages(phone);
    setShowMessagesModal(true);
    setLoadingMessages(true);
    setMessagesError('');
    setHasMoreMessages(false);
    setLoadedMessageOffset(0);
    setLoadingOlderMessages(false);
    setRegistrationManualMessage('');
    try {
      const [messagesPage, botStatusResponse] = await Promise.all([
        fetchRegistrationMessagesPage(phone, 0, REGISTRATION_MESSAGES_PAGE_SIZE),
        fetch(`${config.opsApiBase}/driver-registration/${phone}/bot-status`),
      ]);

      shouldAutoScrollRef.current = true;
      setDriverMessages(messagesPage.messages);
      setLoadedMessageOffset(messagesPage.messages.length);
      setHasMoreMessages(messagesPage.hasMore);

      const botData = await botStatusResponse.json();
      if (botStatusResponse.ok && botData.success) {
        setIsRegistrationBotPaused(Boolean(botData.bot_paused));
      }
    } catch (err) {
      setMessagesError(err.message || 'Error loading messages');
    } finally {
      setLoadingMessages(false);
    }
  };

  const closeMessagesModal = () => {
    setShowMessagesModal(false);
    setSelectedDriverForMessages(null);
    setDriverMessages([]);
    setLoadedMessageOffset(0);
    setHasMoreMessages(false);
    setLoadingOlderMessages(false);
    setIsRegistrationBotPaused(false);
    setRegistrationManualMessage('');
    setIsRegistrationBotStatusLoading(false);
    setIsRegistrationBotToggling(false);
    setIsSendingRegistrationManualMessage(false);
  };

  const handleRemoveRegisteredRegistration = async (registrationId) => {
    try {
      if (!window.confirm('Are you sure you want to remove this registered driver entry?')) {
        return;
      }
      setRegistrationError('');
      setRegistrationSuccess('');

      const response = await fetch(`${config.opsApiBase}/driver-registration/${registrationId}/registered`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.detail || data.message || 'Failed to remove registered driver');
      }

      setRegistrationSuccess('Registered driver removed successfully');
      await fetchRegistrationRecords();
    } catch (error) {
      console.error('Error removing registered driver:', error);
      setRegistrationError(error.message || 'Failed to remove registered driver');
    }
  };

  const handleEditRegistration = (row, errorMsg = '') => {
    setEditRegRow(row);
    setEditRegName(row.name || '');
    setEditRegPhone(row.phone_number || '');
    setEditRegVehicle(row.vehicle_name || '');
    setEditRegMake(row.make || '');
    setEditRegModel(row.model || '');
    setEditRegRegistrationNo(row.registration_no || '');
    setEditRegClass(row.vehicle_class != null ? String(row.vehicle_class) : '');
    setEditRegAddress(row.address || '');
    setEditRegBaseLat(row.base_lat != null ? String(row.base_lat) : '');
    setEditRegBaseLng(row.base_lng != null ? String(row.base_lng) : '');
    setEditRegComments(row.comments || '');
    setEditRegSpecs(specsFromRegistrationRow(row));
    setEditRegExtPhone('');
    setEditRegError(errorMsg);
    setShowEditRegModal(true);
  };

  const handleUpdateRegistration = async () => {
    if (!editRegRow) return;
    if (!editRegName.trim()) { setEditRegError('Name is required'); return; }
    if (!editRegPhone.trim()) { setEditRegError('Phone is required'); return; }
    setEditRegSaving(true);
    setEditRegError('');
    try {
      const resolvedVehicle = [editRegMake.trim(), editRegModel.trim()].filter(Boolean).join(' ').trim() || editRegVehicle.trim() || null;
      const body = {
        name: editRegName.trim(),
        phone_number: editRegPhone.trim(),
        vehicle_name: resolvedVehicle,
        make: editRegMake.trim() || null,
        model: editRegModel.trim() || null,
        registration_no: editRegRegistrationNo.trim() || null,
        vehicle_class: editRegClass !== '' ? Number(editRegClass) : null,
        address: editRegAddress.trim() || null,
        base_lat: editRegBaseLat !== '' ? Number(editRegBaseLat) : null,
        base_lng: editRegBaseLng !== '' ? Number(editRegBaseLng) : null,
        comments: editRegComments.trim() || null,
        ...specsPayloadFromForm(editRegSpecs),
      };
      const response = await fetch(`${config.opsApiBase}/driver-registration/${editRegRow.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.detail || data.message || 'Failed to update registration');
      }
      setShowEditRegModal(false);
      setRegistrationSuccess('Registration updated successfully');
      await fetchRegistrationRecords();
    } catch (err) {
      setEditRegError(err.message || 'Failed to update');
    } finally {
      setEditRegSaving(false);
    }
  };

  const handleEditRegGeocodePlace = async () => {
    const placeValue = (editRegAddress || '').trim();
    if (!placeValue) {
      setEditRegError('Enter a place or address, then tap Get Coordinates');
      return;
    }

    setIsEditRegGeocoding(true);
    setEditRegError('');

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.opsApiBase}/onboarding/resolve-location`, {
        method: 'POST',
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ address_text: placeValue }),
      });
      const data = await response.json();

      if (response.ok && data.latitude != null && data.longitude != null) {
        const resolved = data.resolved_address || data.formatted_address || placeValue;
        setEditRegAddress(resolved);
        setEditRegBaseLat(String(data.latitude));
        setEditRegBaseLng(String(data.longitude));
      } else {
        setEditRegError(data.message || `Could not find coordinates for "${placeValue}".`);
      }
    } catch (err) {
      console.error('Edit registration geocode error:', err);
      setEditRegError('Error getting coordinates. Please try again.');
    } finally {
      setIsEditRegGeocoding(false);
    }
  };


  const handleRemoveSession = async (phoneNumber) => {
    try {
      if (!window.confirm('Are you sure you want to delete this onboarding session? This will force the driver to start over.')) {
        return;
      }
      setRegistrationError('');
      setRegistrationSuccess('');

      const response = await fetch(`${config.opsApiBase}/driver-registration/${phoneNumber}/session`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.detail || data.message || 'Failed to remove onboarding session');
      }

      setRegistrationSuccess('Onboarding session removed successfully');
      await fetchRegistrationRecords();
    } catch (error) {
      console.error('Error removing onboarding session:', error);
      setRegistrationError(error.message || 'Failed to remove onboarding session');
    }
  };

  useEffect(() => {
    dispatch(fetchPendingDriverRegistrationCount());
  }, [dispatch]);

  useEffect(() => {
    if (activeTab !== 'register-vehicle') return;
    fetchRegistrationRecords();
  }, [activeTab]);

  useEffect(() => {
    if (registrationRecords.length > 0) {
      dispatch(updatePendingDriverRegistrationCount({
        pendingCount: registrationRecords.filter((r) => !r.is_registered && r.has_texted).length,
        registeredCount: registrationRecords.filter((r) => isNewRegisteredRow(r, vehicles)).length,
      }));
    }
  }, [registrationRecords, vehicles, dispatch]);

  useEffect(() => {
    setPendingCurrentPage(1);
    setRegisteredCurrentPage(1);
  }, [registrationSearchTerm]);

  useEffect(() => {
    setVehiclesCurrentPage(1);
  }, [searchTerm, searchMode, statusFilter, selectedCities, transporterOnly, quickFilter, missingDocTypeFilter, longTripTypeFilter]);

  // Fetch pincodes for selected cities to enable precise filtering
  useEffect(() => {
    const fetchPincodes = async () => {
      if (!selectedCities || selectedCities.length === 0 || selectedCities.includes('All')) {
        setSelectedPincodes([]);
        return;
      }
      try {
        const resp = await fetch(`${config.dispatchApiBase}/cities/pincodes?cities=${encodeURIComponent(selectedCities.join(','))}`);
        const data = await resp.json();
        if (data && data.success) {
          setSelectedPincodes(data.pincodes || []);
        }
      } catch (e) {
        console.error('Error fetching pincodes for selected cities:', e);
      }
    };
    fetchPincodes();
  }, [selectedCities, config.dispatchApiBase]);



  // Resolve base location addresses from base_lat/base_lng
  // Uses localStorage to persist across page reloads.
  // Cache key includes lat+lng so it auto-invalidates if the DB value changes.
  useEffect(() => {
    const resolveBaseLocations = async () => {
      // Vehicles with a saved label: use DB text, skip reverse-geocode.
      const savedLabelUpdates = {};
      vehicles.forEach((v) => {
        const saved = String(v.base_location ?? '').trim();
        if (saved && baseLocationCache[v.vehicle_id] === undefined) {
          savedLabelUpdates[v.vehicle_id] = saved;
        }
      });
      if (Object.keys(savedLabelUpdates).length > 0) {
        setBaseLocationCache((prev) => ({ ...prev, ...savedLabelUpdates }));
      }

      // Resolve only when there is no saved base_location (coords-only legacy rows).
      const toResolve = vehicles.filter(v => {
        if (!v.base_lat || !v.base_lng) return false;
        if (String(v.base_location ?? '').trim()) return false;
        if (baseLocationCache[v.vehicle_id] !== undefined) return false;
        const lsKey = `base_loc_${v.vehicle_id}_${v.base_lat}_${v.base_lng}`;
        const cached = localStorage.getItem(lsKey);
        if (cached !== null) {
          setBaseLocationCache(prev => ({ ...prev, [v.vehicle_id]: cached }));
          return false;
        }
        return true;
      });
      if (toResolve.length === 0) return;
      try {
        const updates = {};
        await Promise.all(toResolve.map(async (v) => {
          try {
            const resp = await fetch(`${config.opsApiBase}/resolve-city?lat=${v.base_lat}&lng=${v.base_lng}`);
            const data = await resp.json();
            if (data && data.success !== false) {
              const address = data.address || '';
              updates[v.vehicle_id] = address;
              // Persist to localStorage keyed by vehicle + coordinates
              const lsKey = `base_loc_${v.vehicle_id}_${v.base_lat}_${v.base_lng}`;
              localStorage.setItem(lsKey, address);
            }
          } catch (e) {
            // ignore per-vehicle errors
          }
        }));
        if (Object.keys(updates).length > 0) {
          setBaseLocationCache(prev => ({ ...prev, ...updates }));
        }
      } catch (e) {
        // ignore batch error
      }
    };
    resolveBaseLocations();
  }, [vehicles, config.opsApiBase]);

  // Fetch unassigned bookings
  useEffect(() => {
    let websocket = null;
    let isComponentMounted = true;

    const connectWebSocket = () => {
      if (!isComponentMounted) return;

      console.log('🔌 Connecting to escalation WebSocket for unassigned bookings');
      websocket = new WebSocket(config.escalationWebsocket);

      websocket.onopen = () => {
        if (!isComponentMounted) return;
        console.log('✅ Connected to WebSocket for unassigned bookings');
      };

      websocket.onmessage = (event) => {
        if (!isComponentMounted) return;
        try {
          const data = JSON.parse(event.data);
          console.log('📥 Received data in VehicleDetails:', data);

          if (data.type === 'escalation' && data.unassigned && data.unassigned.length > 0) {
            console.log('📊 Setting unassigned bookings:', data.unassigned.length);
            setUnassignedBookings(data.unassigned);
          } else if (data.type === 'clear_all') {
            console.log('🧹 Clearing unassigned bookings');
            setUnassignedBookings([]);
          }
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };

      websocket.onerror = (error) => {
        console.error('❌ WebSocket error:', error);
      };

      websocket.onclose = (event) => {
        console.log('❌ WebSocket connection closed', event.code);
        if (isComponentMounted && event.code !== 1000) {
          // Attempt to reconnect after 3 seconds
          setTimeout(connectWebSocket, 3000);
        }
      };
    };

    connectWebSocket();

    return () => {
      isComponentMounted = false;
      if (websocket) {
        websocket.close();
      }
    };
  }, []);

  useEffect(() => {
    let filtered = vehicles;

    // City filter: match by pincode.
    // We extract the 6-digit pincode from the resolved address and check if it's in selectedPincodes.
    // If selectedPincodes hasn't loaded yet or address has no pincode, we fall back to name matching.
    if (selectedCities && selectedCities.length > 0) {
      filtered = filtered.filter(vehicle => {
        const resolvedAddress = (
          vehicle.base_location || baseLocationCache[vehicle.vehicle_id] || ''
        ).trim();
        if (!resolvedAddress || resolvedAddress.toLowerCase() === 'unknown base') return true;

        // Try extracting 6-digit pincode
        const match = resolvedAddress.match(/\b\d{6}\b/);
        const pincode = match ? match[0] : null;

        if (pincode && selectedPincodes.length > 0) {
          return selectedPincodes.includes(pincode);
        } else {
          // Fallback to name search in case pincodes are not loaded yet or address is not fully resolved
          return selectedCities.some(city => resolvedAddress.toLowerCase().includes(city.toLowerCase()));
        }
      });
    }

    if (searchTerm && searchTerm.trim() !== '') {
      const rawQ = searchTerm.trim();
      const q = rawQ.toLowerCase();

      if (searchMode === 'model') {
        filtered = filtered.filter(vehicle => matchesVehicleModel(vehicle, rawQ));
      } else if (searchMode === 'phone') {
        const phoneDigits = rawQ.replace(/\D/g, '');
        filtered = filtered.filter(vehicle => {
          if (!phoneDigits) return true;
          const vPhone = String(vehicle.driver_phone || '').replace(/\D/g, '');
          return vPhone.includes(phoneDigits);
        });
      } else if (searchMode === 'tonnage') {
        const tonnageTarget = nearTarget(rawQ);
        if (tonnageTarget != null) {
          filtered = filtered.filter(vehicle => withinOne(vehicle.tonnage, tonnageTarget));
          filtered.sort((a, b) => {
            const distA = Math.abs(Number(a.tonnage) - tonnageTarget);
            const distB = Math.abs(Number(b.tonnage) - tonnageTarget);
            return distA - distB;
          });
        }
      } else if (searchMode === 'length') {
        const lengthTarget = nearTarget(rawQ);
        if (lengthTarget != null) {
          filtered = filtered.filter(vehicle => withinOne(vehicle.length, lengthTarget));
          filtered.sort((a, b) => {
            const distA = Math.abs(Number(a.length) - lengthTarget);
            const distB = Math.abs(Number(b.length) - lengthTarget);
            return distA - distB;
          });
        }
      } else if (searchMode === 'class') {
        const classNumbers = q.match(/\d+/g);
        if (classNumbers && classNumbers.length > 0) {
          filtered = filtered.filter(vehicle => {
            const vehicleNorm = (vehicle.vehicle_type || '').toLowerCase();
            const vehicleClassMatch = vehicleNorm.match(/\d+/);
            if (vehicleClassMatch) {
              return classNumbers.includes(vehicleClassMatch[0]);
            }
            return vehicleNorm.includes(q);
          });
        } else {
          filtered = filtered.filter(vehicle =>
            (vehicle.vehicle_type || '').toLowerCase().includes(q)
          );
        }
      } else if (searchMode === 'location') {
        filtered = filtered.filter(vehicle => {
          const base = (vehicle.base_location || baseLocationCache[vehicle.vehicle_id] || '').toLowerCase();
          const current = (cityCache[vehicle.vehicle_id] || '').toLowerCase();
          return base.includes(q) || current.includes(q);
        });
      } else if (searchMode === 'route') {
        filtered = filtered.filter(vehicle => {
          const routes = Array.isArray(vehicle.transport_routes) ? vehicle.transport_routes : [];
          return routes.some((r) => {
            const fromAddr = (r?.from?.address || '').toLowerCase();
            const toAddr = (r?.to?.address || '').toLowerCase();
            const combined = `${fromAddr} to ${toAddr}`;
            return fromAddr.includes(q) || toAddr.includes(q) || combined.includes(q);
          });
        });
      } else {
        // searchMode === 'all' (default)
        const idQuery = rawQ;
        const exactIdMatches = /^\d+$/.test(idQuery)
          ? filtered.filter((vehicle) => String(vehicle.vehicle_id) === idQuery)
          : [];
        if (exactIdMatches.length > 0) {
          filtered = exactIdMatches;
        } else {
          const phoneDigits = rawQ.replace(/\D/g, '');
          filtered = filtered.filter(vehicle =>
            vehicle.vehicle_id.toString().includes(idQuery) ||
            (vehicle.base_location || '').toLowerCase().includes(q) ||
            (vehicle.registration_no || '').toLowerCase().includes(q) ||
            (vehicle.driver_name || '').toLowerCase().includes(q) ||
            (vehicle.make || '').toLowerCase().includes(q) ||
            (vehicle.model || '').toLowerCase().includes(q) ||
            (vehicle.body_type || '').toLowerCase().includes(q) ||
            (vehicle.remarks || '').toLowerCase().includes(q) ||
            matchesVehicleModel(vehicle, rawQ) ||
            (phoneDigits.length >= 4 &&
              String(vehicle.driver_phone || '').replace(/\D/g, '').includes(phoneDigits)) ||
            (vehicle.route || []).some(stop =>
              (stop.location || '').toLowerCase().includes(q) ||
              String(stop.booking_id || '').includes(idQuery)
            )
          );
        }
      }
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(vehicle =>
        statusFilter === 'active' ? vehicle.is_active === true : vehicle.is_active === false
      );
    }

    if (quickFilter !== 'all') {
      if (quickFilter === 'active') {
        filtered = filtered.filter(v => v.is_active);
      } else if (quickFilter === 'inactive') {
        filtered = filtered.filter(v => !v.is_active);
      } else if (quickFilter === 'new') {
        filtered = filtered.filter(v => v.is_new);
      } else if (quickFilter === 'recent_bookings') {
        filtered = filtered.filter(v => v.recent_trips_count > 0);
      } else if (quickFilter === 'no_bookings') {
        filtered = filtered.filter(v => v.completed_trip_count === 0);
      } else if (quickFilter === 'missing_docs') {
        if (missingDocTypeFilter === 'all') {
          filtered = filtered.filter(v => !v.has_dl || !v.has_rc || !v.has_insurance || !v.has_emission || !v.has_vehicle_photo || !v.has_upi);
        } else if (missingDocTypeFilter === 'dl') {
          filtered = filtered.filter(v => !v.has_dl);
        } else if (missingDocTypeFilter === 'rc') {
          filtered = filtered.filter(v => !v.has_rc);
        } else if (missingDocTypeFilter === 'insurance') {
          filtered = filtered.filter(v => !v.has_insurance);
        } else if (missingDocTypeFilter === 'emission') {
          filtered = filtered.filter(v => !v.has_emission);
        } else if (missingDocTypeFilter === 'vehicle') {
          filtered = filtered.filter(v => !v.has_vehicle_photo);
        } else if (missingDocTypeFilter === 'upi') {
          filtered = filtered.filter(v => !v.has_upi);
        }
      } else if (quickFilter === 'open_vehicles') {
        filtered = filtered.filter(v => (v.body_type || '').toLowerCase() === 'open');
      } else if (quickFilter === 'closed_vehicles') {
        filtered = filtered.filter(v => (v.body_type || '').toLowerCase() === 'closed');
      } else if (quickFilter === 'long_trip_vehicles') {
        if (longTripTypeFilter === 'inter_state') {
          filtered = filtered.filter(v => v.long_inter_state);
        } else if (longTripTypeFilter === 'local') {
          filtered = filtered.filter(v => v.local);
        } else {
          filtered = filtered.filter(v => v.long_inter_district);
        }
      }
    }

    if (transporterOnly) {
      filtered = filtered.filter(vehicle => !!vehicle.is_transporter);
    }

    // Apply date filter on vehicle route bookings
    filtered = filtered.map(vehicle => {
      if (!vehicle.route || vehicle.route.length === 0) return vehicle;

      const filteredRoute = vehicle.route.filter(stop => {
        const stopDate = stop.pickup_time || stop.dropoff_time || stop.timestamp;
        return !stopDate || isDateInRange(stopDate);
      });

      // Return vehicle with filtered route
      return { ...vehicle, route: filteredRoute };
    }).filter(vehicle => {
      // Keep vehicles that still have stops after date filtering, or have no stops originally
      return !vehicle.route || vehicle.route.length > 0 || vehicles.find(v => v.vehicle_id === vehicle.vehicle_id)?.route.length === 0;
    });

    const inboundAt = (vehicle) => {
      const at = Date.parse(vehicle.last_driver_message_at || '');
      if (!Number.isFinite(at)) return null;
      const age = attentionNow - at;
      return age >= 0 && age < 3 * 60 * 60 * 1000 ? at : null;
    };
    filtered = [...filtered].sort((a, b) => {
      const aAt = inboundAt(a);
      const bAt = inboundAt(b);
      if (aAt && bAt) return bAt - aAt;
      if (aAt) return -1;
      if (bAt) return 1;
      return 0;
    });

    setFilteredVehicles(filtered);
  }, [vehicles, searchTerm, searchMode, statusFilter, transporterOnly, selectedCities, selectedPincodes, isDateInRange, baseLocationCache, cityCache, quickFilter, missingDocTypeFilter, longTripTypeFilter, attentionNow]);

  const totalVehiclePages = Math.max(1, Math.ceil(filteredVehicles.length / VEHICLES_PAGE_SIZE));

  useEffect(() => {
    setVehiclesPageInput(vehiclesCurrentPage);
  }, [vehiclesCurrentPage]);

  const handleVehiclesPageInputChange = (e) => {
    setVehiclesPageInput(e.target.value);
  };

  const handleVehiclesPageInputKeyDown = (e) => {
    if (e.key === 'Enter') {
      const num = parseInt(vehiclesPageInput, 10);
      if (Number.isFinite(num) && num >= 1 && num <= totalVehiclePages) {
        setVehiclesCurrentPage(num);
      } else {
        setVehiclesPageInput(vehiclesCurrentPage);
      }
    }
  };

  const handleVehiclesPageInputBlur = () => {
    const num = parseInt(vehiclesPageInput, 10);
    if (Number.isFinite(num) && num >= 1 && num <= totalVehiclePages) {
      setVehiclesCurrentPage(num);
    } else {
      setVehiclesPageInput(vehiclesCurrentPage);
    }
  };

  const renderVehiclesPagination = (position) => {
    if (filteredVehicles.length === 0) return null;

    return (
      <div
        className="vehicles-pagination"
        style={position === 'top' ? { borderTop: 'none', borderBottom: '1px solid #e5e7eb', padding: '12px 16px' } : {}}
      >
        <div className="vehicles-pagination-left">
          <span className="vehicles-pagination-info">
            Showing {Math.min((vehiclesCurrentPage - 1) * VEHICLES_PAGE_SIZE + 1, filteredVehicles.length)}-
            {Math.min(vehiclesCurrentPage * VEHICLES_PAGE_SIZE, filteredVehicles.length)} of {filteredVehicles.length}
          </span>
          <button
            onClick={handleDownloadCSV}
            className="btn-download-csv"
            style={{
              padding: '6px 12px',
              backgroundColor: '#10b981',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: '600',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
              transition: 'background-color 0.2s'
            }}
            onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#059669'}
            onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#10b981'}
            title="Download CSV for all currently filtered vehicles"
          >
            <Download size={14} />
            Download CSV
          </button>
        </div>
        <div className="vehicles-pagination-controls">
          <button
            onClick={() => setVehiclesCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={vehiclesCurrentPage === 1}
            className="vehicles-pagination-btn"
          >
            <ChevronLeft size={16} /> Previous
          </button>

          <span className="vehicles-pagination-page-box">
            Page
            <input
              type="number"
              min={1}
              max={totalVehiclePages}
              value={vehiclesPageInput}
              onChange={handleVehiclesPageInputChange}
              onKeyDown={handleVehiclesPageInputKeyDown}
              onBlur={handleVehiclesPageInputBlur}
              className="vehicles-pagination-input"
            />
            of {totalVehiclePages}
          </span>

          <button
            onClick={() => setVehiclesCurrentPage(prev => Math.min(totalVehiclePages, prev + 1))}
            disabled={vehiclesCurrentPage === totalVehiclePages}
            className="vehicles-pagination-btn"
          >
            Next <ChevronRight size={16} />
          </button>
        </div>
        <div className="vehicles-pagination-right" />
      </div>
    );
  };

  const handleDownloadCSV = () => {
    if (filteredVehicles.length === 0) return;

    const headers = [
      'Vehicle ID',
      'Transporter ID',
      'Transporter Name',
      'Driver Name',
      'Driver Phone',
      'Type/Class',
      'Make',
      'Model',
      'Length (ft)',
      'Tonnage (ton)',
      'Body Type',
      'Remarks',
      'Active Window',
      'Base Location',
      'Routes',
      'Current Location',
      'Active Status',
      'New Vehicle'
    ];

    const escapeCSV = (val) => {
      if (val === null || val === undefined) return '';
      const str = String(val);
      if (str.includes(',') || str.includes('"') || str.includes('\n') || str.includes('\r')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };

    const csvContent = [
      headers.map(escapeCSV).join(','),
      ...filteredVehicles.map(vehicle => {
        const isInWindow = isDriverInActiveWindow(vehicle.last_driver_message_at);
        const routes = Array.isArray(vehicle.transport_routes) ? vehicle.transport_routes : [];
        const formattedRoutes = routes.map(r => {
          const from = (r?.from?.address || '').trim() || 'others';
          const to = (r?.to?.address || '').trim() || 'others';
          return `${from} -> ${to}`;
        }).join(' | ');
        const baseLoc = getVehicleBaseLocationDisplay(vehicle) || '';
        const currLoc = cityCache[vehicle.vehicle_id] || (vehicle.current_location_lat && vehicle.current_location_long ? `${vehicle.current_location_lat}, ${vehicle.current_location_long}` : '');

        return [
          `V-${vehicle.vehicle_id}`,
          vehicle.transporter_id || '',
          vehicle.transporter_name || '',
          vehicle.driver_name || '',
          vehicle.driver_phone || '',
          vehicle.vehicle_type || '',
          vehicle.make || '',
          vehicle.model || '',
          vehicle.length != null ? vehicle.length : '',
          vehicle.tonnage != null ? vehicle.tonnage : '',
          vehicle.body_type || '',
          vehicle.remarks || '',
          isInWindow ? 'IN' : 'OUT',
          baseLoc,
          formattedRoutes,
          currLoc,
          vehicle.is_active ? 'Active' : 'Inactive',
          vehicle.is_new ? 'Yes' : 'No'
        ].map(escapeCSV).join(',');
      })
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `vehicles_export_${new Date().toISOString().slice(0, 10)}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const pagedVehicles = useMemo(() => {
    const startIndex = (vehiclesCurrentPage - 1) * VEHICLES_PAGE_SIZE;
    return filteredVehicles.slice(startIndex, startIndex + VEHICLES_PAGE_SIZE);
  }, [filteredVehicles, vehiclesCurrentPage]);

  useEffect(() => {
    setVehiclesCurrentPage(prev => Math.min(prev, totalVehiclePages));
  }, [totalVehiclePages]);

  const getVehicleColor = (vehicleType, isTransporter = false) => {
    if (isTransporter) {
      return '#f472b6';
    }
    const colors = {
      'class1': '#ef4444',
      'class2': '#3b82f6',
      'class3': '#10b981',
      'class4': '#f59e0b',
      'class5': '#8b5cf6',
      'class6': '#06b6d4',
      'class7': '#f97316',
      'class8': '#84cc16',
      'class9': '#ec4899'
    };
    return colors[vehicleType] || '#6b7280';
  };

  const getStatusColor = (status) => {
    const colors = {
      'active': '#10b981',
      'en_route': '#f59e0b',
      'idle': '#6b7280',
      'maintenance': '#ef4444'
    };
    return colors[status] || '#6b7280';
  };

  const getStatusLabel = (status) => {
    const labels = {
      'active': 'Active',
      'en_route': 'En Route',
      'idle': 'Idle',
      'maintenance': 'Maintenance'
    };
    return labels[status] || status;
  };

  // Prefer saved base_location; use reverse-geocoded cache only when DB has no label.
  const getVehicleBaseLocationDisplay = (vehicle) => {
    if (!vehicle) return '';
    const saved = String(vehicle.base_location ?? '').trim();
    if (saved) return saved;
    const cached = baseLocationCache[vehicle.vehicle_id];
    if (cached) return cached;
    if (vehicle.base_lat && vehicle.base_lng) {
      return `${Number(vehicle.base_lat).toFixed(4)}, ${Number(vehicle.base_lng).toFixed(4)}`;
    }
    return '';
  };

  const baseLocationDisplay = selectedVehicle
    ? (String(editFormData.base_location ?? '').trim()
      || getVehicleBaseLocationDisplay(selectedVehicle))
    : '';

  // Send reengagement template to driver
  const handleSendReengagementTemplate = async (vehicleId) => {
    setSendingTemplate(prev => new Set(prev).add(vehicleId));

    try {
      const token = localStorage.getItem('token') || 'mock-jwt-token-for-testing';
      const response = await fetch(`${config.opsApiBase}/vehicles/${vehicleId}/send-reengagement-template`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Mark as sent
        setTemplateSentVehicles(prev => new Set(prev).add(vehicleId));
        alert(`Template sent successfully to driver!`);
      } else {
        alert(`Failed to send template: ${data.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error sending template:', error);
      alert(`Error sending template: ${error.message}`);
    } finally {
      setSendingTemplate(prev => {
        const newSet = new Set(prev);
        newSet.delete(vehicleId);
        return newSet;
      });
    }
  };

  // Trigger call server call via modal
  const handlePlaceCall = async (driverPhone, driverName = '') => {
    if (!driverPhone) return;
    const cleanPhone = String(driverPhone).replace(/\D/g, '');
    if (!cleanPhone) return;

    setDriverCallModal({
      open: true,
      driverPhone: cleanPhone,
      driverName: driverName || '',
      onlineSims: [],
      selectedSim: '',
      isLoading: true,
      status: 'idle',
      errorMsg: '',
    });

    try {
      const token = localStorage.getItem('token') || 'mock-jwt-token-for-testing';
      const simRes = await fetch(`${config.opsApiBase}/call-server/active-sims?role=driver`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const simData = await simRes.json();
      const onlineSims = (simData?.success && simData?.sims) ? simData.sims : [];
      setDriverCallModal(prev => ({
        ...prev,
        isLoading: false,
        onlineSims,
        selectedSim: onlineSims.length > 0 ? onlineSims[0].sim_number : '',
      }));
    } catch (err) {
      setDriverCallModal(prev => ({ ...prev, isLoading: false }));
    }
  };

  const handleConfirmDriverCall = async () => {
    const { driverPhone, selectedSim } = driverCallModal;
    if (!driverPhone) return;

    setDriverCallModal(prev => ({ ...prev, status: 'calling' }));
    setCallingDrivers(prev => ({ ...prev, [driverPhone]: true }));

    try {
      const token = localStorage.getItem('token') || 'mock-jwt-token-for-testing';
      const response = await fetch(`${config.opsApiBase}/call-driver`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          driver_phone: driverPhone,
          sim_number: selectedSim || null,
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setDriverCallModal(prev => ({ ...prev, status: 'success' }));
        setTimeout(() => {
          setDriverCallModal(prev => ({ ...prev, open: false }));
        }, 1800);

        // Refresh call logs
        setTimeout(() => {
          if (selectedVehicle?.linked_driver_id || driverPhone) {
            fetchVehicleDriverCallLogs(selectedVehicle?.linked_driver_id, driverPhone);
          }
        }, 1500);
      } else {
        setDriverCallModal(prev => ({
          ...prev,
          status: 'error',
          errorMsg: data.detail || 'Failed to place call'
        }));
      }
    } catch (error) {
      setDriverCallModal(prev => ({
        ...prev,
        status: 'error',
        errorMsg: error.message || 'Network error while placing call'
      }));
    } finally {
      setCallingDrivers(prev => ({ ...prev, [driverPhone]: false }));
    }
  };

  // NEW: Step 1 - Validate assignment before proceeding
  const handleValidateAssignment = async (vehicleId, bookingId) => {
    setSelectedBookingForValidation(bookingId);
    setIsValidating(true);

    try {
      const response = await fetch(`${config.dispatchApiBase}/manual-assignment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          booking_id: bookingId,
          vehicle_id: vehicleId,
          check_only: true  // Step 1: Validation only
        }),
      });

      const result = await response.json();
      console.log('Validation result:', result);

      setValidationResult(result);

      if (!result.can_proceed) {
        // Hard block - show error, don't allow proceeding
        setValidationStep('warnings');
      } else if (result.warnings && result.warnings.length > 0) {
        // Has warnings - show them for acknowledgment
        setValidationStep('warnings');
      } else {
        // No issues - proceed directly
        await handleConfirmAssignment(vehicleId, bookingId, result.validation_token);
      }
    } catch (error) {
      console.error('Error during validation:', error);
      alert(`Error during validation: ${error.message}`);
      setSelectedBookingForValidation(null);
    } finally {
      setIsValidating(false);
    }
  };

  // NEW: Step 2 - Confirm assignment after warnings acknowledged
  const handleConfirmAssignment = async (vehicleId, bookingId, validationToken = null) => {
    setIsAssigning(true);

    try {
      const operatorName = localStorage.getItem('user_name') || localStorage.getItem('username') || 'Unknown Operator';

      const response = await fetch(`${config.dispatchApiBase}/manual-assignment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          vehicle_id: vehicleId,
          booking_id: bookingId,
          assigned_by: operatorName,
          source: 'vehicle_page',
          confirmed: true,  // Step 2: Confirmed assignment
          validation_token: validationToken || validationResult?.validation_token,
          warnings_acknowledged: warningsAcknowledged
        }),
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Assignment successful:', data);

        alert(`Booking ${bookingId} successfully assigned to Vehicle V-${vehicleId}`);
        // Close modal and refresh
        closeAssignBookingModal();
      } else if (response.status === 409) {
        // Race condition - state changed
        const error = await response.json();
        alert(`⚠️ State changed: ${error.detail}\n\nPlease close and try again.`);
        // Reset to select step
        setValidationStep('select');
        setValidationResult(null);
        setSelectedBookingForValidation(null);
      } else {
        const data = await response.json();
        alert(`Failed to assign booking: ${data.error || data.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error assigning booking:', error);
      alert('Error assigning booking. Please try again.');
    } finally {
      setIsAssigning(false);
    }
  };

  // Helper function to handle back button in warnings step
  const handleBackToSelect = () => {
    setValidationStep('select');
    setValidationResult(null);
    setSelectedBookingForValidation(null);
    setWarningsAcknowledged(false);
  };

  // Helper function to get severity color
  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return '#dc3545'; // red
      case 'high': return '#fd7e14'; // orange
      case 'medium': return '#ffc107'; // yellow
      case 'low': return '#6c757d'; // gray
      default: return '#6c757d';
    }
  };

  // Helper function to get severity background color
  const getSeverityBgColor = (severity) => {
    switch (severity) {
      case 'critical': return '#f8d7da'; // light red
      case 'high': return '#fff3cd'; // light orange
      case 'medium': return '#fff9e6'; // light yellow
      case 'low': return '#e9ecef'; // light gray
      default: return '#e9ecef';
    }
  };

  // Legacy function - now redirects to validation flow
  const handleAssignBooking = async (vehicleId, bookingId) => {
    await handleValidateAssignment(vehicleId, bookingId);
  };

  const getFilteredBookings = () => {
    if (!bookingSearchTerm) return unassignedBookings;

    return unassignedBookings.filter(booking =>
      booking.booking_id.toString().includes(bookingSearchTerm) ||
      booking.pickup_location.toLowerCase().includes(bookingSearchTerm.toLowerCase())
    );
  };

  const normalizedRegistrationSearch = registrationSearchTerm.trim().toLowerCase();

  const matchesRegistrationSearch = (row) => {
    if (!normalizedRegistrationSearch) return true;

    const searchableValues = [
      row.id,
      row.name,
      row.phone_number,
      row.vehicle_name,
      row.vehicle_class,
      row.source,
      row.address,
      row.current_step,
      row.last_texted_at,
      row.updated_at,
    ]
      .filter(value => value !== null && value !== undefined)
      .join(' ')
      .toLowerCase();

    return searchableValues.includes(normalizedRegistrationSearch);
  };

  const pendingRegistrationRows = useMemo(
    () => registrationRecords.filter(r => !r.is_registered && r.has_texted).filter(matchesRegistrationSearch),
    [registrationRecords, normalizedRegistrationSearch]
  );

  const registeredRegistrationRows = useMemo(() => {
    const rows = registrationRecords
      .filter(r => r.is_registered)
      .filter(matchesRegistrationSearch);

    const waitingToOnboard = (row) => {
      if (row.onboarded_vehicle_id) return false;
      const rowReg = String(row.registration_no || '').replace(/[^A-Za-z0-9]/g, '').toUpperCase();
      if (!rowReg) return true;
      return !vehicles.some((vehicle) => (
        String(vehicle.registration_no || '').replace(/[^A-Za-z0-9]/g, '').toUpperCase() === rowReg
      ));
    };

    return [...rows].sort((a, b) => {
      const aIsNew = isNewRegisteredRow(a, vehicles);
      const bIsNew = isNewRegisteredRow(b, vehicles);

      // Rows with the NEW tag should always be at the top
      if (aIsNew && !bIsNew) return -1;
      if (!aIsNew && bIsNew) return 1;

      const aWaiting = waitingToOnboard(a);
      const bWaiting = waitingToOnboard(b);
      if (aWaiting && !bWaiting) return -1;
      if (!aWaiting && bWaiting) return 1;

      // Inside each group, most recent first
      const aTime = a.updated_at
        ? new Date(a.updated_at).getTime()
        : a.created_at
          ? new Date(a.created_at).getTime()
          : 0;
      const bTime = b.updated_at
        ? new Date(b.updated_at).getTime()
        : b.created_at
          ? new Date(b.created_at).getTime()
          : 0;

      if (bTime !== aTime) {
        return bTime - aTime;
      }
      return (b.id || 0) - (a.id || 0);
    });
  }, [registrationRecords, normalizedRegistrationSearch, vehicles]);

  const pendingTotalPages = Math.max(1, Math.ceil(pendingRegistrationRows.length / REGISTRATION_PAGE_SIZE));
  const registeredTotalPages = Math.max(1, Math.ceil(registeredRegistrationRows.length / REGISTRATION_PAGE_SIZE));

  useEffect(() => {
    setPendingCurrentPage(prev => Math.min(prev, pendingTotalPages));
  }, [pendingTotalPages]);

  useEffect(() => {
    setRegisteredCurrentPage(prev => Math.min(prev, registeredTotalPages));
  }, [registeredTotalPages]);

  const pendingRowsForPage = useMemo(() => {
    const startIdx = (pendingCurrentPage - 1) * REGISTRATION_PAGE_SIZE;
    return pendingRegistrationRows.slice(startIdx, startIdx + REGISTRATION_PAGE_SIZE);
  }, [pendingRegistrationRows, pendingCurrentPage]);

  const registeredRowsForPage = useMemo(() => {
    const startIdx = (registeredCurrentPage - 1) * REGISTRATION_PAGE_SIZE;
    return registeredRegistrationRows.slice(startIdx, startIdx + REGISTRATION_PAGE_SIZE);
  }, [registeredRegistrationRows, registeredCurrentPage]);

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading vehicle details...</p>
      </div>
    );
  }
  return (
    <div className={`vehicle-details${popupVehicleId ? ' vehicle-details-popup' : ''}`}>
      <div className="page-header" style={{ alignItems: 'center', marginBottom: '24px' }}>
        <div className="page-header-left">
          <h1>Vehicle Details</h1>
          <p>Complete journey paths and route visualization for all vehicles</p>
        </div>

        {/* ── Tab navigation ── */}
        <div className="dispatch-tab-container" style={{ margin: 0 }}>
          <button
            onClick={() => setActiveTab('vehicles')}
            className={`dispatch-tab-btn ${activeTab === 'vehicles' ? 'active' : ''}`}
          >
            🚛 Vehicles
          </button>
          <button
            onClick={() => setActiveTab('driver-pay')}
            className={`dispatch-tab-btn ${activeTab === 'driver-pay' ? 'active' : ''}`}
          >
            💰 Driver Pay
          </button>
          <button
            onClick={() => setRegisterView('main')}
            className={`dispatch-tab-btn ${activeTab === 'register-vehicle' ? 'active' : ''}`}
          >
            📝 Register Vehicle
            {registerVehicleBadgeCount > 0 && (
              <span className="dispatch-tab-badge">{registerVehicleBadgeCount}</span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('call-logs')}
            className={`dispatch-tab-btn ${activeTab === 'call-logs' ? 'active' : ''}`}
          >
            📞 Call Logs
          </button>
          <button
            onClick={() => setActiveTab('transporters')}
            className={`dispatch-tab-btn ${activeTab === 'transporters' ? 'active' : ''}`}
          >
            🏭 Transporters
          </button>
          <button
            onClick={() => setActiveTab('voice-bot-logs')}
            className={`dispatch-tab-btn ${activeTab === 'voice-bot-logs' ? 'active' : ''}`}
          >
            Live logs bot call
          </button>
        </div>

        <div className="page-header-right" style={{ position: 'relative' }}>
          <button
            type="button"
            className="btn-add-vehicle"
            title="Choose normal or transporter onboarding"
            onClick={() => setShowAddVehicleMenu((v) => !v)}
          >
            <span className="btn-icon">+</span>
            Add New Vehicle
          </button>
          {showAddVehicleMenu && (
            <div
              style={{
                position: 'absolute',
                right: 0,
                top: '110%',
                zIndex: 30,
                background: '#fff',
                border: '1px solid #e5e7eb',
                borderRadius: 8,
                boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                minWidth: 240,
                overflow: 'hidden',
              }}
            >
              <Link
                to="/app/drivers/new"
                className="dropdown-item"
                style={{ display: 'block', padding: '10px 14px', color: '#111827', textDecoration: 'none' }}
                onClick={() => setShowAddVehicleMenu(false)}
              >
                Onboard Normal Vehicle
              </Link>
              <Link
                to="/app/drivers/new?type=transporter"
                className="dropdown-item"
                style={{ display: 'block', padding: '10px 14px', color: '#111827', textDecoration: 'none', borderTop: '1px solid #f3f4f6' }}
                onClick={() => setShowAddVehicleMenu(false)}
              >
                Onboard Transporter Vehicle
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* ── Quick Stats Row (Only shown for Vehicles tab) ── */}
      {activeTab === 'vehicles' && (() => {
        const totalCount = vehicles.length;
        const inactiveCount = vehicles.filter(v => !v.is_active).length;
        const newCount = vehicles.filter(v => v.is_new).length;
        const recentCount = vehicles.filter(v => v.recent_trips_count > 0).length;
        const noBookingsCount = vehicles.filter(v => v.completed_trip_count === 0).length;
        const missingDocsCount = vehicles.filter(v => !v.has_dl || !v.has_rc || !v.has_insurance || !v.has_emission || !v.has_vehicle_photo || !v.has_upi).length;
        const openVehiclesCount = vehicles.filter(v => (v.body_type || '').toLowerCase() === 'open').length;
        const closedVehiclesCount = vehicles.filter(v => (v.body_type || '').toLowerCase() === 'closed').length;
        const longTripCount = vehicles.filter(v => v.ready_for_long_trip).length;

        return (
          <>
            <div className="quick-stats-row">
              <div
                className={`quick-stat-card ${quickFilter === 'all' ? 'active' : ''}`}
                onClick={() => {
                  setQuickFilter('all');
                  setMissingDocTypeFilter('all');
                }}
              >
                <div className="quick-stat-icon-wrapper all">
                  <Car size={20} />
                </div>
                <div className="quick-stat-info">
                  <span className="quick-stat-number">{totalCount}</span>
                  <span className="quick-stat-label">Total Vehicles</span>
                </div>
              </div>

              <div
                className={`quick-stat-card ${quickFilter === 'inactive' ? 'active' : ''}`}
                onClick={() => {
                  setQuickFilter(quickFilter === 'inactive' ? 'all' : 'inactive');
                  setMissingDocTypeFilter('all');
                }}
              >
                <div className="quick-stat-icon-wrapper inactive-veh">
                  <XCircle size={20} />
                </div>
                <div className="quick-stat-info">
                  <span className="quick-stat-number">{inactiveCount}</span>
                  <span className="quick-stat-label">Inactive Vehicles</span>
                </div>
              </div>

              <div
                className={`quick-stat-card ${quickFilter === 'new' ? 'active' : ''}`}
                onClick={() => {
                  setQuickFilter(quickFilter === 'new' ? 'all' : 'new');
                  setMissingDocTypeFilter('all');
                }}
              >
                <div className="quick-stat-icon-wrapper new-veh">
                  <AlertCircle size={20} />
                </div>
                <div className="quick-stat-info">
                  <span className="quick-stat-number">{newCount}</span>
                  <span className="quick-stat-label">New Vehicles</span>
                </div>
              </div>

              <div
                className={`quick-stat-card ${quickFilter === 'recent_bookings' ? 'active' : ''}`}
                onClick={() => {
                  setQuickFilter(quickFilter === 'recent_bookings' ? 'all' : 'recent_bookings');
                  setMissingDocTypeFilter('all');
                }}
              >
                <div className="quick-stat-icon-wrapper recent-veh">
                  <Calendar size={20} />
                </div>
                <div className="quick-stat-info">
                  <span className="quick-stat-number">{recentCount}</span>
                  <span className="quick-stat-label">vehicles recently completed bookings</span>
                </div>
              </div>

              <div
                className={`quick-stat-card ${quickFilter === 'no_bookings' ? 'active' : ''}`}
                onClick={() => {
                  setQuickFilter(quickFilter === 'no_bookings' ? 'all' : 'no_bookings');
                  setMissingDocTypeFilter('all');
                }}
              >
                <div className="quick-stat-icon-wrapper nobook-veh">
                  <MinusCircle size={20} />
                </div>
                <div className="quick-stat-info">
                  <span className="quick-stat-number">{noBookingsCount}</span>
                  <span className="quick-stat-label">vehicles with no bookings</span>
                </div>
              </div>

              <div
                className={`quick-stat-card ${quickFilter === 'missing_docs' ? 'active' : ''}`}
                onClick={() => {
                  if (quickFilter === 'missing_docs') {
                    setQuickFilter('all');
                    setMissingDocTypeFilter('all');
                  } else {
                    setQuickFilter('missing_docs');
                    setMissingDocTypeFilter('all');
                  }
                }}
              >
                <div className="quick-stat-icon-wrapper inactive-veh" style={{ background: '#fef3c7', color: '#d97706' }}>
                  <FileText size={20} />
                </div>
                <div className="quick-stat-info">
                  <span className="quick-stat-number">{missingDocsCount}</span>
                  <span className="quick-stat-label">Missing Documents</span>
                </div>
              </div>

              <div
                className={`quick-stat-card ${quickFilter === 'open_vehicles' ? 'active' : ''}`}
                onClick={() => {
                  setQuickFilter(quickFilter === 'open_vehicles' ? 'all' : 'open_vehicles');
                  setMissingDocTypeFilter('all');
                }}
              >
                <div className="quick-stat-icon-wrapper open-veh">
                  <Unlock size={20} />
                </div>
                <div className="quick-stat-info">
                  <span className="quick-stat-number">{openVehiclesCount}</span>
                  <span className="quick-stat-label">Open Vehicles</span>
                </div>
              </div>

              <div
                className={`quick-stat-card ${quickFilter === 'closed_vehicles' ? 'active' : ''}`}
                onClick={() => {
                  setQuickFilter(quickFilter === 'closed_vehicles' ? 'all' : 'closed_vehicles');
                  setMissingDocTypeFilter('all');
                }}
              >
                <div className="quick-stat-icon-wrapper closed-veh">
                  <Lock size={20} />
                </div>
                <div className="quick-stat-info">
                  <span className="quick-stat-number">{closedVehiclesCount}</span>
                  <span className="quick-stat-label">Closed Vehicles</span>
                </div>
              </div>

              <div
                className={`quick-stat-card ${quickFilter === 'long_trip_vehicles' ? 'active' : ''}`}
                onClick={() => {
                  setQuickFilter(quickFilter === 'long_trip_vehicles' ? 'all' : 'long_trip_vehicles');
                  setMissingDocTypeFilter('all');
                  setLongTripTypeFilter('inter_district');
                }}
              >
                <div className="quick-stat-icon-wrapper long-trip-veh">
                  <Compass size={20} />
                </div>
                <div className="quick-stat-info">
                  <span className="quick-stat-number">{longTripCount}</span>
                  <span className="quick-stat-label">Long Trip Vehicles</span>
                </div>
              </div>
            </div>

            {/* ── Missing Documents Sub-Filters ── */}
            {quickFilter === 'missing_docs' && (() => {
              const dlMissingCount = vehicles.filter(v => !v.has_dl).length;
              const rcMissingCount = vehicles.filter(v => !v.has_rc).length;
              const insMissingCount = vehicles.filter(v => !v.has_insurance).length;
              const emMissingCount = vehicles.filter(v => !v.has_emission).length;
              const photoMissingCount = vehicles.filter(v => !v.has_vehicle_photo).length;
              const upiMissingCount = vehicles.filter(v => !v.has_upi).length;

              const subFilters = [
                { key: 'all', label: 'All Missing Docs', count: missingDocsCount, icon: '⚠️' },
                { key: 'dl', label: 'Missing Driving License', count: dlMissingCount, icon: '🪪' },
                { key: 'rc', label: 'Missing Registration Certificate (RC)', count: rcMissingCount, icon: '📄' },
                { key: 'insurance', label: 'Missing Insurance', count: insMissingCount, icon: '🛡️' },
                { key: 'emission', label: 'Missing Emission / PUC', count: emMissingCount, icon: '🌿' },
                { key: 'vehicle', label: 'Missing Vehicle Photo', count: photoMissingCount, icon: '🚛' },
                { key: 'upi', label: 'Missing UPI ID', count: upiMissingCount, icon: '📱' },
              ];

              return (
                <div className="quick-stats-subrow" style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'nowrap', overflowX: 'auto', background: '#f9fafb', padding: '12px 16px', borderRadius: 10, border: '1px dashed #d1d5db' }}>
                  {subFilters.map(sf => (
                    <button
                      key={sf.key}
                      onClick={() => setMissingDocTypeFilter(sf.key)}
                      className={`quick-stat-subrow-btn ${missingDocTypeFilter === sf.key ? 'active' : ''}`}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        padding: '8px 14px',
                        border: '1px solid',
                        borderColor: missingDocTypeFilter === sf.key ? '#3b82f6' : '#e5e7eb',
                        borderRadius: 8,
                        background: missingDocTypeFilter === sf.key ? '#eff6ff' : '#ffffff',
                        color: missingDocTypeFilter === sf.key ? '#1d4ed8' : '#374151',
                        cursor: 'pointer',
                        fontWeight: 600,
                        fontSize: 13,
                        transition: 'all 0.2s ease',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.02)',
                        flexShrink: 0,
                      }}
                    >
                      <span>{sf.icon}</span>
                      <span>{sf.label}</span>
                      <span style={{
                        background: missingDocTypeFilter === sf.key ? '#3b82f6' : '#f3f4f6',
                        color: missingDocTypeFilter === sf.key ? '#ffffff' : '#4b5563',
                        padding: '2px 8px',
                        borderRadius: 12,
                        fontSize: 11,
                        fontWeight: 700,
                        marginLeft: 4
                      }}>
                        {sf.count}
                      </span>
                    </button>
                  ))}
                </div>
              );
            })()}

            {/* ── Long Trip Preference Sub-Filters ── */}
            {quickFilter === 'long_trip_vehicles' && (() => {
              const subFilters = [
                { key: 'inter_district', label: 'Long Intra-District', count: vehicles.filter(v => v.long_inter_district).length, icon: '🛣️' },
                { key: 'inter_state', label: 'Long Intra-State', count: vehicles.filter(v => v.long_inter_state).length, icon: '🗺️' },
                { key: 'local', label: 'Local', count: vehicles.filter(v => v.local).length, icon: '📍' },
              ];

              return (
                <div className="quick-stats-subrow" style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'nowrap', overflowX: 'auto', background: '#f9fafb', padding: '12px 16px', borderRadius: 10, border: '1px dashed #d1d5db' }}>
                  {subFilters.map(sf => (
                    <button
                      key={sf.key}
                      onClick={() => setLongTripTypeFilter(sf.key)}
                      className={`quick-stat-subrow-btn ${longTripTypeFilter === sf.key ? 'active' : ''}`}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        padding: '8px 14px',
                        border: '1px solid',
                        borderColor: longTripTypeFilter === sf.key ? '#6366f1' : '#e5e7eb',
                        borderRadius: 8,
                        background: longTripTypeFilter === sf.key ? '#eef2ff' : '#ffffff',
                        color: longTripTypeFilter === sf.key ? '#4338ca' : '#374151',
                        cursor: 'pointer',
                        fontWeight: 600,
                        fontSize: 13,
                        transition: 'all 0.2s ease',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.02)',
                        flexShrink: 0,
                      }}
                    >
                      <span>{sf.icon}</span>
                      <span>{sf.label}</span>
                      <span style={{
                        background: longTripTypeFilter === sf.key ? '#6366f1' : '#f3f4f6',
                        color: longTripTypeFilter === sf.key ? '#ffffff' : '#4b5563',
                        padding: '2px 8px',
                        borderRadius: 12,
                        fontSize: 11,
                        fontWeight: 700,
                        marginLeft: 4
                      }}>
                        {sf.count}
                      </span>
                    </button>
                  ))}
                </div>
              );
            })()}
          </>
        );
      })()}

      {/* ── Filter Controls (Only shown for Vehicles tab) ── */}
      {activeTab === 'vehicles' && (
        <div className="controls-section unified-controls-bar" style={{ marginBottom: '16px' }}>
          {/* Unified Search Box with Foreground Category Popover on Badge Click */}
          <div
            className="unified-search-wrapper"
            ref={searchBoxRef}
          >
            <div className="unified-search-box">
              <button
                type="button"
                className={`search-category-badge-btn ${searchMenuOpen ? 'active' : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setSearchMenuOpen(prev => !prev);
                }}
                title="Click to select search type (Tonnage, Length, Class, Model, Phone, etc.)"
              >
                {React.createElement(
                  SEARCH_CATEGORIES.find(c => c.id === searchMode)?.icon || Search,
                  { size: 16, className: 'category-badge-icon' }
                )}
                <span className="category-badge-label">
                  {SEARCH_CATEGORIES.find(c => c.id === searchMode)?.shortLabel || 'All'}
                </span>
                <ChevronDown size={14} className={`category-badge-chevron ${searchMenuOpen ? 'open' : ''}`} />
              </button>

              <div className="unified-search-separator" />

              <input
                ref={searchInputRef}
                type={searchMode === 'tonnage' || searchMode === 'length' ? 'number' : 'text'}
                step={searchMode === 'tonnage' || searchMode === 'length' ? '0.01' : undefined}
                placeholder={SEARCH_CATEGORIES.find(c => c.id === searchMode)?.placeholder || 'Search vehicles...'}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onFocus={() => setSearchMenuOpen(false)}
                className="unified-search-input"
              />

              {searchTerm && (
                <button
                  type="button"
                  className="unified-search-clear-btn"
                  onClick={() => {
                    setSearchTerm('');
                    if (searchInputRef.current) searchInputRef.current.focus();
                  }}
                  title="Clear search"
                >
                  <X size={15} />
                </button>
              )}
            </div>

            {/* Foreground Category Popover Dropdown on Badge Click */}
            {searchMenuOpen && (
              <div
                className="search-category-popover"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="search-popover-header">
                  <span className="popover-heading">SELECT SEARCH OPTION</span>
                  <span className="popover-subtext">Click any option to search by that field</span>
                </div>
                <div className="search-category-grid">
                  {SEARCH_CATEGORIES.map((cat) => {
                    const IconComp = cat.icon;
                    const isSelected = searchMode === cat.id;
                    return (
                      <div
                        key={cat.id}
                        className={`search-category-card ${isSelected ? 'active' : ''}`}
                        onClick={() => handleSelectSearchCategory(cat.id)}
                      >
                        <div className="category-card-icon-wrap">
                          <IconComp size={18} />
                        </div>
                        <div className="category-card-text">
                          <div className="category-card-title-row">
                            <span className="category-card-name">{cat.label}</span>
                            {isSelected && <span className="category-card-pill">Active</span>}
                          </div>
                          <span className="category-card-desc">{cat.description}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <div className="filter-box">
            <Filter size={18} />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              aria-label="Filter by status"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          <div className="filter-box">
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, margin: 0, cursor: 'pointer', whiteSpace: 'nowrap' }}>
              <input
                type="checkbox"
                checked={transporterOnly}
                onChange={(e) => setTransporterOnly(e.target.checked)}
              />
              Transporters only
            </label>
          </div>
        </div>
      )}

      {/* ── Driver Pay Tab ── */}
      {activeTab === 'driver-pay' && (
        <div style={{ padding: '24px', background: '#fff', borderRadius: '0 0 8px 8px', border: '1px solid #e5e7eb', borderTop: 'none' }}>
          <DriverPayTab />
        </div>
      )}

      {activeTab === 'register-vehicle' && (
        <div style={{ padding: '24px', background: '#f8fafc', borderRadius: '0 0 8px 8px', border: '1px solid #e5e7eb', borderTop: 'none' }}>
          {registerPage === 'main' && (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap', marginBottom: 20 }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: 22, color: '#0f172a' }}>Register Vehicle</h3>
                  <p style={{ margin: '6px 0 0', color: '#64748b', fontSize: 14 }}>Create a new vehicle row, then open pending or registered lists.</p>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 14, marginBottom: 22 }}>
                <button
                  type="button"
                  onClick={() => { setRegisterView('pending'); setPendingCurrentPage(1); }}
                  style={{
                    textAlign: 'left',
                    padding: '18px 20px',
                    border: '1px solid #fecaca',
                    borderRadius: 12,
                    background: '#fff',
                    cursor: 'pointer',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#b91c1c', letterSpacing: 0.3 }}>PENDING REQUESTS</div>
                    {pendingDriverRegCount > 0 && (
                      <span className="dispatch-tab-badge">{pendingDriverRegCount}</span>
                    )}
                  </div>
                  <div style={{ fontSize: 28, fontWeight: 800, color: '#0f172a', marginTop: 6 }}>{pendingRegistrationRows.length}</div>
                  <div style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>Texted but not registered</div>
                </button>
                <button
                  type="button"
                  onClick={() => { setRegisterView('registered'); setRegisteredCurrentPage(1); }}
                  style={{
                    textAlign: 'left',
                    padding: '18px 20px',
                    border: '1px solid #bfdbfe',
                    borderRadius: 12,
                    background: '#fff',
                    cursor: 'pointer',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#1d4ed8', letterSpacing: 0.3 }}>REGISTERED VEHICLES</div>
                    {registeredDriverRegCount > 0 && (
                      <span className="dispatch-tab-badge">{registeredDriverRegCount}</span>
                    )}
                  </div>
                  <div style={{ fontSize: 28, fontWeight: 800, color: '#0f172a', marginTop: 6 }}>{registeredRegistrationRows.length}</div>
                  <div style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>Ready to onboard or already onboarded</div>
                </button>
              </div>

              <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 22, boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }}>
                <h3 style={{ margin: '0 0 16px', fontSize: 18, color: '#0f172a' }}>New vehicle</h3>

                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '12px' }}>
                  <input
                    type="text"
                    placeholder="Driver Name"
                    value={registerName}
                    onChange={(e) => setRegisterName(e.target.value)}
                    style={{ padding: '10px', minWidth: '220px', border: '1px solid #d1d5db', borderRadius: '6px' }}
                  />
                  <input
                    type="text"
                    placeholder="Phone Number"
                    value={registerPhone}
                    onChange={(e) => setRegisterPhone(e.target.value)}
                    style={{ padding: '10px', minWidth: '220px', border: '1px solid #d1d5db', borderRadius: '6px' }}
                  />
                  <input
                    type="text"
                    placeholder="Vehicle (optional)"
                    value={registerVehicle}
                    onChange={(e) => setRegisterVehicle(e.target.value)}
                    style={{ padding: '10px', minWidth: '220px', border: '1px solid #d1d5db', borderRadius: '6px' }}
                  />
                  <input
                    type="text"
                    placeholder="Reg No (KA 19 AE 8030)"
                    value={registerRegistrationNo}
                    onChange={(e) => setRegisterRegistrationNo(e.target.value.toUpperCase())}
                    style={{ padding: '10px', minWidth: '220px', border: '1px solid #d1d5db', borderRadius: '6px' }}
                  />
                  <button
                    onClick={handleManualRegister}
                    disabled={isRegistering}
                    style={{
                      padding: '10px 16px',
                      border: 'none',
                      background: isRegistering ? '#93c5fd' : '#2563eb',
                      color: '#fff',
                      borderRadius: '6px',
                      cursor: isRegistering ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {isRegistering ? (registerPhotoUploading ? 'Uploading photo…' : 'Registering…') : 'Register'}
                  </button>
                </div>

                <RegistrationSpecsFields
                  specs={registerSpecs}
                  setSpecs={setRegisterSpecs}
                  extValue={registerExtPhone}
                  setExtValue={setRegisterExtPhone}
                  extLabelValue={registerExtLabel}
                  setExtLabelValue={setRegisterExtLabel}
                />

                <div style={{ marginBottom: '12px', maxWidth: '720px' }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>
                    Vehicle Photo <span style={{ fontWeight: 400, color: '#9ca3af' }}>(optional)</span>
                  </label>
                  {registerVehiclePhotoPreview ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <img
                        src={registerVehiclePhotoPreview}
                        alt="Vehicle preview"
                        onClick={() => { setLightboxImage(registerVehiclePhotoPreview); setShowLightbox(true); }}
                        style={{
                          width: 72, height: 72, borderRadius: 10,
                          objectFit: 'cover', border: '2px solid #10b981',
                          cursor: 'zoom-in', boxShadow: '0 2px 8px rgba(16,185,129,0.25)',
                        }}
                        title="Click to view full image"
                      />
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#065f46', marginBottom: 4 }}>Photo selected</div>
                        <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 6 }}>{registerVehiclePhoto?.name}</div>
                        <button
                          type="button"
                          onClick={clearRegisterVehiclePhoto}
                          style={{
                            fontSize: 12, padding: '4px 10px',
                            border: '1px solid #fca5a5', borderRadius: 6,
                            background: '#fff1f1', color: '#dc2626', cursor: 'pointer',
                          }}
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div style={{ position: 'relative', display: 'inline-block' }} ref={registerPhotoPickerRef}>
                      <input
                        ref={registerCameraInputRef}
                        type="file"
                        accept="image/*"
                        capture="environment"
                        style={{ display: 'none' }}
                        onChange={handleRegisterPhotoFile}
                      />
                      <input
                        ref={registerGalleryInputRef}
                        type="file"
                        accept="image/*"
                        style={{ display: 'none' }}
                        onChange={handleRegisterPhotoFile}
                      />
                      <button
                        type="button"
                        onClick={() => setRegisterPhotoPickerOpen((v) => !v)}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 8,
                          padding: '10px 16px',
                          border: '2px dashed #d1d5db',
                          borderRadius: 10,
                          background: '#f9fafb',
                          color: '#374151',
                          fontSize: 14,
                          fontWeight: 600,
                          cursor: 'pointer',
                        }}
                      >
                        <span style={{ fontSize: 20 }}>📷</span>
                        Add Vehicle Photo
                      </button>
                      {registerPhotoPickerOpen && (
                        <div style={{
                          position: 'absolute', top: 'calc(100% + 6px)', left: 0,
                          background: '#fff',
                          borderRadius: 10,
                          boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                          border: '1px solid #e5e7eb',
                          overflow: 'hidden',
                          zIndex: 100,
                          minWidth: 200,
                        }}>
                          <button
                            type="button"
                            onClick={() => { setRegisterPhotoPickerOpen(false); registerCameraInputRef.current?.click(); }}
                            style={{
                              display: 'flex', alignItems: 'center', gap: 10,
                              width: '100%', padding: '13px 16px',
                              border: 'none', background: 'none',
                              fontSize: 14, fontWeight: 600, color: '#111',
                              cursor: 'pointer', textAlign: 'left',
                              borderBottom: '1px solid #f3f4f6',
                            }}
                          >
                            <span style={{ fontSize: 20 }}>📸</span>
                            Open Camera
                          </button>
                          <button
                            type="button"
                            onClick={() => { setRegisterPhotoPickerOpen(false); registerGalleryInputRef.current?.click(); }}
                            style={{
                              display: 'flex', alignItems: 'center', gap: 10,
                              width: '100%', padding: '13px 16px',
                              border: 'none', background: 'none',
                              fontSize: 14, fontWeight: 600, color: '#111',
                              cursor: 'pointer', textAlign: 'left',
                            }}
                          >
                            <span style={{ fontSize: 20 }}>🖼️</span>
                            Upload from Gallery
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                  <div style={{ fontSize: 12, color: '#6b7280', marginTop: 6 }}>
                    Take a photo of the vehicle on site or choose from gallery — stored securely
                  </div>
                </div>

                <div style={{ marginBottom: '12px', maxWidth: '720px' }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '4px' }}>Comments <span style={{ fontWeight: 400, color: '#9ca3af' }}>(optional)</span></label>
                  <textarea
                    placeholder="Any notes about this driver, reference, or context…"
                    value={registerComments}
                    onChange={(e) => setRegisterComments(e.target.value)}
                    rows={2}
                    style={{ width: '100%', padding: '10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box' }}
                  />
                </div>

                <div style={{ marginBottom: '16px', maxWidth: '720px' }}>
                  <div style={{ fontWeight: 600, marginBottom: '8px', color: '#374151' }}>Stand / base location</div>
                  <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px' }}>
                    {mapsLoaded
                      ? <>Type to search — <strong>select from the dropdown</strong> to auto-fill coordinates.</>
                      : <>Type an address and click <strong>Get Coordinates</strong> — coordinates update automatically.</>}
                  </div>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                    <PlacesAutocompleteInput
                      value={registerPlaceAddress}
                      onChange={setRegisterPlaceAddress}
                      onPlaceSelect={(place) => {
                        setRegisterPlaceAddress(place.address);
                        setRegisterBaseLat(place.lat);
                        setRegisterBaseLng(place.lng);
                        setRegisterResolvedAddress(place.address);
                      }}
                      placeholder="Type to search — select from dropdown..."
                      inputStyle={{ padding: '10px', minWidth: '200px', border: '1px solid #d1d5db', borderRadius: '6px' }}
                    />
                    {!mapsLoaded && (
                      <button
                        type="button"
                        onClick={handleRegisterGeocodePlace}
                        disabled={isRegisterGeocoding}
                        style={{
                          padding: '10px 16px',
                          border: 'none',
                          background: '#4F46E5',
                          color: '#fff',
                          borderRadius: '6px',
                          cursor: isRegisterGeocoding ? 'not-allowed' : 'pointer',
                          opacity: isRegisterGeocoding ? 0.6 : 1,
                        }}
                      >
                        {isRegisterGeocoding ? 'Loading...' : 'Get Coordinates'}
                      </button>
                    )}
                  </div>
                  {registerResolvedAddress && registerBaseLat && registerBaseLng && (
                    <div
                      style={{
                        marginTop: '10px',
                        padding: '10px 12px',
                        background: '#ecfdf3',
                        color: '#065f46',
                        borderRadius: '8px',
                        fontSize: '13px',
                        fontWeight: 600,
                      }}
                    >
                      Using address: {registerResolvedAddress}
                      <span style={{ fontWeight: 400, color: '#047857', marginLeft: '8px' }}>
                        ({registerBaseLat}, {registerBaseLng})
                      </span>
                    </div>
                  )}
                </div>

                {registrationError && <div style={{ color: '#dc2626', marginBottom: '10px' }}>{registrationError}</div>}
                {registrationSuccess && <div style={{ color: '#16a34a', marginBottom: '10px' }}>{registrationSuccess}</div>}
              </div>
            </>
          )}

          {registerPage !== 'main' && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
                <div>
                  <button
                    type="button"
                    onClick={() => history.goBack()}
                    style={{
                      border: '1px solid #d1d5db',
                      background: '#fff',
                      borderRadius: 8,
                      padding: '8px 12px',
                      cursor: 'pointer',
                      fontWeight: 600,
                      color: '#374151',
                      marginBottom: 8,
                    }}
                  >
                    ← Back to Register Vehicle
                  </button>
                  <h3 style={{ margin: 0, fontSize: 20, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 8 }}>
                    {registerPage === 'pending' ? 'Pending Requests' : 'Registered Vehicles'}
                    {registerPage === 'pending' && pendingDriverRegCount > 0 && (
                      <span className="dispatch-tab-badge">{pendingDriverRegCount}</span>
                    )}
                    {registerPage === 'registered' && registeredDriverRegCount > 0 && (
                      <span className="dispatch-tab-badge">{registeredDriverRegCount}</span>
                    )}
                  </h3>
                  <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: 13 }}>
                    {registerPage === 'pending'
                      ? 'Drivers who were texted but are not registered yet.'
                      : 'Registered rows — onboard to create a vehicle on the same driver if the phone already exists.'}
                  </p>
                </div>
                <input
                  type="text"
                  placeholder="Search any value (ID, name, phone, vehicle, class, source...)"
                  value={registrationSearchTerm}
                  onChange={(e) => setRegistrationSearchTerm(e.target.value)}
                  style={{ padding: '10px', width: '100%', maxWidth: '420px', border: '1px solid #d1d5db', borderRadius: '8px', background: '#fff' }}
                />
              </div>

              {registrationError && <div style={{ color: '#dc2626', marginBottom: '10px' }}>{registrationError}</div>}
              {registrationSuccess && <div style={{ color: '#16a34a', marginBottom: '10px' }}>{registrationSuccess}</div>}
            </>
          )}

          {registerPage === 'pending' && (
            <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 16 }}>
              {registrationLoading ? (
                <div style={{ color: '#6b7280' }}>Loading registration records...</div>
              ) : (
                <>
                  <div className="registration-table-wrapper" style={{ marginBottom: '20px' }}>
                    <table className="registration-table">
                      <thead>
                        <tr>
                          <th style={{ width: '65px' }}>ID</th>
                          <th>Driver</th>
                          <th>Vehicle</th>
                          <th>Base Location</th>
                          <th>Comments</th>
                          <th>Time</th>
                          <th>Step</th>
                          <th className="col-action" style={{ textAlign: 'right' }}>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pendingRowsForPage.map((row, rowIdx) => {
                          const isExpanded = expandedRegRowId === row.id;
                          const isActionOpen = actionMenuRowId === row.id;
                          const isNearBottom = pendingRowsForPage.length > 1 && rowIdx >= pendingRowsForPage.length - 2;
                          return (
                            <React.Fragment key={row.id}>
                              <tr
                                className={`reg-row ${isExpanded ? 'expanded' : ''} ${isActionOpen ? 'has-open-menu' : ''}`}
                                style={{ position: 'relative', zIndex: isActionOpen ? 80 : (isExpanded ? 2 : 1) }}
                                onClick={() => setExpandedRegRowId(isExpanded ? null : row.id)}
                              >
                                <td style={{ whiteSpace: 'nowrap' }}>
                                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }} title="Click to expand details">
                                    <span className={`reg-expand-chevron ${isExpanded ? 'open' : ''}`}>
                                      <ChevronRight size={16} />
                                    </span>
                                    <span className="reg-id-badge">
                                      {row.id}
                                    </span>
                                  </div>
                                </td>
                                <td>
                                  <div className="reg-driver-cell">
                                    {row.vehicle_photo_url ? (
                                      <img
                                        src={row.vehicle_photo_url}
                                        alt="Vehicle"
                                        className="reg-driver-avatar"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setLightboxImage(row.vehicle_photo_url);
                                          setShowLightbox(true);
                                        }}
                                        title="Click to zoom image"
                                      />
                                    ) : (
                                      <div className="reg-driver-avatar-placeholder" title="No photo">
                                        🚛
                                      </div>
                                    )}
                                    <div className="reg-driver-info">
                                      <span className="reg-driver-name">{row.name || '—'}</span>
                                      <span className="reg-driver-phone">
                                        <Phone size={10} style={{ color: '#94a3b8' }} /> {row.phone_number}
                                      </span>
                                    </div>
                                  </div>
                                </td>
                                <td>
                                  <div className="reg-vehicle-cell">
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                      <span className="reg-vehicle-regno">{row.registration_no || '—'}</span>
                                      {row.vehicle_class != null && (
                                        <span style={{ padding: '1px 6px', borderRadius: 4, background: '#f1f5f9', color: '#475569', fontWeight: 600, fontSize: 10 }}>
                                          Class {row.vehicle_class}
                                        </span>
                                      )}
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                      <span className="reg-vehicle-name" style={{ fontWeight: 600 }}>
                                        {row.vehicle_name || [row.make, row.model].filter(Boolean).join(' ') || '—'}
                                      </span>
                                      {(() => {
                                        const { ton, len } = getRegTonAndLength(row);
                                        if (!ton && !len) return null;
                                        return (
                                          <span style={{ fontSize: 11, color: '#64748b', fontWeight: 500 }}>
                                            {ton && (
                                              <span>Ton: <strong style={{ color: '#334155' }}>{ton}{String(ton).toLowerCase().includes('ton') || String(ton).toLowerCase().includes('t') || String(ton).toLowerCase().includes('kg') ? '' : ' Ton'}</strong></span>
                                            )}
                                            {ton && len && ' · '}
                                            {len && (
                                              <span>Length: <strong style={{ color: '#334155' }}>{len}{String(len).toLowerCase().includes('ft') || String(len).toLowerCase().includes('m') ? '' : ' ft'}</strong></span>
                                            )}
                                          </span>
                                        );
                                      })()}
                                    </div>
                                  </div>
                                </td>
                                <td>
                                  <div className="reg-location-cell" title={row.address || 'No location set'}>
                                    <MapPin size={13} style={{ color: '#ef4444', flexShrink: 0, marginTop: 2 }} />
                                    <span className="reg-location-text">
                                      {row.address || '—'}
                                    </span>
                                  </div>
                                </td>
                                <td style={{ maxWidth: '120px' }}>
                                  {row.comments ? (
                                    <div
                                      title={row.comments}
                                      style={{
                                        fontSize: '12px',
                                        color: '#334155',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '5px',
                                        maxWidth: '100%',
                                      }}
                                    >
                                      <span
                                        style={{
                                          width: '6px',
                                          height: '6px',
                                          borderRadius: '50%',
                                          background: '#3b82f6',
                                          flexShrink: 0,
                                        }}
                                      />
                                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {row.comments}
                                      </span>
                                    </div>
                                  ) : (
                                    <span style={{ color: '#94a3b8', fontSize: '12px' }}>—</span>
                                  )}
                                </td>
                                <td>
                                  <div className="reg-time-cell">
                                    <span className="reg-time-main">
                                      {row.last_texted_at
                                        ? new Date(row.last_texted_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
                                        : row.updated_at
                                          ? new Date(row.updated_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
                                          : '—'}
                                    </span>
                                    {row.created_at && (
                                      <span className="reg-time-sub">
                                        Reg: {new Date(row.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                                      </span>
                                    )}
                                  </div>
                                </td>
                                <td>
                                  {row.current_step ? (
                                    <span style={{
                                      background: row.current_step === 'completion' ? '#dcfce7' : '#fef08a',
                                      color: row.current_step === 'completion' ? '#166534' : '#854d0e',
                                      padding: '2px 7px',
                                      borderRadius: '4px',
                                      fontSize: '11px',
                                      fontWeight: '600',
                                      textTransform: 'capitalize',
                                      whiteSpace: 'nowrap',
                                    }}>
                                      {row.current_step}
                                    </span>
                                  ) : '—'}
                                </td>
                                <td
                                  className="col-action"
                                  style={{
                                    textAlign: 'right',
                                    whiteSpace: 'nowrap',
                                    zIndex: isActionOpen ? 85 : undefined,
                                  }}
                                >
                                  <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'flex-end', gap: '4px' }} onClick={(e) => e.stopPropagation()}>
                                    <button
                                      type="button"
                                      onClick={() => handleApproveRegistration(row.id)}
                                      style={{
                                        padding: '4px 9px',
                                        border: 'none',
                                        background: '#16a34a',
                                        color: '#fff',
                                        borderRadius: '6px',
                                        cursor: 'pointer',
                                        fontSize: '11px',
                                        fontWeight: 600,
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '4px',
                                      }}
                                    >
                                      <Check size={13} /> Register
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => handleEditRegistration(row)}
                                      title="Edit Details"
                                      className="reg-action-btn-secondary"
                                      style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: 26,
                                        height: 26,
                                        borderRadius: 6,
                                        border: '1px solid #cbd5e1',
                                        background: '#f8fafc',
                                        color: '#0284c7',
                                        cursor: 'pointer',
                                        padding: 0,
                                      }}
                                    >
                                      <Pencil size={12} />
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => handleViewMessages(row.phone_number)}
                                      title="View WhatsApp Messages"
                                      className="reg-action-btn-secondary"
                                      style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: 26,
                                        height: 26,
                                        borderRadius: 6,
                                        border: '1px solid #cbd5e1',
                                        background: '#f8fafc',
                                        color: '#2563eb',
                                        cursor: 'pointer',
                                        padding: 0,
                                      }}
                                    >
                                      <MessageSquare size={12} />
                                    </button>
                                    <div style={{ position: 'relative' }}>
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setActionMenuRowId(actionMenuRowId === row.id ? null : row.id);
                                        }}
                                        title="More Actions"
                                        className="reg-action-btn-more"
                                        style={{
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          width: 26,
                                          height: 26,
                                          borderRadius: 6,
                                          border: '1px solid #cbd5e1',
                                          background: actionMenuRowId === row.id ? '#e2e8f0' : '#f8fafc',
                                          color: '#475569',
                                          cursor: 'pointer',
                                          padding: 0,
                                        }}
                                      >
                                        <MoreVertical size={13} />
                                      </button>
                                      {actionMenuRowId === row.id && (
                                        <div
                                          onClick={(e) => e.stopPropagation()}
                                          className={`reg-action-dropdown ${isNearBottom ? 'open-upward' : ''}`}
                                          style={{
                                            position: 'absolute',
                                            right: 0,
                                            top: isNearBottom ? 'auto' : 'calc(100% + 4px)',
                                            bottom: isNearBottom ? 'calc(100% + 4px)' : 'auto',
                                            zIndex: 150,
                                            background: '#fff',
                                            border: '1px solid #cbd5e1',
                                            borderRadius: 8,
                                            boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.18), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
                                            minWidth: 175,
                                            padding: '4px',
                                            textAlign: 'left',
                                          }}
                                        >
                                          <button
                                            type="button"
                                            onClick={() => {
                                              setActionMenuRowId(null);
                                              handleEditRegistration(row);
                                            }}
                                            className="reg-menu-item"
                                          >
                                            <Pencil size={14} style={{ marginRight: 8, color: '#0284c7' }} />
                                            Edit details
                                          </button>
                                          <button
                                            type="button"
                                            onClick={() => {
                                              setActionMenuRowId(null);
                                              handleViewMessages(row.phone_number);
                                            }}
                                            className="reg-menu-item"
                                          >
                                            <MessageSquare size={14} style={{ marginRight: 8, color: '#2563eb' }} />
                                            View messages
                                          </button>
                                          <button
                                            type="button"
                                            onClick={() => {
                                              setActionMenuRowId(null);
                                              handleRemoveSession(row.phone_number);
                                            }}
                                            title="Delete row from onboarding_sessions"
                                            className="reg-menu-item"
                                            style={{ color: '#d97706' }}
                                          >
                                            <AlertCircle size={14} style={{ marginRight: 8, color: '#d97706' }} />
                                            Remove session
                                          </button>
                                          <div style={{ height: 1, background: '#f1f5f9', margin: '4px 0' }} />
                                          <button
                                            type="button"
                                            onClick={() => {
                                              setActionMenuRowId(null);
                                              handleRemovePendingRegistration(row.id);
                                            }}
                                            className="reg-menu-item"
                                            style={{ color: '#dc2626' }}
                                          >
                                            <Trash2 size={14} style={{ marginRight: 8, color: '#dc2626' }} />
                                            Delete request
                                          </button>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </td>
                              </tr>

                              {/* Expanded Accordion Row for Pending */}
                              {isExpanded && (
                                <tr className="reg-expanded-row">
                                  <td colSpan={8}>
                                    <div className="reg-expanded-panel">
                                      {row.comments && (
                                        <div className="reg-expanded-remarks">
                                          <div style={{ fontSize: 11, fontWeight: 700, color: '#1e40af', textTransform: 'uppercase', marginBottom: 4 }}>
                                            Comments / Remarks
                                          </div>
                                          <div style={{ fontSize: 13, color: '#1e293b', whiteSpace: 'pre-wrap' }}>
                                            {row.comments}
                                          </div>
                                        </div>
                                      )}

                                      <div className="reg-facts-grid">
                                        <div className="reg-fact-item">
                                          <span className="reg-fact-label">Vehicle Class</span>
                                          <span className="reg-fact-value">{row.vehicle_class != null ? `Class ${row.vehicle_class}` : '—'}</span>
                                        </div>
                                        <div className="reg-fact-item">
                                          <span className="reg-fact-label">Body Type</span>
                                          <span className="reg-fact-value">{row.body_type || '—'}</span>
                                        </div>
                                        <div className="reg-fact-item" style={{ gridColumn: 'span 2' }}>
                                          <span className="reg-fact-label">Base Location / Address</span>
                                          <span className="reg-fact-value" style={{ display: 'flex', alignItems: 'flex-start', gap: 4 }}>
                                            <MapPin size={14} style={{ color: '#ef4444', flexShrink: 0, marginTop: 2 }} />
                                            {row.address || '—'}
                                          </span>
                                        </div>
                                        <div className="reg-fact-item">
                                          <span className="reg-fact-label">Last Texted</span>
                                          <span className="reg-fact-value">
                                            {row.last_texted_at ? new Date(row.last_texted_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'}
                                          </span>
                                        </div>
                                        <div className="reg-fact-item">
                                          <span className="reg-fact-label">Last Updated</span>
                                          <span className="reg-fact-value">
                                            {row.updated_at ? new Date(row.updated_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
                                          </span>
                                        </div>
                                      </div>

                                      <div className="reg-expanded-actions">
                                        <span style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', marginRight: 4 }}>
                                          Buttons & Actions:
                                        </span>
                                        <button
                                          type="button"
                                          onClick={() => handleApproveRegistration(row.id)}
                                          className="reg-btn"
                                          style={{ background: '#16a34a', color: '#fff', border: 'none' }}
                                        >
                                          <Check size={13} /> Register Driver / Vehicle
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => handleEditRegistration(row)}
                                          className="reg-btn"
                                          style={{ background: '#f0f9ff', color: '#0284c7', border: '1px solid #bae6fd' }}
                                        >
                                          <Pencil size={13} /> Edit Details
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => handleViewMessages(row.phone_number)}
                                          className="reg-btn"
                                          style={{ background: '#eff6ff', color: '#2563eb', border: '1px solid #bfdbfe' }}
                                        >
                                          <MessageSquare size={13} /> View WhatsApp Messages
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => handleRemoveSession(row.phone_number)}
                                          className="reg-btn"
                                          style={{ background: '#fffbeb', color: '#d97706', border: '1px solid #fde68a' }}
                                        >
                                          <AlertCircle size={13} /> Remove Session
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => handleRemovePendingRegistration(row.id)}
                                          className="reg-btn"
                                          style={{ background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca' }}
                                        >
                                          <Trash2 size={13} /> Delete Request
                                        </button>
                                      </div>
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })}
                        {pendingRegistrationRows.length === 0 && (
                          <tr><td colSpan={7} style={{ textAlign: 'center', color: '#6b7280', padding: '24px' }}>No pending requests</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {pendingTotalPages > 1 && (
                    <div style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      gap: '8px',
                      marginTop: '-8px',
                      marginBottom: '24px',
                      padding: '8px 0'
                    }}>
                      <button
                        onClick={() => setPendingCurrentPage(prev => Math.max(1, prev - 1))}
                        disabled={pendingCurrentPage === 1}
                        style={{
                          padding: '8px 16px',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          backgroundColor: pendingCurrentPage === 1 ? '#f3f4f6' : 'white',
                          cursor: pendingCurrentPage === 1 ? 'not-allowed' : 'pointer',
                          fontSize: '14px',
                          fontWeight: '600'
                        }}
                      >
                        Previous
                      </button>
                      <span style={{ fontSize: '14px', color: '#374151' }}>
                        Pending Page {pendingCurrentPage} of {pendingTotalPages}
                      </span>
                      <button
                        onClick={() => setPendingCurrentPage(prev => Math.min(pendingTotalPages, prev + 1))}
                        disabled={pendingCurrentPage === pendingTotalPages}
                        style={{
                          padding: '8px 16px',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          backgroundColor: pendingCurrentPage === pendingTotalPages ? '#f3f4f6' : 'white',
                          cursor: pendingCurrentPage === pendingTotalPages ? 'not-allowed' : 'pointer',
                          fontSize: '14px',
                          fontWeight: '600'
                        }}
                      >
                        Next
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {registerPage === 'registered' && (
            <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 16 }}>
              {registrationLoading ? (
                <div style={{ color: '#6b7280' }}>Loading registration records...</div>
              ) : (
                <>
                  <div className="registration-table-wrapper" style={{ marginBottom: '20px' }}>
                    <table className="registration-table">
                      <thead>
                        <tr>
                          <th style={{ width: '65px' }}>ID</th>
                          <th>Driver</th>
                          <th>Vehicle</th>
                          <th>Base Location</th>
                          <th>Comments</th>
                          <th>Updated</th>
                          <th className="col-action" style={{ textAlign: 'right' }}>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {registeredRowsForPage.map((row, rowIdx) => {
                          const isExpanded = expandedRegRowId === row.id;
                          const rowReg = String(row.registration_no || '').replace(/[^A-Za-z0-9]/g, '').toUpperCase();
                          const matchingVehicle = rowReg
                            ? vehicles.find((v) => String(v.registration_no || '').replace(/[^A-Za-z0-9]/g, '').toUpperCase() === rowReg)
                            : null;
                          const onboardedVehicleId = row.onboarded_vehicle_id || (matchingVehicle ? matchingVehicle.vehicle_id : null);
                          const alreadyOnboarded = Boolean(onboardedVehicleId);
                          const isOnboarding = onboardingRegistrationIds.has(row.id);
                          const blocked = alreadyOnboarded || isOnboarding;
                          const menuOpen = onboardMenuRowId === row.id && !blocked;
                          const isActionOpen = actionMenuRowId === row.id;
                          const hasMenuOpen = menuOpen || isActionOpen;
                          const isNearBottom = registeredRowsForPage.length > 1 && rowIdx >= registeredRowsForPage.length - 2;

                          return (
                            <React.Fragment key={row.id}>
                              <tr
                                className={`reg-row ${isExpanded ? 'expanded' : ''} ${hasMenuOpen ? 'has-open-menu' : ''}`}
                                style={{ position: 'relative', zIndex: hasMenuOpen ? 80 : (isExpanded ? 2 : 1) }}
                                onClick={() => setExpandedRegRowId(isExpanded ? null : row.id)}
                              >
                                {/* ID + Chevron */}
                                <td style={{ whiteSpace: 'nowrap' }}>
                                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }} title="Click to expand details">
                                    <span className={`reg-expand-chevron ${isExpanded ? 'open' : ''}`}>
                                      <ChevronRight size={16} />
                                    </span>
                                    <span className="reg-id-badge">
                                      {row.id}
                                    </span>
                                    {!alreadyOnboarded && (
                                      <span
                                        title="Yet to be onboarded"
                                        style={{
                                          background: '#dc2626',
                                          color: '#fff',
                                          borderRadius: 999,
                                          padding: '1px 6px',
                                          fontSize: 9,
                                          fontWeight: 700,
                                          letterSpacing: 0.3,
                                        }}
                                      >
                                        NEW
                                      </span>
                                    )}
                                  </div>
                                </td>

                                {/* Driver (Photo avatar + Name + Phone) */}
                                <td>
                                  <div className="reg-driver-cell">
                                    {row.vehicle_photo_url ? (
                                      <img
                                        src={row.vehicle_photo_url}
                                        alt="Vehicle"
                                        className="reg-driver-avatar"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setLightboxImage(row.vehicle_photo_url);
                                          setShowLightbox(true);
                                        }}
                                        title="Click to zoom image"
                                      />
                                    ) : (
                                      <div className="reg-driver-avatar-placeholder" title="No photo">
                                        🚛
                                      </div>
                                    )}
                                    <div className="reg-driver-info">
                                      <span className="reg-driver-name">{row.name || '—'}</span>
                                      <span className="reg-driver-phone">
                                        <Phone size={10} style={{ color: '#94a3b8' }} /> {row.phone_number}
                                      </span>
                                    </div>
                                  </div>
                                </td>

                                {/* Vehicle (Reg No + Model + Class) */}
                                <td>
                                  <div className="reg-vehicle-cell">
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                      <span className="reg-vehicle-regno">{row.registration_no || '—'}</span>
                                      {row.vehicle_class != null && (
                                        <span style={{ padding: '1px 6px', borderRadius: 4, background: '#f1f5f9', color: '#475569', fontWeight: 600, fontSize: 10 }}>
                                          Class {row.vehicle_class}
                                        </span>
                                      )}
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                      <span className="reg-vehicle-name" style={{ fontWeight: 600 }}>
                                        {row.vehicle_name || [row.make, row.model].filter(Boolean).join(' ') || '—'}
                                      </span>
                                      {(() => {
                                        const { ton, len } = getRegTonAndLength(row);
                                        if (!ton && !len) return null;
                                        return (
                                          <span style={{ fontSize: 11, color: '#64748b', fontWeight: 500 }}>
                                            {ton && (
                                              <span>Ton: <strong style={{ color: '#334155' }}>{ton}{String(ton).toLowerCase().includes('ton') || String(ton).toLowerCase().includes('t') || String(ton).toLowerCase().includes('kg') ? '' : ' Ton'}</strong></span>
                                            )}
                                            {ton && len && ' · '}
                                            {len && (
                                              <span>Length: <strong style={{ color: '#334155' }}>{len}{String(len).toLowerCase().includes('ft') || String(len).toLowerCase().includes('m') ? '' : ' ft'}</strong></span>
                                            )}
                                          </span>
                                        );
                                      })()}
                                    </div>
                                  </div>
                                </td>

                                {/* Base Location */}
                                <td>
                                  <div className="reg-location-cell" title={row.address || 'No location set'}>
                                    <MapPin size={13} style={{ color: '#ef4444', flexShrink: 0, marginTop: 2 }} />
                                    <span className="reg-location-text">
                                      {row.address || '—'}
                                    </span>
                                  </div>
                                </td>

                                {/* Comments Column */}
                                <td style={{ maxWidth: '120px' }}>
                                  {row.comments ? (
                                    <div
                                      title={row.comments}
                                      style={{
                                        fontSize: '12px',
                                        color: '#334155',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '5px',
                                        maxWidth: '100%',
                                      }}
                                    >
                                      <span
                                        style={{
                                          width: '6px',
                                          height: '6px',
                                          borderRadius: '50%',
                                          background: '#3b82f6',
                                          flexShrink: 0,
                                        }}
                                      />
                                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {row.comments}
                                      </span>
                                    </div>
                                  ) : (
                                    <span style={{ color: '#94a3b8', fontSize: '12px' }}>—</span>
                                  )}
                                </td>

                                {/* Updated At */}
                                <td>
                                  <div className="reg-time-cell">
                                    <span className="reg-time-main">
                                      {row.updated_at
                                        ? new Date(row.updated_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
                                        : '—'}
                                    </span>
                                    {row.created_at && (
                                      <span className="reg-time-sub">
                                        Reg: {new Date(row.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                                      </span>
                                    )}
                                  </div>
                                </td>

                                {/* Action Column */}
                                <td
                                  className="col-action"
                                  style={{
                                    textAlign: 'right',
                                    whiteSpace: 'nowrap',
                                    zIndex: hasMenuOpen ? 85 : undefined,
                                  }}
                                >
                                  <div
                                    style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'flex-end', gap: '4px' }}
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    {/* Onboard Button */}
                                    <div style={{ position: 'relative' }}>
                                      <button
                                        type="button"
                                        className="reg-onboard-btn"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          if (blocked) return;
                                          setOnboardMenuRowId(menuOpen ? null : row.id);
                                          setActionMenuRowId(null);
                                        }}
                                        disabled={blocked}
                                        title={
                                          alreadyOnboarded
                                            ? `Already onboarded as V-${onboardedVehicleId}`
                                            : 'Onboard as a normal vehicle or under a transporter'
                                        }
                                        style={{
                                          padding: '4px 9px',
                                          border: alreadyOnboarded ? '1px solid #d1d5db' : 'none',
                                          background: alreadyOnboarded ? '#f3f4f6' : blocked ? '#86efac' : '#16a34a',
                                          color: alreadyOnboarded ? '#4b5563' : '#fff',
                                          borderRadius: '6px',
                                          cursor: blocked ? 'not-allowed' : 'pointer',
                                          fontWeight: 600,
                                          fontSize: '11px',
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          gap: '4px',
                                        }}
                                      >
                                        {isOnboarding ? (
                                          'Onboarding...'
                                        ) : alreadyOnboarded ? (
                                          `Onboarded (V-${onboardedVehicleId})`
                                        ) : (
                                          <>
                                            Onboard <ChevronDown size={13} />
                                          </>
                                        )}
                                      </button>
                                      {menuOpen && (
                                        <div
                                          onClick={(e) => e.stopPropagation()}
                                          className={`reg-onboard-menu ${isNearBottom ? 'open-upward' : ''}`}
                                          style={{
                                            position: 'absolute',
                                            right: 0,
                                            top: isNearBottom ? 'auto' : 'calc(100% + 4px)',
                                            bottom: isNearBottom ? 'calc(100% + 4px)' : 'auto',
                                            zIndex: 150,
                                            background: '#fff',
                                            border: '1px solid #e5e7eb',
                                            borderRadius: 8,
                                            boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                                            minWidth: 220,
                                            overflow: 'hidden',
                                            textAlign: 'left',
                                          }}
                                        >
                                          <button
                                            type="button"
                                            onClick={() => {
                                              setOnboardMenuRowId(null);
                                              handleOnboardFromRegistration(row);
                                            }}
                                            style={{
                                              display: 'flex',
                                              alignItems: 'center',
                                              width: '100%',
                                              textAlign: 'left',
                                              padding: '9px 12px',
                                              background: 'none',
                                              border: 'none',
                                              color: '#111827',
                                              cursor: 'pointer',
                                              fontSize: 13,
                                              fontWeight: 500,
                                            }}
                                          >
                                            Onboard Normal Vehicle
                                          </button>
                                          <button
                                            type="button"
                                            onClick={() => {
                                              setOnboardMenuRowId(null);
                                              handleOpenTransporterOnboard(row);
                                            }}
                                            style={{
                                              display: 'flex',
                                              alignItems: 'center',
                                              width: '100%',
                                              textAlign: 'left',
                                              padding: '9px 12px',
                                              background: 'none',
                                              border: 'none',
                                              borderTop: '1px solid #f3f4f6',
                                              color: '#111827',
                                              cursor: 'pointer',
                                              fontSize: 13,
                                              fontWeight: 500,
                                            }}
                                          >
                                            Onboard Transporter Vehicle
                                          </button>
                                        </div>
                                      )}
                                    </div>

                                    {/* Edit Details Button */}
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleEditRegistration(row);
                                      }}
                                      title="Edit Details"
                                      className="reg-action-btn-secondary"
                                      style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: 26,
                                        height: 26,
                                        borderRadius: 6,
                                        border: '1px solid #cbd5e1',
                                        background: '#f8fafc',
                                        color: '#0284c7',
                                        cursor: 'pointer',
                                        padding: 0,
                                      }}
                                    >
                                      <Pencil size={12} />
                                    </button>

                                    {/* View Messages Button */}
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleViewMessages(row.phone_number);
                                      }}
                                      title="View WhatsApp Messages"
                                      className="reg-action-btn-secondary"
                                      style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: 26,
                                        height: 26,
                                        borderRadius: 6,
                                        border: '1px solid #cbd5e1',
                                        background: '#f8fafc',
                                        color: '#2563eb',
                                        cursor: 'pointer',
                                        padding: 0,
                                      }}
                                    >
                                      <MessageSquare size={12} />
                                    </button>

                                    {/* More Options Dropdown Button */}
                                    <div style={{ position: 'relative' }}>
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setActionMenuRowId(actionMenuRowId === row.id ? null : row.id);
                                          setOnboardMenuRowId(null);
                                        }}
                                        title="More Actions"
                                        className="reg-action-btn-more"
                                        style={{
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          width: 26,
                                          height: 26,
                                          borderRadius: 6,
                                          border: '1px solid #cbd5e1',
                                          background: actionMenuRowId === row.id ? '#e2e8f0' : '#f8fafc',
                                          color: '#475569',
                                          cursor: 'pointer',
                                          padding: 0,
                                        }}
                                      >
                                        <MoreVertical size={13} />
                                      </button>

                                      {actionMenuRowId === row.id && (
                                        <div
                                          onClick={(e) => e.stopPropagation()}
                                          className={`reg-action-dropdown ${isNearBottom ? 'open-upward' : ''}`}
                                          style={{
                                            position: 'absolute',
                                            right: 0,
                                            top: isNearBottom ? 'auto' : 'calc(100% + 4px)',
                                            bottom: isNearBottom ? 'calc(100% + 4px)' : 'auto',
                                            zIndex: 150,
                                            background: '#fff',
                                            border: '1px solid #cbd5e1',
                                            borderRadius: 8,
                                            boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.18), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
                                            minWidth: 185,
                                            padding: '4px',
                                            textAlign: 'left',
                                          }}
                                        >
                                          {!alreadyOnboarded && (
                                            <button
                                              type="button"
                                              onClick={() => {
                                                setActionMenuRowId(null);
                                                handleMarkRegistrationRead(row);
                                              }}
                                              className="reg-menu-item"
                                            >
                                              <Check size={14} style={{ marginRight: 8, color: '#16a34a' }} />
                                              Mark as read
                                            </button>
                                          )}

                                          <button
                                            type="button"
                                            onClick={() => {
                                              setActionMenuRowId(null);
                                              handleEditRegistration(row);
                                            }}
                                            className="reg-menu-item"
                                          >
                                            <Pencil size={14} style={{ marginRight: 8, color: '#0284c7' }} />
                                            Edit details
                                          </button>

                                          <button
                                            type="button"
                                            onClick={() => {
                                              setActionMenuRowId(null);
                                              handleViewMessages(row.phone_number);
                                            }}
                                            className="reg-menu-item"
                                          >
                                            <MessageSquare size={14} style={{ marginRight: 8, color: '#2563eb' }} />
                                            View messages
                                          </button>

                                          {row.source === 'ops_manual' && (
                                            <button
                                              type="button"
                                              onClick={() => {
                                                setActionMenuRowId(null);
                                                handleSendTemplate(row.id);
                                              }}
                                              disabled={sendingTemplate.has(row.id)}
                                              className="reg-menu-item"
                                            >
                                              <Send size={14} style={{ marginRight: 8, color: '#7c3aed' }} />
                                              {sendingTemplate.has(row.id)
                                                ? 'Sending...'
                                                : templateSentVehicles.has(row.id)
                                                  ? 'Send again'
                                                  : 'Send template'}
                                            </button>
                                          )}

                                          <div style={{ height: 1, background: '#f1f5f9', margin: '4px 0' }} />

                                          <button
                                            type="button"
                                            onClick={() => {
                                              setActionMenuRowId(null);
                                              handleRemoveSession(row.phone_number);
                                            }}
                                            title="Delete row from onboarding_sessions"
                                            className="reg-menu-item"
                                            style={{ color: '#d97706' }}
                                          >
                                            <AlertCircle size={14} style={{ marginRight: 8, color: '#d97706' }} />
                                            Remove session
                                          </button>

                                          <button
                                            type="button"
                                            onClick={() => {
                                              setActionMenuRowId(null);
                                              handleRemoveRegisteredRegistration(row.id);
                                            }}
                                            className="reg-menu-item"
                                            style={{ color: '#dc2626' }}
                                          >
                                            <Trash2 size={14} style={{ marginRight: 8, color: '#dc2626' }} />
                                            Delete registration
                                          </button>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </td>
                              </tr>

                              {/* Expanded Accordion Row for Registered */}
                              {isExpanded && (
                                <tr className="reg-expanded-row">
                                  <td colSpan={7}>
                                    <div className="reg-expanded-panel">
                                      {/* Remarks / Comments Card */}
                                      {row.comments && (
                                        <div className="reg-expanded-remarks">
                                          <div style={{ fontSize: 11, fontWeight: 700, color: '#1e40af', textTransform: 'uppercase', marginBottom: 4 }}>
                                            Comments / Remarks
                                          </div>
                                          <div style={{ fontSize: 13, color: '#1e293b', whiteSpace: 'pre-wrap' }}>
                                            {row.comments}
                                          </div>
                                        </div>
                                      )}

                                      {/* Grid of Removed Columns */}
                                      <div className="reg-facts-grid">
                                        <div className="reg-fact-item">
                                          <span className="reg-fact-label">Vehicle Class</span>
                                          <span className="reg-fact-value">{row.vehicle_class != null ? `Class ${row.vehicle_class}` : '—'}</span>
                                        </div>
                                        <div className="reg-fact-item">
                                          <span className="reg-fact-label">Body Type</span>
                                          <span className="reg-fact-value">{row.body_type || '—'}</span>
                                        </div>
                                        <div className="reg-fact-item">
                                          <span className="reg-fact-label">Source</span>
                                          <span className="reg-fact-value">{row.source || '—'}</span>
                                        </div>
                                        <div className="reg-fact-item" style={{ gridColumn: 'span 2' }}>
                                          <span className="reg-fact-label">Base Location / Address</span>
                                          <span className="reg-fact-value" style={{ display: 'flex', alignItems: 'flex-start', gap: 4 }}>
                                            <MapPin size={14} style={{ color: '#ef4444', flexShrink: 0, marginTop: 2 }} />
                                            {row.address || '—'}
                                          </span>
                                        </div>
                                        <div className="reg-fact-item">
                                          <span className="reg-fact-label">Last Updated</span>
                                          <span className="reg-fact-value">
                                            {row.updated_at ? new Date(row.updated_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
                                          </span>
                                        </div>
                                        {row.created_at && (
                                          <div className="reg-fact-item">
                                            <span className="reg-fact-label">Registered Date</span>
                                            <span className="reg-fact-value">
                                              {new Date(row.created_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                                            </span>
                                          </div>
                                        )}
                                        {row.registration_specs?.tonnage && (
                                          <div className="reg-fact-item">
                                            <span className="reg-fact-label">Tonnage</span>
                                            <span className="reg-fact-value">{row.registration_specs.tonnage} Ton</span>
                                          </div>
                                        )}
                                        {row.intra_state_permit && (
                                          <div className="reg-fact-item">
                                            <span className="reg-fact-label">All India permit</span>
                                            <span className="reg-fact-value">Yes</span>
                                          </div>
                                        )}
                                        {(row.bed_length || row.registration_specs?.bed_length) && (
                                          <div className="reg-fact-item">
                                            <span className="reg-fact-label">Bed length</span>
                                            <span className="reg-fact-value">{row.bed_length || row.registration_specs.bed_length}</span>
                                          </div>
                                        )}
                                        {row.registration_specs?.per_km_price && (
                                          <div className="reg-fact-item">
                                            <span className="reg-fact-label">Price / KM</span>
                                            <span className="reg-fact-value">₹{row.registration_specs.per_km_price}/km</span>
                                          </div>
                                        )}
                                      </div>

                                      {/* Visible Action Buttons Toolbar */}
                                      <div className="reg-expanded-actions">
                                        <span style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', marginRight: 4 }}>
                                          Buttons & Actions:
                                        </span>

                                        <button
                                          type="button"
                                          onClick={() => handleEditRegistration(row)}
                                          className="reg-btn"
                                          style={{ background: '#f0f9ff', color: '#0284c7', border: '1px solid #bae6fd' }}
                                        >
                                          <Pencil size={13} /> Edit Details
                                        </button>

                                        <button
                                          type="button"
                                          onClick={() => handleViewMessages(row.phone_number)}
                                          className="reg-btn"
                                          style={{ background: '#eff6ff', color: '#2563eb', border: '1px solid #bfdbfe' }}
                                        >
                                          <MessageSquare size={13} /> View WhatsApp Messages
                                        </button>

                                        {row.source === 'ops_manual' && (
                                          <button
                                            type="button"
                                            onClick={() => handleSendTemplate(row.id)}
                                            disabled={sendingTemplate.has(row.id)}
                                            className="reg-btn"
                                            style={{ background: '#faf5ff', color: '#7c3aed', border: '1px solid #e9d5ff' }}
                                          >
                                            <Send size={13} />
                                            {sendingTemplate.has(row.id)
                                              ? 'Sending...'
                                              : templateSentVehicles.has(row.id)
                                                ? 'Resend WhatsApp Template'
                                                : 'Send WhatsApp Template'}
                                          </button>
                                        )}

                                        {!alreadyOnboarded ? (
                                          <>
                                            <button
                                              type="button"
                                              onClick={() => handleOnboardFromRegistration(row)}
                                              disabled={blocked}
                                              className="reg-btn"
                                              style={{ background: '#16a34a', color: '#fff', border: 'none' }}
                                            >
                                              <Car size={13} /> Onboard Normal Vehicle
                                            </button>

                                            <button
                                              type="button"
                                              onClick={() => handleOpenTransporterOnboard(row)}
                                              disabled={blocked}
                                              className="reg-btn"
                                              style={{ background: '#0f766e', color: '#fff', border: 'none' }}
                                            >
                                              <CheckCircle size={13} /> Onboard Under Transporter
                                            </button>
                                          </>
                                        ) : (
                                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '5px 10px', background: '#dcfce7', color: '#166534', borderRadius: 6, fontSize: 12, fontWeight: 600 }}>
                                            <CheckCircle size={13} /> Already onboarded as V-{onboardedVehicleId}
                                          </span>
                                        )}

                                        {!alreadyOnboarded && (
                                          <button
                                            type="button"
                                            onClick={() => handleMarkRegistrationRead(row)}
                                            className="reg-btn"
                                            style={{ background: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0' }}
                                          >
                                            <Check size={13} /> Mark as Read
                                          </button>
                                        )}

                                        <button
                                          type="button"
                                          onClick={() => handleRemoveSession(row.phone_number)}
                                          className="reg-btn"
                                          style={{ background: '#fffbeb', color: '#d97706', border: '1px solid #fde68a' }}
                                        >
                                          <AlertCircle size={13} /> Remove Session
                                        </button>

                                        <button
                                          type="button"
                                          onClick={() => handleRemoveRegisteredRegistration(row.id)}
                                          className="reg-btn"
                                          style={{ background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca' }}
                                        >
                                          <Trash2 size={13} /> Delete Registration
                                        </button>
                                      </div>
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })}
                        {registeredRegistrationRows.length === 0 && (
                          <tr><td colSpan="7" style={{ textAlign: 'center', color: '#6b7280', padding: '24px' }}>No registered drivers yet</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {registeredTotalPages > 1 && (
                    <div style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      gap: '8px',
                      marginTop: '16px',
                      padding: '8px 0'
                    }}>
                      <button
                        onClick={() => setRegisteredCurrentPage(prev => Math.max(1, prev - 1))}
                        disabled={registeredCurrentPage === 1}
                        style={{
                          padding: '8px 16px',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          backgroundColor: registeredCurrentPage === 1 ? '#f3f4f6' : 'white',
                          cursor: registeredCurrentPage === 1 ? 'not-allowed' : 'pointer',
                          fontSize: '14px',
                          fontWeight: '600'
                        }}
                      >
                        Previous
                      </button>
                      <span style={{ fontSize: '14px', color: '#374151' }}>
                        Registered Page {registeredCurrentPage} of {registeredTotalPages}
                      </span>
                      <button
                        onClick={() => setRegisteredCurrentPage(prev => Math.min(registeredTotalPages, prev + 1))}
                        disabled={registeredCurrentPage === registeredTotalPages}
                        style={{
                          padding: '8px 16px',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          backgroundColor: registeredCurrentPage === registeredTotalPages ? '#f3f4f6' : 'white',
                          cursor: registeredCurrentPage === registeredTotalPages ? 'not-allowed' : 'pointer',
                          fontSize: '14px',
                          fontWeight: '600'
                        }}
                      >
                        Next
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      )}

      {activeTab === 'call-logs' && (
        <div style={{ padding: '24px', background: '#fff', borderRadius: '0 0 8px 8px', border: '1px solid #e5e7eb', borderTop: 'none' }}>
          <DriverCallLogs embedded />
        </div>
      )}

      {activeTab === 'transporters' && (
        <div style={{ padding: '24px', background: '#fff', borderRadius: '0 0 8px 8px', border: '1px solid #e5e7eb', borderTop: 'none' }}>
          <TransportersTab openOnboard={openTransporterOnboard} />
        </div>
      )}

      {activeTab === 'voice-bot-logs' && (
        <div style={{ padding: '24px', background: '#fff', borderRadius: '0 0 8px 8px', border: '1px solid #e5e7eb', borderTop: 'none' }}>
          <VoiceBotLiveLogs />
        </div>
      )}

      {/* ── Vehicles Tab ── */}
      <div className="vehicles-table-container" style={{ display: activeTab === 'vehicles' ? '' : 'none' }}>
        {renderVehiclesPagination('top')}
        <table className="vehicles-table">
          <thead>
            <tr>
              <th>Vehicle ID</th>
              <th>Driver Name</th>
              <th>Driver Phone</th>
              <th>Type</th>
              <th>Base Location / Routes</th>
              <th>Vehicle Specs</th>
              <th>Remarks</th>
              <th>Specs & Pricing</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {pagedVehicles.map(vehicle => {
              const incoming = isRecentInbound(vehicle.last_driver_message_at);
              return (
                <React.Fragment key={vehicle.vehicle_id}>
                  <tr
                    className={`vehicle-row ${vehicle.current_status.toLowerCase()} ${vehicle.bot_paused ? 'bot-paused' : ''} ${!vehicle.is_active ? 'inactive-vehicle' : ''}`}
                    style={incoming
                      ? { backgroundColor: '#fff7ed', borderLeft: '3px solid #f59e0b' }
                      : (!vehicle.is_active ? { backgroundColor: 'rgba(239, 68, 68, 0.07)', borderLeft: '3px solid #ef4444' } : {})}
                  >
                    <td className="vehicle-id-cell">
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', justifyContent: 'center' }}>
                        {incoming && (
                          <div style={{ marginBottom: '2px' }}>
                            <span
                              title={`Recently texted at ${vehicle.last_driver_message_at ? new Date(vehicle.last_driver_message_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}`}
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '3px',
                                padding: '1px 6px',
                                backgroundColor: '#fef3c7',
                                color: '#92400e',
                                border: '1px solid #fcd34d',
                                borderRadius: '4px',
                                fontSize: '0.62rem',
                                fontWeight: '700',
                                textTransform: 'uppercase',
                                letterSpacing: '0.3px',
                                whiteSpace: 'nowrap',
                                boxShadow: '0 1px 2px rgba(0,0,0,0.04)'
                              }}
                            >
                              Recently Texted
                            </span>
                          </div>
                        )}
                        <div className="vehicle-id-with-icon">
                          {vehicle.vehicle_photo_url ? (
                            <img
                              src={vehicle.vehicle_photo_url}
                              alt="Vehicle"
                              onClick={(e) => {
                                e.stopPropagation();
                                setLightboxImage(vehicle.vehicle_photo_url);
                                setShowLightbox(true);
                              }}
                              style={{
                                width: '28px',
                                height: '28px',
                                borderRadius: '50%',
                                objectFit: 'cover',
                                cursor: 'pointer',
                                border: '1.5px solid #10b981',
                                flexShrink: 0,
                              }}
                              title="Click to view full image"
                            />
                          ) : (
                            <Car size={18} style={{ color: getVehicleColor(vehicle.vehicle_type, vehicle.is_transporter), flexShrink: 0 }} />
                          )}
                          <span style={{ whiteSpace: 'nowrap', fontWeight: '600' }}>V-{vehicle.vehicle_id}</span>
                          {(vehicle.is_transporter || vehicle.transporter_id) && (
                            <span
                              onClick={(e) => {
                                if (vehicle.transporter_id) {
                                  e.stopPropagation();
                                  openTransporterModal(vehicle.transporter_id, vehicle.driver_phone);
                                }
                              }}
                              style={{
                                padding: '1px 6px',
                                backgroundColor: '#dbeafe',
                                color: '#1e40af',
                                borderRadius: '6px',
                                fontSize: '0.72rem',
                                fontWeight: '700',
                                whiteSpace: 'nowrap',
                                display: 'inline-flex',
                                alignItems: 'center',
                                flexShrink: 0,
                                cursor: vehicle.transporter_id ? 'pointer' : 'help'
                              }}
                              title={
                                vehicle.transporter_name
                                  ? `Transporter: ${vehicle.transporter_name} (T-${vehicle.transporter_id}) - Click to view details`
                                  : vehicle.transporter_id
                                    ? `Transporter T-${vehicle.transporter_id} - Click to view details`
                                    : 'Transporter vehicle'
                              }
                            >
                              T-{vehicle.transporter_id || 'P'}
                            </span>
                          )}
                          {vehicle.is_new && (
                            <span style={{
                              marginLeft: '4px',
                              padding: '2px 8px',
                              backgroundColor: '#fbbf24',
                              color: '#78350f',
                              borderRadius: '12px',
                              fontSize: '0.7rem',
                              fontWeight: '700',
                              textTransform: 'uppercase',
                              whiteSpace: 'nowrap'
                            }}>NEW</span>
                          )}
                        </div>
                        {vehicle.transporter_name && (
                          <div style={{ marginLeft: vehicle.vehicle_photo_url ? '34px' : '24px', marginTop: '2px' }}>
                            <span
                              onClick={(e) => {
                                e.stopPropagation();
                                openTransporterModal(vehicle.transporter_id, vehicle.driver_phone);
                              }}
                              title={`Click to view transporter details: ${vehicle.transporter_name} (T-${vehicle.transporter_id})`}
                              style={{
                                fontSize: '10.5px',
                                fontWeight: '600',
                                color: '#1d4ed8',
                                backgroundColor: '#eff6ff',
                                border: '1px solid #bfdbfe',
                                borderRadius: '4px',
                                padding: '1px 6px',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                maxWidth: '180px',
                                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                                lineHeight: '1.3',
                                boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                                cursor: 'pointer',
                                transition: 'all 0.15s ease'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#dbeafe';
                                e.currentTarget.style.borderColor = '#93c5fd';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = '#eff6ff';
                                e.currentTarget.style.borderColor = '#bfdbfe';
                              }}
                            >
                              <span style={{ fontSize: '10px' }}>🏢</span>
                              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {vehicle.transporter_name}
                              </span>
                            </span>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="driver-cell">
                      <span className="driver-name">{vehicle.driver_name}</span>
                    </td>
                    <td className="driver-cell">
                      {vehicle.driver_phone ? (
                        (() => {
                          const clean = String(vehicle.driver_phone).replace(/\D/g, '');
                          const isCalling = callingDrivers[clean];
                          return (
                            <button
                              onClick={() => handlePlaceCall(vehicle.driver_phone)}
                              className="driver-phone-btn"
                              disabled={isCalling}
                              style={{
                                background: 'none',
                                border: 'none',
                                color: isCalling ? '#f59e0b' : '#2563eb',
                                cursor: isCalling ? 'wait' : 'pointer',
                                textDecoration: 'underline',
                                whiteSpace: 'nowrap',
                                padding: 0,
                                fontFamily: 'inherit',
                                fontSize: 'inherit',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px'
                              }}
                            >
                              <span>{isCalling ? '📞 Calling...' : `📞 ${vehicle.driver_phone}`}</span>
                            </button>
                          );
                        })()
                      ) : (
                        <span style={{ color: '#9ca3af' }}>—</span>
                      )}
                    </td>
                    <td>
                      <span
                        className="vehicle-type-badge"
                        style={{ color: getVehicleColor(vehicle.vehicle_type, vehicle.is_transporter) }}
                      >
                        {vehicle.vehicle_type}
                      </span>
                    </td>
                    <td className="location-cell" style={{ maxWidth: '220px', whiteSpace: 'normal', wordBreak: 'break-word' }}>
                      {vehicle.is_transporter ? (() => {
                        const routes = Array.isArray(vehicle.transport_routes) ? vehicle.transport_routes : [];
                        const isExpanded = expandedRouteVehicles.has(vehicle.vehicle_id);
                        const formatRoute = (r, short = false) => {
                          const fromRaw = (r?.from?.address || '').trim();
                          const toRaw = (r?.to?.address || '').trim();
                          const fromFull = !fromRaw || /^unknown$/i.test(fromRaw) ? 'others' : fromRaw;
                          const toFull = !toRaw || /^unknown$/i.test(toRaw) ? 'others' : toRaw;
                          const fromLabel = short ? (fromFull.split(',')[0].trim() || fromFull) : fromFull;
                          const toLabel = short ? (toFull.split(',')[0].trim() || toFull) : toFull;
                          const priceMin = r?.price_min;
                          const priceMax = r?.price_max;
                          const priceLabel =
                            priceMin != null && priceMax != null && Number(priceMax) !== Number(priceMin)
                              ? `₹${priceMin}–₹${priceMax}`
                              : priceMin != null
                                ? `₹${priceMin}`
                                : '';
                          const full = `${fromFull} → ${toFull}${priceLabel ? ` (${priceLabel})` : ''}`;
                          return { fromLabel, toLabel, priceLabel, full };
                        };
                        if (routes.length === 0) {
                          return (
                            <div className="base-location">
                              <Route size={16} />
                              <span>No routes</span>
                            </div>
                          );
                        }
                        const shown = isExpanded ? routes : routes.slice(0, 1);
                        return (
                          <div className="base-location" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '4px', position: 'relative' }}>
                            {shown.map((r, idx) => {
                              const f = formatRoute(r, !isExpanded);
                              return (
                                <div key={`route-${vehicle.vehicle_id}-${idx}`} style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', width: '100%' }}>
                                  <Route size={14} style={{ flexShrink: 0, marginTop: 2 }} />
                                  <span title={f.full} style={{ fontSize: '12px', lineHeight: 1.3, flex: 1, whiteSpace: isExpanded ? 'normal' : 'nowrap', overflow: isExpanded ? 'visible' : 'hidden', textOverflow: 'ellipsis' }}>
                                    {f.fromLabel} → {f.toLabel}
                                    {f.priceLabel ? (
                                      <span style={{ color: '#6b7280', fontSize: '11px', marginLeft: '6px', fontWeight: '500' }}>({f.priceLabel})</span>
                                    ) : null}
                                  </span>
                                  {idx === 0 && (
                                    <button
                                      type="button"
                                      title={isExpanded ? 'Show short address' : 'Show full address'}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setExpandedRouteVehicles((prev) => {
                                          const next = new Set(prev);
                                          if (next.has(vehicle.vehicle_id)) next.delete(vehicle.vehicle_id);
                                          else next.add(vehicle.vehicle_id);
                                          return next;
                                        });
                                      }}
                                      style={{
                                        background: 'none',
                                        border: '1px solid #d1d5db',
                                        borderRadius: 4,
                                        padding: '1px 3px',
                                        cursor: 'pointer',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        color: '#374151',
                                        flexShrink: 0,
                                      }}
                                    >
                                      {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                                      {routes.length > 1 && !isExpanded && (
                                        <span style={{ fontSize: '10px', marginLeft: 2 }}>{routes.length}</span>
                                      )}
                                    </button>
                                  )}
                                </div>
                              );
                            })}
                            {isExpanded && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setExpandedRouteVehicles((prev) => {
                                    const next = new Set(prev);
                                    next.delete(vehicle.vehicle_id);
                                    return next;
                                  });
                                }}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  color: '#6366f1',
                                  fontSize: '11px',
                                  cursor: 'pointer',
                                  padding: 0,
                                  marginLeft: 20,
                                }}
                              >
                                Collapse
                              </button>
                            )}
                          </div>
                        );
                      })() : (
                        <div className="base-location">
                          <Home size={16} />
                          <span title={getVehicleBaseLocationDisplay(vehicle) || ''}>
                            {getVehicleBaseLocationDisplay(vehicle) || 'No data'}
                          </span>
                        </div>
                      )}
                    </td>
                    <td className="location-cell" style={{ maxWidth: '210px', whiteSpace: 'normal', wordBreak: 'break-word', padding: '10px 14px' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '12px' }}>
                        {(vehicle.make || vehicle.model) ? (
                          <div style={{ fontWeight: '700', color: '#1e293b', fontSize: '13px', lineHeight: '1.2' }}>
                            {[vehicle.make, vehicle.model].filter(Boolean).join(' ')}
                          </div>
                        ) : (
                          <div style={{ color: '#9ca3af', fontStyle: 'italic', fontSize: '12px' }}>—</div>
                        )}
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '2px' }}>
                          {vehicle.length != null && vehicle.length !== '' && (
                            <span
                              title={`Length: ${vehicle.length} ft`}
                              style={{ backgroundColor: '#f1f5f9', color: '#334155', border: '1px solid #e2e8f0', padding: '1px 6px', borderRadius: '4px', fontSize: '11px', fontWeight: '600' }}
                            >
                              📏 {vehicle.length} ft
                            </span>
                          )}
                          {vehicle.tonnage != null && vehicle.tonnage !== '' && (
                            <span
                              title={`Tonnage: ${vehicle.tonnage} Ton`}
                              style={{ backgroundColor: '#f1f5f9', color: '#334155', border: '1px solid #e2e8f0', padding: '1px 6px', borderRadius: '4px', fontSize: '11px', fontWeight: '600' }}
                            >
                              ⚖️ {vehicle.tonnage} T
                            </span>
                          )}
                          {vehicle.body_type ? (
                            <span
                              title={`Body Type: ${vehicle.body_type}`}
                              style={{ backgroundColor: '#e0e7ff', color: '#3730a3', border: '1px solid #c7d2fe', padding: '1px 6px', borderRadius: '4px', fontSize: '11px', fontWeight: '600', textTransform: 'capitalize' }}
                            >
                              🚚 {vehicle.body_type}
                            </span>
                          ) : null}
                        </div>
                      </div>
                    </td>
                    <td className="location-cell" style={{ maxWidth: '190px', whiteSpace: 'normal', wordBreak: 'break-word', padding: '10px 14px' }}>
                      {vehicle.remarks ? (
                        <div
                          title={vehicle.remarks}
                          style={{
                            fontSize: '12px',
                            color: '#374151',
                            lineHeight: '1.4',
                            wordBreak: 'break-word',
                            backgroundColor: '#f8fafc',
                            border: '1px solid #e2e8f0',
                            borderRadius: '6px',
                            padding: '6px 8px',
                            maxHeight: '75px',
                            overflowY: 'auto'
                          }}
                        >
                          {vehicle.remarks}
                        </div>
                      ) : (
                        <span style={{ color: '#9ca3af', fontSize: '12px' }}>—</span>
                      )}
                    </td>
                    <td className="location-cell" style={{ maxWidth: '220px', whiteSpace: 'normal', wordBreak: 'break-word', padding: '10px' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '12px' }}>
                        {vehicle.per_km_price != null ? (
                          <div><strong>Price:</strong> ₹{vehicle.per_km_price}/km</div>
                        ) : (
                          <div style={{ color: '#9ca3af' }}>No Price Set</div>
                        )}
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '2px' }}>
                          {vehicle.ready_for_long_trip && (
                            <span style={{ backgroundColor: '#e0f2fe', color: '#0369a1', padding: '1px 6px', borderRadius: '4px', fontSize: '10px', fontWeight: '700' }}>LONG</span>
                          )}
                          {vehicle.long_inter_district && (
                            <span style={{ backgroundColor: '#fef3c7', color: '#b45309', padding: '1px 6px', borderRadius: '4px', fontSize: '10px', fontWeight: '700' }}>DISTRICT</span>
                          )}
                          {vehicle.long_inter_state && (
                            <span style={{ backgroundColor: '#dcfce7', color: '#15803d', padding: '1px 6px', borderRadius: '4px', fontSize: '10px', fontWeight: '700' }}>STATE</span>
                          )}
                          {vehicle.local && (
                            <span style={{ backgroundColor: '#ede9fe', color: '#6d28d9', padding: '1px 6px', borderRadius: '4px', fontSize: '10px', fontWeight: '700' }}>LOCAL</span>
                          )}
                          {vehicle.intra_state_permit && (
                            <span style={{ backgroundColor: '#fee2e2', color: '#b91c1c', padding: '1px 6px', borderRadius: '4px', fontSize: '10px', fontWeight: '700' }}>ALL INDIA</span>
                          )}
                        </div>
                        {vehicle.phone_extensions && vehicle.phone_extensions.length > 0 && (
                          <div style={{ marginTop: '4px', fontSize: '11px', color: '#4b5563', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                            <span style={{ fontWeight: '600' }}>Alt Phones:</span>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                              {vehicle.phone_extensions.map((entry, idx) => {
                                const phone = extPhoneOf(entry);
                                const label = extLabelOf(entry);
                                const clean = String(phone).replace(/\D/g, '');
                                const isCalling = callingDrivers[clean];
                                return (
                                  <button
                                    key={idx}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handlePlaceCall(phone);
                                    }}
                                    style={{
                                      background: 'none',
                                      border: 'none',
                                      color: isCalling ? '#f59e0b' : '#2563eb',
                                      cursor: isCalling ? 'wait' : 'pointer',
                                      textDecoration: 'underline',
                                      padding: 0,
                                      fontSize: '11px',
                                      textAlign: 'left',
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      gap: '2px',
                                      fontFamily: 'inherit'
                                    }}
                                    disabled={isCalling}
                                  >
                                    <span>{isCalling ? '📞 Calling...' : `📞 ${phone}${label ? ` — ${label}` : ''}`}</span>
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        )}
                        <button
                          onClick={() => openSpecsModal(vehicle)}
                          style={{
                            marginTop: '4px',
                            padding: '4px 8px',
                            backgroundColor: '#6366f1',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '11px',
                            fontWeight: '600',
                            alignSelf: 'flex-start',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          Edit Specs
                        </button>
                      </div>
                    </td>
                    <td className="actions-cell">
                      <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', flexWrap: 'wrap' }}>
                        <button
                          className="btn-view-details"
                          onClick={() => openVehicleModal(vehicle)}
                          style={{ fontSize: '13px', padding: '6px 12px' }}
                        >
                          View Details
                        </button>
                        <button
                          className={`btn-view-details ${incoming ? 'chat-incoming' : (vehicle.bot_paused ? 'chat-paused' : 'chat-active')}`}
                          onClick={() => openVehicleChatModal(vehicle)}
                          style={{ fontSize: '13px', padding: '6px 12px' }}
                        >
                          {incoming ? '💬 Active' : '💬 Start'}
                        </button>

                      </div>
                    </td>
                  </tr>
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
        {renderVehiclesPagination('bottom')}
      </div>

      {filteredVehicles.length === 0 && (
        <div className="no-results">
          <Car size={48} />
          <h3>No vehicles found</h3>
          <p>Try adjusting your search or filter criteria</p>
        </div>
      )}

      {/* Vehicle Details Modal */}
      {selectedVehicle && (
        <div
          className="modal-overlay"
          onMouseDown={(e) => {
            mainModalMouseDownTarget.current = e.target;
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget && mainModalMouseDownTarget.current === e.currentTarget) {
              closeVehicleModal();
            }
          }}
        >
          <div
            className="modal-content modal-content-wide"
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <div className="modal-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {(driverDocs?.vehicle?.url || selectedVehicle?.vehicle_photo_url) ? (
                  <img
                    src={driverDocs?.vehicle?.url || selectedVehicle.vehicle_photo_url}
                    alt="Vehicle"
                    onClick={() => {
                      setLightboxImage(driverDocs?.vehicle?.url || selectedVehicle.vehicle_photo_url);
                      setShowLightbox(true);
                    }}
                    style={{
                      width: '36px',
                      height: '36px',
                      borderRadius: '50%',
                      objectFit: 'cover',
                      border: '2px solid #10b981',
                      cursor: 'pointer',
                      flexShrink: 0,
                      boxShadow: '0 2px 8px rgba(16,185,129,0.3)',
                      transition: 'transform 0.2s, box-shadow 0.2s',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'scale(1.1)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(16,185,129,0.5)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'scale(1)';
                      e.currentTarget.style.boxShadow = '0 2px 8px rgba(16,185,129,0.3)';
                    }}
                    title="Click to view full vehicle image"
                  />
                ) : (
                  <Car size={24} style={{ color: getVehicleColor(selectedVehicle.vehicle_type, selectedVehicle.is_transporter) }} />
                )}
                <h2>Vehicle #{selectedVehicle.vehicle_id}</h2>
                <button
                  className={`btn-edit-toggle ${isEditing ? 'editing' : ''}`}
                  onClick={handleEditToggle}
                  style={{ marginLeft: '12px', flexShrink: 0 }}
                >
                  {isEditing ? '✕ Cancel' : '✏️ Edit'}
                </button>
              </div>
              <div className="modal-header-actions">
                <button
                  className="btn-assign-booking-header"
                  onClick={() => openAssignBookingModal(selectedVehicle)}
                >
                  Assign Booking
                </button>
                <button
                  className="btn-bonus-pay-header"
                  onClick={() => openBonusPayModal(selectedVehicle)}
                  title="Add bonus payment for driver"
                >
                  Bonus Pay
                </button>
                <button
                  className="btn-part-booking-header"
                  onClick={async () => {
                    const newValue = !selectedVehicle.is_part;
                    if (window.confirm(`Are you sure you want to ${newValue ? 'ACTIVATE' : 'DEACTIVATE'} Part Booking mode for this vehicle?`)) {
                      try {
                        const token = localStorage.getItem('token');
                        const url = `${config.opsApiBase}/vehicles/${selectedVehicle.vehicle_id}/part-booking?is_part=${newValue}`;
                        const response = await fetch(url, {
                          method: 'PUT',
                          headers: { 'Authorization': `Bearer ${token}` }
                        });
                        if (response.ok) {
                          setSelectedVehicle(prev => ({ ...prev, is_part: newValue }));
                        } else {
                          const data = await response.json();
                          alert(`Error: ${data.detail || data.message || 'Failed to update part booking state'}`);
                        }
                      } catch (error) {
                        console.error('Error toggling part booking:', error);
                        alert('Network error while updating part booking state');
                      }
                    }
                  }}
                  title={selectedVehicle.is_part ? "Vehicle is in Part Booking mode" : "Enable Part Booking mode"}
                  style={{
                    backgroundColor: selectedVehicle.is_part ? '#6f42c1' : '#f8f9fa',
                    color: selectedVehicle.is_part ? 'white' : '#495057',
                    border: '1px solid #ced4da',
                    borderRadius: '6px',
                    padding: '8px 16px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    fontSize: '14px',
                  }}
                >
                  {selectedVehicle.is_part ? 'Part Booking ✓' : 'Make Part Booking'}
                </button>
                <button
                  className="btn-bonus-history-header"
                  onClick={() => openBonusHistoryModal(selectedVehicle)}
                >
                  Bonus History
                </button>
                <button className="modal-close-btn" onClick={closeVehicleModal}>
                  ×
                </button>
              </div>
            </div>

            <div className="modal-body-split">
              {/* Left Half - Vehicle Information & Edit Form */}
              <div className="modal-left-half">
                <div className="vehicle-info-header">
                  <h3>Vehicle Information</h3>
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <button
                      className={`btn-state-toggle ${editFormData.is_active ? 'active' : 'inactive'}`}
                      onClick={handleToggleActiveState}
                      title={editFormData.is_active ? 'Click to deactivate vehicle' : 'Click to activate vehicle'}
                    >
                      {editFormData.is_active ? '🟢 Active' : '🔴 Inactive'}
                    </button>
                    <button
                      type="button"
                      onClick={handleToggleBlockedState}
                      title={editFormData.is_blocked ? 'Ops blocked — click to unblock' : 'Ops-only: block vehicle (drivers cannot override via app)'}
                      style={{
                        backgroundColor: editFormData.is_blocked ? '#f59e0b' : '#fef3c7',
                        color: editFormData.is_blocked ? '#fff' : '#92400e',
                        border: '1px solid #fbbf24',
                        borderRadius: '6px',
                        padding: '8px 16px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        fontSize: '14px',
                      }}
                    >
                      {editFormData.is_blocked ? '🚫 Blocked (ops)' : '✓ Not blocked'}
                    </button>
                    <button
                      className={`btn-edit-toggle ${isEditing ? 'editing' : ''}`}
                      style={{ display: 'none' }}
                      onClick={handleEditToggle}
                    >
                      {isEditing ? 'Cancel' : 'Edit'}
                    </button>
                    <button
                      onClick={openDocsModal}
                      title="View and update driver documents"
                      style={{
                        backgroundColor: '#17a2b8',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '6px',
                        padding: '6px 14px',
                        fontSize: '13px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '5px'
                      }}
                    >
                      📁 Vehicle Docs
                    </button>
                    {selectedVehicle?.vehicle_id && (
                      <button
                        onClick={handleDeleteDriver}
                        title="Delete this vehicle (past trips and driver profile are preserved)"
                        style={{
                          backgroundColor: '#dc3545',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '6px',
                          padding: '6px 14px',
                          fontSize: '13px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '5px'
                        }}
                      >
                        🗑️ Delete Vehicle
                      </button>
                    )}
                  </div>
                </div>

                {/* Vehicle Owner Section */}
                <div style={{
                  borderRadius: '10px',
                  border: '1px solid #e2e8f0',
                  marginBottom: '16px',
                  padding: '16px',
                  backgroundColor: '#ffffff',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03)'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: ownerFormOpen ? '10px' : '0px' }}>
                    <h3 style={{
                      margin: 0,
                      fontSize: '15px',
                      fontWeight: '700',
                      color: '#1f2937',
                      borderBottom: 'none',
                      paddingBottom: 0
                    }}>
                      Vehicle Owner
                    </h3>
                    <button
                      type="button"
                      onClick={() => {
                        if (!ownerFormOpen) {
                          setOwnerForm({
                            name: registeredOwner?.name || '',
                            phone: registeredOwner?.phone || ''
                          });
                        }
                        setOwnerFormOpen(!ownerFormOpen);
                      }}
                      style={{
                        padding: '6px 14px',
                        fontSize: '13px',
                        fontWeight: '600',
                        backgroundColor: '#2563eb',
                        color: '#ffffff',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        transition: 'background-color 0.2s',
                      }}
                    >
                      {ownerFormOpen ? 'Cancel' : registeredOwner ? 'Change Owner' : 'Add Owner'}
                    </button>
                  </div>

                  {ownerFormOpen ? (
                    <div style={{ marginTop: '12px' }}>
                      <div style={{ fontSize: '13px', color: '#4b5563', marginBottom: '16px', lineHeight: '1.4' }}>
                        Enter owner details. A user account will be created or updated by phone number.
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                        <div>
                          <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
                            Owner Name *
                          </label>
                          <input
                            type="text"
                            placeholder="Full name"
                            value={ownerForm.name}
                            onChange={(e) => setOwnerForm(prev => ({ ...prev, name: e.target.value }))}
                            className="form-input"
                            style={{ width: '100%', boxSizing: 'border-box' }}
                          />
                        </div>
                        <div>
                          <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
                            Phone Number *
                          </label>
                          <input
                            type="text"
                            placeholder="10 digits, e.g. 8861271956 (91 added)"
                            value={ownerForm.phone}
                            onChange={(e) => setOwnerForm(prev => ({ ...prev, phone: e.target.value }))}
                            className="form-input"
                            style={{ width: '100%', boxSizing: 'border-box' }}
                          />
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={handleSaveOwner}
                        disabled={ownerSaving}
                        style={{
                          padding: '8px 18px',
                          backgroundColor: '#2563eb',
                          color: '#ffffff',
                          border: 'none',
                          borderRadius: '6px',
                          fontWeight: '600',
                          fontSize: '13px',
                          cursor: 'pointer',
                          opacity: ownerSaving ? 0.7 : 1,
                        }}
                      >
                        {ownerSaving ? 'Saving...' : 'Save Owner'}
                      </button>
                    </div>
                  ) : (
                    registeredOwner ? (
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginTop: '12px' }}>
                        <div>
                          <span style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6b7280', marginBottom: '4px' }}>Owner Name</span>
                          <input
                            type="text"
                            value={registeredOwner.name || '—'}
                            readOnly
                            className="form-input readonly"
                            style={{ width: '100%', boxSizing: 'border-box' }}
                          />
                        </div>
                        <div>
                          <span style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6b7280', marginBottom: '4px' }}>Phone Number</span>
                          <input
                            type="text"
                            value={registeredOwner.phone || '—'}
                            readOnly
                            className="form-input readonly"
                            style={{ width: '100%', boxSizing: 'border-box' }}
                          />
                        </div>
                      </div>
                    ) : (
                      <div style={{ marginTop: '12px', fontSize: '13px', color: '#6b7280', fontStyle: 'italic' }}>
                        No owner registered for this vehicle.
                      </div>
                    )
                  )}
                </div>

                {/* Base Location Card */}
                <div style={{
                  borderRadius: '10px',
                  border: '1px solid #e2e8f0',
                  marginBottom: '16px',
                  padding: '16px',
                  backgroundColor: '#ffffff',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03)'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                    <h3 style={{
                      margin: 0,
                      fontSize: '15px',
                      fontWeight: '700',
                      color: '#1f2937',
                      borderBottom: 'none',
                      paddingBottom: 0
                    }}>
                      {(editFormData.is_transporter || selectedVehicle?.is_transporter)
                        ? 'Base Location / Routes'
                        : 'Base Location'}
                    </h3>
                    {!isEditing && (
                      <button
                        type="button"
                        onClick={() => {
                          const lat = parseFloat(editFormData.base_lat);
                          const lng = parseFloat(editFormData.base_lng);
                          const addr = editFormData.base_location || editFormData.resolved_address || '';
                          if (lat && lng) {
                            setMapModalCoords({ lat, lng, label: addr || `${lat}, ${lng}` });
                            setShowMapModal(true);
                          } else if (addr.trim()) {
                            setMapModalCoords({ lat: null, lng: null, label: addr.trim() });
                            setShowMapModal(true);
                          } else {
                            alert('No location coordinates or address available.');
                          }
                        }}
                        style={{
                          padding: '6px 14px',
                          fontSize: '13px',
                          fontWeight: '600',
                          backgroundColor: '#10b981',
                          color: '#ffffff',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          transition: 'background-color 0.2s',
                        }}
                      >
                        🗺️ Check Map
                      </button>
                    )}
                  </div>

                  {isEditing ? (
                    <div style={{ marginTop: '12px' }}>
                      <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                        <div style={{ flex: 1 }}>
                          <PlacesAutocompleteInput
                            value={editFormData.base_location}
                            onChange={(text) => handleEditFormChange({ target: { name: 'base_location', value: text } })}
                            onPlaceSelect={(place) => {
                              setEditFormData(prev => ({
                                ...prev,
                                base_location: place.address,
                                base_lat: place.lat,
                                base_lng: place.lng,
                                resolved_address: place.address,
                              }));
                            }}
                            placeholder="Type to search address..."
                            inputStyle={{ padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', width: '100%', boxSizing: 'border-box' }}
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            const lat = parseFloat(editFormData.base_lat);
                            const lng = parseFloat(editFormData.base_lng);
                            const addr = editFormData.base_location || editFormData.resolved_address || '';
                            if (lat && lng) {
                              setMapModalCoords({ lat, lng, label: addr || `${lat}, ${lng}` });
                              setShowMapModal(true);
                            } else if (addr.trim()) {
                              setMapModalCoords({ lat: null, lng: null, label: addr.trim() });
                              setShowMapModal(true);
                            } else {
                              alert('Enter an address or select a location first.');
                            }
                          }}
                          style={{
                            padding: '10px 14px',
                            fontSize: '13px',
                            fontWeight: '600',
                            backgroundColor: '#10b981',
                            color: '#ffffff',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          🗺️ Check Map
                        </button>
                      </div>

                      {(editFormData.is_transporter || selectedVehicle?.is_transporter) && (
                        <div style={{ marginTop: 16 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 8 }}>
                            Transport Routes
                          </div>
                          {(editFormData.transport_routes || []).map((row, index) => (
                            <div
                              key={`edit-route-${index}`}
                              style={{
                                border: '1px solid #e5e7eb',
                                borderRadius: 8,
                                padding: 12,
                                marginBottom: 10,
                                background: '#f9fafb',
                              }}
                            >
                              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                                <strong style={{ fontSize: 13 }}>Route {index + 1}</strong>
                                {(editFormData.transport_routes || []).length > 1 && (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setEditFormData((prev) => ({
                                        ...prev,
                                        transport_routes: prev.transport_routes.filter((_, i) => i !== index),
                                      }));
                                    }}
                                    style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: 12 }}
                                  >
                                    Remove
                                  </button>
                                )}
                              </div>
                              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                                <div>
                                  <label style={{ fontSize: 12, color: '#6b7280' }}>From *</label>
                                  <PlacesAutocompleteInput
                                    value={row.fromInput || ''}
                                    onChange={(text) => {
                                      setEditFormData((prev) => {
                                        const next = [...(prev.transport_routes || [])];
                                        next[index] = { ...next[index], fromInput: text, from: null };
                                        return { ...prev, transport_routes: next };
                                      });
                                    }}
                                    onPlaceSelect={(place) => {
                                      setEditFormData((prev) => {
                                        const next = [...(prev.transport_routes || [])];
                                        next[index] = {
                                          ...next[index],
                                          fromInput: place.address,
                                          from: {
                                            address: place.address,
                                            latitude: place.lat,
                                            longitude: place.lng,
                                            place_id: place.place_id || null,
                                          },
                                        };
                                        return { ...prev, transport_routes: next };
                                      });
                                    }}
                                    placeholder="From city / area..."
                                    inputStyle={{ padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', width: '100%', boxSizing: 'border-box' }}
                                  />
                                </div>
                                <div>
                                  <label style={{ fontSize: 12, color: '#6b7280' }}>To *</label>
                                  <PlacesAutocompleteInput
                                    value={row.toInput || ''}
                                    onChange={(text) => {
                                      setEditFormData((prev) => {
                                        const next = [...(prev.transport_routes || [])];
                                        next[index] = { ...next[index], toInput: text, to: null };
                                        return { ...prev, transport_routes: next };
                                      });
                                    }}
                                    onPlaceSelect={(place) => {
                                      setEditFormData((prev) => {
                                        const next = [...(prev.transport_routes || [])];
                                        next[index] = {
                                          ...next[index],
                                          toInput: place.address,
                                          to: {
                                            address: place.address,
                                            latitude: place.lat,
                                            longitude: place.lng,
                                            place_id: place.place_id || null,
                                          },
                                        };
                                        return { ...prev, transport_routes: next };
                                      });
                                    }}
                                    placeholder="To city / area..."
                                    inputStyle={{ padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', width: '100%', boxSizing: 'border-box' }}
                                  />
                                </div>
                                <div>
                                  <label style={{ fontSize: 12, color: '#6b7280' }}>Price / Min *</label>
                                  <input
                                    type="number"
                                    min="0"
                                    value={row.priceMin}
                                    onChange={(e) => {
                                      setEditFormData((prev) => {
                                        const next = [...(prev.transport_routes || [])];
                                        next[index] = { ...next[index], priceMin: e.target.value };
                                        return { ...prev, transport_routes: next };
                                      });
                                    }}
                                    className="form-input"
                                    style={{ width: '100%', boxSizing: 'border-box' }}
                                    placeholder="3000"
                                  />
                                </div>
                                <div>
                                  <label style={{ fontSize: 12, color: '#6b7280' }}>Max Price (optional)</label>
                                  <input
                                    type="number"
                                    min="0"
                                    value={row.priceMax}
                                    onChange={(e) => {
                                      setEditFormData((prev) => {
                                        const next = [...(prev.transport_routes || [])];
                                        next[index] = { ...next[index], priceMax: e.target.value };
                                        return { ...prev, transport_routes: next };
                                      });
                                    }}
                                    className="form-input"
                                    style={{ width: '100%', boxSizing: 'border-box' }}
                                    placeholder="Leave blank for single price"
                                  />
                                </div>
                              </div>
                            </div>
                          ))}
                          <button
                            type="button"
                            onClick={() => {
                              setEditFormData((prev) => ({
                                ...prev,
                                transport_routes: [...(prev.transport_routes || []), emptyRouteRow()],
                              }));
                            }}
                            style={{
                              background: 'none',
                              border: '1px dashed #9ca3af',
                              borderRadius: 6,
                              padding: '8px 12px',
                              cursor: 'pointer',
                              fontSize: 13,
                              color: '#374151',
                            }}
                          >
                            + Add Route
                          </button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div style={{ marginTop: '12px' }}>
                      {!(editFormData.is_transporter || selectedVehicle?.is_transporter) ? (
                        <div>
                          <input
                            type="text"
                            value={baseLocationDisplay || 'No base location'}
                            readOnly
                            className="form-input readonly"
                            style={{ width: '100%', boxSizing: 'border-box' }}
                          />
                        </div>
                      ) : (
                        <div>
                          <input
                            type="text"
                            value={baseLocationDisplay || 'No base location'}
                            readOnly
                            className="form-input readonly"
                            style={{ width: '100%', boxSizing: 'border-box', marginBottom: 10 }}
                          />
                          {(() => {
                            const rows = editFormData.transport_routes || [];
                            const valid = rows.filter((r) => r.from && r.to);
                            if (valid.length === 0) {
                              return <div style={{ fontSize: 13, color: '#6b7280' }}>No routes. Click Edit to add routes and prices.</div>;
                            }
                            const shown = detailRoutesExpanded ? valid : valid.slice(0, 1);
                            return (
                              <div>
                                <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Routes</div>
                                {shown.map((r, idx) => {
                                  const fromRaw = (r.from?.address || '').trim();
                                  const toRaw = (r.to?.address || '').trim();
                                  const fromFull = !fromRaw || /^unknown$/i.test(fromRaw) ? 'others' : fromRaw;
                                  const toFull = !toRaw || /^unknown$/i.test(toRaw) ? 'others' : toRaw;
                                  const fromLabel = detailRoutesExpanded
                                    ? fromFull
                                    : (fromFull.split(',')[0].trim() || fromFull);
                                  const toLabel = detailRoutesExpanded
                                    ? toFull
                                    : (toFull.split(',')[0].trim() || toFull);
                                  const priceMin = r.priceMin;
                                  const priceMax = r.priceMax;
                                  const priceLabel =
                                    priceMin && priceMax && Number(priceMax) !== Number(priceMin)
                                      ? `₹${priceMin}–₹${priceMax}`
                                      : priceMin
                                        ? `₹${priceMin}`
                                        : '';
                                  return (
                                    <div key={`view-route-${idx}`} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 6 }}>
                                      <Route size={14} style={{ marginTop: 2, flexShrink: 0 }} />
                                      <div style={{ flex: 1, fontSize: 13 }} title={`${fromFull} → ${toFull}`}>
                                        <div>{fromLabel} → {toLabel}</div>
                                        {priceLabel && <div style={{ color: '#6b7280', fontSize: 12 }}>{priceLabel}</div>}
                                      </div>
                                      {idx === 0 && (
                                        <button
                                          type="button"
                                          onClick={() => setDetailRoutesExpanded((v) => !v)}
                                          style={{
                                            background: 'none',
                                            border: '1px solid #d1d5db',
                                            borderRadius: 4,
                                            padding: '2px 4px',
                                            cursor: 'pointer',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                          }}
                                          title={detailRoutesExpanded ? 'Show short address' : 'Show full address'}
                                        >
                                          {detailRoutesExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                                          {valid.length > 1 && !detailRoutesExpanded && (
                                            <span style={{ fontSize: 10, marginLeft: 2 }}>{valid.length}</span>
                                          )}
                                        </button>
                                      )}
                                    </div>
                                  );
                                })}
                                <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                                  Click Edit to add or change routes and prices.
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="vehicle-info-form vehicle-info-form-compact">
                  <section className="vehicle-section-card">
                    <div className="vehicle-section-header">
                      <div>
                        <h3>Driver Information</h3>
                      </div>
                    </div>

                    <div className="vehicle-section-grid vehicle-section-grid-tight">
                      <div className="form-group vehicle-field-full">
                        <label>
                          <span className="required">Registration Number *</span>
                        </label>
                        <input
                          type="text"
                          name="registration_no"
                          value={editFormData.registration_no}
                          onChange={handleEditFormChange}
                          disabled={!isEditing}
                          className={`form-input ${!isEditing ? 'readonly' : ''}`}
                          placeholder={isEditing ? 'Enter registration number' : ''}
                        />
                      </div>

                      <div className="form-group">
                        <label>Make</label>
                        <input
                          type="text"
                          name="make"
                          value={editFormData.make}
                          onChange={handleEditFormChange}
                          disabled={!isEditing}
                          className={`form-input ${!isEditing ? 'readonly' : ''}`}
                          placeholder={isEditing ? 'Auto-filled or enter manually' : ''}
                        />
                      </div>

                      <div className="form-group">
                        <label>Model</label>
                        <input
                          type="text"
                          name="model"
                          value={editFormData.model}
                          onChange={handleEditFormChange}
                          disabled={!isEditing}
                          className={`form-input ${!isEditing ? 'readonly' : ''}`}
                          placeholder={isEditing ? 'Auto-filled or enter manually' : ''}
                        />
                      </div>

                      <div className="form-group">
                        <label>Driver Name</label>
                        <input
                          type="text"
                          name="driver_name"
                          value={editFormData.driver_name}
                          onChange={handleEditFormChange}
                          disabled={!isEditing}
                          className={`form-input ${!isEditing ? 'readonly' : ''}`}
                          placeholder="Driver name"
                        />
                      </div>

                      <div className="form-group">
                        <label>Phone Number</label>
                        <input
                          type="tel"
                          name="driver_phone"
                          value={editFormData.driver_phone}
                          onChange={handleEditFormChange}
                          disabled={!isEditing}
                          className={`form-input ${!isEditing ? 'readonly' : ''}`}
                        />
                      </div>

                      {isEditing ? (
                        <div className="form-group" style={{ gridColumn: 'span 2' }}>
                          <label>Phone Extensions / Alternate Contacts</label>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '6px' }}>
                            {editFormData.phone_extensions && editFormData.phone_extensions.length > 0 ? (
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                {editFormData.phone_extensions.map((entry, idx) => {
                                  const phone = extPhoneOf(entry);
                                  const label = extLabelOf(entry);
                                  return (
                                  <div
                                    key={`${phone}-${idx}`}
                                    style={{
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      gap: '6px',
                                      backgroundColor: '#f3f4f6',
                                      border: '1px solid #d1d5db',
                                      borderRadius: '6px',
                                      padding: '4px 10px',
                                      fontSize: '13px',
                                      color: '#374151'
                                    }}
                                  >
                                    <span>📞 {phone}{label ? ` — ${label}` : ''}</span>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setEditFormData(prev => ({
                                          ...prev,
                                          phone_extensions: prev.phone_extensions.filter(e => extPhoneOf(e) !== phone)
                                        }));
                                      }}
                                      style={{
                                        background: 'none',
                                        border: 'none',
                                        color: '#dc2626',
                                        cursor: 'pointer',
                                        fontSize: '16px',
                                        fontWeight: 'bold',
                                        padding: '0 2px'
                                      }}
                                    >
                                      &times;
                                    </button>
                                  </div>
                                  );
                                })}
                              </div>
                            ) : (
                              <span style={{ fontSize: '12px', color: '#9ca3af', fontStyle: 'italic' }}>No alternate contacts added yet.</span>
                            )}

                            <div style={{ display: 'flex', gap: '8px', maxWidth: '420px' }}>
                              <input
                                type="text"
                                value={detailExtPhone}
                                onChange={(e) => setDetailExtPhone(e.target.value)}
                                placeholder="e.g. 919900000000"
                                style={{
                                  flex: 1,
                                  padding: '8px 10px',
                                  border: '1px solid #d1d5db',
                                  borderRadius: '6px',
                                  fontSize: '13px'
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    addDetailPhoneExtension();
                                  }
                                }}
                              />
                              <input
                                type="text"
                                value={detailExtLabel}
                                onChange={(e) => setDetailExtLabel(e.target.value)}
                                placeholder="Label (optional) e.g. gpay no"
                                style={{
                                  flex: 1,
                                  padding: '8px 10px',
                                  border: '1px solid #d1d5db',
                                  borderRadius: '6px',
                                  fontSize: '13px'
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    addDetailPhoneExtension();
                                  }
                                }}
                              />
                              <button
                                type="button"
                                onClick={addDetailPhoneExtension}
                                style={{
                                  padding: '6px 12px',
                                  backgroundColor: '#6366f1',
                                  color: '#fff',
                                  border: 'none',
                                  borderRadius: '6px',
                                  cursor: 'pointer',
                                  fontSize: '13px',
                                  fontWeight: '600'
                                }}
                              >
                                Add
                              </button>
                            </div>
                          </div>
                        </div>
                      ) : (
                        selectedVehicle && selectedVehicle.phone_extensions && selectedVehicle.phone_extensions.length > 0 && (
                          <div className="form-group" style={{ gridColumn: 'span 2' }}>
                            <label>Alternate Phones / Phone Extensions</label>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '6px' }}>
                              {selectedVehicle.phone_extensions.map((entry, idx) => {
                                const phone = extPhoneOf(entry);
                                const label = extLabelOf(entry);
                                const clean = String(phone).replace(/\D/g, '');
                                const isCalling = callingDrivers[clean];
                                return (
                                  <button
                                    key={idx}
                                    type="button"
                                    onClick={() => handlePlaceCall(phone)}
                                    className="driver-phone-btn"
                                    disabled={isCalling}
                                    style={{
                                      background: '#f3f4f6',
                                      border: '1px solid #e5e7eb',
                                      borderRadius: '6px',
                                      padding: '6px 12px',
                                      fontSize: '13px',
                                      color: isCalling ? '#f59e0b' : '#1f2937',
                                      cursor: isCalling ? 'wait' : 'pointer',
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      gap: '6px',
                                      fontWeight: '600'
                                    }}
                                  >
                                    <span>{isCalling ? '📞 Calling...' : `📞 ${phone}${label ? ` — ${label}` : ''}`}</span>
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        )
                      )}

                      <div className="form-group">
                        <label>Language Preference</label>
                        <select
                          name="driver_language_preference"
                          value={editFormData.driver_language_preference}
                          onChange={handleEditFormChange}
                          disabled={!isEditing}
                          className={`form-input ${!isEditing ? 'readonly' : ''}`}
                        >
                          <option value="">Select Language</option>
                          <option value="English">English</option>
                          <option value="Hindi">Hindi</option>
                          <option value="Tamil">Tamil</option>
                          <option value="Telugu">Telugu</option>
                          <option value="Kannada">Kannada</option>
                          <option value="Malayalam">Malayalam</option>
                        </select>
                      </div>

                      <div className="form-group">
                        <label>Driver Rating</label>
                        <input
                          type="text"
                          value={editFormData.driver_rating ? `${editFormData.driver_rating} ⭐` : 'Not rated'}
                          disabled
                          className="form-input readonly"
                        />
                      </div>

                      <div className="vehicle-joined-bonus-card">
                        <div className="vehicle-joined-bonus-main">
                          <label>Joining Bonus</label>
                          <input
                            type="text"
                            value={editFormData.joining_bonus ? 'Paid' : 'Not Paid'}
                            disabled
                            className="form-input readonly"
                          />
                          <button
                            type="button"
                            className="btn-save-vehicle compact-action-btn"
                            onClick={handleMarkJoiningBonusPaid}
                            disabled={editFormData.joining_bonus || isMarkingJoiningBonus}
                          >
                            {isMarkingJoiningBonus
                              ? 'Marking...'
                              : (editFormData.joining_bonus ? 'Already Paid' : 'Mark as Paid')}
                          </button>
                        </div>
                        <div className="vehicle-driver-remarks-block">
                          <label>Remarks</label>
                          <textarea
                            name="driver_remarks"
                            value={editFormData.driver_remarks}
                            onChange={handleEditFormChange}
                            disabled={!isEditing}
                            className={`form-input ${!isEditing ? 'readonly' : ''}`}
                            placeholder={isEditing ? 'Enter remarks for this vehicle...' : 'No remarks added'}
                            rows={2}
                            style={{ resize: 'vertical' }}
                          />
                        </div>
                      </div>
                    </div>
                  </section>

                  <section className="vehicle-section-card">
                    <div className="vehicle-section-header">
                      <div>
                        <h3>Vehicle Details</h3>
                      </div>
                    </div>

                    <div className="vehicle-section-grid">
                      <div className="form-group">
                        <label>Make</label>
                        <input
                          type="text"
                          name="make"
                          value={editFormData.make}
                          onChange={handleEditFormChange}
                          disabled={!isEditing}
                          className={`form-input ${!isEditing ? 'readonly' : ''}`}
                          placeholder={isEditing ? 'Auto-filled or enter manually' : ''}
                        />
                      </div>
                      <div className="form-group">
                        <label>Model</label>
                        <input
                          type="text"
                          name="model"
                          value={editFormData.model}
                          onChange={handleEditFormChange}
                          disabled={!isEditing}
                          className={`form-input ${!isEditing ? 'readonly' : ''}`}
                          placeholder={isEditing ? 'Auto-filled or enter manually' : ''}
                        />
                      </div>
                      <div className="form-group">
                        <label>Length</label>
                        <input
                          type="text"
                          name="length_ft"
                          value={editFormData.length_ft || ''}
                          onChange={handleEditFormChange}
                          disabled={!isEditing}
                          className={`form-input ${!isEditing ? 'readonly' : ''}`}
                          placeholder={isEditing ? 'Length' : '—'}
                        />
                      </div>
                      <div className="form-group">
                        <label>Breadth</label>
                        <input
                          type="text"
                          name="breadth_ft"
                          value={editFormData.breadth_ft || ''}
                          onChange={handleEditFormChange}
                          disabled={!isEditing}
                          className={`form-input ${!isEditing ? 'readonly' : ''}`}
                          placeholder={isEditing ? 'Breadth' : '—'}
                        />
                      </div>
                      <div className="form-group">
                        <label>Height</label>
                        <input
                          type="text"
                          name="height_ft"
                          value={editFormData.height_ft || ''}
                          onChange={handleEditFormChange}
                          disabled={!isEditing}
                          className={`form-input ${!isEditing ? 'readonly' : ''}`}
                          placeholder={isEditing ? 'Height' : '—'}
                        />
                      </div>
                      <div className="form-group">
                        <label>Body Type</label>
                        <select
                          name="body_type"
                          value={selectedBodyType(editFormData.body_type)}
                          onChange={handleEditFormChange}
                          disabled={!isEditing}
                          className={`form-input ${!isEditing ? 'readonly' : ''}`}
                        >
                          <option value="">Select body type</option>
                          {selectedBodyType(editFormData.body_type)
                            && !BODY_TYPE_OPTIONS.includes(selectedBodyType(editFormData.body_type)) && (
                              <option value={selectedBodyType(editFormData.body_type)}>
                                {selectedBodyType(editFormData.body_type)}
                              </option>
                            )}
                          {BODY_TYPE_OPTIONS.map((option) => (
                            <option key={option} value={option}>{option}</option>
                          ))}
                        </select>
                      </div>
                      <div className="form-group">
                        <label>Vehicle Class</label>
                        <input
                          type="number"
                          name="class"
                          value={editFormData.class}
                          onChange={handleEditFormChange}
                          disabled={!isEditing}
                          className={`form-input ${!isEditing ? 'readonly' : ''}`}
                          min="1"
                        />
                      </div>
                      <div className="form-group" style={{ gridColumn: 'auto' }}>
                        <label>Specialization</label>
                        <select
                          name="specialization"
                          value={editFormData.specialization}
                          onChange={handleEditFormChange}
                          disabled={!isEditing}
                          className={`form-input ${!isEditing ? 'readonly' : ''}`}
                        >
                          <option value="">None</option>
                          <option value="Tipper">Tipper</option>
                          <option value="Refrigerated">Refrigerated</option>
                        </select>
                      </div>
                      <div className="form-group">
                        <label>Tonnage (ton)</label>
                        <input
                          type="number"
                          step="0.01"
                          name="tonnage"
                          value={editFormData.tonnage}
                          onChange={handleEditFormChange}
                          disabled={!isEditing}
                          className={`form-input ${!isEditing ? 'readonly' : ''}`}
                          placeholder={isEditing ? 'e.g. 1.5' : '—'}
                        />
                      </div>
                    </div>
                  </section>

                  {/* Driver Call Logs Section */}
                  <section className="vehicle-section-card" style={{ marginTop: '20px' }}>
                    <div className="vehicle-section-header">
                      <div>
                        <h3>Driver Call Logs</h3>
                      </div>
                      {/* Unknown Calls Toggle Button */}
                      <button
                        id="toggle-unknown-call-logs-btn"
                        onClick={() => {
                          const next = !showUnknownCallLogs;
                          setShowUnknownCallLogs(next);
                          if (next && vehicleUnknownCallLogs.length === 0) {
                            fetchVehicleUnknownCallLogs(
                              vehicleDriverCallLogsRef.driverId,
                              vehicleDriverCallLogsRef.driverPhone
                            );
                          }
                        }}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '5px',
                          padding: '4px 10px',
                          fontSize: '11px',
                          fontWeight: 600,
                          border: showUnknownCallLogs ? '1px solid #f59e0b' : '1px solid #d1d5db',
                          borderRadius: '6px',
                          backgroundColor: showUnknownCallLogs ? '#fef3c7' : '#f9fafb',
                          color: showUnknownCallLogs ? '#92400e' : '#6b7280',
                          cursor: 'pointer',
                          transition: 'all 0.15s',
                        }}
                        title={showUnknownCallLogs ? 'Hide Unknown Calls' : 'Show Unknown / Lead Calls'}
                      >
                        <span style={{ fontSize: 13 }}>{showUnknownCallLogs ? '🔒' : '👁️'}</span>
                        {showUnknownCallLogs ? 'Hide Unknown' : 'Show Unknown Calls'}
                        {vehicleUnknownCallLogsTotal > 0 && (
                          <span style={{
                            backgroundColor: '#f59e0b',
                            color: '#fff',
                            borderRadius: '10px',
                            padding: '0px 5px',
                            fontSize: '10px',
                            fontWeight: 700,
                            lineHeight: '16px',
                          }}>
                            {vehicleUnknownCallLogsTotal}
                          </span>
                        )}
                      </button>
                    </div>
                    {vehicleDriverCallLogsLoading ? (
                      <div style={{ textAlign: 'center', padding: '20px', color: '#6b7280' }}>
                        Loading call logs...
                      </div>
                    ) : vehicleDriverCallLogs.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: '20px', color: '#9ca3af', fontSize: '13px' }}>
                        No call logs found for this driver.
                      </div>
                    ) : (
                      <div className="table-responsive">
                        <table className="table table-striped table-hover" style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
                          <thead>
                            <tr style={{ borderBottom: '1px solid #e5e7eb', textAlign: 'left', color: '#4b5563' }}>
                              <th style={{ padding: '8px', width: 20 }} />
                              <th style={{ padding: '8px' }}>Time</th>
                              <th style={{ padding: '8px' }}>Operator</th>
                              <th style={{ padding: '8px' }}>Status</th>
                              <th style={{ padding: '8px' }}>Duration</th>
                              <th style={{ padding: '8px' }}>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {vehicleDriverCallLogs.map((log) => {
                              const isExpanded = expandedVehicleCallLogId === log.id;
                              const formatTs = (v) => {
                                if (!v) return '—';
                                try {
                                  return new Date(v).toLocaleString([], { dateStyle: 'short', timeStyle: 'medium' });
                                } catch {
                                  return String(v);
                                }
                              };
                              const formatDur = (secs) => {
                                const s = Number(secs) || 0;
                                if (s < 60) return `${s}s`;
                                return `${Math.floor(s / 60)}m ${s % 60}s`;
                              };
                              return (
                                <React.Fragment key={log.id}>
                                  <tr
                                    style={{
                                      borderBottom: isExpanded ? 'none' : '1px solid #f3f4f6',
                                      cursor: 'pointer',
                                      backgroundColor: isExpanded ? '#f8fafc' : undefined,
                                    }}
                                    onClick={() =>
                                      setExpandedVehicleCallLogId(isExpanded ? null : log.id)
                                    }
                                  >
                                    <td style={{ padding: '8px', color: '#9ca3af', fontSize: 10 }}>
                                      {isExpanded ? '▼' : '▶'}
                                    </td>
                                    <td style={{ padding: '8px', whiteSpace: 'nowrap' }}>
                                      {formatTs(log.started_at || log.created_at)}
                                    </td>
                                    <td style={{ padding: '8px' }}>
                                      {log.operator_id ? `User #${log.operator_id}` : 'System'}
                                    </td>
                                    <td style={{ padding: '8px' }}>
                                      <span style={{
                                        padding: '2px 6px',
                                        borderRadius: '4px',
                                        fontSize: '11px',
                                        fontWeight: '600',
                                        backgroundColor:
                                          log.status === 'completed' || log.status === 'accepted' ? '#d1fae5' :
                                            (log.status === 'rejected' || log.status === 'failed') ? '#fee2e2' :
                                              log.status === 'no_answer' ? '#ffedd5' :
                                                '#fef3c7',
                                        color:
                                          log.status === 'completed' || log.status === 'accepted' ? '#065f46' :
                                            (log.status === 'rejected' || log.status === 'failed') ? '#991b1b' :
                                              log.status === 'no_answer' ? '#9a3412' :
                                                '#92400e'
                                      }}>
                                        {log.status === 'completed' ? 'accepted' :
                                          log.status === 'no_answer' ? 'not received' :
                                            log.status}
                                      </span>
                                    </td>
                                    <td style={{ padding: '8px' }}>
                                      {formatDur(log.duration_seconds)}
                                    </td>
                                    <td style={{ padding: '8px' }} onClick={(e) => e.stopPropagation()}>
                                      <button
                                        onClick={() => handlePlaceCall(log.driver_phone || log.customer_phone)}
                                        className="btn btn-xs btn-success"
                                        style={{
                                          padding: '2px 6px',
                                          fontSize: '11px',
                                          backgroundColor: '#10b981',
                                          color: '#fff',
                                          border: 'none',
                                          borderRadius: '4px',
                                          cursor: 'pointer'
                                        }}
                                      >
                                        Call
                                      </button>
                                    </td>
                                  </tr>
                                  {isExpanded && (
                                    <tr style={{ backgroundColor: '#f8fafc', borderBottom: '1px solid #e5e7eb' }}>
                                      <td colSpan={6} style={{ padding: '10px 14px 14px 28px' }}>
                                        <div style={{
                                          display: 'grid',
                                          gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
                                          gap: '10px 16px',
                                          fontSize: 12,
                                          color: '#374151',
                                        }}>
                                          <div>
                                            <div style={{ color: '#9ca3af', fontSize: 10, marginBottom: 2 }}>Call ID</div>
                                            <code style={{ fontWeight: 600, color: '#1e293b' }}>#{log.id}</code>
                                          </div>
                                          <div>
                                            <div style={{ color: '#9ca3af', fontSize: 10, marginBottom: 2 }}>Direction</div>
                                            <span style={{
                                              padding: '2px 6px',
                                              borderRadius: 4,
                                              fontWeight: 600,
                                              fontSize: 11,
                                              backgroundColor: log.call_type === 'incoming' ? '#dbeafe' : '#e0e7ff',
                                              color: log.call_type === 'incoming' ? '#1e40af' : '#3730a3',
                                              textTransform: 'capitalize',
                                              display: 'inline-flex',
                                              alignItems: 'center',
                                              gap: 3,
                                            }}>
                                              {log.call_type === 'incoming' ? '📥 Incoming' : '↗ Outgoing'}
                                            </span>
                                          </div>
                                          <div>
                                            <div style={{ color: '#9ca3af', fontSize: 10, marginBottom: 2 }}>Phone</div>
                                            <code>{log.driver_phone || log.customer_phone || '—'}</code>
                                          </div>
                                          <div>
                                            <div style={{ color: '#9ca3af', fontSize: 10, marginBottom: 2 }}>SIM</div>
                                            <code>{log.sim_number || '—'}</code>
                                          </div>
                                          <div>
                                            <div style={{ color: '#9ca3af', fontSize: 10, marginBottom: 2 }}>Started</div>
                                            {formatTs(log.started_at)}
                                          </div>
                                          <div>
                                            <div style={{ color: '#9ca3af', fontSize: 10, marginBottom: 2 }}>Ended</div>
                                            {formatTs(log.ended_at)}
                                          </div>
                                          <div>
                                            <div style={{ color: '#9ca3af', fontSize: 10, marginBottom: 2 }}>Created</div>
                                            {formatTs(log.created_at)}
                                          </div>
                                          <div style={{ gridColumn: '1 / -1' }}>
                                            <div style={{ color: '#9ca3af', fontSize: 10, marginBottom: 4 }}>Recording</div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                              {log.recording_url ? (
                                                playingVehicleRecordingId === log.id ? (
                                                  <div id={`vehicle-audio-container-${log.id}`} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                    <audio
                                                      controls
                                                      autoPlay
                                                      src={log.recording_url}
                                                      style={{ height: 32, maxWidth: 280 }}
                                                      onEnded={() => setPlayingVehicleRecordingId(null)}
                                                    />
                                                    <button
                                                      type="button"
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        setPlayingVehicleRecordingId(null);
                                                      }}
                                                      style={{
                                                        background: 'none',
                                                        border: 'none',
                                                        color: '#6b7280',
                                                        fontSize: '14px',
                                                        cursor: 'pointer',
                                                        padding: '0 4px',
                                                      }}
                                                      title="Close Player"
                                                    >
                                                      ✕
                                                    </button>
                                                  </div>
                                                ) : (
                                                  <button
                                                    type="button"
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      setPlayingVehicleRecordingId(log.id);
                                                    }}
                                                    style={{
                                                      padding: '4px 10px',
                                                      fontSize: 11,
                                                      backgroundColor: '#3b82f6',
                                                      color: '#fff',
                                                      border: 'none',
                                                      borderRadius: 4,
                                                      cursor: 'pointer',
                                                      display: 'inline-flex',
                                                      alignItems: 'center',
                                                      gap: 4,
                                                    }}
                                                  >
                                                    ▶ Play Recording
                                                  </button>
                                                )
                                              ) : (
                                                <span style={{ color: '#9ca3af' }}>No Recording</span>
                                              )}

                                              {log.recording_url && (
                                                <button
                                                  type="button"
                                                  disabled={summarizingCallLogId === log.id}
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleSummarizeCallLog(log, !!(log.conversation_summary || log.has_conversation_summary));
                                                  }}
                                                  style={{
                                                    padding: '4px 10px',
                                                    fontSize: 11,
                                                    backgroundColor: '#f3e8ff',
                                                    color: '#7e22ce',
                                                    border: '1px solid #d8b4fe',
                                                    borderRadius: 4,
                                                    cursor: summarizingCallLogId === log.id ? 'wait' : 'pointer',
                                                    fontWeight: 500,
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: 4,
                                                  }}
                                                >
                                                  {summarizingCallLogId === log.id ? (
                                                    <>⏳ Summarizing...</>
                                                  ) : (
                                                    <>
                                                      <i className="fa fa-magic" style={{ fontSize: 10 }} />
                                                      {log.conversation_summary ? 'Re-summarize' : 'Summarize'}
                                                    </>
                                                  )}
                                                </button>
                                              )}
                                            </div>
                                          </div>
                                        </div>

                                        {/* Summary */}
                                        {log.conversation_summary && (
                                          <div style={{ marginTop: 14 }}>
                                            <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 6, fontWeight: 500 }}>Summary</div>
                                            <div style={{
                                              background: '#f8fafc',
                                              border: '1px solid #e2e8f0',
                                              borderRadius: 8,
                                              padding: 12,
                                              fontSize: 13,
                                              lineHeight: 1.5,
                                              color: '#1e293b',
                                            }}>
                                              {log.conversation_summary}
                                            </div>
                                          </div>
                                        )}

                                        {/* Ops / Customer / Driver notes (from audio) */}
                                        {log.conversation_transcript && log.conversation_transcript !== log.conversation_summary && (
                                          <div style={{ marginTop: 14 }}>
                                            <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 6, fontWeight: 500 }}>
                                              {log.caller_type === 'customer'
                                                ? 'Ops / Customer notes (from audio)'
                                                : log.caller_type === 'lead'
                                                  ? 'Ops / Marketing lead notes (from audio)'
                                                  : log.caller_type === 'unknown'
                                                    ? 'Ops / Unknown Caller notes (from audio)'
                                                    : log.caller_type === 'transporter'
                                                      ? 'Ops / Transporter notes (from audio)'
                                                      : 'Ops / Driver notes (from audio)'}
                                            </div>
                                            <div style={{
                                              background: '#fff',
                                              border: '1px solid #e2e8f0',
                                              borderRadius: 8,
                                              padding: 12,
                                              fontSize: 12,
                                              lineHeight: 1.45,
                                              color: '#334155',
                                              maxHeight: 220,
                                              overflowY: 'auto',
                                            }}>
                                              {renderVehicleCallMarkdownNotes(log.conversation_transcript)}
                                            </div>
                                          </div>
                                        )}

                                        {/* Call Link Details */}
                                        {renderVehicleCallLinkDetails(log)}
                                      </td>
                                    </tr>
                                  )}
                                </React.Fragment>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                    {!vehicleDriverCallLogsLoading && vehicleDriverCallLogsTotal > VEHICLE_CALL_LOGS_PAGE_SIZE && (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '12px' }}>
                        <span style={{ fontSize: '12px', color: '#6b7280' }}>
                          {`Showing ${(vehicleDriverCallLogsPage - 1) * VEHICLE_CALL_LOGS_PAGE_SIZE + 1}–${Math.min(vehicleDriverCallLogsPage * VEHICLE_CALL_LOGS_PAGE_SIZE, vehicleDriverCallLogsTotal)} of ${vehicleDriverCallLogsTotal}`}
                        </span>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            onClick={() => fetchVehicleDriverCallLogs(vehicleDriverCallLogsRef.driverId, vehicleDriverCallLogsRef.driverPhone, vehicleDriverCallLogsPage - 1)}
                            disabled={vehicleDriverCallLogsPage === 1}
                            style={{
                              padding: '4px 12px',
                              fontSize: '12px',
                              border: '1px solid #d1d5db',
                              borderRadius: '4px',
                              backgroundColor: vehicleDriverCallLogsPage === 1 ? '#f3f4f6' : '#fff',
                              color: vehicleDriverCallLogsPage === 1 ? '#9ca3af' : '#374151',
                              cursor: vehicleDriverCallLogsPage === 1 ? 'not-allowed' : 'pointer'
                            }}
                          >
                            Previous
                          </button>
                          <button
                            onClick={() => fetchVehicleDriverCallLogs(vehicleDriverCallLogsRef.driverId, vehicleDriverCallLogsRef.driverPhone, vehicleDriverCallLogsPage + 1)}
                            disabled={vehicleDriverCallLogsPage * VEHICLE_CALL_LOGS_PAGE_SIZE >= vehicleDriverCallLogsTotal}
                            style={{
                              padding: '4px 12px',
                              fontSize: '12px',
                              border: '1px solid #d1d5db',
                              borderRadius: '4px',
                              backgroundColor: vehicleDriverCallLogsPage * VEHICLE_CALL_LOGS_PAGE_SIZE >= vehicleDriverCallLogsTotal ? '#f3f4f6' : '#fff',
                              color: vehicleDriverCallLogsPage * VEHICLE_CALL_LOGS_PAGE_SIZE >= vehicleDriverCallLogsTotal ? '#9ca3af' : '#374151',
                              cursor: vehicleDriverCallLogsPage * VEHICLE_CALL_LOGS_PAGE_SIZE >= vehicleDriverCallLogsTotal ? 'not-allowed' : 'pointer'
                            }}
                          >
                            Next
                          </button>
                        </div>
                      </div>
                    )}

                    {/* ── Unknown / Lead Calls (collapsible) ── */}
                    {showUnknownCallLogs && (
                      <div style={{ marginTop: '16px', borderTop: '1px dashed #fde68a', paddingTop: '14px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                          <span style={{ fontSize: 13, fontWeight: 700, color: '#92400e' }}>⚠️ Unknown / Lead Calls</span>
                          <span style={{ fontSize: 11, color: '#b45309' }}>
                            Numbers not found in drivers or customers at time of call
                          </span>
                          {vehicleUnknownCallLogsLoading && (
                            <span style={{ fontSize: 11, color: '#9ca3af', marginLeft: 'auto' }}>Loading...</span>
                          )}
                        </div>

                        {!vehicleUnknownCallLogsLoading && vehicleUnknownCallLogs.length === 0 ? (
                          <div style={{ textAlign: 'center', padding: '14px', color: '#9ca3af', fontSize: 12, background: '#fefce8', borderRadius: 6 }}>
                            No unknown calls found for this driver.
                          </div>
                        ) : (
                          <div className="table-responsive">
                            <table className="table table-striped table-hover" style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
                              <thead>
                                <tr style={{ borderBottom: '1px solid #fde68a', textAlign: 'left', color: '#92400e', backgroundColor: '#fef9c3' }}>
                                  <th style={{ padding: '7px 8px', width: 20 }} />
                                  <th style={{ padding: '7px 8px' }}>Time</th>
                                  <th style={{ padding: '7px 8px' }}>Number</th>
                                  <th style={{ padding: '7px 8px' }}>Type</th>
                                  <th style={{ padding: '7px 8px' }}>Status</th>
                                  <th style={{ padding: '7px 8px' }}>Duration</th>
                                  <th style={{ padding: '7px 8px' }}>Action</th>
                                </tr>
                              </thead>
                              <tbody>
                                {vehicleUnknownCallLogs.map((log) => {
                                  const isExp = expandedVehicleUnknownCallLogId === log.id;
                                  const fmtTs = (v) => {
                                    if (!v) return '—';
                                    try { return new Date(v).toLocaleString([], { dateStyle: 'short', timeStyle: 'medium' }); }
                                    catch { return String(v); }
                                  };
                                  const fmtDur = (s) => {
                                    const sec = Number(s) || 0;
                                    return sec < 60 ? `${sec}s` : `${Math.floor(sec / 60)}m ${sec % 60}s`;
                                  };
                                  const numDisplay = log.unknown_number || log.driver_phone || log.customer_phone || '—';
                                  return (
                                    <React.Fragment key={log.id}>
                                      <tr
                                        style={{
                                          borderBottom: isExp ? 'none' : '1px solid #fef3c7',
                                          cursor: 'pointer',
                                          backgroundColor: isExp ? '#fffbeb' : undefined,
                                        }}
                                        onClick={() => setExpandedVehicleUnknownCallLogId(isExp ? null : log.id)}
                                      >
                                        <td style={{ padding: '7px 8px', color: '#d97706', fontSize: 10 }}>{isExp ? '▼' : '▶'}</td>
                                        <td style={{ padding: '7px 8px', whiteSpace: 'nowrap' }}>{fmtTs(log.started_at || log.created_at)}</td>
                                        <td style={{ padding: '7px 8px' }}>
                                          <code style={{ fontSize: 11, background: '#fef3c7', padding: '1px 5px', borderRadius: 3 }}>{numDisplay}</code>
                                          {log.resolved_name && (
                                            <div style={{ fontSize: 11, color: '#92400e', fontWeight: 600, marginTop: 2 }}>
                                              {log.resolved_name}
                                              {log.resolved_caller_type && (
                                                <span style={{
                                                  marginLeft: 4,
                                                  padding: '1px 5px',
                                                  fontSize: 9,
                                                  borderRadius: 3,
                                                  backgroundColor: log.resolved_caller_type === 'customer' ? '#d1fae5' : '#fde68a',
                                                  color: log.resolved_caller_type === 'customer' ? '#065f46' : '#78350f',
                                                  textTransform: 'capitalize'
                                                }}>
                                                  {log.resolved_caller_type}
                                                </span>
                                              )}
                                            </div>
                                          )}
                                        </td>

                                        <td style={{ padding: '7px 8px' }}>
                                          <span style={{
                                            padding: '2px 5px', borderRadius: 3, fontSize: 10, fontWeight: 600,
                                            backgroundColor: log.call_type === 'incoming' ? '#dbeafe' : '#ede9fe',
                                            color: log.call_type === 'incoming' ? '#1e40af' : '#5b21b6',
                                            textTransform: 'capitalize',
                                          }}>{log.call_type || 'outgoing'}</span>
                                        </td>
                                        <td style={{ padding: '7px 8px' }}>
                                          <span style={{
                                            padding: '2px 5px', borderRadius: 3, fontSize: 10, fontWeight: 600,
                                            backgroundColor:
                                              log.status === 'completed' || log.status === 'accepted' ? '#d1fae5' :
                                                (log.status === 'rejected' || log.status === 'failed') ? '#fee2e2' :
                                                  log.status === 'no_answer' ? '#ffedd5' : '#fef3c7',
                                            color:
                                              log.status === 'completed' || log.status === 'accepted' ? '#065f46' :
                                                (log.status === 'rejected' || log.status === 'failed') ? '#991b1b' :
                                                  log.status === 'no_answer' ? '#9a3412' : '#92400e',
                                          }}>{log.status === 'completed' ? 'accepted' : log.status === 'no_answer' ? 'not received' : log.status}</span>
                                        </td>
                                        <td style={{ padding: '7px 8px' }}>{fmtDur(log.duration_seconds)}</td>
                                        <td style={{ padding: '7px 8px' }} onClick={(e) => e.stopPropagation()}>
                                          <button
                                            onClick={() => handlePlaceCall(numDisplay)}
                                            style={{ padding: '2px 6px', fontSize: 10, backgroundColor: '#f59e0b', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}
                                          >Call</button>
                                        </td>
                                      </tr>
                                      {isExp && (
                                        <tr style={{ backgroundColor: '#fffbeb', borderBottom: '1px solid #fde68a' }}>
                                          <td colSpan={7} style={{ padding: '10px 14px 14px 26px' }}>
                                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '8px 14px', fontSize: 11, color: '#374151' }}>
                                              <div><div style={{ color: '#9ca3af', fontSize: 10, marginBottom: 2 }}>Call ID</div><code style={{ fontWeight: 600 }}>#{log.id}</code></div>
                                              <div><div style={{ color: '#9ca3af', fontSize: 10, marginBottom: 2 }}>Phone</div><code>{numDisplay}</code></div>
                                              <div><div style={{ color: '#9ca3af', fontSize: 10, marginBottom: 2 }}>SIM</div><code>{log.sim_number || '—'}</code></div>
                                              <div><div style={{ color: '#9ca3af', fontSize: 10, marginBottom: 2 }}>Source</div>{log.source || '—'}</div>
                                              <div><div style={{ color: '#9ca3af', fontSize: 10, marginBottom: 2 }}>Started</div>{fmtTs(log.started_at)}</div>
                                              <div><div style={{ color: '#9ca3af', fontSize: 10, marginBottom: 2 }}>Ended</div>{fmtTs(log.ended_at)}</div>
                                              <div><div style={{ color: '#9ca3af', fontSize: 10, marginBottom: 2 }}>Created</div>{fmtTs(log.created_at)}</div>
                                              <div style={{ gridColumn: '1 / -1' }}>
                                                <div style={{ color: '#9ca3af', fontSize: 10, marginBottom: 4 }}>Recording</div>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                                  {log.recording_url ? (
                                                    <audio controls src={log.recording_url} style={{ height: 30, maxWidth: 260 }} />
                                                  ) : (
                                                    <span style={{ color: '#9ca3af' }}>No Recording</span>
                                                  )}
                                                  {log.recording_url && (
                                                    <button
                                                      type="button"
                                                      disabled={summarizingCallLogId === log.id}
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleSummarizeCallLog(log, !!(log.conversation_summary || log.has_conversation_summary));
                                                      }}
                                                      style={{
                                                        padding: '3px 8px',
                                                        fontSize: 10,
                                                        backgroundColor: '#f3e8ff',
                                                        color: '#7e22ce',
                                                        border: '1px solid #d8b4fe',
                                                        borderRadius: 4,
                                                        cursor: summarizingCallLogId === log.id ? 'wait' : 'pointer',
                                                        fontWeight: 500,
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        gap: 4,
                                                      }}
                                                    >
                                                      {summarizingCallLogId === log.id ? '⏳ Summarizing...' : (log.conversation_summary ? 'Re-summarize' : 'Summarize')}
                                                    </button>
                                                  )}
                                                </div>
                                              </div>
                                            </div>

                                            {/* Summary */}
                                            {log.conversation_summary && (
                                              <div style={{ marginTop: 12 }}>
                                                <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 4, fontWeight: 500 }}>Summary</div>
                                                <div style={{
                                                  background: '#fff',
                                                  border: '1px solid #fde68a',
                                                  borderRadius: 6,
                                                  padding: 10,
                                                  fontSize: 12,
                                                  lineHeight: 1.45,
                                                  color: '#1e293b',
                                                }}>
                                                  {log.conversation_summary}
                                                </div>
                                              </div>
                                            )}

                                            {/* Ops / Customer / Notes */}
                                            {log.conversation_transcript && log.conversation_transcript !== log.conversation_summary && (
                                              <div style={{ marginTop: 12 }}>
                                                <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 4, fontWeight: 500 }}>
                                                  {log.caller_type === 'customer'
                                                    ? 'Ops / Customer notes (from audio)'
                                                    : log.caller_type === 'lead'
                                                      ? 'Ops / Marketing lead notes (from audio)'
                                                      : log.caller_type === 'unknown'
                                                        ? 'Ops / Unknown Caller notes (from audio)'
                                                        : log.caller_type === 'transporter'
                                                          ? 'Ops / Transporter notes (from audio)'
                                                          : 'Ops / Driver notes (from audio)'}
                                                </div>
                                                <div style={{
                                                  background: '#fff',
                                                  border: '1px solid #fde68a',
                                                  borderRadius: 6,
                                                  padding: 10,
                                                  fontSize: 11,
                                                  lineHeight: 1.45,
                                                  color: '#334155',
                                                  maxHeight: 200,
                                                  overflowY: 'auto',
                                                }}>
                                                  {renderVehicleCallMarkdownNotes(log.conversation_transcript)}
                                                </div>
                                              </div>
                                            )}

                                            {/* Link Details */}
                                            {renderVehicleCallLinkDetails(log)}
                                          </td>
                                        </tr>
                                      )}
                                    </React.Fragment>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        )}

                        {/* Unknown calls pagination */}
                        {!vehicleUnknownCallLogsLoading && vehicleUnknownCallLogsTotal > VEHICLE_CALL_LOGS_PAGE_SIZE && (
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '10px' }}>
                            <span style={{ fontSize: 11, color: '#b45309' }}>
                              {`Showing ${(vehicleUnknownCallLogsPage - 1) * VEHICLE_CALL_LOGS_PAGE_SIZE + 1}–${Math.min(vehicleUnknownCallLogsPage * VEHICLE_CALL_LOGS_PAGE_SIZE, vehicleUnknownCallLogsTotal)} of ${vehicleUnknownCallLogsTotal} unknown calls`}
                            </span>
                            <div style={{ display: 'flex', gap: '6px' }}>
                              <button
                                onClick={() => fetchVehicleUnknownCallLogs(vehicleDriverCallLogsRef.driverId, vehicleDriverCallLogsRef.driverPhone, vehicleUnknownCallLogsPage - 1)}
                                disabled={vehicleUnknownCallLogsPage === 1}
                                style={{ padding: '3px 10px', fontSize: 11, border: '1px solid #fcd34d', borderRadius: 4, backgroundColor: vehicleUnknownCallLogsPage === 1 ? '#fef9c3' : '#fff', color: vehicleUnknownCallLogsPage === 1 ? '#d97706' : '#92400e', cursor: vehicleUnknownCallLogsPage === 1 ? 'not-allowed' : 'pointer' }}
                              >Prev</button>
                              <button
                                onClick={() => fetchVehicleUnknownCallLogs(vehicleDriverCallLogsRef.driverId, vehicleDriverCallLogsRef.driverPhone, vehicleUnknownCallLogsPage + 1)}
                                disabled={vehicleUnknownCallLogsPage * VEHICLE_CALL_LOGS_PAGE_SIZE >= vehicleUnknownCallLogsTotal}
                                style={{ padding: '3px 10px', fontSize: 11, border: '1px solid #fcd34d', borderRadius: 4, backgroundColor: vehicleUnknownCallLogsPage * VEHICLE_CALL_LOGS_PAGE_SIZE >= vehicleUnknownCallLogsTotal ? '#fef9c3' : '#fff', color: vehicleUnknownCallLogsPage * VEHICLE_CALL_LOGS_PAGE_SIZE >= vehicleUnknownCallLogsTotal ? '#d97706' : '#92400e', cursor: vehicleUnknownCallLogsPage * VEHICLE_CALL_LOGS_PAGE_SIZE >= vehicleUnknownCallLogsTotal ? 'not-allowed' : 'pointer' }}
                              >Next</button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </section>
                </div>

                {/* Floating Save Bar — sticks to bottom of left panel while editing */}
                {isEditing && (
                  <div style={{
                    position: 'sticky',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    zIndex: 100,
                    background: 'linear-gradient(to top, #fff 80%, rgba(255,255,255,0))',
                    padding: '14px 20px 18px',
                    display: 'flex',
                    gap: '10px',
                    alignItems: 'center',
                    borderTop: '1px solid #e5e7eb',
                  }}>
                    <button
                      className="btn-save-vehicle"
                      onClick={handleSaveVehicle}
                      style={{
                        flex: 1,
                        padding: '11px 0',
                        fontSize: '15px',
                        fontWeight: '700',
                        borderRadius: '8px',
                        background: 'linear-gradient(135deg, #2563eb, #1d4ed8)',
                        color: '#fff',
                        border: 'none',
                        cursor: 'pointer',
                        boxShadow: '0 2px 8px rgba(37,99,235,0.35)',
                        letterSpacing: '0.3px',
                      }}
                    >
                      💾 Save Changes
                    </button>
                    <button
                      onClick={handleEditToggle}
                      style={{
                        padding: '11px 20px',
                        fontSize: '14px',
                        fontWeight: '600',
                        borderRadius: '8px',
                        background: '#f3f4f6',
                        color: '#374151',
                        border: '1px solid #d1d5db',
                        cursor: 'pointer',
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>

              {/* Right Half - Complete Route Details */}

              {/* ─── Check Map Modal (overlays the vehicle modal) ─── */}
              {showMapModal && (
                <div
                  onMouseDown={(e) => {
                    mapModalMouseDownTarget.current = e.target;
                  }}
                  onClick={(e) => {
                    if (e.target === e.currentTarget && mapModalMouseDownTarget.current === e.currentTarget) {
                      setShowMapModal(false);
                    }
                  }}
                  style={{
                    position: 'absolute',
                    inset: 0,
                    background: 'rgba(0,0,0,0.55)',
                    backdropFilter: 'blur(4px)',
                    zIndex: 200,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '16px',
                  }}
                >
                  <div
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                    style={{
                      background: '#fff',
                      borderRadius: '14px',
                      overflow: 'hidden',
                      width: '680px',
                      maxWidth: '90%',
                      boxShadow: '0 24px 48px rgba(0,0,0,0.28)',
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    {/* Header */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '14px 20px',
                      background: 'linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)',
                      color: '#fff',
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontWeight: 700, fontSize: '15px' }}>
                        <span style={{ fontSize: '18px' }}>📍</span>
                        Base Location Map
                      </div>
                      <button
                        onClick={() => setShowMapModal(false)}
                        style={{
                          background: 'rgba(255,255,255,0.2)',
                          border: 'none',
                          color: '#fff',
                          borderRadius: '50%',
                          width: '30px',
                          height: '30px',
                          fontSize: '18px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          lineHeight: 1,
                        }}
                      >
                        ×
                      </button>
                    </div>

                    {/* Address chip */}
                    {mapModalCoords.label && (
                      <div style={{
                        padding: '10px 20px',
                        background: '#f0f9ff',
                        borderBottom: '1px solid #e0e7ff',
                        fontSize: '13px',
                        color: '#1e3a8a',
                        fontWeight: 600,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                      }}>
                        <span>📍</span>
                        {mapModalCoords.label}
                      </div>
                    )}

                    {/* Google Map */}
                    <div style={{ position: 'relative', height: '420px', width: '100%', background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {!mapsLoaded ? (
                        <div style={{ color: '#4b5563', fontSize: '14px' }}>Loading Google Maps...</div>
                      ) : !mapCenter ? (
                        <div style={{ color: '#4b5563', fontSize: '14px' }}>Locating coordinates...</div>
                      ) : (
                        <GoogleMap
                          mapContainerStyle={{ width: '100%', height: '100%' }}
                          zoom={15}
                          center={mapCenter}
                          options={{
                            disableDefaultUI: false,
                            zoomControl: true,
                          }}
                        >
                          <Marker position={mapCenter} />
                        </GoogleMap>
                      )}
                    </div>

                    {/* Footer */}
                    <div style={{
                      padding: '12px 20px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      borderTop: '1px solid #e5e7eb',
                      background: '#fafafa',
                    }}>
                      <span style={{ fontSize: '12px', color: '#9ca3af' }}>
                        {mapCenter
                          ? `📍 ${mapCenter.lat.toFixed(6)}, ${mapCenter.lng.toFixed(6)}`
                          : 'Locating...'}
                      </span>
                      <button
                        onClick={() => setShowMapModal(false)}
                        style={{
                          padding: '7px 20px',
                          background: '#4F46E5',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '8px',
                          fontWeight: 600,
                          fontSize: '13px',
                          cursor: 'pointer',
                        }}
                      >
                        Close
                      </button>
                    </div>
                  </div>
                </div>
              )}



              <div className="modal-right-half">
                {/* KM Statistics Section - Enhanced with Total Pay */}
                {selectedVehicle.total_km !== undefined && (
                  <div className="km-stats-section" style={{
                    backgroundColor: '#f8f9fa',
                    padding: '15px',
                    borderRadius: '8px',
                    marginBottom: '20px',
                    border: '1px solid #e0e0e0'
                  }}>
                    <div style={{
                      backgroundColor: '#e8f5e9',
                      padding: '16px 20px',
                      borderRadius: '8px',
                      textAlign: 'center',
                      border: '1px solid #c8e6c9',
                    }}>
                      <div style={{ fontSize: '13px', color: '#2e7d32', fontWeight: '600', marginBottom: '4px' }}>
                        💰 Total Pay
                      </div>
                      <div style={{ fontSize: '26px', fontWeight: 'bold', color: '#1b5e20' }}>
                        ₹{(detailBookingsTotalPay !== null ? detailBookingsTotalPay : (selectedVehicle.total_pay || 0)).toFixed(2)}
                      </div>
                    </div>
                  </div>
                )}

                <CompleteRouteDetails
                  vehicleId={selectedVehicle.vehicle_id}
                  onTotalPayCalculated={setDetailBookingsTotalPay}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─── Image Lightbox Modal (global — works from list AND vehicle modal) ─── */}
      {showLightbox && (
        <div
          onClick={() => setShowLightbox(false)}
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.82)',
            backdropFilter: 'blur(6px)',
            zIndex: 99999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'zoom-out',
          }}
        >
          <div style={{ position: 'relative', maxWidth: '92vw', maxHeight: '92vh' }}>
            <img
              src={lightboxImage}
              alt="Vehicle Full"
              style={{
                maxWidth: '100%',
                maxHeight: '88vh',
                borderRadius: '14px',
                boxShadow: '0 30px 60px -12px rgba(0, 0, 0, 0.7)',
                objectFit: 'contain',
                cursor: 'default',
                display: 'block',
              }}
              onClick={(e) => e.stopPropagation()}
            />
            <button
              onClick={() => setShowLightbox(false)}
              style={{
                position: 'absolute',
                top: '-14px',
                right: '-14px',
                background: '#ef4444',
                color: 'white',
                border: 'none',
                borderRadius: '50%',
                width: '34px',
                height: '34px',
                fontSize: '20px',
                fontWeight: 'bold',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 10px rgba(0, 0, 0, 0.35)',
                lineHeight: 1,
              }}
              title="Close"
            >
              ×
            </button>
            <div style={{
              position: 'absolute',
              bottom: '-36px',
              left: '50%',
              transform: 'translateX(-50%)',
              color: 'rgba(255,255,255,0.6)',
              fontSize: '13px',
              whiteSpace: 'nowrap',
            }}>
              Click anywhere outside to close
            </div>
          </div>
        </div>
      )}

      {/* Assign Booking Modal - Separate overlay with Two-Step Validation */}
      {selectedVehicleForAssignment && (
        <div className="modal-overlay" onClick={closeAssignBookingModal} style={{ zIndex: 2000 }}>
          <div className="modal-content assign-booking-modal-full" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">
                <h2>Assign Booking to Vehicle V-{selectedVehicleForAssignment.vehicle_id}</h2>
                <p className="modal-subtitle">
                  {selectedVehicleForAssignment.vehicle_type} • {selectedVehicleForAssignment.base_location}
                </p>
              </div>
              <button className="modal-close-btn" onClick={closeAssignBookingModal}>×</button>
            </div>

            <div className="modal-body">
              {/* Step 1: Select Booking */}
              {validationStep === 'select' && (
                <>
                  {/* Search Box */}
                  <div className="booking-search-section">
                    <div className="booking-search-box">
                      <Search size={20} />
                      <input
                        type="text"
                        placeholder="Search by Booking ID or Pickup Location..."
                        value={bookingSearchTerm}
                        onChange={(e) => setBookingSearchTerm(e.target.value)}
                        disabled={isValidating}
                      />
                    </div>
                  </div>

                  {/* Bookings List */}
                  <div className="modal-bookings-section">
                    <h3>Available Unassigned Bookings ({getFilteredBookings().length})</h3>

                    {getFilteredBookings().length > 0 ? (
                      <div className="modal-bookings-list">
                        {getFilteredBookings().map(booking => (
                          <div key={booking.booking_id} className="booking-card">
                            <div className="booking-header">
                              <div className="booking-id-badge">
                                <span className="booking-label">Booking ID</span>
                                <span className="booking-number">B-{booking.booking_id}</span>
                              </div>
                              <button
                                className="btn-assign-booking"
                                onClick={() => handleAssignBooking(selectedVehicleForAssignment.vehicle_id, booking.booking_id)}
                                disabled={isValidating && selectedBookingForValidation === booking.booking_id}
                              >
                                {isValidating && selectedBookingForValidation === booking.booking_id ? 'Validating...' : 'Assign'}
                              </button>
                            </div>

                            <div className="booking-details">
                              <div className="booking-location">
                                <MapPin size={16} />
                                <div>
                                  <label>Pickup Location</label>
                                  <p>{booking.pickup_location}</p>
                                </div>
                              </div>
                              {booking.dropoff_location && (
                                <div className="booking-location">
                                  <MapPin size={16} />
                                  <div>
                                    <label>Dropoff Location</label>
                                    <p>{booking.dropoff_location}</p>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="modal-no-bookings">
                        <MapPin size={48} />
                        <h4>{bookingSearchTerm ? 'No bookings match your search' : 'No unassigned bookings available'}</h4>
                        <p>{bookingSearchTerm ? 'Try different search terms' : 'All bookings are currently assigned'}</p>
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* Step 2: Show Warnings */}
              {validationStep === 'warnings' && validationResult && (
                <div style={{ padding: '20px' }}>
                  <h3 style={{ marginBottom: '16px', color: '#333' }}>
                    Assignment Validation - Booking #{selectedBookingForValidation}
                  </h3>

                  {/* Hard Block - Cannot Proceed */}
                  {!validationResult.can_proceed && (
                    <div style={{
                      backgroundColor: '#f8d7da',
                      border: '1px solid #f5c6cb',
                      borderRadius: '8px',
                      padding: '16px',
                      marginBottom: '20px'
                    }}>
                      <h4 style={{ color: '#721c24', margin: '0 0 8px 0' }}>
                        ❌ Assignment Blocked
                      </h4>
                      <p style={{ color: '#721c24', margin: 0 }}>
                        {validationResult.hard_block}
                      </p>
                    </div>
                  )}

                  {/* Warnings List */}
                  {validationResult.warnings && validationResult.warnings.length > 0 && (
                    <div style={{ marginBottom: '20px' }}>
                      <h4 style={{ marginBottom: '12px', color: '#333' }}>
                        ⚠️ Warnings ({validationResult.warnings.length})
                      </h4>
                      {validationResult.warnings.map((warning, index) => (
                        <div
                          key={index}
                          style={{
                            backgroundColor: getSeverityBgColor(warning.severity),
                            borderLeft: `4px solid ${getSeverityColor(warning.severity)}`,
                            padding: '12px',
                            marginBottom: '8px',
                            borderRadius: '4px'
                          }}
                        >
                          <div style={{
                            fontWeight: 'bold',
                            color: getSeverityColor(warning.severity),
                            marginBottom: '4px',
                            textTransform: 'uppercase',
                            fontSize: '12px'
                          }}>
                            {warning.severity}
                          </div>
                          <div style={{ color: '#333' }}>
                            {warning.message}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Booking & Vehicle Info */}
                  {validationResult.booking_info && (
                    <div style={{
                      backgroundColor: '#f8f9fa',
                      padding: '12px',
                      borderRadius: '4px',
                      marginBottom: '20px',
                      fontSize: '14px'
                    }}>
                      <div><strong>Booking:</strong> #{validationResult.booking_info.booking_id}</div>
                      <div><strong>Vehicle Type Required:</strong> {validationResult.booking_info.vehicle_type || 'N/A'}</div>
                      <div><strong>Target Vehicle:</strong> #{validationResult.vehicle_info?.vehicle_id} ({validationResult.vehicle_info?.vehicle_type || 'N/A'})</div>
                    </div>
                  )}

                  {/* Acknowledgment Checkbox (only if can proceed with warnings) */}
                  {validationResult.can_proceed && validationResult.warnings?.length > 0 && (
                    <div style={{ marginBottom: '20px' }}>
                      <label style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        cursor: 'pointer'
                      }}>
                        <input
                          type="checkbox"
                          checked={warningsAcknowledged}
                          onChange={(e) => setWarningsAcknowledged(e.target.checked)}
                          style={{ width: '18px', height: '18px' }}
                        />
                        <span style={{ color: '#333' }}>
                          I acknowledge the warnings and want to proceed with this assignment
                        </span>
                      </label>
                    </div>
                  )}

                  <div style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: '12px'
                  }}>
                    <button
                      onClick={handleBackToSelect}
                      disabled={isAssigning}
                      style={{
                        padding: '8px 16px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        backgroundColor: '#f8f9fa',
                        color: '#333',
                        cursor: isAssigning ? 'not-allowed' : 'pointer'
                      }}
                    >
                      ← Back to Bookings
                    </button>
                    <button
                      onClick={closeAssignBookingModal}
                      disabled={isAssigning}
                      style={{
                        padding: '8px 16px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        backgroundColor: '#f8f9fa',
                        color: '#333',
                        cursor: isAssigning ? 'not-allowed' : 'pointer'
                      }}
                    >
                      Cancel
                    </button>
                    {validationResult.can_proceed && (
                      <button
                        onClick={() => handleConfirmAssignment(
                          selectedVehicleForAssignment.vehicle_id,
                          selectedBookingForValidation
                        )}
                        disabled={isAssigning || (validationResult.warnings?.length > 0 && !warningsAcknowledged)}
                        style={{
                          padding: '8px 16px',
                          border: 'none',
                          borderRadius: '4px',
                          backgroundColor: (isAssigning || (validationResult.warnings?.length > 0 && !warningsAcknowledged))
                            ? '#ccc'
                            : (validationResult.warnings?.some(w => w.severity === 'critical') ? '#dc3545' : '#28a745'),
                          color: 'white',
                          cursor: (isAssigning || (validationResult.warnings?.length > 0 && !warningsAcknowledged))
                            ? 'not-allowed'
                            : 'pointer'
                        }}
                      >
                        {isAssigning ? 'Assigning...' :
                          validationResult.warnings?.some(w => w.severity === 'critical')
                            ? '⚠️ Confirm Despite Warnings'
                            : '✓ Confirm Assignment'}
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Vehicle Chat Modal */}
      {selectedVehicleForChat && (
        <div className="modal-overlay" onClick={closeVehicleChatModal} style={{ zIndex: 2100 }}>
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{
              width: '800px',
              maxWidth: '90vw',
              height: '700px',
              maxHeight: '85vh',
              display: 'flex',
              flexDirection: 'column',
              padding: 0,
              overflow: 'hidden'
            }}
          >
            {/* Chat Header */}
            <div style={{
              backgroundColor: '#4F46E5',
              color: 'white',
              padding: '20px',
              borderRadius: '12px 12px 0 0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '50%',
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '24px'
                  }}>
                    💬
                  </div>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600' }}>
                        Driver Conversation
                      </h2>
                      <button
                        onClick={refreshChatHistory}
                        disabled={isChatRefreshing}
                        style={{
                          padding: '6px 10px',
                          borderRadius: '8px',
                          border: '1px solid rgba(255,255,255,0.4)',
                          backgroundColor: 'rgba(255,255,255,0.15)',
                          color: 'white',
                          cursor: isChatRefreshing ? 'not-allowed' : 'pointer',
                          fontSize: '12px',
                          fontWeight: '600',
                          opacity: isChatRefreshing ? 0.6 : 1
                        }}
                        title="Refresh chat history"
                      >
                        {isChatRefreshing ? 'Refreshing...' : 'Refresh'}
                      </button>
                    </div>
                    <p style={{
                      margin: '4px 0 0 0',
                      fontSize: '14px',
                      opacity: 0.9,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                        backgroundColor: 'rgba(255,255,255,0.2)',
                        padding: '2px 8px',
                        borderRadius: '12px',
                        fontSize: '12px'
                      }}>
                        🚗 V-{selectedVehicleForChat.vehicle_id}
                      </span>
                      <span>{selectedVehicleForChat.driver_name}</span>

                      {/* WebSocket connection status indicator */}
                      <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '5px',
                        fontSize: '11px',
                        padding: '4px 10px',
                        borderRadius: '12px',
                        backgroundColor: wsConnected ? 'rgba(16, 185, 129, 0.2)' : 'rgba(239, 68, 68, 0.2)',
                        color: wsConnected ? '#10b981' : '#ef4444',
                        fontWeight: '500'
                      }}>
                        <span style={{
                          width: '7px',
                          height: '7px',
                          borderRadius: '50%',
                          backgroundColor: wsConnected ? '#10b981' : '#ef4444',
                          boxShadow: wsConnected ? '0 0 4px #10b981' : 'none'
                        }}></span>
                        {wsConnected ? 'Live' : 'Offline'}
                      </span>
                    </p>
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                <button
                  onClick={handleToggleChatBot}
                  style={{
                    padding: '8px 16px',
                    borderRadius: '8px',
                    border: 'none',
                    backgroundColor: isChatBotPaused ? '#10b981' : '#ef4444',
                    color: 'white',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    transition: 'all 0.2s'
                  }}
                >
                  {isChatBotPaused ? '▶️ Resume Bot' : '⏸️ Manual Mode'}
                </button>
                <button
                  onClick={closeVehicleChatModal}
                  style={{
                    background: 'rgba(255,255,255,0.2)',
                    border: 'none',
                    color: 'white',
                    fontSize: '24px',
                    cursor: 'pointer',
                    width: '36px',
                    height: '36px',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'background 0.2s'
                  }}
                  onMouseEnter={(e) => e.target.style.background = 'rgba(255,255,255,0.3)'}
                  onMouseLeave={(e) => e.target.style.background = 'rgba(255,255,255,0.2)'}
                >
                  ×
                </button>
              </div>
            </div>

            {/* Bot Status Banner */}
            {isChatBotPaused && (
              <div style={{
                backgroundColor: '#fef3c7',
                borderLeft: '4px solid #f59e0b',
                padding: '12px 20px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                fontSize: '14px',
                color: '#92400e'
              }}>
                <span style={{ fontSize: '20px' }}>⚠️</span>
                <div>
                  <strong>Manual Mode Active</strong>
                  <p style={{ margin: '2px 0 0 0', fontSize: '13px' }}>
                    {driverHasApp
                      ? '📱 Bot is paused. Messages will be sent via push notification to the driver app.'
                      : '💬 Bot is paused. Messages will be sent via WhatsApp to the driver.'}
                  </p>
                </div>
              </div>
            )}

            {/* Chat Messages Area */}
            <div
              ref={chatContainerRef}
              onScroll={handleChatScroll}
              style={{
                flex: 1,
                overflowY: 'auto',
                padding: '20px',
                backgroundColor: '#f9fafb',
                backgroundImage: 'radial-gradient(circle at 20px 20px, #e5e7eb 1px, transparent 1px)',
                backgroundSize: '40px 40px'
              }}>
              {chatHistory.length === 0 ? (
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  color: '#9ca3af',
                  gap: '12px'
                }}>
                  <div style={{ fontSize: '48px' }}>💬</div>
                  <p style={{ fontSize: '16px', fontWeight: '500' }}>No messages yet</p>
                  <p style={{ fontSize: '14px', textAlign: 'center', maxWidth: '400px' }}>
                    {driverHasApp
                      ? 'Messages will be sent via push notification to the driver app. Start a conversation by typing below.'
                      : "Messages will be sent via WhatsApp to the driver's phone. Start a conversation by typing below."}
                  </p>
                </div>
              ) : (
                // Remove duplicates and map unique messages
                getFlatChatHistory(chatHistory)
                  .filter((msg, index, self) => {
                    // Remove duplicates by stringifying and comparing
                    const msgStr = typeof msg === 'string' ? msg : JSON.stringify(msg);
                    return index === self.findIndex(m => {
                      const mStr = typeof m === 'string' ? m : JSON.stringify(m);
                      return msgStr === mStr;
                    });
                  })
                  .map((msg, index) => {
                    // Parse message if it's a JSON string
                    let parsedMsg = msg;
                    if (typeof msg === 'string') {
                      try {
                        parsedMsg = JSON.parse(msg);
                      } catch (e) {
                        parsedMsg = formatNonJsonChatMessage(msg);
                      }
                    }

                    // Determine message sender type
                    // Driver messages: role='driver' or sender='driver'
                    // Bot messages: role='bot'
                    // Operator messages: role='operator' or anything else that's not driver/bot/system
                    const isDriver = parsedMsg.role === 'driver' || parsedMsg.sender === 'driver' || parsedMsg.from === 'driver';
                    const isBot = parsedMsg.role === 'bot';
                    const isSystem = parsedMsg.role === 'system' || parsedMsg.sender === 'system';
                    // If not driver, bot, or system, it's an operator message
                    const isOperator = !isDriver && !isBot && !isSystem;

                    // Parse special message types
                    const messageText = parsedMsg.message || parsedMsg.content || '';
                    const isPhoto = messageText.startsWith('photo:');
                    const isLocation = messageText.startsWith('location ');
                    const isAudio = messageText.startsWith('audio:') || parsedMsg.message_type === 'audio' || parsedMsg.type === 'audio';

                    // Sanitize driver app event payloads (BUTTON_CLICK:...) into a friendly label
                    const driverEvent = formatDriverEventMessage(messageText);
                    let displayContent = driverEvent ? driverEvent.label : messageText;
                    if (isPhoto) {
                      const rawPhotoUrl = messageText.replace('photo:', '').trim();
                      const shouldProxy = /(^|\.)facebook\.com\b|(^|\.)fbsbx\.com\b|(^|\.)fbcdn\.net\b|(^|\.)whatsapp\.net\b/i.test(rawPhotoUrl);
                      const photoUrl = shouldProxy
                        ? `${config.opsApiBase}/media-proxy?url=${encodeURIComponent(rawPhotoUrl)}`
                        : rawPhotoUrl;
                      const linkColor = isDriver ? '#1d4ed8' : (isBot ? '#6d28d9' : '#047857');
                      if (/^https?:\/\//i.test(photoUrl)) {
                        displayContent = (
                          <div>
                            <img
                              src={photoUrl}
                              alt="Driver photo"
                              style={{
                                maxWidth: '100%',
                                maxHeight: '240px',
                                borderRadius: '8px',
                                display: 'block',
                                objectFit: 'contain',
                                backgroundColor: 'rgba(255,255,255,0.5)',
                              }}
                            />
                            <a
                              href={photoUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ color: linkColor, textDecoration: 'none', fontSize: '12px', marginTop: '8px', display: 'inline-block', fontWeight: 600 }}
                            >
                              Open full image
                            </a>
                          </div>
                        );
                      } else {
                        displayContent = (
                          <a href={photoUrl} target="_blank" rel="noopener noreferrer"
                            style={{ color: linkColor, textDecoration: 'underline' }}>
                            View Photo
                          </a>
                        );
                      }
                    } else if (isLocation) {
                      const coords = messageText.replace('location ', '').trim().split(' ');
                      if (coords.length === 2) {
                        const [lat, lng] = coords;
                        const mapsUrl = `https://www.google.com/maps?q=${lat},${lng}`;
                        displayContent = (
                          <a href={mapsUrl} target="_blank" rel="noopener noreferrer"
                            style={{ color: 'white', textDecoration: 'underline' }}>
                            📍 View Location on Map
                          </a>
                        );
                      }
                    } else if (isAudio) {
                      let audioUrl = '';
                      let transcript = '';
                      if (messageText.includes('|transcript:')) {
                        const parts = messageText.split('|transcript:');
                        audioUrl = parts[0].replace('audio:', '').trim();
                        transcript = parts[1].trim();
                      } else {
                        audioUrl = messageText.replace('audio:', '').trim();
                      }

                      displayContent = (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', minWidth: '220px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', fontWeight: 600 }}>
                            <span>🎤</span>
                            <span>Voice Note</span>
                          </div>
                          {audioUrl && (
                            <audio
                              controls
                              src={audioUrl}
                              style={{ width: '100%', height: '40px', borderRadius: '4px' }}
                            />
                          )}
                          {transcript && (
                            <div style={{
                              fontSize: '13px',
                              fontStyle: 'italic',
                              opacity: 0.9,
                              borderLeft: '2px solid rgba(0,0,0,0.2)',
                              paddingLeft: '6px',
                              marginTop: '2px'
                            }}>
                              "{transcript}"
                            </div>
                          )}
                        </div>
                      );
                    }

                    return (
                      <div
                        key={index}
                        style={{
                          display: 'flex',
                          // Operator/Bot messages on RIGHT (outgoing), Driver messages on LEFT (incoming)
                          justifyContent: isDriver ? 'flex-start' : isSystem ? 'center' : 'flex-end',
                          marginBottom: '16px',
                          animation: 'slideIn 0.3s ease-out'
                        }}
                      >
                        {isSystem ? (
                          <div style={{
                            backgroundColor: '#e0e7ff',
                            color: '#4338ca',
                            padding: '8px 16px',
                            borderRadius: '16px',
                            fontSize: '13px',
                            maxWidth: '80%',
                            textAlign: 'center',
                            fontStyle: 'italic'
                          }}>
                            {parsedMsg.message || parsedMsg.content}
                          </div>
                        ) : (
                          <div style={{
                            display: 'flex',
                            // Driver avatar on left, Operator/Bot avatar on right
                            flexDirection: isDriver ? 'row' : 'row-reverse',
                            gap: '12px',
                            maxWidth: '75%',
                            alignItems: 'flex-end'
                          }}>
                            {/* Avatar */}
                            <div style={{
                              width: '36px',
                              height: '36px',
                              borderRadius: '50%',
                              backgroundColor: isDriver ? '#3b82f6' : (isBot ? '#8b5cf6' : '#10b981'),
                              color: 'white',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '16px',
                              fontWeight: 'bold',
                              flexShrink: 0,
                              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                            }}>
                              {isDriver ? '🚗' : (isBot ? '🤖' : '👤')}
                            </div>

                            {/* Message Bubble */}
                            <div style={{
                              // Driver (incoming) = light blue on left, Bot = purple, Operator = green on right
                              backgroundColor: isDriver ? '#dbeafe' : (isBot ? '#ede9fe' : '#d1fae5'),
                              color: isDriver ? '#1e40af' : (isBot ? '#5b21b6' : '#065f46'),
                              padding: '12px 16px',
                              // Driver on left: tail on left side, Operator/Bot on right: tail on right side
                              borderRadius: isDriver ? '16px 16px 16px 4px' : '16px 16px 4px 16px',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                              wordWrap: 'break-word'
                            }}>
                              {isDriver && (
                                <div style={{
                                  fontSize: '11px',
                                  opacity: 0.7,
                                  marginBottom: '4px',
                                  fontWeight: '500'
                                }}>
                                  Driver
                                </div>
                              )}
                              {!isDriver && (
                                <div style={{
                                  fontSize: '11px',
                                  opacity: 0.8,
                                  marginBottom: '4px',
                                  fontWeight: '500'
                                }}>
                                  {isBot ? 'Bot' : (parsedMsg.sender || 'Ops')}
                                </div>
                              )}
                              <div style={{
                                fontSize: '14px',
                                lineHeight: '1.5',
                                whiteSpace: 'pre-wrap'
                              }}>
                                {displayContent}
                              </div>
                              <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginTop: '6px',
                                gap: '8px'
                              }}>
                                {parsedMsg.timestamp && (
                                  <span style={{
                                    fontSize: '11px',
                                    opacity: 0.7
                                  }}>
                                    {new Date(parsedMsg.timestamp).toLocaleTimeString('en-US', {
                                      hour: '2-digit',
                                      minute: '2-digit'
                                    })}
                                  </span>
                                )}
                                {/* Message status indicator for operator messages */}
                                {!isDriver && parsedMsg.status && (
                                  <span style={{
                                    fontSize: '12px',
                                    opacity: 0.9
                                  }}>
                                    {parsedMsg.status === 'pending' && '⏳'}
                                    {parsedMsg.status === 'sent' && '✓'}
                                    {parsedMsg.status === 'saved' && '💾'}
                                    {parsedMsg.status === 'failed' && '❌'}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })
              )}
              {/* Scroll anchor */}
              <div ref={chatMessagesEndRef} />
            </div>

            {/* Message Input Area */}
            <div style={{
              padding: '20px',
              backgroundColor: 'white',
              borderTop: '1px solid #e5e7eb',
              borderRadius: '0 0 12px 12px'
            }}>
              <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end' }}>
                <textarea
                  value={chatMessage}
                  onChange={(e) => setChatMessage(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSendChatMessage();
                    }
                  }}
                  placeholder="Type your message to the driver..."
                  disabled={isSendingMessage}
                  style={{
                    flex: 1,
                    padding: '12px 16px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '12px',
                    fontSize: '14px',
                    resize: 'none',
                    minHeight: '50px',
                    maxHeight: '120px',
                    fontFamily: 'inherit',
                    transition: 'border-color 0.2s',
                    outline: 'none'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#4F46E5'}
                  onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                />
                <button
                  onClick={handleSendChatMessage}
                  disabled={!chatMessage.trim() || isSendingMessage}
                  style={{
                    padding: '12px 24px',
                    backgroundColor: chatMessage.trim() && !isSendingMessage ? '#4F46E5' : '#d1d5db',
                    color: 'white',
                    border: 'none',
                    borderRadius: '12px',
                    cursor: chatMessage.trim() && !isSendingMessage ? 'pointer' : 'not-allowed',
                    fontSize: '14px',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    transition: 'all 0.2s',
                    minHeight: '50px'
                  }}
                  onMouseEnter={(e) => {
                    if (chatMessage.trim() && !isSendingMessage) {
                      e.target.style.backgroundColor = '#4338ca';
                      e.target.style.transform = 'scale(1.02)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (chatMessage.trim() && !isSendingMessage) {
                      e.target.style.backgroundColor = '#4F46E5';
                      e.target.style.transform = 'scale(1)';
                    }
                  }}
                >
                  {isSendingMessage ? (
                    <>
                      <span style={{
                        display: 'inline-block',
                        width: '16px',
                        height: '16px',
                        border: '2px solid white',
                        borderTopColor: 'transparent',
                        borderRadius: '50%',
                        animation: 'spin 0.6s linear infinite'
                      }} />
                      Sending...
                    </>
                  ) : (
                    <>
                      ✉️ Send Message
                    </>
                  )}
                </button>
              </div>
              <p style={{
                fontSize: '12px',
                color: '#6b7280',
                margin: '8px 0 0 0',
                fontStyle: 'italic'
              }}>
                Press Enter to send, Shift+Enter for new line
              </p>
            </div>
          </div>
        </div>
      )}

      {/* ==================== BONUS PAY MODAL ==================== */}
      {showBonusPayModal && selectedVehicleForBonus && (
        <div className="modal-overlay" onClick={closeBonusPayModal} style={{ zIndex: 2100 }}>
          <div className="modal-content bonus-pay-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">
                <h2>Add Bonus Payment - Vehicle V-{selectedVehicleForBonus.vehicle_id}</h2>
              </div>
              <button className="modal-close-btn" onClick={closeBonusPayModal}>×</button>
            </div>

            <div className="modal-body">
              {!showBonusConfirmation ? (
                <div className="bonus-pay-form">
                  {/* Date Field */}
                  <div className="form-group-bonus">
                    <label>
                      Date <span className="required">*</span>
                    </label>
                    <div className="date-selector-with-today">
                      <input
                        type="date"
                        value={bonusDate}
                        onChange={(e) => handleBonusDateChange(e.target.value)}
                        max={new Date().toISOString().split('T')[0]}
                        style={{
                          flex: 1,
                          padding: '10px',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          fontSize: '14px'
                        }}
                      />
                      <button
                        className="btn-today"
                        onClick={handleSetToday}
                      >
                        Today
                      </button>
                    </div>
                  </div>

                  {/* Trip Dropdown */}
                  <div className="form-group-bonus">
                    <label>Trip (Optional)</label>
                    {isLoadingTrips ? (
                      <div style={{ padding: '10px', color: '#6b7280' }}>Loading trips...</div>
                    ) : bonusTrips.length > 0 ? (
                      <select
                        value={selectedTripId}
                        onChange={(e) => setSelectedTripId(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '10px',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          fontSize: '14px',
                          backgroundColor: 'white'
                        }}
                      >
                        <option value="">-- Select Trip (Optional) --</option>
                        {bonusTrips.map(trip => (
                          <option key={trip.trip_id} value={trip.trip_id}>
                            Trip #{trip.trip_id} - {trip.pickup_location} → {trip.dropoff_location}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <div style={{
                        padding: '10px',
                        backgroundColor: '#fef3c7',
                        border: '1px solid #fbbf24',
                        borderRadius: '6px',
                        color: '#92400e',
                        fontSize: '14px'
                      }}>
                        No trips found for this date
                      </div>
                    )}
                  </div>

                  {/* Reason Field */}
                  <div className="form-group-bonus">
                    <label>
                      Reason <span className="required">*</span>
                    </label>
                    <textarea
                      value={bonusReason}
                      onChange={(e) => setBonusReason(e.target.value)}
                      placeholder="e.g., Toll reimbursement, Customer inconvenience compensation, Extra effort, etc."
                      rows="3"
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px',
                        fontFamily: 'inherit',
                        resize: 'vertical'
                      }}
                    />
                    <small style={{ color: '#6b7280', fontSize: '12px' }}>
                      Minimum 10 characters ({bonusReason.length}/10)
                    </small>
                  </div>

                  {/* Amount Field */}
                  <div className="form-group-bonus">
                    <label>
                      Amount <span className="required">*</span>
                    </label>
                    <div style={{ position: 'relative' }}>
                      <span style={{
                        position: 'absolute',
                        left: '12px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        fontSize: '16px',
                        color: '#374151',
                        fontWeight: '600'
                      }}>₹</span>
                      <input
                        type="number"
                        value={bonusAmount}
                        onChange={(e) => setBonusAmount(e.target.value)}
                        placeholder="0.00"
                        min="1"
                        step="0.01"
                        style={{
                          width: '100%',
                          padding: '10px 10px 10px 30px',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          fontSize: '14px'
                        }}
                      />
                    </div>
                  </div>

                  {/* Form Actions */}
                  <div style={{
                    display: 'flex',
                    gap: '12px',
                    marginTop: '24px',
                    justifyContent: 'flex-end'
                  }}>
                    <button
                      onClick={closeBonusPayModal}
                      style={{
                        padding: '10px 20px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        backgroundColor: 'white',
                        color: '#374151',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '600'
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleBonusSubmit}
                      disabled={isSubmittingBonus}
                      style={{
                        padding: '10px 20px',
                        border: 'none',
                        borderRadius: '6px',
                        backgroundColor: isSubmittingBonus ? '#9ca3af' : '#10b981',
                        color: 'white',
                        cursor: isSubmittingBonus ? 'not-allowed' : 'pointer',
                        fontSize: '14px',
                        fontWeight: '600'
                      }}
                    >
                      {isSubmittingBonus ? 'Submitting...' : 'Submit'}
                    </button>
                  </div>
                </div>
              ) : (
                /* Confirmation Dialog */
                <div className="confirmation-dialog">
                  <div style={{ marginBottom: '20px' }}>
                    <h3 style={{ color: '#856404', marginBottom: '12px', fontSize: '18px' }}>
                      ⚠️ Confirm Bonus Payment
                    </h3>
                    <div className="confirmation-text" style={{ fontSize: '16px', lineHeight: '1.6' }}>
                      <p style={{ marginBottom: '8px' }}>
                        <strong>Amount:</strong> ₹{bonusAmount}
                      </p>
                      <p style={{ marginBottom: '8px' }}>
                        <strong>Date:</strong> {bonusDate}
                      </p>
                      {selectedTripId && (
                        <p style={{ marginBottom: '8px' }}>
                          <strong>Trip:</strong> #{selectedTripId}
                        </p>
                      )}
                      <p style={{ marginBottom: '8px' }}>
                        <strong>Reason:</strong> {bonusReason}
                      </p>
                      <p style={{ marginTop: '16px', fontWeight: '600' }}>
                        This bonus will be added to the driver's payment calculation.
                        The status will remain as "Pending" until it's included in the daily payment.
                      </p>
                    </div>
                  </div>

                  <div style={{
                    display: 'flex',
                    gap: '12px',
                    justifyContent: 'flex-end'
                  }}>
                    <button
                      onClick={() => setShowBonusConfirmation(false)}
                      disabled={isSubmittingBonus}
                      style={{
                        padding: '10px 20px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        backgroundColor: 'white',
                        color: '#374151',
                        cursor: isSubmittingBonus ? 'not-allowed' : 'pointer',
                        fontSize: '14px',
                        fontWeight: '600'
                      }}
                    >
                      Back
                    </button>
                    <button
                      onClick={handleBonusConfirm}
                      disabled={isSubmittingBonus}
                      style={{
                        padding: '10px 20px',
                        border: 'none',
                        borderRadius: '6px',
                        backgroundColor: isSubmittingBonus ? '#9ca3af' : '#059669',
                        color: 'white',
                        cursor: isSubmittingBonus ? 'not-allowed' : 'pointer',
                        fontSize: '14px',
                        fontWeight: '600'
                      }}
                    >
                      {isSubmittingBonus ? 'Processing...' : 'Yes, Confirm'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ==================== BONUS HISTORY MODAL ==================== */}
      {showBonusHistoryModal && selectedVehicleForBonus && (
        <div className="modal-overlay" onClick={closeBonusHistoryModal} style={{ zIndex: 2100 }}>
          <div className="modal-content bonus-history-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">
                <h2>Bonus Payment History - Vehicle V-{selectedVehicleForBonus.vehicle_id}</h2>
              </div>
              <button className="modal-close-btn" onClick={closeBonusHistoryModal}>×</button>
            </div>

            {/* Date Filter */}
            <div style={{
              padding: '16px 24px',
              backgroundColor: '#f9fafb',
              borderBottom: '1px solid #e5e7eb',
              display: 'flex',
              gap: '12px',
              alignItems: 'center'
            }}>
              <label style={{ fontSize: '14px', fontWeight: '600', color: '#374151' }}>Filter by Date:</label>
              <input
                type="date"
                value={bonusHistoryDateFilter.startDate}
                onChange={(e) => {
                  setBonusHistoryDateFilter({ ...bonusHistoryDateFilter, startDate: e.target.value });
                  setBonusHistoryCurrentPage(1);
                }}
                style={{
                  padding: '6px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
                placeholder="Start Date"
              />
              <span style={{ color: '#6b7280' }}>to</span>
              <input
                type="date"
                value={bonusHistoryDateFilter.endDate}
                onChange={(e) => {
                  setBonusHistoryDateFilter({ ...bonusHistoryDateFilter, endDate: e.target.value });
                  setBonusHistoryCurrentPage(1);
                }}
                style={{
                  padding: '6px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
                placeholder="End Date"
              />
              {(bonusHistoryDateFilter.startDate || bonusHistoryDateFilter.endDate) && (
                <button
                  onClick={() => {
                    setBonusHistoryDateFilter({ startDate: '', endDate: '' });
                    setBonusHistoryCurrentPage(1);
                  }}
                  style={{
                    padding: '6px 12px',
                    backgroundColor: '#ef4444',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '14px',
                    cursor: 'pointer'
                  }}
                >
                  Clear Filter
                </button>
              )}
            </div>

            <div className="modal-body">
              {isLoadingHistory ? (
                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  padding: '40px'
                }}>
                  <div style={{ textAlign: 'center' }}>
                    <div className="loading-spinner"></div>
                    <p style={{ marginTop: '16px', color: '#6b7280' }}>Loading bonus history...</p>
                  </div>
                </div>
              ) : bonusHistory.length > 0 ? (
                <>
                  {/* Summary Cards - Filtered Stats */}
                  {(() => {
                    // Apply date filter to get filtered data
                    let filtered = bonusHistory;
                    if (bonusHistoryDateFilter.startDate) {
                      filtered = filtered.filter(b => new Date(b.date) >= new Date(bonusHistoryDateFilter.startDate));
                    }
                    if (bonusHistoryDateFilter.endDate) {
                      filtered = filtered.filter(b => new Date(b.date) <= new Date(bonusHistoryDateFilter.endDate));
                    }

                    return (
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(3, 1fr)',
                        gap: '12px',
                        marginBottom: '24px'
                      }}>
                        <div style={{
                          backgroundColor: '#fef3c7',
                          padding: '16px',
                          borderRadius: '8px',
                          textAlign: 'center'
                        }}>
                          <div style={{ fontSize: '12px', color: '#92400e', marginBottom: '4px' }}>
                            Total Pending
                          </div>
                          <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#b45309' }}>
                            ₹{filtered
                              .filter(b => !b.paid)
                              .reduce((sum, b) => sum + b.amount, 0)
                              .toFixed(2)}
                          </div>
                          <div style={{ fontSize: '11px', color: '#92400e', marginTop: '4px' }}>
                            {filtered.filter(b => !b.paid).length} records
                          </div>
                        </div>
                        <div style={{
                          backgroundColor: '#d1fae5',
                          padding: '16px',
                          borderRadius: '8px',
                          textAlign: 'center'
                        }}>
                          <div style={{ fontSize: '12px', color: '#065f46', marginBottom: '4px' }}>
                            Total Paid
                          </div>
                          <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#059669' }}>
                            ₹{filtered
                              .filter(b => b.paid)
                              .reduce((sum, b) => sum + b.amount, 0)
                              .toFixed(2)}
                          </div>
                          <div style={{ fontSize: '11px', color: '#065f46', marginTop: '4px' }}>
                            {filtered.filter(b => b.paid).length} records
                          </div>
                        </div>
                        <div style={{
                          backgroundColor: '#e0e7ff',
                          padding: '16px',
                          borderRadius: '8px',
                          textAlign: 'center'
                        }}>
                          <div style={{ fontSize: '12px', color: '#3730a3', marginBottom: '4px' }}>
                            Total Records
                          </div>
                          <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#4f46e5' }}>
                            {filtered.length}
                          </div>
                          <div style={{ fontSize: '11px', color: '#3730a3', marginTop: '4px' }}>
                            {(bonusHistoryDateFilter.startDate || bonusHistoryDateFilter.endDate) ? 'Filtered' : 'All payments'}
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                  {/* History Table */}
                  <div style={{ overflowX: 'auto' }}>
                    <table className="bonus-history-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Trip ID</th>
                          <th>Amount</th>
                          <th>Reason</th>
                          <th>Status</th>
                          <th>Created By</th>
                          <th>Created At</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(() => {
                          // Filter by date
                          let filtered = bonusHistory;
                          if (bonusHistoryDateFilter.startDate) {
                            filtered = filtered.filter(b => new Date(b.date) >= new Date(bonusHistoryDateFilter.startDate));
                          }
                          if (bonusHistoryDateFilter.endDate) {
                            filtered = filtered.filter(b => new Date(b.date) <= new Date(bonusHistoryDateFilter.endDate));
                          }

                          // Pagination
                          const startIdx = (bonusHistoryCurrentPage - 1) * BONUS_HISTORY_PAGE_SIZE;
                          const endIdx = startIdx + BONUS_HISTORY_PAGE_SIZE;
                          const paginatedData = filtered.slice(startIdx, endIdx);

                          return paginatedData.length > 0 ? paginatedData.map(bonus => (
                            <tr key={bonus.id}>
                              <td>
                                {editingBonusId === bonus.id ? (
                                  <input
                                    type="date"
                                    value={editBonusData.date}
                                    onChange={(e) => {
                                      setEditBonusData({ ...editBonusData, date: e.target.value });
                                      fetchTripsForDate(selectedVehicleForBonus.vehicle_id, e.target.value);
                                    }}
                                    max={new Date().toISOString().split('T')[0]}
                                    style={{
                                      padding: '4px 8px',
                                      border: '1px solid #d1d5db',
                                      borderRadius: '4px',
                                      fontSize: '13px',
                                      width: '130px'
                                    }}
                                    disabled={isUpdatingBonus}
                                  />
                                ) : (
                                  new Date(bonus.date).toLocaleDateString()
                                )}
                              </td>
                              <td>
                                {editingBonusId === bonus.id ? (
                                  isLoadingTrips ? (
                                    <span style={{ fontSize: '13px', color: '#6b7280' }}>Loading...</span>
                                  ) : (
                                    <select
                                      value={editBonusData.trip_id}
                                      onChange={(e) => setEditBonusData({ ...editBonusData, trip_id: e.target.value })}
                                      style={{
                                        padding: '4px 8px',
                                        border: '1px solid #d1d5db',
                                        borderRadius: '4px',
                                        fontSize: '13px',
                                        width: '120px'
                                      }}
                                      disabled={isUpdatingBonus}
                                    >
                                      <option value="">None</option>
                                      {bonusTrips.map(trip => (
                                        <option key={trip.trip_id} value={trip.trip_id}>
                                          Trip #{trip.trip_id}
                                        </option>
                                      ))}
                                    </select>
                                  )
                                ) : bonus.trip_id ? (
                                  <span style={{
                                    backgroundColor: '#dbeafe',
                                    padding: '2px 8px',
                                    borderRadius: '4px',
                                    fontSize: '13px',
                                    fontWeight: '600',
                                    color: '#1e40af'
                                  }}>
                                    #{bonus.trip_id}
                                  </span>
                                ) : (
                                  <span style={{ color: '#9ca3af', fontSize: '13px' }}>N/A</span>
                                )}
                              </td>
                              <td style={{ fontWeight: '600', color: '#059669' }}>
                                {editingBonusId === bonus.id ? (
                                  <input
                                    type="number"
                                    value={editBonusData.amount}
                                    onChange={(e) => setEditBonusData({ ...editBonusData, amount: e.target.value })}
                                    min="1"
                                    step="0.01"
                                    style={{
                                      padding: '4px 8px',
                                      border: '1px solid #d1d5db',
                                      borderRadius: '4px',
                                      fontSize: '13px',
                                      width: '90px'
                                    }}
                                    disabled={isUpdatingBonus}
                                  />
                                ) : (
                                  `₹${bonus.amount.toFixed(2)}`
                                )}
                              </td>
                              <td style={{ maxWidth: '200px', fontSize: '13px' }}>
                                {editingBonusId === bonus.id ? (
                                  <textarea
                                    value={editBonusData.reason}
                                    onChange={(e) => setEditBonusData({ ...editBonusData, reason: e.target.value })}
                                    rows="2"
                                    style={{
                                      padding: '4px 8px',
                                      border: '1px solid #d1d5db',
                                      borderRadius: '4px',
                                      fontSize: '13px',
                                      width: '180px',
                                      resize: 'vertical'
                                    }}
                                    disabled={isUpdatingBonus}
                                  />
                                ) : (
                                  bonus.reason
                                )}
                              </td>
                              <td>
                                <span className={`status-badge ${bonus.paid ? 'paid' : 'pending'}`}>
                                  {bonus.paid ? '✓ Paid' : '⏳ Pending'}
                                </span>
                              </td>
                              <td style={{ fontSize: '13px' }}>{bonus.created_by}</td>
                              <td style={{ fontSize: '13px', color: '#6b7280' }}>
                                {new Date(bonus.created_at).toLocaleString()}
                              </td>
                              <td>
                                {!bonus.paid && (
                                  editingBonusId === bonus.id ? (
                                    <div style={{ display: 'flex', gap: '4px' }}>
                                      <button
                                        onClick={handleSaveEditBonus}
                                        disabled={isUpdatingBonus}
                                        style={{
                                          padding: '4px 8px',
                                          backgroundColor: isUpdatingBonus ? '#9ca3af' : '#059669',
                                          color: 'white',
                                          border: 'none',
                                          borderRadius: '4px',
                                          fontSize: '12px',
                                          cursor: isUpdatingBonus ? 'not-allowed' : 'pointer',
                                          fontWeight: '600'
                                        }}
                                      >
                                        {isUpdatingBonus ? 'Saving...' : 'Save'}
                                      </button>
                                      <button
                                        onClick={handleCancelEditBonus}
                                        disabled={isUpdatingBonus}
                                        style={{
                                          padding: '4px 8px',
                                          backgroundColor: '#6b7280',
                                          color: 'white',
                                          border: 'none',
                                          borderRadius: '4px',
                                          fontSize: '12px',
                                          cursor: isUpdatingBonus ? 'not-allowed' : 'pointer'
                                        }}
                                      >
                                        Cancel
                                      </button>
                                    </div>
                                  ) : (
                                    <button
                                      onClick={() => handleEditBonus(bonus)}
                                      style={{
                                        padding: '4px 12px',
                                        backgroundColor: '#3b82f6',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        fontSize: '12px',
                                        cursor: 'pointer',
                                        fontWeight: '600'
                                      }}
                                    >
                                      Modify
                                    </button>
                                  )
                                )}
                              </td>
                            </tr>
                          )) : (
                            <tr>
                              <td colSpan="8" style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                                No records found for the selected date range.
                              </td>
                            </tr>
                          );
                        })()}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {(() => {
                    let filtered = bonusHistory;
                    if (bonusHistoryDateFilter.startDate) {
                      filtered = filtered.filter(b => new Date(b.date) >= new Date(bonusHistoryDateFilter.startDate));
                    }
                    if (bonusHistoryDateFilter.endDate) {
                      filtered = filtered.filter(b => new Date(b.date) <= new Date(bonusHistoryDateFilter.endDate));
                    }
                    const totalPages = Math.ceil(filtered.length / BONUS_HISTORY_PAGE_SIZE);

                    if (totalPages <= 1) return null;

                    return (
                      <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: '8px',
                        marginTop: '24px',
                        padding: '16px'
                      }}>
                        <button
                          onClick={() => setBonusHistoryCurrentPage(prev => Math.max(1, prev - 1))}
                          disabled={bonusHistoryCurrentPage === 1}
                          style={{
                            padding: '8px 16px',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            backgroundColor: bonusHistoryCurrentPage === 1 ? '#f3f4f6' : 'white',
                            cursor: bonusHistoryCurrentPage === 1 ? 'not-allowed' : 'pointer',
                            fontSize: '14px',
                            fontWeight: '600'
                          }}
                        >
                          Previous
                        </button>
                        <span style={{ fontSize: '14px', color: '#374151' }}>
                          Page {bonusHistoryCurrentPage} of {totalPages}
                        </span>
                        <button
                          onClick={() => setBonusHistoryCurrentPage(prev => Math.min(totalPages, prev + 1))}
                          disabled={bonusHistoryCurrentPage === totalPages}
                          style={{
                            padding: '8px 16px',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            backgroundColor: bonusHistoryCurrentPage === totalPages ? '#f3f4f6' : 'white',
                            cursor: bonusHistoryCurrentPage === totalPages ? 'not-allowed' : 'pointer',
                            fontSize: '14px',
                            fontWeight: '600'
                          }}
                        >
                          Next
                        </button>
                      </div>
                    );
                  })()}
                </>
              ) : (
                <div style={{
                  textAlign: 'center',
                  padding: '60px 20px',
                  color: '#6b7280'
                }}>
                  <div style={{ fontSize: '48px', marginBottom: '16px' }}>📋</div>
                  <h3 style={{ marginBottom: '8px', color: '#374151' }}>No Bonus Payments Yet</h3>
                  <p>No bonus payments have been added for this vehicle.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {/* ── Vehicle Docs Modal ──────────────────────────────────────────── */}
      {showDocsModal && (
        <div
          className="modal-overlay"
          onMouseDown={(e) => {
            docsModalMouseDownTarget.current = e.target;
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget && docsModalMouseDownTarget.current === e.currentTarget) {
              setShowDocsModal(false);
            }
          }}
          style={{ zIndex: 3000 }}
        >
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            style={{ maxWidth: 640, width: '90%', maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}
          >
            {/* Header */}
            <div className="modal-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div className="modal-title">
                <span style={{ fontSize: 18 }}>📁</span>
                <span style={{ marginLeft: 8 }}>Vehicle Documents — #{selectedVehicle?.vehicle_id}</span>
              </div>
              <button
                onClick={() => setShowDocsModal(false)}
                style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#6b7280' }}
              >
                ×
              </button>
            </div>

            {/* Body */}
            <div style={{ overflowY: 'auto', padding: '16px 20px', flex: 1 }}>
              {docsLoading && (
                <div style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>
                  <div style={{ fontSize: 32, marginBottom: 8 }}>⏳</div>
                  Loading documents…
                </div>
              )}
              {docsError && (
                <div style={{ background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 8, padding: '12px 16px', color: '#b91c1c', marginBottom: 12 }}>
                  ⚠️ {docsError}
                </div>
              )}
              {!docsLoading && !docsError && driverDocs && (() => {
                const missingCount = DRIVER_DOC_TYPES.filter(d => !driverDocs[d.key]?.present).length;
                return (
                  <>
                    {/* Summary */}
                    <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
                      {missingCount > 0 ? (
                        <span style={{ background: '#fee2e2', color: '#b91c1c', borderRadius: 20, padding: '3px 12px', fontSize: 13, fontWeight: 600 }}>
                          ⚠️ {missingCount} missing
                        </span>
                      ) : (
                        <span style={{ background: '#d1fae5', color: '#065f46', borderRadius: 20, padding: '3px 12px', fontSize: 13, fontWeight: 600 }}>
                          ✅ All documents present
                        </span>
                      )}
                      {DRIVER_DOC_TYPES.map(dt => (
                        <span
                          key={dt.key}
                          style={{
                            background: driverDocs[dt.key]?.present ? '#d1fae5' : '#fee2e2',
                            color: driverDocs[dt.key]?.present ? '#065f46' : '#b91c1c',
                            borderRadius: 20, padding: '3px 10px', fontSize: 12,
                          }}
                        >
                          {dt.icon} {dt.label} {driverDocs[dt.key]?.present ? '✓' : '✗'}
                        </span>
                      ))}
                    </div>

                    {/* Document cards */}
                    {DRIVER_DOC_TYPES.map(dt => (
                      <DriverDocCard
                        key={dt.key}
                        docType={dt}
                        doc={driverDocs[dt.key] || {}}
                        vehicleId={selectedVehicle?.vehicle_id}
                        onRefresh={() => fetchDriverDocs(selectedVehicle?.vehicle_id)}
                      />
                    ))}
                  </>
                );
              })()}

              {/* Bank details always shown (independent of docs load) */}
              {!docsLoading && showDocsModal && (
                <BankDetailsSection vehicleId={selectedVehicle?.vehicle_id} />
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Driver Messages Modal ──────────────────────────────────────────── */}
      {showMessagesModal && (
        <div className="modal-overlay" onClick={closeMessagesModal} style={{ zIndex: 3000 }}>
          <div
            className="modal-content"
            onClick={e => e.stopPropagation()}
            style={{ maxWidth: 600, width: '90%', maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}
          >
            <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ fontSize: '20px' }}>💬</span>
                <h3 style={{ margin: 0 }}>Messages for {selectedDriverForMessages}</h3>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <button
                  onClick={handleToggleRegistrationBot}
                  disabled={isRegistrationBotStatusLoading || isRegistrationBotToggling}
                  style={{
                    padding: '7px 12px',
                    border: 'none',
                    borderRadius: '7px',
                    color: '#fff',
                    fontWeight: 600,
                    cursor: (isRegistrationBotStatusLoading || isRegistrationBotToggling) ? 'not-allowed' : 'pointer',
                    background: isRegistrationBotPaused ? '#16a34a' : '#ef4444',
                    opacity: (isRegistrationBotStatusLoading || isRegistrationBotToggling) ? 0.65 : 1,
                    fontSize: '12px'
                  }}
                >
                  {isRegistrationBotToggling
                    ? 'Updating...'
                    : isRegistrationBotPaused
                      ? '▶ Resume Bot'
                      : '⏸ Manual Mode'}
                </button>
                <button onClick={closeMessagesModal} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#6b7280' }}>&times;</button>
              </div>
            </div>

            {isRegistrationBotPaused && (
              <div style={{
                backgroundColor: '#fef3c7',
                borderLeft: '4px solid #f59e0b',
                padding: '10px 14px',
                color: '#92400e',
                fontSize: '13px'
              }}>
                <strong>Manual Mode Active</strong>
                <div style={{ marginTop: '3px' }}>
                  Bot replies are paused. Ops messages are sent manually to driver WhatsApp.
                </div>
              </div>
            )}

            <div
              ref={messagesContainerRef}
              onScroll={handleScrollMessages}
              style={{ padding: '20px', overflowY: 'auto', flex: 1, backgroundColor: '#f9fafb' }}
            >
              {loadingMessages ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>Loading messages...</div>
              ) : messagesError ? (
                <div style={{ color: '#dc2626', background: '#fee2e2', padding: '12px', borderRadius: '6px' }}>{messagesError}</div>
              ) : driverMessages.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>No messages found for this driver.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {loadingOlderMessages && (
                    <div style={{ textAlign: 'center', fontSize: '12px', color: '#6b7280', marginBottom: '8px' }}>
                      Loading older messages...
                    </div>
                  )}
                  {hasMoreMessages && !loadingOlderMessages && (
                    <div style={{ textAlign: 'center', fontSize: '12px', color: '#6b7280', marginBottom: '8px' }}>
                      Scroll up to load older messages
                    </div>
                  )}
                  {!hasMoreMessages && (
                    <div style={{ textAlign: 'center', fontSize: '12px', color: '#9ca3af', marginBottom: '8px' }}>
                      Start of conversation
                    </div>
                  )}
                  {driverMessages.map((msg, idx) => {
                    const isDriver = msg.sender === 'driver' || msg.sender === 'user';
                    return (
                      <div key={idx} style={{
                        alignSelf: isDriver ? 'flex-start' : 'flex-end',
                        maxWidth: '80%',
                        backgroundColor: isDriver ? '#ffffff' : '#d1fae5',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        padding: '10px 14px',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                      }}>
                        <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '4px', fontWeight: 'bold' }}>
                          {isDriver ? 'Driver' : 'Bot/Ops'}
                        </div>
                        <div style={{ fontSize: '14px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                          {msg.type === 'image' && msg.content.startsWith('http') ? (
                            <div>
                              <img
                                src={msg.content}
                                alt="Uploaded document"
                                style={{ maxWidth: '100%', borderRadius: '4px', marginTop: '4px', maxHeight: '200px', objectFit: 'contain' }}
                                onLoad={scrollToBottom}
                              />
                              <div style={{ marginTop: '4px' }}>
                                <a href={msg.content} target="_blank" rel="noopener noreferrer" style={{ color: '#3b82f6', textDecoration: 'none', fontSize: '12px' }}>
                                  Open full image ↗
                                </a>
                              </div>
                            </div>
                          ) : msg.type === 'location' ? (
                            <div>
                              <div style={{ fontWeight: 600, marginBottom: '6px' }}>📍 Shared location</div>
                              {(msg.latitude != null && msg.longitude != null) ? (
                                <div style={{ fontFamily: 'monospace', fontSize: '13px', marginBottom: '6px' }}>
                                  {Number(msg.latitude).toFixed(6)}, {Number(msg.longitude).toFixed(6)}
                                </div>
                              ) : null}
                              {(msg.location_name || msg.location_address) ? (
                                <div style={{ fontSize: '13px', color: '#374151', marginBottom: '8px' }}>
                                  {[msg.location_name, msg.location_address].filter(Boolean).join(' — ')}
                                </div>
                              ) : null}
                              {msg.maps_url ? (
                                <a href={msg.maps_url} target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb', fontWeight: 600, fontSize: '13px' }}>
                                  Open in Google Maps ↗
                                </a>
                              ) : (
                                <span>{msg.content}</span>
                              )}
                            </div>
                          ) : (
                            msg.content
                          )}
                        </div>
                        {msg.timestamp && (
                          <div style={{ fontSize: '10px', color: '#9ca3af', marginTop: '6px', textAlign: 'right' }}>
                            {new Date(msg.timestamp).toLocaleString()}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </div>
              )}
            </div>

            <div style={{ padding: '12px 14px', borderTop: '1px solid #e5e7eb', background: '#fff' }}>
              <div style={{ display: 'flex', gap: '8px' }}>
                <input
                  value={registrationManualMessage}
                  onChange={(e) => setRegistrationManualMessage(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSendRegistrationManualMessage();
                    }
                  }}
                  disabled={!isRegistrationBotPaused || isSendingRegistrationManualMessage}
                  placeholder={isRegistrationBotPaused ? 'Type manual message to driver...' : 'Enable Manual Mode to send messages'}
                  style={{
                    flex: 1,
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    padding: '10px 12px',
                    fontSize: '14px',
                    opacity: (!isRegistrationBotPaused || isSendingRegistrationManualMessage) ? 0.7 : 1
                  }}
                />
                <button
                  onClick={handleSendRegistrationManualMessage}
                  disabled={!isRegistrationBotPaused || !registrationManualMessage.trim() || isSendingRegistrationManualMessage}
                  style={{
                    padding: '10px 14px',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: 600,
                    color: '#fff',
                    background: (!isRegistrationBotPaused || !registrationManualMessage.trim() || isSendingRegistrationManualMessage) ? '#9ca3af' : '#2563eb',
                    cursor: (!isRegistrationBotPaused || !registrationManualMessage.trim() || isSendingRegistrationManualMessage) ? 'not-allowed' : 'pointer'
                  }}
                >
                  {isSendingRegistrationManualMessage ? 'Sending...' : 'Send'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showRegTransporterModal && regTransporterRow && (
        <div
          className="modal-overlay"
          onClick={closeRegTransporterModal}
          style={{ zIndex: 3200 }}
        >
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 480, width: '90%', overflow: 'visible' }}
          >
            <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0 }}>Onboard under transporter</h3>
              <button
                type="button"
                onClick={closeRegTransporterModal}
                style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#6b7280' }}
              >
                &times;
              </button>
            </div>
            <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div style={{ fontSize: 13, color: '#4b5563' }}>
                Vehicle for <strong>{regTransporterRow.name}</strong> ({regTransporterRow.phone_number}) will be registered under an existing transporter agency.
              </div>
              {regTransporterLookupError && (
                <div style={{ color: '#dc2626', background: '#fee2e2', padding: '10px 14px', borderRadius: '6px', fontSize: '14px' }}>
                  {regTransporterLookupError}
                </div>
              )}
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '4px', color: '#374151' }}>
                  Transporter name or number *
                </label>
                <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                  <div style={{ flex: 1, position: 'relative' }}>
                    <input
                      type="text"
                      value={regTransporterPhone}
                      onChange={(e) => {
                        setRegTransporterPhone(e.target.value);
                        setRegTransporterProfile(null);
                        setRegTransporterLookupStatus(null);
                        setRegTransporterLookupError('');
                        setRegTransporterSuggestOpen(true);
                      }}
                      onFocus={() => setRegTransporterSuggestOpen(true)}
                      onBlur={() => setTimeout(() => setRegTransporterSuggestOpen(false), 150)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          const matches = matchOnboardedTransporters(regTransporterPhone);
                          if (matches.length === 1) {
                            selectTransporterSuggestion(matches[0]);
                          } else {
                            handleLookupTransporterForOnboard();
                          }
                        }
                      }}
                      placeholder="Agency name or 9876543210"
                      autoComplete="off"
                      style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' }}
                    />
                    {regTransporterSuggestOpen && matchOnboardedTransporters(regTransporterPhone).length > 0 && (
                      <div style={{
                        position: 'absolute',
                        left: 0,
                        right: 0,
                        top: 'calc(100% + 4px)',
                        zIndex: 50,
                        background: '#fff',
                        border: '1px solid #e5e7eb',
                        borderRadius: 8,
                        boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                        maxHeight: 220,
                        overflowY: 'auto',
                      }}>
                        {matchOnboardedTransporters(regTransporterPhone).map((t) => (
                          <button
                            key={t.id || t.user_id || t.phone_number}
                            type="button"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => selectTransporterSuggestion(t)}
                            style={{
                              display: 'block',
                              width: '100%',
                              textAlign: 'left',
                              padding: '10px 12px',
                              border: 'none',
                              borderBottom: '1px solid #f3f4f6',
                              background: '#fff',
                              cursor: 'pointer',
                            }}
                          >
                            <div style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>
                              {t.agency_name || t.contact_name || 'Transporter'}
                            </div>
                            <div style={{ fontSize: 12, color: '#6b7280' }}>
                              {[t.contact_name, t.phone_number].filter(Boolean).join(' · ')}
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={handleLookupTransporterForOnboard}
                    disabled={regTransporterLookupStatus === 'loading'}
                    style={{
                      padding: '8px 14px',
                      border: 'none',
                      background: '#4f46e5',
                      color: '#fff',
                      borderRadius: 6,
                      cursor: regTransporterLookupStatus === 'loading' ? 'not-allowed' : 'pointer',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {regTransporterLookupStatus === 'loading' ? 'Looking up…' : 'Look up'}
                  </button>
                </div>
              </div>
              {regTransporterLookupStatus === 'found' && regTransporterProfile && (
                <div style={{ background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 8, padding: '10px 12px', fontSize: 13 }}>
                  <div><strong>{regTransporterProfile.agency_name || 'Transporter'}</strong></div>
                  {regTransporterProfile.contact_name && (
                    <div style={{ color: '#374151' }}>{regTransporterProfile.contact_name}</div>
                  )}
                  <div style={{ color: '#6b7280' }}>{regTransporterProfile.phone_number}</div>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 6 }}>
                <button
                  type="button"
                  onClick={closeRegTransporterModal}
                  style={{ padding: '8px 14px', border: '1px solid #d1d5db', background: '#fff', borderRadius: 6, cursor: 'pointer' }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={
                    regTransporterLookupStatus !== 'found'
                    || !regTransporterProfile
                    || onboardingRegistrationIds.has(regTransporterRow.id)
                  }
                  onClick={() => handleOnboardFromRegistration(regTransporterRow, {
                    isTransporter: true,
                    transporterPhone: normalizeRegistrationPhone(
                      regTransporterProfile?.phone_number || regTransporterPhone
                    ) || (regTransporterProfile?.phone_number || regTransporterPhone),
                  })}
                  style={{
                    padding: '8px 14px',
                    border: 'none',
                    background: (regTransporterLookupStatus === 'found' && regTransporterProfile) ? '#16a34a' : '#86efac',
                    color: '#fff',
                    borderRadius: 6,
                    cursor: (regTransporterLookupStatus === 'found' && regTransporterProfile) ? 'pointer' : 'not-allowed',
                  }}
                >
                  {onboardingRegistrationIds.has(regTransporterRow.id) ? 'Onboarding...' : 'Onboard under transporter'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showEditRegModal && editRegRow && (
        <div
          className="modal-overlay edit-reg-overlay"
          onClick={() => setShowEditRegModal(false)}
          style={{ zIndex: 3100 }}
        >
          <div
            className="modal-content edit-reg-modal"
            onClick={e => e.stopPropagation()}
          >
            <div className="modal-header edit-reg-modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0 }}>Edit Registration #{editRegRow.id}</h3>
              <button onClick={() => setShowEditRegModal(false)} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#6b7280' }}>&times;</button>
            </div>
            <div className="edit-reg-modal-body">
              {editRegError && (
                <div style={{ color: '#dc2626', background: '#fee2e2', padding: '10px 14px', borderRadius: '6px', fontSize: '14px' }}>{editRegError}</div>
              )}
              <div className="edit-reg-two-col">
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '4px', color: '#374151' }}>Name *</label>
                  <input
                    value={editRegName}
                    onChange={e => setEditRegName(e.target.value)}
                    style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '4px', color: '#374151' }}>Phone *</label>
                  <input
                    value={editRegPhone}
                    onChange={e => setEditRegPhone(e.target.value)}
                    style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '4px', color: '#374151' }}>Make</label>
                  <input
                    type="text"
                    name="make"
                    id="edit-reg-make"
                    autoComplete="on"
                    value={editRegMake}
                    onChange={e => {
                      const mk = e.target.value;
                      setEditRegMake(mk);
                      setEditRegVehicle([mk, editRegModel].filter(Boolean).join(' ').trim());
                    }}
                    placeholder="e.g. Ashok Leyland"
                    style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '4px', color: '#374151' }}>Model</label>
                  <input
                    type="text"
                    name="model"
                    id="edit-reg-model"
                    autoComplete="on"
                    value={editRegModel}
                    onChange={e => {
                      const mo = e.target.value;
                      setEditRegModel(mo);
                      setEditRegVehicle([editRegMake, mo].filter(Boolean).join(' ').trim());
                    }}
                    placeholder="e.g. 2525"
                    style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '4px', color: '#374151' }}>Reg No</label>
                  <input
                    value={editRegRegistrationNo}
                    onChange={e => setEditRegRegistrationNo(e.target.value.toUpperCase())}
                    placeholder="KA 19 AE 8030"
                    style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '4px', color: '#374151' }}>Vehicle Class</label>
                  <input
                    type="number"
                    value={editRegClass}
                    onChange={e => setEditRegClass(e.target.value)}
                    placeholder="Leave blank to auto-detect"
                    style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' }}
                  />
                </div>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '4px', color: '#374151' }}>Base address</label>
                <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '6px' }}>
                  {mapsLoaded
                    ? 'Type to search — select from the dropdown to auto-fill coordinates.'
                    : mapsLoadError
                      ? 'Maps unavailable; tap Get Coordinates to resolve from text.'
                      : 'Loading maps… You can still tap Get Coordinates.'}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: mapsLoaded ? '1fr' : '1fr auto', gap: '10px', alignItems: 'center' }}>
                  <PlacesAutocompleteInput
                    value={editRegAddress}
                    onChange={setEditRegAddress}
                    onPlaceSelect={(place) => {
                      setEditRegAddress(place.address);
                      setEditRegBaseLat(place.lat);
                      setEditRegBaseLng(place.lng);
                    }}
                    placeholder="Type to search — select from dropdown..."
                    inputStyle={{ padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', width: '100%' }}
                  />
                  {!mapsLoaded && (
                    <button
                      type="button"
                      onClick={handleEditRegGeocodePlace}
                      disabled={isEditRegGeocoding}
                      style={{
                        whiteSpace: 'nowrap',
                        padding: '8px 14px',
                        border: 'none',
                        background: isEditRegGeocoding ? '#93c5fd' : '#4F46E5',
                        color: '#fff',
                        borderRadius: '8px',
                        cursor: isEditRegGeocoding ? 'not-allowed' : 'pointer',
                        fontSize: '13px',
                        fontWeight: 600,
                      }}
                    >
                      {isEditRegGeocoding ? '⏳ Resolving...' : 'Get Coordinates'}
                    </button>
                  )}
                </div>
              </div>
              <div className="edit-reg-two-col">
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '4px', color: '#374151' }}>Base lat</label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={editRegBaseLat}
                    onChange={e => setEditRegBaseLat(e.target.value)}
                    style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '4px', color: '#374151' }}>Base lng</label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={editRegBaseLng}
                    onChange={e => setEditRegBaseLng(e.target.value)}
                    style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' }}
                  />
                </div>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '4px', color: '#374151' }}>Comments</label>
                <textarea
                  value={editRegComments}
                  onChange={e => setEditRegComments(e.target.value)}
                  placeholder="Any notes about this driver, reference, or context…"
                  rows={2}
                  style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box' }}
                />
              </div>
              <RegistrationSpecsFields
                specs={editRegSpecs}
                setSpecs={setEditRegSpecs}
                extValue={editRegExtPhone}
                setExtValue={setEditRegExtPhone}
                extLabelValue={editRegExtLabel}
                setExtLabelValue={setEditRegExtLabel}
              />
            </div>
            <div className="edit-reg-modal-footer">
              <button
                onClick={() => setShowEditRegModal(false)}
                style={{ padding: '8px 18px', border: '1px solid #d1d5db', background: '#fff', borderRadius: '6px', cursor: 'pointer', fontSize: '14px' }}
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateRegistration}
                disabled={editRegSaving}
                style={{ padding: '8px 18px', border: 'none', background: editRegSaving ? '#93c5fd' : '#2563eb', color: '#fff', borderRadius: '6px', cursor: editRegSaving ? 'not-allowed' : 'pointer', fontSize: '14px', fontWeight: '600' }}
              >
                {editRegSaving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showSpecsModal && specsVehicle && (
        <div
          className="modal-overlay"
          onClick={closeSpecsModal}
          style={{ zIndex: 3200 }}
        >
          <div
            className="modal-content"
            onClick={e => e.stopPropagation()}
            style={{ maxWidth: 450, width: '90%', borderRadius: '12px' }}
          >
            <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid #e5e7eb' }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#111827' }}>
                Edit Specs: V-{specsVehicle.vehicle_id}
              </h3>
              <button
                onClick={closeSpecsModal}
                style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#9ca3af', lineHeight: 1 }}
              >
                &times;
              </button>
            </div>

            <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {specsError && (
                <div style={{ color: '#dc2626', background: '#fee2e2', padding: '10px 14px', borderRadius: '6px', fontSize: '13px' }}>
                  {specsError}
                </div>
              )}

              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Per KM Price (₹)</label>
                <input
                  type="number"
                  name="per_km_price"
                  value={specsFormData.per_km_price}
                  onChange={handleSpecsFormChange}
                  placeholder="e.g. 15.50"
                  step="0.01"
                  style={{ width: '100%', padding: '10px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Body Type</label>
                  <select
                    name="body_type"
                    value={selectedBodyType(specsFormData.body_type)}
                    onChange={handleSpecsFormChange}
                    style={{ width: '100%', padding: '10px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box', background: '#fff' }}
                  >
                    <option value="">Select body type</option>
                    {selectedBodyType(specsFormData.body_type)
                      && !BODY_TYPE_OPTIONS.includes(selectedBodyType(specsFormData.body_type)) && (
                        <option value={selectedBodyType(specsFormData.body_type)}>
                          {selectedBodyType(specsFormData.body_type)}
                        </option>
                      )}
                    {BODY_TYPE_OPTIONS.map((option) => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Tonnage (ton)</label>
                  <input
                    type="number"
                    step="0.01"
                    name="tonnage"
                    value={specsFormData.tonnage}
                    onChange={handleSpecsFormChange}
                    placeholder="e.g. 1.5"
                    style={{ width: '100%', padding: '10px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }}
                  />
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Phone Extensions / Alternate Contacts</label>

                {specsFormData.phone_extensions && specsFormData.phone_extensions.length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', maxHeight: '100px', overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '8px 12px', backgroundColor: '#f9fafb' }}>
                    {specsFormData.phone_extensions.map((entry, idx) => {
                      const phone = extPhoneOf(entry);
                      const label = extLabelOf(entry);
                      return (
                      <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '13px', color: '#374151', padding: '2px 0' }}>
                        <span>📞 {phone}{label ? ` — ${label}` : ''}</span>
                        <button
                          type="button"
                          onClick={() => handleRemovePhoneExtension(phone)}
                          style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: '16px', fontWeight: 'bold', padding: '0 4px' }}
                        >
                          &times;
                        </button>
                      </div>
                      );
                    })}
                  </div>
                ) : (
                  <div style={{ fontSize: '12px', color: '#9ca3af', fontStyle: 'italic', padding: '4px' }}>No alternate phone numbers added.</div>
                )}

                <div style={{ display: 'flex', gap: '8px' }}>
                  <input
                    type="text"
                    value={newExtPhone}
                    onChange={(e) => setNewExtPhone(e.target.value)}
                    placeholder="e.g. 919900000000"
                    style={{ flex: 1, padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '13px' }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddPhoneExtension();
                      }
                    }}
                  />
                  <input
                    type="text"
                    value={newExtLabel}
                    onChange={(e) => setNewExtLabel(e.target.value)}
                    placeholder="Label (optional) e.g. gpay no"
                    style={{ flex: 1, padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '13px' }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddPhoneExtension();
                      }
                    }}
                  />
                  <button
                    type="button"
                    onClick={handleAddPhoneExtension}
                    style={{ padding: '8px 14px', backgroundColor: '#6366f1', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' }}
                  >
                    Add
                  </button>
                </div>
              </div>

              <div style={{ backgroundColor: '#f9fafb', padding: '12px 16px', borderRadius: '8px', border: '1px solid #f3f4f6' }}>
                <label style={{ fontSize: '13px', fontWeight: '700', color: '#374151', marginBottom: '10px', display: 'block' }}>Trip Preferences</label>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 16px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#374151', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      name="intra_state_permit"
                      checked={!!specsFormData.intra_state_permit}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setSpecsFormData((prev) => ({
                          ...prev,
                          intra_state_permit: checked,
                          ready_for_long_trip: checked ? true : prev.ready_for_long_trip,
                          long_inter_state: checked ? true : prev.long_inter_state,
                        }));
                      }}
                      style={{ width: '14px', height: '14px', cursor: 'pointer' }}
                    />
                    All India permit
                  </label>

                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#374151', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      name="ready_for_long_trip"
                      checked={specsFormData.ready_for_long_trip}
                      onChange={handleSpecsFormChange}
                      style={{ width: '14px', height: '14px', cursor: 'pointer' }}
                    />
                    Ready for Long Trip
                  </label>

                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#374151', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      name="long_inter_district"
                      checked={specsFormData.long_inter_district}
                      onChange={handleSpecsFormChange}
                      style={{ width: '14px', height: '14px', cursor: 'pointer' }}
                    />
                    Long Intra-District
                  </label>

                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#374151', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      name="long_inter_state"
                      checked={specsFormData.long_inter_state}
                      onChange={handleSpecsFormChange}
                      style={{ width: '14px', height: '14px', cursor: 'pointer' }}
                    />
                    Long Intra-State
                  </label>

                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#374151', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      name="local"
                      checked={specsFormData.local}
                      onChange={handleSpecsFormChange}
                      style={{ width: '14px', height: '14px', cursor: 'pointer' }}
                    />
                    Local
                  </label>
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '10px', paddingTop: '12px', borderTop: '1px solid #e5e7eb' }}>
                <button
                  onClick={closeSpecsModal}
                  style={{ padding: '8px 16px', border: '1px solid #d1d5db', background: '#fff', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: 600, color: '#374151' }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveSpecs}
                  disabled={isSavingSpecs}
                  style={{ padding: '8px 16px', border: 'none', background: isSavingSpecs ? '#93c5fd' : '#10b981', color: '#fff', borderRadius: '8px', cursor: isSavingSpecs ? 'not-allowed' : 'pointer', fontSize: '14px', fontWeight: '600' }}
                >
                  {isSavingSpecs ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Driver Call Modal */}
      {driverCallModal.open && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.65)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 99999,
          }}
          onClick={() => setDriverCallModal(prev => ({ ...prev, open: false }))}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: '#ffffff',
              borderRadius: '20px',
              padding: '28px 26px',
              width: '420px',
              maxWidth: '92vw',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
              position: 'relative',
              textAlign: 'center',
              fontFamily: 'inherit'
            }}
          >
            {/* Close Button */}
            <button
              onClick={() => setDriverCallModal(prev => ({ ...prev, open: false }))}
              style={{
                position: 'absolute',
                top: 14,
                right: 14,
                width: 32,
                height: 32,
                border: 'none',
                background: '#f1f5f9',
                borderRadius: '8px',
                cursor: 'pointer',
                color: '#64748b',
                fontSize: '18px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              &times;
            </button>

            {/* Icon */}
            <div
              style={{
                width: '64px',
                height: '64px',
                borderRadius: '50%',
                background: driverCallModal.status === 'success'
                  ? '#ecfdf5'
                  : driverCallModal.status === 'error'
                    ? '#fef2f2'
                    : '#eff6ff',
                color: driverCallModal.status === 'success'
                  ? '#059669'
                  : driverCallModal.status === 'error'
                    ? '#dc2626'
                    : '#2563eb',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px',
                fontSize: '26px'
              }}
            >
              {driverCallModal.status === 'calling' ? (
                <i className="fa fa-spinner fa-spin" />
              ) : driverCallModal.status === 'success' ? (
                '✓'
              ) : driverCallModal.status === 'error' ? (
                '!'
              ) : (
                '📞'
              )}
            </div>

            {/* Title */}
            <h3 style={{ margin: '0 0 6px', fontSize: '20px', fontWeight: '700', color: '#0f172a' }}>
              {driverCallModal.status === 'calling'
                ? 'Initiating Call...'
                : driverCallModal.status === 'success'
                  ? 'Call Dispatched!'
                  : driverCallModal.status === 'error'
                    ? 'Call Failed'
                    : 'Initiate Driver Call'}
            </h3>

            {/* Driver details */}
            <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '12px', margin: '14px 0' }}>
              <div style={{ fontSize: '16px', fontWeight: '700', color: '#1e293b' }}>
                📞 {driverCallModal.driverPhone}
              </div>
              {driverCallModal.driverName && (
                <div style={{ fontSize: '13px', color: '#64748b', marginTop: '4px' }}>
                  Driver: {driverCallModal.driverName}
                </div>
              )}
            </div>

            {/* SIM Selection */}
            {driverCallModal.status === 'idle' && (
              <div style={{ textAlign: 'left', margin: '14px 0 20px' }}>
                <div style={{ fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' }}>
                  Choose Outbound Office Phone (SIM):
                </div>
                {driverCallModal.isLoading ? (
                  <div style={{ fontSize: '13px', color: '#64748b', padding: '8px 0' }}>
                    <i className="fa fa-spinner fa-spin" /> Checking connected phones...
                  </div>
                ) : driverCallModal.onlineSims.length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {driverCallModal.onlineSims.map((sim, idx) => {
                      const isSelected = driverCallModal.selectedSim === sim.sim_number;
                      return (
                        <div
                          key={sim.sim_number}
                          onClick={() => setDriverCallModal(prev => ({ ...prev, selectedSim: sim.sim_number }))}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            padding: '10px 14px',
                            borderRadius: '10px',
                            border: isSelected ? '2px solid #2563eb' : '1.5px solid #cbd5e1',
                            background: isSelected ? '#eff6ff' : '#ffffff',
                            cursor: 'pointer',
                            transition: 'all 0.15s ease'
                          }}
                        >
                          <div
                            style={{
                              width: '16px',
                              height: '16px',
                              borderRadius: '50%',
                              border: isSelected ? '2px solid #2563eb' : '1.5px solid #94a3b8',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            {isSelected && (
                              <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#2563eb' }} />
                            )}
                          </div>
                          <div style={{ flex: 1 }}>
                            <span style={{ fontWeight: '700', color: '#334155', fontSize: '13px' }}>
                              Phone {idx + 1}:
                            </span>{' '}
                            <span style={{ fontWeight: '500', color: '#0f172a', fontSize: '14px' }}>
                              {sim.sim_number}
                            </span>
                          </div>
                          <span
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                              fontSize: '11px',
                              fontWeight: '600',
                              color: '#059669',
                              background: '#ecfdf5',
                              padding: '2px 8px',
                              borderRadius: '12px'
                            }}
                          >
                            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#10b981' }} />
                            Online
                          </span>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div style={{ fontSize: '12px', color: '#b45309', background: '#fffbeb', padding: '8px 12px', borderRadius: '8px', border: '1px solid #fde68a' }}>
                    ⚠️ No Driver SIM phone is currently detected online. (App will attempt fallback).
                  </div>
                )}
              </div>
            )}

            {/* Error state */}
            {driverCallModal.status === 'error' && (
              <div style={{ color: '#dc2626', background: '#fef2f2', padding: '10px 14px', borderRadius: '8px', fontSize: '13px', margin: '12px 0' }}>
                {driverCallModal.errorMsg}
              </div>
            )}

            {/* Success state */}
            {driverCallModal.status === 'success' && (
              <div style={{ color: '#059669', background: '#ecfdf5', padding: '10px 14px', borderRadius: '8px', fontSize: '13px', margin: '12px 0', fontWeight: '600' }}>
                Dial command sent successfully! Calling driver now...
              </div>
            )}

            {/* Action Buttons */}
            {driverCallModal.status === 'idle' && (
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', marginTop: '12px' }}>
                <button
                  type="button"
                  onClick={() => setDriverCallModal(prev => ({ ...prev, open: false }))}
                  style={{
                    padding: '10px 22px',
                    borderRadius: '10px',
                    border: '1px solid #cbd5e1',
                    background: '#ffffff',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#475569',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleConfirmDriverCall}
                  style={{
                    padding: '10px 28px',
                    borderRadius: '10px',
                    border: 'none',
                    background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                    fontSize: '14px',
                    fontWeight: '700',
                    color: '#ffffff',
                    cursor: 'pointer',
                    boxShadow: '0 4px 12px rgba(37, 99, 235, 0.3)'
                  }}
                >
                  📞 Place Call
                </button>
              </div>
            )}

            {driverCallModal.status === 'error' && (
              <div style={{ display: 'flex', justifyContent: 'center', marginTop: '12px' }}>
                <button
                  type="button"
                  onClick={() => setDriverCallModal(prev => ({ ...prev, open: false }))}
                  style={{
                    padding: '10px 24px',
                    borderRadius: '10px',
                    border: '1px solid #cbd5e1',
                    background: '#ffffff',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#475569',
                    cursor: 'pointer'
                  }}
                >
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Transporter Details Modal */}
      <TransporterDetailsModal
        isOpen={transporterModalOpen}
        transporterId={selectedTransporterId}
        transporterPhone={selectedTransporterPhone}
        onClose={() => {
          setTransporterModalOpen(false);
          setSelectedTransporterId(null);
          setSelectedTransporterPhone(null);
        }}
        onSaved={() => {
          if (fetchVehiclesRef.current) fetchVehiclesRef.current(false);
        }}
      />
    </div>
  );
};

export default VehicleDetails;
